import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import {
  FiTrendingUp,
  FiTarget,
  FiDollarSign,
  FiShoppingBag,
  FiUsers,
  FiCalendar,
  FiClock,
  FiAlertCircle,
  FiRefreshCw,
  FiBarChart2,
  FiAward,
  FiLayers,
  FiZap,
  FiHelpCircle,
  FiCheckSquare,
  FiX,
  FiChevronDown,
  FiPackage,
  FiChevronLeft,
  FiChevronRight,
  FiMaximize2,
  FiMinimize2,
  FiInfo,
  FiCopy,
  FiCheck,
  FiCpu,
  FiSliders,
  FiCode,
  FiActivity,
  FiCheckCircle,
  FiTerminal,
  FiBriefcase,
  FiMapPin,
  FiSearch
} from 'react-icons/fi';
import {
  ResponsiveContainer,
  AreaChart,
  Area,
  BarChart,
  Bar,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ComposedChart,
  ReferenceLine,
  Cell
} from 'recharts';
import { marked } from 'marked';
import { faturamentoService } from '../../services/api';
import Loading from '../common/Loading';
import './BIMetas.css';

// Formatador de Moeda
const formatBRL = (val) => {
  return new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: 'BRL',
    minimumFractionDigits: 2
  }).format(Number(val || 0));
};

// Formatador de Data PT-BR
const formatDateBR = (dateStr) => {
  if (!dateStr) return '';
  try {
    const clean = String(dateStr).split('T')[0].split(' ')[0];
    const parts = clean.split('-');
    if (parts.length === 3) {
      return `${parts[2]}/${parts[1]}/${parts[0]}`;
    }
    const d = new Date(dateStr);
    return isNaN(d.getTime()) ? dateStr : d.toLocaleDateString('pt-BR');
  } catch (_) {
    return dateStr;
  }
};

// Formatador de Data e Hora PT-BR
const formatDateTimeBR = (dtStr) => {
  if (!dtStr) return '';
  try {
    const d = new Date(dtStr);
    if (isNaN(d.getTime())) return dtStr;
    return d.toLocaleDateString('pt-BR') + ' às ' + d.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' });
  } catch (_) {
    return dtStr;
  }
};

// Cópia universal para Área de Transferência (Compatível com Localhost, HTTPS e Redes Locais HTTP)
const copyToClipboard = (text, onSuccess) => {
  if (!text) return;

  // 1. Tentar API moderna navigator.clipboard (se em HTTPS ou localhost)
  if (typeof navigator !== 'undefined' && navigator.clipboard && typeof navigator.clipboard.writeText === 'function') {
    navigator.clipboard.writeText(text)
      .then(() => {
        if (onSuccess) onSuccess();
      })
      .catch(() => {
        fallbackCopy(text, onSuccess);
      });
    return;
  }

  // 2. Fallback via document.execCommand para redes locais HTTP (ex: 192.168.x.x)
  fallbackCopy(text, onSuccess);
};

const fallbackCopy = (text, onSuccess) => {
  try {
    const textArea = document.createElement('textarea');
    textArea.value = text;
    textArea.style.position = 'fixed';
    textArea.style.top = '0';
    textArea.style.left = '0';
    textArea.style.width = '2em';
    textArea.style.height = '2em';
    textArea.style.padding = '0';
    textArea.style.border = 'none';
    textArea.style.outline = 'none';
    textArea.style.boxShadow = 'none';
    textArea.style.background = 'transparent';
    textArea.style.opacity = '0';
    textArea.setAttribute('readonly', '');
    document.body.appendChild(textArea);
    textArea.focus();
    textArea.select();
    textArea.setSelectionRange(0, 99999);
    const successful = document.execCommand('copy');
    document.body.removeChild(textArea);
    if (successful && onSuccess) {
      onSuccess();
    }
  } catch (err) {
    console.error('Falha ao copiar texto:', err);
  }
};

// Tooltip Customizado para os Gráficos
const CustomChartTooltip = ({ active, payload, label }) => {
  if (active && payload && payload.length) {
    return (
      <div className="bi-custom-tooltip">
        <div className="bi-custom-tooltip-title">{label}</div>
        {payload.map((item, idx) => {
          const nameLower = String(item.name || '').toLowerCase();
          const dataKey = String(item.dataKey || '').toLowerCase();
          let formattedValue = item.value;
          if (item.value === null || item.value === undefined) {
            formattedValue = '-';
          } else if (
            item.unit === 'cli' ||
            dataKey === 'clients' ||
            nameLower.includes('cliente')
          ) {
            const num = Number(item.value || 0);
            formattedValue = `${num.toLocaleString('pt-BR', { minimumFractionDigits: Number.isInteger(num) ? 0 : 2, maximumFractionDigits: 2 })} cli`;
          } else if (
            item.unit === 'un' ||
            dataKey === 'pa' ||
            dataKey === 'items' ||
            dataKey === 'item' ||
            nameLower.includes('itens') ||
            nameLower.includes('peça') ||
            nameLower.includes('peca') ||
            nameLower.includes('(pa)') ||
            /\bpa\b/.test(nameLower)
          ) {
            const num = Number(item.value || 0);
            formattedValue = `${num.toLocaleString('pt-BR', { minimumFractionDigits: Number.isInteger(num) ? 0 : 2, maximumFractionDigits: 2 })} un`;
          } else if (
            item.unit === 'ped' ||
            dataKey === 'orders' ||
            nameLower.includes('pedido') ||
            nameLower.includes('cupom') ||
            nameLower.includes('atendimento')
          ) {
            formattedValue = `${Number(item.value || 0).toLocaleString('pt-BR')} ped`;
          } else if (
            item.unit === 'R$' ||
            nameLower.includes('ticket') ||
            nameLower.includes('realizado') ||
            nameLower.includes('meta') ||
            nameLower.includes('ritmo') ||
            nameLower.includes('faturamento') ||
            (nameLower.includes('venda') && !nameLower.includes('itens')) ||
            nameLower.includes('via')
          ) {
            formattedValue = formatBRL(item.value);
          }
          return (
            <div key={idx} className="bi-custom-tooltip-row">
              <span style={{ color: item.color || '#3b82f6', fontWeight: 600 }}>
                {item.name}:
              </span>
              <span style={{ fontWeight: 700 }}>
                {formattedValue}
              </span>
            </div>
          );
        })}
      </div>
    );
  }
  return null;
};

// Paleta de cores para diferenciação visual de metas nos cards e gráficos concatenados
const GOAL_COLORS = [
  '#2563eb', // Azul Royal
  '#10b981', // Esmeralda
  '#8b5cf6', // Violeta
  '#f59e0b', // Âmbar
  '#ec4899', // Rosa Pink
  '#06b6d4', // Ciano
  '#f97316', // Laranja
  '#14b8a6', // Teal
  '#6366f1', // Índigo
  '#ef4444'  // Vermelho Coral
];

const getRefComparison = (actual, ref) => {
  if (ref === null || ref === undefined || ref === '' || Number(ref) <= 0) return null;
  const numActual = Number(actual) || 0;
  const numRef = Number(ref);
  const pct = numRef > 0 ? (numActual / numRef) * 100 : 0;
  const isOk = numActual >= numRef;
  const isNear = !isOk && numActual >= numRef * 0.85;

  let status = 'success';
  let statusColor = '#16a34a';
  let bgColor = '#f0fdf4';
  let borderColor = '#86efac';
  let badgeText = `✓ No Alvo (${pct.toFixed(0)}%)`;

  if (!isOk) {
    if (isNear) {
      status = 'warning';
      statusColor = '#d97706';
      bgColor = '#fffbeb';
      borderColor = '#fcd34d';
      badgeText = `⚠️ Atenção (${pct.toFixed(0)}%)`;
    } else {
      status = 'danger';
      statusColor = '#dc2626';
      bgColor = '#fef2f2';
      borderColor = '#fca5a5';
      badgeText = `🔻 Abaixo (${pct.toFixed(0)}%)`;
    }
  }

  return {
    isOk,
    isNear,
    status,
    statusColor,
    bgColor,
    borderColor,
    badgeText,
    pct,
    numRef
  };
};

const getCollabIA = (collab) => {
  if (!collab) return 0;
  if (collab.pecas_por_atendimento !== undefined && collab.pecas_por_atendimento !== null) {
    return Number(collab.pecas_por_atendimento);
  }
  if (collab.daily_metrics && Object.keys(collab.daily_metrics).length > 0) {
    const dailyAvgs = Object.values(collab.daily_metrics)
      .filter(d => (d.orders || 0) > 0)
      .map(d => Number(d.items || 0) / Number(d.orders));
    if (dailyAvgs.length > 0) {
      return dailyAvgs.reduce((acc, v) => acc + v, 0) / dailyAvgs.length;
    }
  }
  const totalOrders = collab.orders_count ?? collab.orders ?? 0;
  const totalItems = collab.items_count ?? collab.items ?? 0;
  return totalOrders > 0 ? (totalItems / totalOrders) : 0;
};

const MONTHS_LIST = [
  { value: 1, label: '01 - Janeiro', short: 'Jan' },
  { value: 2, label: '02 - Fevereiro', short: 'Fev' },
  { value: 3, label: '03 - Março', short: 'Mar' },
  { value: 4, label: '04 - Abril', short: 'Abr' },
  { value: 5, label: '05 - Maio', short: 'Mai' },
  { value: 6, label: '06 - Junho', short: 'Jun' },
  { value: 7, label: '07 - Julho', short: 'Jul' },
  { value: 8, label: '08 - Agosto', short: 'Ago' },
  { value: 9, label: '09 - Setembro', short: 'Set' },
  { value: 10, label: '10 - Outubro', short: 'Out' },
  { value: 11, label: '11 - Novembro', short: 'Nov' },
  { value: 12, label: '12 - Dezembro', short: 'Dez' }
];

const CLASSIFICATIONS_LIST = [
  { value: 'vendedor', label: 'Vendedor Geral' },
  { value: 'bijou', label: 'Bijou / Acessórios' },
  { value: 'operador_caixa', label: 'Operador de Caixa' }
];

const STATUSES_LIST = [
  { value: 'batida', label: '🏆 Batida (≥ 100%)' },
  { value: 'no_ritmo', label: '⚡ No Ritmo (80–99%)' },
  { value: 'atencao', label: '⚠️ Atenção (50–79%)' },
  { value: 'critica', label: '🚨 Crítica (< 50%)' }
];

const TARGET_TYPES_LIST = [
  { value: 'vendor', label: 'Vendedores', icon: FiUsers },
  { value: 'employee', label: 'Funcionários', icon: FiBriefcase },
  { value: 'branch', label: 'Filiais', icon: FiMapPin },
  { value: 'general', label: 'Geral', icon: FiLayers }
];

const getTargetTypeBadge = (targetType) => {
  const type = targetType || 'vendor';
  switch (type) {
    case 'vendor':
      return (
        <span className="badge-pill-modern badge-target-vendor" title="Alvo: Vendedores">
          <FiUsers size={11} className="badge-ico" />
          <span>Vendedores</span>
        </span>
      );
    case 'employee':
      return (
        <span className="badge-pill-modern badge-target-employee" title="Alvo: Funcionários">
          <FiBriefcase size={11} className="badge-ico" />
          <span>Funcionários</span>
        </span>
      );
    case 'branch':
      return (
        <span className="badge-pill-modern badge-target-branch" title="Alvo: Filiais">
          <FiMapPin size={11} className="badge-ico" />
          <span>Filial</span>
        </span>
      );
    case 'general':
    default:
      return (
        <span className="badge-pill-modern badge-target-general" title="Alvo: Geral">
          <FiLayers size={11} className="badge-ico" />
          <span>Geral</span>
        </span>
      );
  }
};

const SUBTYPES_LIST = [
  { value: 'vendas', label: 'Vendas (Padrão)', icon: FiDollarSign },
  { value: 'produtos_destaque', label: 'Produtos Destaque', icon: FiPackage },
  { value: 'clientes_positivados', label: 'Clientes Positivados', icon: FiCheckCircle }
];

const isQuantitySubtype = (subtype) => {
  return subtype === 'produtos_destaque' || subtype === 'clientes_positivados';
};

const getGoalSubtypeBadge = (subtype) => {
  const type = subtype || 'vendas';
  switch (type) {
    case 'produtos_destaque':
      return (
        <span className="badge-pill-modern badge-subtype-destaque" title="Subtipo: Produtos Destaque (Unidades)">
          <FiPackage size={11} className="badge-ico" />
          <span>Destaque</span>
        </span>
      );
    case 'clientes_positivados':
      return (
        <span className="badge-pill-modern badge-subtype-positivados" title="Subtipo: Clientes Positivados (Clientes)">
          <FiCheckCircle size={11} className="badge-ico" />
          <span>Positivação</span>
        </span>
      );
    case 'vendas':
    default:
      return (
        <span className="badge-pill-modern badge-subtype-vendas" title="Subtipo: Vendas Padrão (R$)">
          <FiDollarSign size={11} className="badge-ico" />
          <span>Vendas</span>
        </span>
      );
  }
};

const getDefaultDatesForMonth = (targetYear, targetMonth) => {
  return getDefaultDatesForMonthsAndYears([targetYear], [targetMonth]);
};

const getDefaultDatesForMonthsAndYears = (targetYears, targetMonths) => {
  const years = Array.isArray(targetYears) && targetYears.length > 0 ? targetYears : [new Date().getFullYear()];
  const months = Array.isArray(targetMonths) && targetMonths.length > 0 ? targetMonths : [new Date().getMonth() + 1];

  const pairs = [];
  years.forEach(y => {
    months.forEach(m => {
      pairs.push({ year: Number(y), month: Number(m) });
    });
  });
  pairs.sort((a, b) => (a.year * 100 + a.month) - (b.year * 100 + b.month));
  const minP = pairs[0];
  const maxP = pairs[pairs.length - 1];

  const currentDate = new Date();
  const currYear = currentDate.getFullYear();
  const currMonth = currentDate.getMonth() + 1;
  const currDay = currentDate.getDate();

  const start = `${minP.year}-${String(minP.month).padStart(2, '0')}-01`;
  let end = '';
  if (maxP.year === currYear && maxP.month === currMonth) {
    const yMonth = String(currMonth).padStart(2, '0');
    const yDay = String(currDay).padStart(2, '0');
    end = `${currYear}-${yMonth}-${yDay}`;
  } else {
    const lastDay = new Date(maxP.year, maxP.month, 0).getDate();
    end = `${maxP.year}-${String(maxP.month).padStart(2, '0')}-${String(lastDay).padStart(2, '0')}`;
  }

  return { start, end };
};

const getFullDatesForMonthsAndYears = (targetYears, targetMonths) => {
  const years = Array.isArray(targetYears) && targetYears.length > 0 ? targetYears : [new Date().getFullYear()];
  const months = Array.isArray(targetMonths) && targetMonths.length > 0 ? targetMonths : [new Date().getMonth() + 1];

  const pairs = [];
  years.forEach(y => {
    months.forEach(m => {
      pairs.push({ year: Number(y), month: Number(m) });
    });
  });
  pairs.sort((a, b) => (a.year * 100 + a.month) - (b.year * 100 + b.month));
  const minP = pairs[0];
  const maxP = pairs[pairs.length - 1];

  const start = `${minP.year}-${String(minP.month).padStart(2, '0')}-01`;
  const lastDay = new Date(maxP.year, maxP.month, 0).getDate();
  const end = `${maxP.year}-${String(maxP.month).padStart(2, '0')}-${String(lastDay).padStart(2, '0')}`;

  return { start, end };
};

function BIMetas() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // Resumo de Filiais derivado de data
  const branchesSummary = data?.branches_summary || [];

  // Estados dos Filtros com suporte a Multi-Seleção
  const now = new Date();
  const [selectedMonths, setSelectedMonths] = useState([now.getMonth() + 1]);
  const [tempSelectedMonths, setTempSelectedMonths] = useState([now.getMonth() + 1]);

  const [selectedYears, setSelectedYears] = useState([now.getFullYear()]);
  const [tempSelectedYears, setTempSelectedYears] = useState([now.getFullYear()]);
  const [availableYears, setAvailableYears] = useState([now.getFullYear()]);

  const yearsList = useMemo(() => {
    if (Array.isArray(availableYears) && availableYears.length > 0) {
      return availableYears;
    }
    return [now.getFullYear()];
  }, [availableYears, now]);

  const initialDates = getDefaultDatesForMonthsAndYears([now.getFullYear()], [now.getMonth() + 1]);
  const [startDate, setStartDate] = useState(initialDates.start);
  const [endDate, setEndDate] = useState(initialDates.end);
  const [tempStartDate, setTempStartDate] = useState(initialDates.start);
  const [tempEndDate, setTempEndDate] = useState(initialDates.end);

  // Estados para aba de Movimentações de Pedidos no modal da meta
  const [goalOrdersData, setGoalOrdersData] = useState(null);
  const [goalOrdersLoading, setGoalOrdersLoading] = useState(false);
  const [goalOrdersError, setGoalOrdersError] = useState('');
  const [goalOrdersSearch, setGoalOrdersSearch] = useState('');
  const [goalOrdersSaleType, setGoalOrdersSaleType] = useState('all');
  const [goalOrdersPage, setGoalOrdersPage] = useState(1);

  const [branch, setBranch] = useState('all');
  const [granularity] = useState('day'); // 'day', 'week', 'month'

  const [selectedTargetTypes, setSelectedTargetTypes] = useState(['vendor', 'employee', 'branch', 'general']);
  const [tempSelectedTargetTypes, setTempSelectedTargetTypes] = useState(['vendor', 'employee', 'branch', 'general']);

  const [selectedClassifications, setSelectedClassifications] = useState(['vendedor', 'bijou', 'operador_caixa']);
  const [tempSelectedClassifications, setTempSelectedClassifications] = useState(['vendedor', 'bijou', 'operador_caixa']);

  const [selectedSubtypes, setSelectedSubtypes] = useState(['vendas', 'produtos_destaque', 'clientes_positivados']);
  const [tempSelectedSubtypes, setTempSelectedSubtypes] = useState(['vendas', 'produtos_destaque', 'clientes_positivados']);

  const [selectedStatuses, setSelectedStatuses] = useState(['batida', 'no_ritmo', 'atencao', 'critica']);
  const [tempSelectedStatuses, setTempSelectedStatuses] = useState(['batida', 'no_ritmo', 'atencao', 'critica']);

  const [sortField, setSortField] = useState('pct');
  const [sortOrder, setSortOrder] = useState('desc');
  const [sortBy, setSortBy] = useState('pct_desc'); // Sincronizado com o select de ordenação: 'pct_desc', 'realized_desc', 'ticket_desc', 'gap_desc'

  // Popover aberto: null, 'month', 'year', 'period', 'target_type', 'subtype', 'classification', 'status'
  const [openFilterPopover, setOpenFilterPopover] = useState(null);
  const toolbarFiltersRef = useRef(null);

  // Controle de visibilidade dos filtros secundários no mobile
  const [isMobileFiltersExpanded, setIsMobileFiltersExpanded] = useState(false);

  // Dropdown e Navegação de Filiais no Modal de Metas
  const branchNavDropdownRef = useRef(null);
  const [isBranchNavDropdownOpen, setIsBranchNavDropdownOpen] = useState(false);

  // Estados do Submodal de Busca e Seleção Múltipla de Metas
  const [selectedGoalIds, setSelectedGoalIds] = useState([]);
  const [tempSelectedGoalIds, setTempSelectedGoalIds] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [branchGoals, setBranchGoals] = useState([]);
  const searchDropdownRef = useRef(null);

  // Estados de Seleção na Tabela de Metas para Análise Concatenada
  const [selectedTableGoalIds, setSelectedTableGoalIds] = useState([]);
  const [isConcatenatedModalOpen, setIsConcatenatedModalOpen] = useState(false);
  const [isConcatenatedFullscreen, setIsConcatenatedFullscreen] = useState(false);
  const [concatenatedTab, setConcatenatedTab] = useState('overview'); // 'overview', 'comparison', 'charts', 'reports', 'ai_diagnosis'
  const [concatChartCategory, setConcatChartCategory] = useState('all'); // 'all', 'pace', 'ticket_items', 'weekday', 'multi_goals'
  const [concatTrajectoryMode, setConcatTrajectoryMode] = useState('accum'); // 'accum', 'daily', 'orders'
  const [concatChartTypes, setConcatChartTypes] = useState({
    progression: 'area', // 'area' | 'bar' | 'line'
    dailySales: 'bar',   // 'bar' | 'line' | 'area'
    ticketMedio: 'bar',  // 'bar' | 'line' | 'area'
    weekday: 'composed', // 'composed' | 'bar' | 'line' | 'area'
    salesTarget: 'bar'   // 'bar' | 'line' | 'area'
  });
  const [concatWeekdayMode, setConcatWeekdayMode] = useState('by_goal'); // 'by_goal' | 'consolidated'
  const [concatMatrixMode, setConcatMatrixMode] = useState('by_goal'); // 'by_goal' | 'by_period'
  const toggleConcatChartType = (key, type) => {
    setConcatChartTypes((prev) => ({ ...prev, [key]: type }));
  };
  const [concatCardsGridCols, setConcatCardsGridCols] = useState('3'); // '3', '4', '2'
  const [concatCardsSearch, setConcatCardsSearch] = useState('');

  // Modal de Detalhes da Vendedora / Carrossel
  const [selectedCollabIndex, setSelectedCollabIndex] = useState(null);
  const [isIndividualFullscreen, setIsIndividualFullscreen] = useState(false);
  const [modalTab, setModalTab] = useState('overview'); // 'overview', 'charts', 'recommendations', 'reports'
  const [modalChartCategory, setModalChartCategory] = useState('all'); // 'all', 'pace', 'ticket_items', 'weekday'
  const [modalRecCategory, setModalRecCategory] = useState('all'); // 'all', 'gap', 'ai', 'ticket', 'cross_sell'

  // Modos de Exibição dos Gráficos Analíticos (Barras, Linhas, Área, Misto)
  const [chartTypes, setChartTypes] = useState({
    progression: 'area', // 'area' | 'bar' | 'line'
    dailySales: 'bar',   // 'bar' | 'line' | 'area'
    ticketMedio: 'bar',  // 'bar' | 'line' | 'area'
    weekday: 'composed'  // 'composed' | 'bar' | 'line' | 'area'
  });

  const toggleChartType = (key, type) => {
    setChartTypes((prev) => ({ ...prev, [key]: type }));
  };
  const [aiGenerating, setAiGenerating] = useState(false);
  const [aiLiveReport, setAiLiveReport] = useState({});
  const [copiedPitch, setCopiedPitch] = useState(false);

  // Modal Interativo de Análise ao Vivo com IA, Ferramentas e Prompt Customizável
  const [isAIModalOpen, setIsAIModalOpen] = useState(false);
  const [aiModalTab, setAiModalTab] = useState('execution'); // 'execution', 'prompt'
  const [aiPromptText, setAiPromptText] = useState('');
  const [aiDefaultCompiledPrompt, setAiDefaultCompiledPrompt] = useState('');
  const [aiRawTemplate, setAiRawTemplate] = useState('');
  const [aiProviders, setAiProviders] = useState([]);
  const [aiSelectedProviderId, setAiSelectedProviderId] = useState('');
  const [aiExecutionStep, setAiExecutionStep] = useState(0); // 0, 1, 2, 3, 4
  const [aiStatusMessage, setAiStatusMessage] = useState('');
  const [aiToolsLog, setAiToolsLog] = useState([]);
  const [aiUpdatedParams, setAiUpdatedParams] = useState(null);
  const [aiExpandedToolId, setAiExpandedToolId] = useState(null);
  const [aiCopiedScriptModal, setAiCopiedScriptModal] = useState(false);
  const [aiCurrentReport, setAiCurrentReport] = useState('');
  const [aiProviderInfo, setAiProviderInfo] = useState(null);

  // Modal Interativo de Análise Executiva ao Vivo com IA para Metas Concatenadas
  const [isConcatAIModalOpen, setIsConcatAIModalOpen] = useState(false);
  const [concatAIModalTab, setConcatAIModalTab] = useState('execution'); // 'execution', 'prompt'
  const [concatAIPromptText, setConcatAIPromptText] = useState('');
  const [concatAIDefaultCompiledPrompt, setConcatAIDefaultCompiledPrompt] = useState('');
  const [concatAIRawTemplate, setConcatAIRawTemplate] = useState('');
  const [concatAIProviders, setConcatAIProviders] = useState([]);
  const [concatAISelectedProviderId, setConcatAISelectedProviderId] = useState('');
  const [concatAIExecutionStep, setConcatAIExecutionStep] = useState(0); // 0, 1, 2, 3, 4
  const [concatAIGenerating, setConcatAIGenerating] = useState(false);
  const [concatAIStatusMessage, setConcatAIStatusMessage] = useState('');
  const [concatAIToolsLog, setConcatAIToolsLog] = useState([]);
  const [concatAIUpdatedParams, setConcatAIUpdatedParams] = useState(null);
  const [concatAIExpandedToolId, setConcatAIExpandedToolId] = useState(null);
  const [concatAICopiedPitch, setConcatAICopiedPitch] = useState(false);
  const [concatAICopiedReport, setConcatAICopiedReport] = useState(false);
  const [concatAIReport, setConcatAIReport] = useState('');
  const [concatAIProviderInfo, setConcatAIProviderInfo] = useState(null);

  // Simulador de Produtos
  const [simProduct, setSimProduct] = useState(null);
  const [simQuantity, setSimQuantity] = useState(5);

  // Fechar dropdowns e popovers ao clicar fora
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (searchDropdownRef.current && !searchDropdownRef.current.contains(event.target)) {
        setIsDropdownOpen(false);
      }
      if (toolbarFiltersRef.current && !toolbarFiltersRef.current.contains(event.target)) {
        setOpenFilterPopover(null);
      }
      if (branchNavDropdownRef.current && !branchNavDropdownRef.current.contains(event.target)) {
        setIsBranchNavDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('touchstart', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('touchstart', handleClickOutside);
    };
  }, []);

  // Resetar seleção ao mudar Filial
  const handleBranchChange = (newBranch) => {
    setBranch(newBranch);
    setSelectedGoalIds([]);
    setTempSelectedGoalIds([]);
    setSelectedTableGoalIds([]);
    setSearchQuery('');
  };

  // Aplicação de Mês Referência Multi-Seleção
  const handleApplyMonths = (newMonths) => {
    const validMonths = newMonths.length > 0 ? newMonths : [now.getMonth() + 1];
    setSelectedMonths(validMonths);
    const newDates = getDefaultDatesForMonthsAndYears(selectedYears, validMonths);
    setStartDate(newDates.start);
    setEndDate(newDates.end);
    setTempStartDate(newDates.start);
    setTempEndDate(newDates.end);
    setOpenFilterPopover(null);
    setSelectedGoalIds([]);
    setTempSelectedGoalIds([]);
    setSelectedTableGoalIds([]);
  };

  // Carregar Anos Disponíveis do Banco de Dados
  const loadAvailableYears = useCallback(async () => {
    try {
      const res = await faturamentoService.getBIYears();
      const yrList = Array.isArray(res?.data?.years) && res.data.years.length > 0
        ? res.data.years
        : (Array.isArray(res?.years) && res.years.length > 0 ? res.years : []);
      if (yrList.length > 0) {
        setAvailableYears(yrList);
        setSelectedYears(prev => {
          const valid = prev.filter(y => yrList.includes(Number(y)));
          return valid.length > 0 ? valid : [yrList[0]];
        });
        setTempSelectedYears(prev => {
          const valid = prev.filter(y => yrList.includes(Number(y)));
          return valid.length > 0 ? valid : [yrList[0]];
        });
      }
    } catch (err) {
      console.error('Erro ao carregar anos disponíveis do BI:', err);
    }
  }, []);

  useEffect(() => {
    loadAvailableYears();
  }, [loadAvailableYears]);

  // Aplicação de Ano Multi-Seleção
  const handleApplyYears = (newYears) => {
    const validYears = newYears.length > 0 ? newYears : (yearsList.length > 0 ? [yearsList[0]] : [now.getFullYear()]);
    setSelectedYears(validYears);
    const newDates = getDefaultDatesForMonthsAndYears(validYears, selectedMonths);
    setStartDate(newDates.start);
    setEndDate(newDates.end);
    setTempStartDate(newDates.start);
    setTempEndDate(newDates.end);
    setOpenFilterPopover(null);
    setSelectedGoalIds([]);
    setTempSelectedGoalIds([]);
    setSelectedTableGoalIds([]);
  };

  // Aplicação de Tipo de Alvo Multi-Seleção
  const handleApplyTargetTypes = (newTypes) => {
    const valid = newTypes.length > 0 ? newTypes : TARGET_TYPES_LIST.map(t => t.value);
    setSelectedTargetTypes(valid);
    setOpenFilterPopover(null);
    setSelectedGoalIds([]);
    setTempSelectedGoalIds([]);
    setSelectedTableGoalIds([]);
  };

  // Aplicação de Classificação Multi-Seleção
  const handleApplyClassifications = (newClasses) => {
    const valid = newClasses.length > 0 ? newClasses : CLASSIFICATIONS_LIST.map(c => c.value);
    setSelectedClassifications(valid);
    setOpenFilterPopover(null);
    setSelectedGoalIds([]);
    setTempSelectedGoalIds([]);
    setSelectedTableGoalIds([]);
  };

  // Aplicação de Subtipo Multi-Seleção
  const handleApplySubtypes = (newSubtypes) => {
    const valid = newSubtypes.length > 0 ? newSubtypes : SUBTYPES_LIST.map(s => s.value);
    setSelectedSubtypes(valid);
    setOpenFilterPopover(null);
    setSelectedGoalIds([]);
    setTempSelectedGoalIds([]);
    setSelectedTableGoalIds([]);
  };

  // Aplicação de Status Multi-Seleção
  const handleApplyStatuses = (newStatuses) => {
    const valid = newStatuses.length > 0 ? newStatuses : STATUSES_LIST.map(s => s.value);
    setSelectedStatuses(valid);
    setOpenFilterPopover(null);
  };

  // Aplicação de Intervalo Customizado de Período
  const handleApplyPeriod = (sDate, eDate) => {
    let s = sDate || tempStartDate || startDate;
    let e = eDate || tempEndDate || endDate;
    if (s > e) e = s;
    setStartDate(s);
    setEndDate(e);
    setTempStartDate(s);
    setTempEndDate(e);
    setOpenFilterPopover(null);
    setSelectedGoalIds([]);
    setTempSelectedGoalIds([]);
    setSelectedTableGoalIds([]);
  };

  // Quantidade de filtros secundários ativos (diferentes do padrão)
  const activeSecondaryFiltersCount = useMemo(() => {
    let count = 0;
    if (selectedTargetTypes.length < TARGET_TYPES_LIST.length) count++;
    if (selectedClassifications.length < CLASSIFICATIONS_LIST.length) count++;
    if (selectedSubtypes.length < SUBTYPES_LIST.length) count++;
    if (selectedStatuses.length < STATUSES_LIST.length) count++;
    const defDates = getFullDatesForMonthsAndYears(selectedYears, selectedMonths);
    if (startDate !== defDates.start || endDate !== defDates.end) count++;
    return count;
  }, [selectedTargetTypes, selectedClassifications, selectedSubtypes, selectedStatuses, startDate, endDate, selectedYears, selectedMonths]);

  // Resetar apenas filtros secundários mantendo Mês e Ano
  const handleResetSecondaryFilters = () => {
    setSelectedTargetTypes(TARGET_TYPES_LIST.map(t => t.value));
    setSelectedClassifications(CLASSIFICATIONS_LIST.map(c => c.value));
    setSelectedSubtypes(SUBTYPES_LIST.map(s => s.value));
    setSelectedStatuses(STATUSES_LIST.map(s => s.value));
    const full = getFullDatesForMonthsAndYears(selectedYears, selectedMonths);
    setStartDate(full.start);
    setEndDate(full.end);
    setTempStartDate(full.start);
    setTempEndDate(full.end);
    setSelectedGoalIds([]);
    setTempSelectedGoalIds([]);
    setSelectedTableGoalIds([]);
  };

  // Carregar movimentações dos pedidos de uma meta
  const loadGoalOrders = useCallback(async (goalId, page = 1) => {
    if (!goalId) return;
    setGoalOrdersLoading(true);
    setGoalOrdersError('');
    try {
      const res = await faturamentoService.getBIGoalOrders({
        goal_id: goalId,
        start_date: startDate,
        end_date: endDate,
        sale_type: goalOrdersSaleType,
        search: goalOrdersSearch,
        page,
        per_page: 50
      });
      if (res?.data) {
        setGoalOrdersData(res.data);
        setGoalOrdersPage(page);
      }
    } catch (err) {
      console.error('Erro ao carregar movimentações dos pedidos da meta:', err);
      setGoalOrdersError(err.response?.data?.error || err.message || 'Erro ao carregar pedidos da meta');
    } finally {
      setGoalOrdersLoading(false);
    }
  }, [startDate, endDate, goalOrdersSaleType, goalOrdersSearch]);

  // Carregar lista de todas as metas da filial selecionada (sempre preserva todas)
  const loadBranchGoals = useCallback(async () => {
    try {
      const isAllClass = selectedClassifications.length === 0 || selectedClassifications.length === CLASSIFICATIONS_LIST.length;
      const isAllTargetTypes = selectedTargetTypes.length === 0 || selectedTargetTypes.length === TARGET_TYPES_LIST.length;
      const isAllSubtypes = selectedSubtypes.length === 0 || selectedSubtypes.length === SUBTYPES_LIST.length;
      const params = {
        years: selectedYears.join(','),
        year: selectedYears[0] || now.getFullYear(),
        months: selectedMonths.join(','),
        month: selectedMonths[0] || (now.getMonth() + 1),
        start_date: startDate,
        end_date: endDate,
        branch: branch !== 'all' ? branch : '',
        goal_classifications: !isAllClass ? selectedClassifications.join(',') : '',
        goal_classification: !isAllClass ? selectedClassifications.join(',') : '',
        target_types: !isAllTargetTypes ? selectedTargetTypes.join(',') : '',
        target_type: !isAllTargetTypes ? selectedTargetTypes.join(',') : '',
        goal_subtypes: !isAllSubtypes ? selectedSubtypes.join(',') : '',
        goal_subtype: !isAllSubtypes ? selectedSubtypes.join(',') : ''
      };
      const res = await faturamentoService.getBIGoals(params);
      const goals = res?.data?.goals || res?.goals;
      if (goals && Array.isArray(goals)) {
        setBranchGoals(goals);
      }
    } catch (err) {
      console.warn('Não foi possível carregar metas da filial via getBIGoals:', err);
    }
  }, [selectedYears, selectedMonths, startDate, endDate, branch, selectedClassifications, selectedTargetTypes, selectedSubtypes]);

  useEffect(() => {
    loadBranchGoals();
  }, [loadBranchGoals]);

  // Atualizar branchGoals com available_goals retornado pela API principal do BI
  useEffect(() => {
    if (data?.available_goals && Array.isArray(data.available_goals) && data.available_goals.length > 0) {
      setBranchGoals(data.available_goals);
    }
  }, [data?.available_goals]);

  // Carregar Dados do BI
  const loadBIMetas = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const isAllClass = selectedClassifications.length === 0 || selectedClassifications.length === CLASSIFICATIONS_LIST.length;
      const isAllStatus = selectedStatuses.length === 0 || selectedStatuses.length === STATUSES_LIST.length;
      const isAllTargetTypes = selectedTargetTypes.length === 0 || selectedTargetTypes.length === TARGET_TYPES_LIST.length;
      const isAllSubtypes = selectedSubtypes.length === 0 || selectedSubtypes.length === SUBTYPES_LIST.length;

      const params = {
        years: selectedYears.join(','),
        year: selectedYears[0] || now.getFullYear(),
        months: selectedMonths.join(','),
        month: selectedMonths[0] || (now.getMonth() + 1),
        start_date: startDate,
        end_date: endDate,
        branch: branch !== 'all' ? branch : '',
        granularity,
        goal_classifications: !isAllClass ? selectedClassifications.join(',') : '',
        goal_classification: !isAllClass ? selectedClassifications.join(',') : '',
        target_types: !isAllTargetTypes ? selectedTargetTypes.join(',') : '',
        target_type: !isAllTargetTypes ? selectedTargetTypes.join(',') : '',
        goal_subtypes: !isAllSubtypes ? selectedSubtypes.join(',') : '',
        goal_subtype: !isAllSubtypes ? selectedSubtypes.join(',') : '',
        statuses: !isAllStatus ? selectedStatuses.join(',') : '',
        status: !isAllStatus ? selectedStatuses.join(',') : '',
        goal_ids: selectedGoalIds.length > 0 ? selectedGoalIds.join(',') : ''
      };
      const res = await faturamentoService.getBIMetas(params);
      setData(res?.data || null);
      if (res?.data?.available_years && Array.isArray(res.data.available_years) && res.data.available_years.length > 0) {
        setAvailableYears(res.data.available_years);
      }
    } catch (err) {
      console.error('Erro ao carregar BI de Metas:', err);
      setError(err.response?.data?.error || err.message || 'Erro ao carregar dados do BI');
    } finally {
      setLoading(false);
    }
  }, [selectedYears, selectedMonths, startDate, endDate, branch, granularity, selectedClassifications, selectedStatuses, selectedTargetTypes, selectedSubtypes, selectedGoalIds]);

  useEffect(() => {
    loadBIMetas();
  }, [loadBIMetas]);

  // Lista de Metas Disponíveis para o Dropdown (sempre todas da filial/período)
  const availableGoals = useMemo(() => {
    if (data?.available_goals && Array.isArray(data.available_goals) && data.available_goals.length > 0) {
      return data.available_goals;
    }
    if (branchGoals && Array.isArray(branchGoals) && branchGoals.length > 0) {
      return branchGoals;
    }
    if (data?.collaborators && Array.isArray(data.collaborators)) {
      return data.collaborators.map(c => ({
        id: c.goal_id,
        goal_id: c.goal_id,
        name: c.name,
        code: c.code,
        target: c.target,
        branch_code: c.branch_code,
        branch_name: c.branch_name,
        goal_classification: c.goal_classification,
        target_type: c.target_type,
        goal_subtype: c.goal_subtype,
        is_quantity_goal: c.is_quantity_goal,
        target_quantity: c.target_quantity,
        unit_value: c.unit_value,
        bonus_amount: c.bonus_amount,
        preset_id: c.preset_id,
        preset_name: c.preset_name,
        type: c.type,
        is_branch_goal: c.is_branch_goal,
        is_general: c.is_general,
        vendor_name: c.name,
        codusur: c.code
      }));
    }
    return [];
  }, [data?.available_goals, branchGoals, data?.collaborators]);

  // Meta individual selecionada (se houver apenas 1) para exibição legível no input
  const singleSelectedGoal = useMemo(() => {
    if (selectedGoalIds.length === 1) {
      return availableGoals.find(g => g.id === selectedGoalIds[0]) || null;
    }
    return null;
  }, [selectedGoalIds, availableGoals]);

  // Placeholder informativo do campo de busca
  const inputPlaceholder = useMemo(() => {
    if (searchQuery) return searchQuery;
    if (singleSelectedGoal) {
      const bText = singleSelectedGoal.branch_code ? `Filial ${singleSelectedGoal.branch_code}` : 'Geral';
      return `${singleSelectedGoal.name} (${bText})`;
    }
    if (selectedGoalIds.length > 1) {
      return `${selectedGoalIds.length} metas selecionadas`;
    }
    return 'Buscar Meta / Colaborador...';
  }, [searchQuery, singleSelectedGoal, selectedGoalIds]);

  // Metas filtradas em tempo real pela busca do usuário no dropdown
  const filteredGoals = useMemo(() => {
    if (!searchQuery.trim()) return availableGoals;
    const q = searchQuery.toLowerCase().trim();
    return availableGoals.filter(g => {
      const name = String(g.name || '').toLowerCase();
      const code = String(g.code || '').toLowerCase();
      const vendorName = String(g.vendor_name || '').toLowerCase();
      const codusur = String(g.codusur || '').toLowerCase();
      const empName = String(g.employee_name || '').toLowerCase();
      const matr = String(g.matricula || '').toLowerCase();
      const bCode = String(g.branch_code || '').toLowerCase();
      const bName = String(g.branch_name || '').toLowerCase();
      const classif = String(g.goal_classification || '').toLowerCase();
      const targetType = String(g.target_type || '').toLowerCase();
      const presetName = String(g.preset_name || '').toLowerCase();

      return name.includes(q) ||
        code.includes(q) ||
        vendorName.includes(q) ||
        codusur.includes(q) ||
        empName.includes(q) ||
        matr.includes(q) ||
        bCode.includes(q) ||
        bName.includes(q) ||
        classif.includes(q) ||
        targetType.includes(q) ||
        presetName.includes(q);
    });
  }, [availableGoals, searchQuery]);

  // Checagem de seleção total e parcial na lista filtrada
  const allFilteredSelected = useMemo(() => {
    if (filteredGoals.length === 0) return false;
    return filteredGoals.every(g => tempSelectedGoalIds.includes(g.id));
  }, [filteredGoals, tempSelectedGoalIds]);

  const someFilteredSelected = useMemo(() => {
    return filteredGoals.some(g => tempSelectedGoalIds.includes(g.id));
  }, [filteredGoals, tempSelectedGoalIds]);

  // Abrir o dropdown garantindo que a lista completa de metas da filial seja exibida
  const handleOpenDropdown = () => {
    setTempSelectedGoalIds(selectedGoalIds);
    setSearchQuery(''); // Sempre limpa o filtro ao abrir para exibir todas as metas
    setIsDropdownOpen(true);
  };

  // Alternar seleção de uma meta individual
  const handleToggleGoal = (id) => {
    setTempSelectedGoalIds(prev =>
      prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]
    );
  };

  // Selecionar apenas uma meta e aplicar imediatamente
  const handleSelectOnlyGoal = (id) => {
    setTempSelectedGoalIds([id]);
    setSelectedGoalIds([id]);
    setSearchQuery('');
    setIsDropdownOpen(false);
  };

  // Alternar marcar todos / desmarcar todos da lista filtrada
  const handleToggleSelectAllFiltered = () => {
    if (allFilteredSelected) {
      const filteredIds = new Set(filteredGoals.map(g => g.id));
      setTempSelectedGoalIds(prev => prev.filter(id => !filteredIds.has(id)));
    } else {
      const newSet = new Set(tempSelectedGoalIds);
      filteredGoals.forEach(g => newSet.add(g.id));
      setTempSelectedGoalIds(Array.from(newSet));
    }
  };

  // Limpar seleção por completo
  const handleClearSelection = () => {
    setTempSelectedGoalIds([]);
    setSelectedGoalIds([]);
    setSearchQuery('');
  };

  // Aplicar seleção na análise do BI
  const handleApplySelection = () => {
    setSelectedGoalIds(tempSelectedGoalIds);
    setSearchQuery('');
    setIsDropdownOpen(false);
  };

  // Manipulação da ordenação por coluna na tabela
  const handleSort = (field) => {
    let newOrder = 'asc';
    if (sortField === field) {
      newOrder = sortOrder === 'asc' ? 'desc' : 'asc';
    } else {
      newOrder = ['name', 'branch', 'target_type', 'goal_subtype', 'goal_classification'].includes(field) ? 'asc' : 'desc';
    }
    setSortField(field);
    setSortOrder(newOrder);

    // Sincroniza com o dropdown se coincidir com uma das opções pré-definidas
    if (field === 'pct' && newOrder === 'desc') setSortBy('pct_desc');
    else if (field === 'realized' && newOrder === 'desc') setSortBy('realized_desc');
    else if (field === 'ticket_medio' && newOrder === 'desc') setSortBy('ticket_desc');
    else if (field === 'via' && newOrder === 'desc') setSortBy('via_desc');
    else if (field === 'gap' && newOrder === 'desc') setSortBy('gap_desc');
    else if (field === 'daily_sales' && newOrder === 'desc') setSortBy('daily_sales_desc');
    else if (field === 'orders_day' && newOrder === 'desc') setSortBy('orders_day_desc');
    else setSortBy('');
  };

  const handleSortByDropdownChange = (val) => {
    setSortBy(val);
    if (val === 'pct_desc') { setSortField('pct'); setSortOrder('desc'); }
    else if (val === 'realized_desc') { setSortField('realized'); setSortOrder('desc'); }
    else if (val === 'daily_sales_desc') { setSortField('daily_sales'); setSortOrder('desc'); }
    else if (val === 'orders_day_desc') { setSortField('orders_day'); setSortOrder('desc'); }
    else if (val === 'ticket_desc') { setSortField('ticket_medio'); setSortOrder('desc'); }
    else if (val === 'via_desc') { setSortField('via'); setSortOrder('desc'); }
    else if (val === 'gap_desc') { setSortField('gap'); setSortOrder('desc'); }
  };

  const renderSortIcon = (field) => {
    if (sortField !== field) {
      return <span style={{ marginLeft: 4, opacity: 0.25, fontSize: 10, userSelect: 'none' }}>▲▼</span>;
    }
    return (
      <span style={{ marginLeft: 4, fontSize: 10, userSelect: 'none', color: '#2563eb', fontWeight: 'bold' }}>
        {sortOrder === 'asc' ? '▲' : '▼'}
      </span>
    );
  };

  // Lista de Colaboradores ordenada
  const sortedCollaborators = useMemo(() => {
    if (!data?.collaborators) return [];
    const list = [...data.collaborators];
    list.sort((a, b) => {
      let comp = 0;
      switch (sortField) {
        case 'name': {
          const strA = (a.name || '').trim();
          const strB = (b.name || '').trim();
          comp = strA.localeCompare(strB, 'pt-BR', { sensitivity: 'base' });
          if (comp === 0) {
            comp = String(a.code || '').localeCompare(String(b.code || ''), 'pt-BR', { numeric: true });
          }
          break;
        }
        case 'branch': {
          const strA = String(a.branch_name || a.branch_code || '').trim();
          const strB = String(b.branch_name || b.branch_code || '').trim();
          comp = strA.localeCompare(strB, 'pt-BR', { numeric: true, sensitivity: 'base' });
          break;
        }
        case 'target_type': {
          const strA = String(a.target_type || '').trim();
          const strB = String(b.target_type || '').trim();
          comp = strA.localeCompare(strB, 'pt-BR', { sensitivity: 'base' });
          break;
        }
        case 'goal_subtype': {
          const strA = String(a.goal_subtype || '').trim();
          const strB = String(b.goal_subtype || '').trim();
          comp = strA.localeCompare(strB, 'pt-BR', { sensitivity: 'base' });
          break;
        }
        case 'goal_classification': {
          const strA = String(a.goal_classification || '').trim();
          const strB = String(b.goal_classification || '').trim();
          comp = strA.localeCompare(strB, 'pt-BR', { sensitivity: 'base' });
          break;
        }
        case 'target': {
          const numA = Number(a.target) || 0;
          const numB = Number(b.target) || 0;
          comp = numA - numB;
          break;
        }
        case 'realized': {
          const numA = Number(a.realized) || 0;
          const numB = Number(b.realized) || 0;
          comp = numA - numB;
          break;
        }
        case 'pct': {
          const numA = Number(a.pct) || 0;
          const numB = Number(b.pct) || 0;
          comp = numA - numB;
          if (comp === 0) {
            const gapA = Number(a.gap) || 0;
            const gapB = Number(b.gap) || 0;
            comp = gapA - gapB;
          }
          break;
        }
        case 'gap': {
          const numA = Number(a.gap) || 0;
          const numB = Number(b.gap) || 0;
          comp = numA - numB;
          break;
        }
        case 'ticket_medio': {
          const numA = Number(a.ticket_medio) || 0;
          const numB = Number(b.ticket_medio) || 0;
          comp = numA - numB;
          break;
        }
        case 'itens_atend': {
          const numA = Number(getCollabIA(a)) || 0;
          const numB = Number(getCollabIA(b)) || 0;
          comp = numA - numB;
          break;
        }
        case 'via': {
          const numA = Number(a.via || a.valor_por_produto) || 0;
          const numB = Number(b.via || b.valor_por_produto) || 0;
          comp = numA - numB;
          break;
        }
        case 'required_daily_rate': {
          const numA = Number(a.required_daily_rate) || 0;
          const numB = Number(b.required_daily_rate) || 0;
          comp = numA - numB;
          break;
        }
        case 'probability': {
          const probWeights = { atingida: 4, alta: 3, media: 2, baixa: 1 };
          const weightA = probWeights[a.probability] || 0;
          const weightB = probWeights[b.probability] || 0;
          comp = weightA - weightB;
          if (comp === 0) {
            comp = (Number(a.pct) || 0) - (Number(b.pct) || 0);
          }
          break;
        }
        case 'daily_sales': {
          const numA = Number(a.daily_net_sales_average || a.daily_run_rate) || 0;
          const numB = Number(b.daily_net_sales_average || b.daily_run_rate) || 0;
          comp = numA - numB;
          break;
        }
        case 'orders_day': {
          const numA = Number(a.daily_orders_qty ?? a.avg_orders_per_day) || 0;
          const numB = Number(b.daily_orders_qty ?? b.avg_orders_per_day) || 0;
          comp = numA - numB;
          break;
        }
        default: {
          const numA = Number(a.pct) || 0;
          const numB = Number(b.pct) || 0;
          comp = numA - numB;
          break;
        }
      }

      return sortOrder === 'asc' ? comp : -comp;
    });
    return list;
  }, [data?.collaborators, sortField, sortOrder]);

  // Meta/Colaborador ativo no modal (via índice no array ordenado)
  const selectedCollab = useMemo(() => {
    if (selectedCollabIndex === null || !sortedCollaborators || sortedCollaborators.length === 0) {
      return null;
    }
    if (selectedCollabIndex >= sortedCollaborators.length) {
      return sortedCollaborators[0] || null;
    }
    return sortedCollaborators[selectedCollabIndex] || null;
  }, [selectedCollabIndex, sortedCollaborators]);

  // Manter último colaborador válido para evitar desmontagem/flicker do modal ao alternar filial
  const lastCollabRef = useRef(null);
  if (selectedCollab) {
    lastCollabRef.current = selectedCollab;
  }
  const activeCollab = selectedCollab || (selectedCollabIndex !== null ? lastCollabRef.current : null);

  // Recarregar pedidos quando o modal estiver aberto na aba de pedidos
  useEffect(() => {
    if (selectedCollab?.goal_id && modalTab === 'orders') {
      loadGoalOrders(selectedCollab.goal_id, 1);
    }
  }, [selectedCollab?.goal_id, modalTab, startDate, endDate, goalOrdersSaleType, goalOrdersSearch, loadGoalOrders]);

  // Abertura do modal ao clicar ou duplo-clicar em uma meta
  const handleOpenCollabModal = useCallback((collab) => {
    const index = sortedCollaborators.findIndex(c => c.goal_id === collab.goal_id);
    setSelectedCollabIndex(index !== -1 ? index : 0);
  }, [sortedCollaborators]);

  // Fechamento do modal
  const handleCloseCollabModal = useCallback(() => {
    setSelectedCollabIndex(null);
    setIsIndividualFullscreen(false);
  }, []);

  // Checagem de seleção na tabela de metas para Análise Concatenada
  const allTableSelected = useMemo(() => {
    if (!sortedCollaborators || sortedCollaborators.length === 0) return false;
    return sortedCollaborators.every(c => selectedTableGoalIds.includes(c.goal_id));
  }, [sortedCollaborators, selectedTableGoalIds]);

  const someTableSelected = useMemo(() => {
    return sortedCollaborators.some(c => selectedTableGoalIds.includes(c.goal_id));
  }, [sortedCollaborators, selectedTableGoalIds]);

  const handleToggleTableGoal = (goalId) => {
    setSelectedTableGoalIds(prev =>
      prev.includes(goalId) ? prev.filter(id => id !== goalId) : [...prev, goalId]
    );
  };

  const handleToggleSelectAllTable = () => {
    if (allTableSelected) {
      setSelectedTableGoalIds([]);
    } else {
      setSelectedTableGoalIds(sortedCollaborators.map(c => c.goal_id));
    }
  };

  // Cálculo consolidado das metas selecionadas na tabela
  const concatenatedStats = useMemo(() => {
    if (!sortedCollaborators || sortedCollaborators.length === 0 || selectedTableGoalIds.length < 2) {
      return null;
    }

    const selectedGoals = sortedCollaborators.filter(c => selectedTableGoalIds.includes(c.goal_id));
    if (selectedGoals.length === 0) return null;

    const areAllQtyGoals = selectedGoals.length > 0 && selectedGoals.every(g => isQuantitySubtype(g.goal_subtype));
    const areAllClients = areAllQtyGoals && selectedGoals.every(g => g.goal_subtype === 'clientes_positivados');
    const qtyUnitLabel = areAllQtyGoals ? (areAllClients ? 'cli' : 'un') : null;

    const totalTarget = selectedGoals.reduce((sum, g) => sum + (g.target || 0), 0);
    const totalRealized = selectedGoals.reduce((sum, g) => sum + (g.realized || 0), 0);
    const totalRawNet = selectedGoals.reduce((sum, g) => sum + (g.raw_net_value != null ? Number(g.raw_net_value) : (g.realized || 0)), 0);
    const totalGap = Math.max(0, totalTarget - totalRealized);
    const pct = totalTarget > 0 ? Number(((totalRealized / totalTarget) * 100).toFixed(1)) : 0;

    const totalOrders = selectedGoals.reduce((sum, g) => sum + (g.orders_count || 0), 0);
    const totalItems = selectedGoals.reduce((sum, g) => sum + (g.items_count || 0), 0);
    const ticketMedio = totalOrders > 0 ? totalRealized / totalOrders : 0;
    const pa = totalOrders > 0 ? totalItems / totalOrders : 0;
    const via = totalItems > 0 ? totalRealized / totalItems : 0;

    // Relatórios de todos os períodos selecionados
    const allReports = [];
    selectedGoals.forEach(g => {
      if (Array.isArray(g.reports)) {
        g.reports.forEach(r => {
          allReports.push({
            ...r,
            goal_name: g.name,
            goal_code: g.code,
            collab_name: g.name,
            period_label: g.period_label || `${g.reference_month}/${g.reference_year}`,
            branch_name: g.branch_name || r.branch_name,
            net_value: Number(r.net_value ?? r.net ?? r.raw_net ?? r.total_net_revenue ?? 0),
            adicional_amount: Number(r.adicional_amount ?? r.commission_amount ?? r.bonus_amount ?? 0),
            commission_amount: Number(r.adicional_amount ?? r.commission_amount ?? r.bonus_amount ?? 0)
          });
        });
      }
    });

    // Períodos únicos
    const periods = Array.from(new Set(selectedGoals.map(g => g.period_label || `${g.reference_month}/${g.reference_year}`)));

    // Colaboradores únicos
    const collabNames = Array.from(new Set(selectedGoals.map(g => g.name || 'Colaborador')));

    // Trajetória diária concatenada
    const combinedDailyMap = {};
    selectedGoals.forEach(g => {
      if (g.daily_metrics && typeof g.daily_metrics === 'object') {
        Object.entries(g.daily_metrics).forEach(([dDate, dData]) => {
          if (!combinedDailyMap[dDate]) {
            combinedDailyMap[dDate] = { date: dDate, net: 0, orders: 0, items: 0 };
          }
          combinedDailyMap[dDate].net += (dData.net || 0);
          combinedDailyMap[dDate].orders += (dData.orders || 0);
          combinedDailyMap[dDate].items += (dData.items || 0);
        });
      }
    });
    const concatenatedDailyList = Object.values(combinedDailyMap)
      .sort((a, b) => a.date.localeCompare(b.date))
      .map(d => ({
        ...d,
        label: formatDateBR(d.date),
        ticket_medio: d.orders > 0 ? (d.net / d.orders) : 0,
        pa: d.orders > 0 ? (d.items / d.orders) : 0
      }));

    // Metas selecionadas com parâmetros isolados e cores exclusivas para cards, gráficos e matriz comparativa
    const isolatedGoals = selectedGoals.map((g, idx) => {
      const target = Number(g.target || 0);
      const realized = Number(g.realized || 0);
      const rawNet = g.raw_net_value != null ? Number(g.raw_net_value) : realized;
      const pct = target > 0 ? Number(((realized / target) * 100).toFixed(1)) : 0;
      const gap = Math.max(0, target - realized);
      const orders = Number(g.orders_count ?? g.orders ?? 0);
      const items = Number(g.items_count ?? g.items ?? 0);
      const ticketMedio = Number(g.ticket_medio ?? (orders > 0 ? realized / orders : 0));
      const pa = Number(getCollabIA(g) ?? (orders > 0 ? items / orders : 0));
      const via = Number(g.via ?? g.valor_por_produto ?? (items > 0 ? realized / items : 0));
      const prob = g.probability || (pct >= 100 ? 'atingida' : (pct >= 85 ? 'alta' : (pct >= 70 ? 'media' : 'baixa')));
      const periodLabel = g.period_label || `${String(g.reference_month || '').padStart(2, '0')}/${g.reference_year || ''}`;
      const color = GOAL_COLORS[idx % GOAL_COLORS.length];
      const firstName = (g.name || 'Meta').trim().split(' ')[0];
      const shortName = `${firstName} (${periodLabel})`;

      return {
        ...g,
        goal_id: g.goal_id,
        name: g.name || 'Colaborador',
        branch_name: g.branch_name || 'Filial Principal',
        goal_classification: g.goal_classification || 'Padrão',
        goal_subtype: g.goal_subtype || 'vendas',
        is_quantity_goal: isQuantitySubtype(g.goal_subtype),
        target_quantity: g.target_quantity,
        unit_value: g.unit_value,
        bonus_amount: g.bonus_amount,
        target,
        realized,
        rawNet,
        pct,
        gap,
        orders,
        items,
        ticketMedio,
        ticket_medio: ticketMedio,
        pa,
        via,
        probability: prob,
        period: periodLabel,
        periodLabel,
        color,
        shortName,
        goalIndex: idx
      };
    });

    const sideBySideGoals = isolatedGoals;

    // Métricas agrupadas por período (para comparação agrupada por período quando houver múltiplos períodos)
    const periodGroups = {};
    selectedGoals.forEach(g => {
      const pKey = g.period_label || `${String(g.reference_month || '').padStart(2, '0')}/${g.reference_year || ''}`;
      if (!periodGroups[pKey]) {
        periodGroups[pKey] = {
          period: pKey,
          name: '',
          goal_id: g.goal_id,
          target: 0,
          realized: 0,
          orders: 0,
          items: 0,
          daysWithSales: 0,
          goals: []
        };
      }
      periodGroups[pKey].target += (g.target || 0);
      periodGroups[pKey].realized += (g.realized || 0);
      periodGroups[pKey].orders += (g.orders_count ?? g.orders ?? 0);
      periodGroups[pKey].items += (g.items_count ?? g.items ?? 0);
      periodGroups[pKey].daysWithSales += (g.business_days_with_sales || 0);
      periodGroups[pKey].goals.push(g);
    });

    const sideBySidePeriods = Object.values(periodGroups).map((pg, idx) => {
      const pPct = pg.target > 0 ? Number(((pg.realized / pg.target) * 100).toFixed(1)) : 0;
      const pGap = Math.max(0, pg.target - pg.realized);
      const pTicket = pg.orders > 0 ? pg.realized / pg.orders : 0;
      const pPA = pg.orders > 0 ? pg.items / pg.orders : 0;
      const pVIA = pg.items > 0 ? pg.realized / pg.items : 0;
      const pProb = pPct >= 100 ? 'atingida' : (pPct >= 85 ? 'alta' : (pPct >= 70 ? 'media' : 'baixa'));
      const pName = pg.goals.length === 1 ? (pg.goals[0].name || '') : `${pg.goals.length} Metas Consolidadas`;
      return {
        ...pg,
        name: pName,
        pct: pPct,
        gap: pGap,
        ticket_medio: pTicket,
        pa: pPA,
        via: pVIA,
        probability: pProb,
        color: GOAL_COLORS[idx % GOAL_COLORS.length]
      };
    });

    // Função de cálculo de variação entre os extremos (Máximo vs Mínimo)
    const calcVariations = (items) => {
      if (!items || items.length < 2) {
        return {
          target_diff: 0, target_pct: 0,
          sales_diff: 0, sales_pct: 0,
          pct_points: 0,
          ticket_diff: 0, ticket_pct: 0,
          pa_diff: 0, orders_diff: 0
        };
      }
      const salesArr = items.map(p => Number(p.realized || 0));
      const targetArr = items.map(p => Number(p.target || 0));
      const pctArr = items.map(p => Number(p.pct || 0));
      const ticketArr = items.map(p => Number(p.ticket_medio || 0));
      const paArr = items.map(p => Number(p.pa || 0));
      const ordersArr = items.map(p => Number(p.orders || 0));

      const maxSales = Math.max(...salesArr);
      const minSales = Math.min(...salesArr);
      const sales_diff = Number((maxSales - minSales).toFixed(2));
      const sales_pct = minSales > 0 ? Number(((sales_diff / minSales) * 100).toFixed(1)) : 0;

      const maxTarget = Math.max(...targetArr);
      const minTarget = Math.min(...targetArr);
      const target_diff = Number((maxTarget - minTarget).toFixed(2));
      const target_pct = minTarget > 0 ? Number(((target_diff / minTarget) * 100).toFixed(1)) : 0;

      const maxPct = Math.max(...pctArr);
      const minPct = Math.min(...pctArr);
      const pct_points = Number((maxPct - minPct).toFixed(1));

      const maxTicket = Math.max(...ticketArr);
      const minTicket = Math.min(...ticketArr);
      const ticket_diff = Number((maxTicket - minTicket).toFixed(2));
      const ticket_pct = minTicket > 0 ? Number(((ticket_diff / minTicket) * 100).toFixed(1)) : 0;

      const maxPA = Math.max(...paArr);
      const minPA = Math.min(...paArr);
      const pa_diff = Number((maxPA - minPA).toFixed(2));

      const viaArr = items.map(p => Number(p.via || 0));
      const maxVIA = Math.max(...viaArr);
      const minVIA = Math.min(...viaArr);
      const via_diff = Number((maxVIA - minVIA).toFixed(2));
      const via_pct = minVIA > 0 ? Number(((via_diff / minVIA) * 100).toFixed(1)) : 0;

      const maxOrders = Math.max(...ordersArr);
      const minOrders = Math.min(...ordersArr);
      const orders_diff = maxOrders - minOrders;

      return {
        target_diff, target_pct,
        sales_diff, sales_pct,
        pct_points,
        ticket_diff, ticket_pct,
        pa_diff,
        via_diff, via_pct,
        orders_diff
      };
    };

    const goalVariations = calcVariations(sideBySideGoals);
    const periodVariations = calcVariations(sideBySidePeriods);
    const variations = goalVariations;

    // Trajetória temporal diária consolidada e individual (Dias 1 a 31)
    const daysInMonth = Number(data?.period?.days_in_month || 30);
    const businessDaysTotal = Number(data?.period?.business_days_total || 26);
    const idealDailyRate = daysInMonth > 0 ? (totalTarget / daysInMonth) : 0;
    const idealBusinessRate = businessDaysTotal > 0 ? (totalTarget / businessDaysTotal) : 0;

    let concatAccum = 0;
    const runningGoalAccum = {};
    selectedGoals.forEach(g => {
      runningGoalAccum[g.goal_id] = 0;
    });

    const rawConcatTimeline = [];
    let lastActiveDay = 1;

    for (let d = 1; d <= 31; d++) {
      const dayStr = String(d).padStart(2, '0');
      let dayNet = 0;
      let dayOrders = 0;
      let dayItems = 0;
      let dayHasMovement = false;

      selectedGoals.forEach((g) => {
        let gNet = 0;
        let gOrders = 0;
        let gItems = 0;

        if (g.daily_metrics && typeof g.daily_metrics === 'object') {
          const dateKey = Object.keys(g.daily_metrics).find(k => {
            const parts = k.split('-');
            return parts.length === 3 && parseInt(parts[2], 10) === d;
          });
          if (dateKey && g.daily_metrics[dateKey]) {
            gNet = Number(g.daily_metrics[dateKey].net || 0);
            gOrders = Number(g.daily_metrics[dateKey].orders || 0);
            gItems = Number(g.daily_metrics[dateKey].items || 0);
          }
        }

        if (gNet > 0 || gOrders > 0 || gItems > 0) {
          dayHasMovement = true;
        }

        dayNet += gNet;
        dayOrders += gOrders;
        dayItems += gItems;

        const gIsQty = isQuantitySubtype(g.goal_subtype);
        const gQtyVal = g.goal_subtype === 'clientes_positivados' ? gOrders : gItems;
        runningGoalAccum[g.goal_id] += gIsQty ? gQtyVal : gNet;
      });

      if (dayHasMovement) {
        lastActiveDay = d;
      }

      const dayQtyValue = areAllClients ? dayOrders : dayItems;
      const dayRealizedVal = areAllQtyGoals ? dayQtyValue : dayNet;
      concatAccum += dayRealizedVal;
      const idealPace = Number((idealDailyRate * d).toFixed(areAllQtyGoals ? 0 : 2));
      const dayTicket = dayOrders > 0 ? Number((dayNet / dayOrders).toFixed(2)) : null;
      const dayPA = dayOrders > 0 ? Number((dayItems / dayOrders).toFixed(2)) : null;
      const dayVIA = dayItems > 0 ? Number((dayNet / dayItems).toFixed(2)) : null;

      const entry = {
        day: d,
        label: `Dia ${dayStr}`,
        shortLabel: dayStr,
        realized_day: dayRealizedVal,
        accumulated_realized: dayHasMovement || concatAccum > 0 ? Number(concatAccum.toFixed(areAllQtyGoals ? 0 : 2)) : null,
        ideal_pace: idealPace,
        orders: dayOrders,
        items: dayItems,
        ticket_medio: dayTicket,
        pa: dayPA,
        via: dayVIA,
        ref_daily_sales: Number(idealBusinessRate.toFixed(areAllQtyGoals ? 1 : 2)),
        ref_tm: ticketMedio > 0 ? Number(ticketMedio.toFixed(2)) : null,
        ref_pa: pa > 0 ? Number(pa.toFixed(2)) : null,
        ref_via: via > 0 ? Number(via.toFixed(2)) : null,
        hasMovement: dayHasMovement
      };

      // Chaves isoladas por meta para o gráfico comparativo de curvas sobrepostas
      selectedGoals.forEach(g => {
        let gNet = 0;
        let gOrders = 0;
        let gItems = 0;
        if (g.daily_metrics && typeof g.daily_metrics === 'object') {
          const dateKey = Object.keys(g.daily_metrics).find(k => {
            const parts = k.split('-');
            return parts.length === 3 && parseInt(parts[2], 10) === d;
          });
          if (dateKey && g.daily_metrics[dateKey]) {
            gNet = Number(g.daily_metrics[dateKey].net || 0);
            gOrders = Number(g.daily_metrics[dateKey].orders || 0);
            gItems = Number(g.daily_metrics[dateKey].items || 0);
          }
        }
        const gIsQty = isQuantitySubtype(g.goal_subtype);
        const gQtyVal = g.goal_subtype === 'clientes_positivados' ? gOrders : gItems;
        entry[`goal_${g.goal_id}_net`] = gNet;
        entry[`goal_${g.goal_id}_orders`] = gOrders;
        entry[`goal_${g.goal_id}_items`] = gItems;
        entry[`goal_${g.goal_id}_qty`] = gQtyVal;
        entry[`goal_${g.goal_id}_accum`] = Number(runningGoalAccum[g.goal_id].toFixed(2));
      });

      rawConcatTimeline.push(entry);
    }

    const timeline = rawConcatTimeline.filter(e => e.day <= Math.max(5, Math.min(31, lastActiveDay + 1)));

    // Distribuição e performance por dia da semana consolidada das metas selecionadas
    const concatWeekdays = [
      { dayIndex: 1, name: 'Segunda-feira', short: 'Seg', orders: 0, items: 0, net: 0, count: 0 },
      { dayIndex: 2, name: 'Terça-feira', short: 'Ter', orders: 0, items: 0, net: 0, count: 0 },
      { dayIndex: 3, name: 'Quarta-feira', short: 'Qua', orders: 0, items: 0, net: 0, count: 0 },
      { dayIndex: 4, name: 'Quinta-feira', short: 'Qui', orders: 0, items: 0, net: 0, count: 0 },
      { dayIndex: 5, name: 'Sexta-feira', short: 'Sex', orders: 0, items: 0, net: 0, count: 0 },
      { dayIndex: 6, name: 'Sábado', short: 'Sáb', orders: 0, items: 0, net: 0, count: 0 },
      { dayIndex: 0, name: 'Domingo', short: 'Dom', orders: 0, items: 0, net: 0, count: 0 },
    ];

    selectedGoals.forEach(g => {
      if (g.daily_metrics && typeof g.daily_metrics === 'object') {
        Object.entries(g.daily_metrics).forEach(([dateStr, dData]) => {
          const parts = dateStr.split('-');
          if (parts.length === 3) {
            const dObj = new Date(parseInt(parts[0], 10), parseInt(parts[1], 10) - 1, parseInt(parts[2], 10));
            const dayOfWeek = dObj.getDay();
            const orders = Number(dData.orders || 0);
            const items = Number(dData.items || 0);
            const net = Number(dData.net || 0);
            if (orders > 0 || items > 0 || net > 0) {
              const wObj = concatWeekdays.find(w => w.dayIndex === dayOfWeek);
              if (wObj) {
                wObj.orders += orders;
                wObj.items += items;
                wObj.net += net;
                wObj.count += 1;
                const gNetKey = `goal_${g.goal_id}_net`;
                const gOrdersKey = `goal_${g.goal_id}_orders`;
                const gItemsKey = `goal_${g.goal_id}_items`;
                wObj[gNetKey] = (wObj[gNetKey] || 0) + net;
                wObj[gOrdersKey] = (wObj[gOrdersKey] || 0) + orders;
                wObj[gItemsKey] = (wObj[gItemsKey] || 0) + items;
              }
            }
          }
        });
      }
    });

    const weekdayData = concatWeekdays
      .filter(w => w.count > 0 || w.dayIndex !== 0)
      .map(w => {
        const item = {
          name: w.name,
          short: w.short,
          total_net: Number(w.net.toFixed(2)),
          net: Number(w.net.toFixed(2)),
          total_orders: w.orders,
          orders: w.orders,
          total_items: w.items,
          items: w.items,
          ticket_medio: w.orders > 0 ? Number((w.net / w.orders).toFixed(2)) : 0,
          pa: w.orders > 0 ? Number((w.items / w.orders).toFixed(2)) : 0,
          avg_net: w.count > 0 ? Number((w.net / w.count).toFixed(2)) : 0,
          count: w.count,
          days_count: w.count
        };
        selectedGoals.forEach(g => {
          const gNet = Number((w[`goal_${g.goal_id}_net`] || 0).toFixed(2));
          const gOrders = w[`goal_${g.goal_id}_orders`] || 0;
          const gItems = w[`goal_${g.goal_id}_items`] || 0;
          const gIsQty = isQuantitySubtype(g.goal_subtype);
          item[`goal_${g.goal_id}_net`] = gNet;
          item[`goal_${g.goal_id}_orders`] = gOrders;
          item[`goal_${g.goal_id}_items`] = gItems;
          item[`goal_${g.goal_id}_qty`] = g.goal_subtype === 'clientes_positivados' ? gOrders : gItems;
          item[`goal_${g.goal_id}_ticket`] = gOrders > 0 ? Number((gNet / gOrders).toFixed(2)) : 0;
          item[`goal_${g.goal_id}_pa`] = gOrders > 0 ? Number((gItems / gOrders).toFixed(2)) : 0;
        });
        return item;
      });

    return {
      selectedGoals,
      isolatedGoals,
      timeline,
      weekdayData,
      isolatedTimeline: timeline,
      totalTarget,
      totalRealized,
      totalRawNet,
      totalGap,
      pct,
      totalOrders,
      totalItems,
      ticketMedio,
      pa,
      via,
      allReports,
      periods,
      collabs: collabNames,
      collabNames,
      concatenatedDailyList,
      sideBySideGoals,
      sideBySidePeriods,
      goalVariations,
      periodVariations,
      variations,
      areAllQtyGoals,
      areAllClients,
      qtyUnitLabel
    };
  }, [sortedCollaborators, selectedTableGoalIds, data?.period]);

  // Navegação para meta anterior no carrossel
  const handlePrevCollab = useCallback(() => {
    if (!sortedCollaborators || sortedCollaborators.length === 0) return;
    setSelectedCollabIndex(prev => {
      if (prev === null || prev <= 0) return sortedCollaborators.length - 1;
      return prev - 1;
    });
  }, [sortedCollaborators]);

  // Navegação para próxima meta no carrossel
  const handleNextCollab = useCallback(() => {
    if (!sortedCollaborators || sortedCollaborators.length === 0) return;
    setSelectedCollabIndex(prev => {
      if (prev === null || prev >= sortedCollaborators.length - 1) return 0;
      return prev + 1;
    });
  }, [sortedCollaborators]);

  // Lista de filiais navegáveis no modal
  const availableBranchList = useMemo(() => {
    if (!Array.isArray(branchesSummary) || branchesSummary.length === 0) return [];
    // Apenas filiais que possuem metas cadastradas (ou a filial atualmente selecionada)
    const filtered = branchesSummary.filter(b => Number(b.goals_count) > 0 || String(b.code) === String(branch));
    return filtered.length > 0 ? filtered : branchesSummary;
  }, [branchesSummary, branch]);

  // Índice da filial ativa no carrossel do modal
  const currentBranchIndex = useMemo(() => {
    if (!availableBranchList || availableBranchList.length === 0) return 0;
    const targetCode = branch !== 'all' ? String(branch) : String(activeCollab?.branch_code || '');
    const idx = availableBranchList.findIndex(b => String(b.code) === targetCode);
    return idx !== -1 ? idx : 0;
  }, [availableBranchList, branch, activeCollab?.branch_code]);

  // Objeto e rótulos da filial atual
  const currentBranch = availableBranchList[currentBranchIndex] || null;
  const currentBranchLabel = currentBranch
    ? (currentBranch.code === 'geral' ? 'Geral' : `Filial ${currentBranch.code}`)
    : (branch === 'all' ? 'Todas as Filiais' : `Filial ${branch}`);
  const currentBranchFullText = currentBranch
    ? `${currentBranchLabel}: ${currentBranch.name || ''}`
    : currentBranchLabel;

  // Navegação para filial anterior no carrossel do modal
  const handlePrevBranch = useCallback(() => {
    if (!availableBranchList || availableBranchList.length === 0) return;
    const total = availableBranchList.length;
    const prevIndex = currentBranchIndex <= 0 ? total - 1 : currentBranchIndex - 1;
    const nextBranch = availableBranchList[prevIndex];
    if (nextBranch) {
      handleBranchChange(nextBranch.code);
      setSelectedCollabIndex(0);
      setIsBranchNavDropdownOpen(false);
    }
  }, [availableBranchList, currentBranchIndex, handleBranchChange]);

  // Navegação para próxima filial no carrossel do modal
  const handleNextBranch = useCallback(() => {
    if (!availableBranchList || availableBranchList.length === 0) return;
    const total = availableBranchList.length;
    const nextIndex = currentBranchIndex >= total - 1 ? 0 : currentBranchIndex + 1;
    const nextBranch = availableBranchList[nextIndex];
    if (nextBranch) {
      handleBranchChange(nextBranch.code);
      setSelectedCollabIndex(0);
      setIsBranchNavDropdownOpen(false);
    }
  }, [availableBranchList, currentBranchIndex, handleBranchChange]);

  // Atalhos de teclado no carrossel de metas:
  // - Setas Esquerda/Direita: Meta Anterior / Próxima Meta
  // - Alt + Setas ou Shift + Setas: Filial Anterior / Próxima Filial
  // - ESC: Fechar dropdown/simulação ou fechar modal
  useEffect(() => {
    if (selectedCollabIndex === null) return;
    const handleKeyDown = (e) => {
      if (e.key === 'ArrowLeft') {
        if (e.altKey || e.shiftKey) {
          handlePrevBranch();
        } else if (!simProduct) {
          handlePrevCollab();
        }
      } else if (e.key === 'ArrowRight') {
        if (e.altKey || e.shiftKey) {
          handleNextBranch();
        } else if (!simProduct) {
          handleNextCollab();
        }
      } else if (e.key === 'Escape') {
        if (isBranchNavDropdownOpen) {
          setIsBranchNavDropdownOpen(false);
        } else if (simProduct) {
          setSimProduct(null);
        } else if (isIndividualFullscreen) {
          setIsIndividualFullscreen(false);
        } else {
          handleCloseCollabModal();
        }
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [selectedCollabIndex, handlePrevCollab, handleNextCollab, handlePrevBranch, handleNextBranch, handleCloseCollabModal, simProduct, isBranchNavDropdownOpen, isIndividualFullscreen]);

  // Listener de tecla Esc para Modal Concatenado
  useEffect(() => {
    if (!isConcatenatedModalOpen) return;
    const handleConcatKeyDown = (e) => {
      if (e.key === 'Escape') {
        if (isConcatenatedFullscreen) {
          setIsConcatenatedFullscreen(false);
        } else {
          setIsConcatenatedModalOpen(false);
          setIsConcatenatedFullscreen(false);
        }
      }
    };
    window.addEventListener('keydown', handleConcatKeyDown);
    return () => window.removeEventListener('keydown', handleConcatKeyDown);
  }, [isConcatenatedModalOpen, isConcatenatedFullscreen]);

  // Copiar roteiro / pitch para área de transferência com suporte universal (Localhost, HTTPS e HTTP/LAN)
  const handleCopyPitch = useCallback((scriptText) => {
    const textToCopy = scriptText
      || selectedCollab?.ai_analysis?.pitch_script
      || selectedCollab?.ai_analysis?.suggested_pitch
      || '';

    if (!textToCopy) return;

    copyToClipboard(textToCopy, () => {
      setCopiedPitch(true);
      setTimeout(() => setCopiedPitch(false), 2500);
    });
  }, [selectedCollab]);

  // Execução do pipeline de análise IA com ferramentas e calibração de parâmetros
  const runLiveAIAnalysis = useCallback(async (collab, customPrompt = null, providerId = null) => {
    if (!collab) return;
    setAiGenerating(true);
    setAiExecutionStep(1);
    setAiStatusMessage('1/4: Extraindo métricas da meta, PA e ritmo de gap diário...');

    // Feedback progressivo visual para o usuário
    const timer1 = setTimeout(() => {
      setAiExecutionStep(2);
      setAiStatusMessage('2/4: Mapeando cesta de compras & identificando produtos ISCA...');
    }, 450);

    const timer2 = setTimeout(() => {
      setAiExecutionStep(3);
      setAiStatusMessage('3/4: Ranqueando tendência, giro de estoque e margem de ticket...');
    }, 950);

    const timer3 = setTimeout(() => {
      setAiExecutionStep(4);
      setAiStatusMessage('4/4: Processando via Inteligência Artificial & sintetizando plano comercial...');
    }, 1450);

    try {
      const payload = {
        collaborator: {
          goal_id: collab.goal_id,
          collaborator_name: collab.name,
          codusur: collab.codusur,
          branch_code: collab.branch_code,
          branch_name: collab.branch_name,
          goal_classification: collab.goal_classification,
          target: collab.target,
          realized: collab.realized,
          pct: collab.pct,
          gap: collab.gap,
          ticket_medio: collab.ticket_medio,
          ref_average_ticket: collab.ref_average_ticket,
          pa: getCollabIA(collab),
          ref_items_per_attendance: collab.ref_items_per_attendance,
          via: collab.via,
          ref_item_value_per_attendance: collab.ref_item_value_per_attendance,
          business_days_remaining: collab.business_days_remaining,
          required_daily_rate: collab.required_daily_rate,
          recommendations: collab.recommendations || [],
          isca_products: collab.isca_products || [],
          trending_products: collab.trending_products || []
        },
        custom_prompt: customPrompt !== null ? customPrompt : (aiPromptText || undefined),
        provider_id: providerId || aiSelectedProviderId || undefined
      };

      const res = await faturamentoService.getBIAIAnalysis(payload);

      clearTimeout(timer1);
      clearTimeout(timer2);
      clearTimeout(timer3);

      if (res && res.success && res.data) {
        const d = res.data;
        setAiExecutionStep(4);
        setAiStatusMessage('Análise concluída com sucesso! Parâmetros comerciais prontos.');
        setAiToolsLog(d.tools_log || []);
        setAiUpdatedParams(d.updated_parameters || null);
        setAiCurrentReport(d.ai_report || d.ai_text || '');
        setAiProviderInfo(d.provider || null);

        if (d.ai_report) {
          setAiLiveReport(prev => ({
            ...prev,
            [collab.goal_id]: d.ai_report
          }));
        }
      } else {
        setAiStatusMessage('Análise concluída com motor algorítmico de contingência.');
      }
    } catch (err) {
      clearTimeout(timer1);
      clearTimeout(timer2);
      clearTimeout(timer3);
      console.error('Erro na execução da análise IA:', err);
      setAiStatusMessage('Erro ao processar análise. Verifique os logs do servidor.');
    } finally {
      setAiGenerating(false);
    }
  }, [aiPromptText, aiSelectedProviderId]);

  // Abertura do Modal de Análise IA com busca prévia do prompt e provedores
  const handleOpenAIModal = useCallback(async (collab) => {
    if (!collab) return;
    setIsAIModalOpen(true);
    setAiModalTab('execution');
    setAiExecutionStep(1);
    setAiStatusMessage('Inicializando painel e consultando template de prompt...');
    setAiToolsLog([]);
    setAiUpdatedParams(null);
    setAiCurrentReport(aiLiveReport[collab.goal_id] || '');

    let promptToUse = '';
    let chosenProviderId = aiSelectedProviderId;

    try {
      const promptRes = await faturamentoService.getBIAIPrompt({
        collaborator: {
          goal_id: collab.goal_id,
          collaborator_name: collab.name,
          branch_name: collab.branch_name,
          goal_classification: collab.goal_classification,
          target: collab.target,
          realized: collab.realized,
          pct: collab.pct,
          gap: collab.gap,
          ticket_medio: collab.ticket_medio,
          ref_average_ticket: collab.ref_average_ticket,
          pa: getCollabIA(collab),
          ref_items_per_attendance: collab.ref_items_per_attendance,
          via: collab.via,
          ref_item_value_per_attendance: collab.ref_item_value_per_attendance,
          business_days_remaining: collab.business_days_remaining,
          required_daily_rate: collab.required_daily_rate
        }
      });

      if (promptRes?.success && promptRes.data) {
        const { compiled_prompt, raw_template, providers, default_provider_id } = promptRes.data;
        promptToUse = compiled_prompt || '';
        setAiDefaultCompiledPrompt(promptToUse);
        setAiPromptText(promptToUse);
        setAiRawTemplate(raw_template || '');
        if (providers && Array.isArray(providers)) {
          setAiProviders(providers);
        }
        if (!chosenProviderId && default_provider_id) {
          chosenProviderId = default_provider_id;
          setAiSelectedProviderId(default_provider_id);
        }
      }
    } catch (err) {
      console.warn('Não foi possível carregar prévia do prompt:', err);
    }

    runLiveAIAnalysis(collab, promptToUse, chosenProviderId);
  }, [aiSelectedProviderId, aiLiveReport, runLiveAIAnalysis]);

  // Aplicar parâmetros atualizados pela IA nos dados da meta selecionada
  const handleApplyAIParameters = useCallback(() => {
    if (!selectedCollab || !aiUpdatedParams) {
      setIsAIModalOpen(false);
      return;
    }

    setData(prev => {
      if (!prev || !prev.collaborators) return prev;
      const updatedCollabs = prev.collaborators.map(c => {
        if (c.goal_id === selectedCollab.goal_id) {
          return {
            ...c,
            ai_analysis: {
              ...(c.ai_analysis || {}),
              ...aiUpdatedParams
            }
          };
        }
        return c;
      });
      return {
        ...prev,
        collaborators: updatedCollabs
      };
    });

    if (aiCurrentReport) {
      setAiLiveReport(prev => ({
        ...prev,
        [selectedCollab.goal_id]: aiCurrentReport
      }));
    }

    setIsAIModalOpen(false);
  }, [selectedCollab, aiUpdatedParams, aiCurrentReport]);

  // Executar Análise Executiva com IA para Múltiplas Metas Concatenadas
  const runConcatAIAnalysis = useCallback(async (customPrompt = null, providerId = null) => {
    if (!concatenatedStats) return;

    setConcatAIGenerating(true);
    setConcatAIExecutionStep(1);
    setConcatAIStatusMessage('Iniciando auditoria executiva e coletando indicadores consolidados...');

    const timer1 = setTimeout(() => {
      setConcatAIExecutionStep(2);
      setConcatAIStatusMessage('Consolidando KPIs globais e auditando dispersão e matriz de cesta...');
    }, 700);

    const timer2 = setTimeout(() => {
      setConcatAIExecutionStep(3);
      setConcatAIStatusMessage('Analisando tendências temporais e variações entre períodos...');
    }, 1500);

    const timer3 = setTimeout(() => {
      setConcatAIExecutionStep(4);
      setConcatAIStatusMessage('Consultando motor de IA para sintetizar veredito executivo e plano de ação...');
    }, 2400);

    try {
      const payload = {
        collaborator: {
          is_concatenated: true,
          name: `Consolidado (${concatenatedStats.selectedGoals.length} Metas)`,
          collaborator_name: `Consolidado (${concatenatedStats.selectedGoals.length} Metas)`,
          collabs: concatenatedStats.collabNames,
          periods: concatenatedStats.periods,
          branch_name: branch !== 'all' ? branch : 'Todas as Filiais',
          target: concatenatedStats.totalTarget,
          realized: concatenatedStats.totalRealized,
          pct: concatenatedStats.pct,
          gap: concatenatedStats.totalGap,
          ticket_medio: concatenatedStats.ticketMedio,
          pa: concatenatedStats.pa,
          via: concatenatedStats.via,
          business_days_remaining: concatenatedStats.selectedGoals[0]?.business_days_remaining || 10,
          required_daily_rate: concatenatedStats.selectedGoals.reduce((sum, g) => sum + (g.required_daily_rate || 0), 0),
          isolated_goals: concatenatedStats.isolatedGoals
        },
        custom_prompt: customPrompt !== null ? customPrompt : (concatAIPromptText || undefined),
        provider_id: providerId || concatAISelectedProviderId || undefined
      };

      const res = await faturamentoService.getBIAIAnalysis(payload);

      clearTimeout(timer1);
      clearTimeout(timer2);
      clearTimeout(timer3);

      if (res && res.success && res.data) {
        const d = res.data;
        setConcatAIExecutionStep(4);
        setConcatAIStatusMessage('Diagnóstico executivo concluído com sucesso! Diretrizes prontas.');
        setConcatAIToolsLog(d.tools_log || []);
        setConcatAIUpdatedParams(d.updated_parameters || null);
        setConcatAIReport(d.ai_report || d.ai_text || '');
        setConcatAIProviderInfo(d.provider || null);
      } else {
        setConcatAIStatusMessage('Diagnóstico concluído com motor algorítmico de contingência.');
      }
    } catch (err) {
      clearTimeout(timer1);
      clearTimeout(timer2);
      clearTimeout(timer3);
      console.error('Erro na execução do diagnóstico executivo IA:', err);
      setConcatAIStatusMessage('Erro ao processar diagnóstico consolidado. Verifique os logs.');
    } finally {
      setConcatAIGenerating(false);
    }
  }, [concatenatedStats, branch, concatAIPromptText, concatAISelectedProviderId]);

  // Abertura do Modal de Diagnóstico IA Concatenado com prévia de prompt
  const handleOpenConcatAIModal = useCallback(async (tab = 'execution') => {
    if (!concatenatedStats) return;

    setIsConcatAIModalOpen(true);
    setConcatAIModalTab(tab);

    let promptToUse = concatAIPromptText;
    let chosenProviderId = concatAISelectedProviderId;

    if (tab === 'execution') {
      setConcatAIExecutionStep(1);
      setConcatAIStatusMessage('Inicializando painel executivo e consultando modelo de prompt...');
      setConcatAIToolsLog([]);
    }

    try {
      const promptRes = await faturamentoService.getBIAIPrompt({
        collaborator: {
          is_concatenated: true,
          name: `Consolidado (${concatenatedStats.selectedGoals.length} Metas)`,
          collaborator_name: `Consolidado (${concatenatedStats.selectedGoals.length} Metas)`,
          collabs: concatenatedStats.collabNames,
          periods: concatenatedStats.periods,
          branch_name: branch !== 'all' ? branch : 'Todas as Filiais',
          target: concatenatedStats.totalTarget,
          realized: concatenatedStats.totalRealized,
          pct: concatenatedStats.pct,
          gap: concatenatedStats.totalGap,
          ticket_medio: concatenatedStats.ticketMedio,
          pa: concatenatedStats.pa,
          via: concatenatedStats.via,
          business_days_remaining: concatenatedStats.selectedGoals[0]?.business_days_remaining || 10,
          required_daily_rate: concatenatedStats.selectedGoals.reduce((sum, g) => sum + (g.required_daily_rate || 0), 0),
          isolated_goals: concatenatedStats.isolatedGoals
        }
      });

      if (promptRes?.success && promptRes.data) {
        const { compiled_prompt, raw_template, providers, default_provider_id } = promptRes.data;
        if (!concatAIPromptText || promptToUse === '') {
          promptToUse = compiled_prompt || '';
          setConcatAIPromptText(promptToUse);
        }
        setConcatAIDefaultCompiledPrompt(compiled_prompt || '');
        setConcatAIRawTemplate(raw_template || '');
        if (providers && Array.isArray(providers)) {
          setConcatAIProviders(providers);
        }
        if (!chosenProviderId && default_provider_id) {
          chosenProviderId = default_provider_id;
          setConcatAISelectedProviderId(default_provider_id);
        }
      }
    } catch (err) {
      console.warn('Não foi possível carregar modelo do prompt executivo:', err);
    }

    if (tab === 'execution') {
      runConcatAIAnalysis(promptToUse, chosenProviderId);
    }
  }, [concatenatedStats, branch, concatAIPromptText, concatAISelectedProviderId, runConcatAIAnalysis]);

  // Totalizadores Principais
  const summary = data?.summary || {};
  const period = useMemo(() => data?.period || {}, [data?.period]);
  const recommendations = useMemo(() => data?.recommendations || [], [data?.recommendations]);

  // Analytics detalhados da meta/colaboradora selecionada para os gráficos do modal
  const collabAnalytics = useMemo(() => {
    if (!selectedCollab) return { timeline: [], weekdayData: [] };
    const metrics = selectedCollab.daily_metrics || {};
    const target = Number(selectedCollab.target || 0);
    const daysInMonth = Number(period?.days_in_month || 30);
    const year = Number(period?.year || new Date().getFullYear());
    const month = Number(period?.month || (new Date().getMonth() + 1));
    const businessDaysTotal = Number(selectedCollab.business_days_total || period?.business_days_total || 26);
    const idealDailyRate = daysInMonth > 0 ? (target / daysInMonth) : 0;
    const idealBusinessRate = businessDaysTotal > 0 ? (target / businessDaysTotal) : 0;
    const isCollabQtyGoal = isQuantitySubtype(selectedCollab.goal_subtype);
    const isCollabClients = selectedCollab.goal_subtype === 'clientes_positivados';
    const collabQtyUnit = isCollabQtyGoal ? (isCollabClients ? 'cli' : 'un') : null;

    let startD = 1;
    let endD = daysInMonth;
    if (startDate && endDate) {
      const sParts = startDate.split('-');
      const eParts = endDate.split('-');
      if (sParts.length === 3 && parseInt(sParts[0], 10) === year && parseInt(sParts[1], 10) === month) {
        startD = Math.max(1, parseInt(sParts[2], 10));
      }
      if (eParts.length === 3 && parseInt(eParts[0], 10) === year && parseInt(eParts[1], 10) === month) {
        endD = Math.min(daysInMonth, parseInt(eParts[2], 10));
      }
    }

    const weekdayNames = ['Dom', 'Seg', 'Ter', 'Qua', 'Qui', 'Sex', 'Sáb'];
    const weekdays = [
      { dayIndex: 1, name: 'Segunda-feira', short: 'Seg', orders: 0, items: 0, net: 0, count: 0 },
      { dayIndex: 2, name: 'Terça-feira', short: 'Ter', orders: 0, items: 0, net: 0, count: 0 },
      { dayIndex: 3, name: 'Quarta-feira', short: 'Qua', orders: 0, items: 0, net: 0, count: 0 },
      { dayIndex: 4, name: 'Quinta-feira', short: 'Qui', orders: 0, items: 0, net: 0, count: 0 },
      { dayIndex: 5, name: 'Sexta-feira', short: 'Sex', orders: 0, items: 0, net: 0, count: 0 },
      { dayIndex: 6, name: 'Sábado', short: 'Sáb', orders: 0, items: 0, net: 0, count: 0 },
      { dayIndex: 0, name: 'Domingo', short: 'Dom', orders: 0, items: 0, net: 0, count: 0 },
    ];

    let accumulated = 0;
    const timelineData = [];

    for (let d = startD; d <= endD; d++) {
      const dayStr = String(d).padStart(2, '0');
      const dateStr = `${year}-${String(month).padStart(2, '0')}-${dayStr}`;
      const dayData = metrics[dateStr] || null;
      const dateObj = new Date(year, month - 1, d);
      const dayOfWeek = dateObj.getDay();
      const wLabel = weekdayNames[dayOfWeek];

      const orders = dayData ? Number(dayData.orders || 0) : 0;
      const items = dayData ? Number(dayData.items || 0) : 0;
      const net = dayData ? Number(dayData.net || 0) : 0;
      const hasMovement = dayData !== null && (orders > 0 || net > 0 || items > 0);

      const dayQtyVal = isCollabClients ? orders : items;
      const dayRealizedVal = isCollabQtyGoal ? dayQtyVal : net;
      accumulated += dayRealizedVal;
      const idealPace = Number((idealDailyRate * d).toFixed(isCollabQtyGoal ? 0 : 2));
      const ticketMedio = orders > 0 ? Number((net / orders).toFixed(2)) : null;
      const pa = orders > 0 ? Number((items / orders).toFixed(2)) : null;
      const via = items > 0 ? Number((net / items).toFixed(2)) : null;

      const refDailySales = isCollabQtyGoal
        ? Number(idealBusinessRate.toFixed(1))
        : (selectedCollab.ref_daily_net_sales
            ? Number(selectedCollab.ref_daily_net_sales)
            : (idealBusinessRate > 0 ? Number(idealBusinessRate.toFixed(2)) : null));

      timelineData.push({
        date: dateStr,
        day: d,
        label: `${dayStr} (${wLabel})`,
        shortLabel: dayStr,
        weekday: wLabel,
        dayOfWeek,
        realized_day: hasMovement ? dayRealizedVal : 0,
        accumulated_realized: hasMovement || accumulated > 0 ? Number(accumulated.toFixed(isCollabQtyGoal ? 0 : 2)) : null,
        ideal_pace: idealPace,
        orders: hasMovement ? orders : 0,
        items: hasMovement ? items : 0,
        ticket_medio: ticketMedio,
        pa: pa,
        via: via,
        ref_daily_sales: refDailySales,
        ref_daily_orders: selectedCollab.ref_daily_orders_qty ? Number(selectedCollab.ref_daily_orders_qty) : null,
        ref_tm: selectedCollab.ref_average_ticket ? Number(selectedCollab.ref_average_ticket) : null,
        ref_pa: selectedCollab.ref_items_per_attendance ? Number(selectedCollab.ref_items_per_attendance) : null,
        ref_via: selectedCollab.ref_item_value_per_attendance ? Number(selectedCollab.ref_item_value_per_attendance) : null,
        hasMovement
      });

      if (hasMovement) {
        const wObj = weekdays.find(w => w.dayIndex === dayOfWeek);
        if (wObj) {
          wObj.orders += orders;
          wObj.items += items;
          wObj.net += net;
          wObj.count += 1;
        }
      }
    }

    const weekdayResult = weekdays
      .filter(w => w.count > 0 || w.dayIndex !== 0)
      .map(w => ({
        name: w.name,
        short: w.short,
        total_net: Number(w.net.toFixed(2)),
        avg_net: w.count > 0 ? Number((w.net / w.count).toFixed(2)) : 0,
        total_orders: w.orders,
        avg_orders: w.count > 0 ? Number((w.orders / w.count).toFixed(1)) : 0,
        total_items: w.items,
        total_qty: isCollabClients ? w.orders : w.items,
        avg_qty: w.count > 0 ? Number(((isCollabClients ? w.orders : w.items) / w.count).toFixed(1)) : 0,
        ticket_medio: w.orders > 0 ? Number((w.net / w.orders).toFixed(2)) : 0,
        pa: w.orders > 0 ? Number((w.items / w.orders).toFixed(2)) : 0,
        days_count: w.count
      }));

    return { timeline: timelineData, weekdayData: weekdayResult, isCollabQtyGoal, isCollabClients, collabQtyUnit };
  }, [selectedCollab, period, startDate, endDate]);

  // Recomendações estratégicas de produtos para a colaboradora selecionada
  const collabRecs = useMemo(() => {
    if (selectedCollab?.recommendations && Array.isArray(selectedCollab.recommendations) && selectedCollab.recommendations.length > 0) {
      return selectedCollab.recommendations;
    }
    return recommendations || [];
  }, [selectedCollab, recommendations]);

  const filteredCollabRecs = useMemo(() => {
    if (modalRecCategory === 'all') return collabRecs;
    if (modalRecCategory === 'ai' || modalRecCategory === 'isca') return [];
    return collabRecs.filter(r => r.tier_type === modalRecCategory);
  }, [collabRecs, modalRecCategory]);

  // Roteiro de abordagem / pitch atual da meta selecionada
  const collabPitchScript = useMemo(() => {
    return selectedCollab?.ai_analysis?.pitch_script
      || selectedCollab?.ai_analysis?.suggested_pitch
      || '';
  }, [selectedCollab]);

  // Renderizador rico da Seção de Produtos ISCA com Matriz de Cesta e Co-ocorrência
  const renderIscaSection = (isStandalone = false) => {
    const iscas = selectedCollab?.isca_products || [];
    return (
      <div className={`bi-section-card ${isStandalone ? 'mt-0' : ''}`}>
        <div className="bi-section-header">
          <div className="bi-section-title-group">
            <h5>
              <span>🧲</span> Mapeamento de Cesta & Produtos ISCA (Âncoras de Atração)
            </h5>
            <p>
              Análise baseada na Matriz de Co-ocorrência por Cupom Fiscal (NUMTRANSVENDA): itens com alto índice de recompra e valor acessível que funcionam como facilitadores de entrada, puxando o maior número de produtos por venda para <strong>{selectedCollab?.name}</strong>.
            </p>
          </div>
          <div className="d-flex align-items-center gap-2 flex-wrap">
            {selectedCollab?.codusur ? (
              <span className="badge bg-success-subtle text-success border border-success-subtle px-2.5 py-1.5 fw-semibold" style={{ fontSize: '0.76rem' }}>
                🎯 Vendas Pessoais (Cód. WinThor: {selectedCollab.codusur})
              </span>
            ) : (
              <span className="badge bg-info-subtle text-info-emphasis border border-info-subtle px-2.5 py-1.5 fw-semibold" style={{ fontSize: '0.76rem' }}>
                🏢 Vendas da Filial ({selectedCollab?.branch_name || 'Geral'})
              </span>
            )}
            <span className="badge bg-warning-subtle text-warning-emphasis border border-warning-subtle px-3 py-1.5 fw-bold" style={{ fontSize: '0.78rem' }}>
              {iscas.length} Produtos Alavancadores
            </span>
            <span className="badge bg-light text-muted border px-2.5 py-1.5 small" style={{ fontSize: '0.75rem' }}>
              Critério: Média de Produtos por Cupom
            </span>
          </div>
        </div>

        {iscas.length === 0 ? (
          <div className="text-center py-4 text-muted bg-light rounded-3 border">
            Nenhum produto ISCA com repetitividade mínima detectado para este período.
          </div>
        ) : (
          <div className="bi-isca-grid">
            {iscas.map((p, idx) => (
              <div key={`isca-${p.codprod}`} className="bi-isca-card">
                <div>
                  <div className="bi-rec-top">
                    <span className="bi-rec-code">Cód. {p.codprod}</span>
                    <div className="d-flex align-items-center gap-1">
                      {p.origem_rotulo && (
                        <span className="badge bg-light text-secondary border px-1.5 py-0.5" style={{ fontSize: '0.67rem' }} title={p.origem_rotulo}>
                          {p.origem_rotulo}
                        </span>
                      )}
                      <span className="bi-isca-badge">
                        🧲 {idx === 0 ? '1º TOP ISCA' : `${idx + 1}º ALAVANCA`}
                      </span>
                    </div>
                  </div>
                  <div className="bi-rec-name" title={p.descricao}>{p.descricao}</div>
                  <div className="bi-rec-details mb-2">
                    <span className="text-muted">{p.departamento || 'Geral'}</span> • Preço: <strong>{formatBRL(p.pvenda)}</strong>
                  </div>

                  {/* 4 Métricas Chave da Matriz de Cesta */}
                  <div className="bi-isca-stats-row-4">
                    <div className="bi-isca-stat-item">
                      <span className="bi-isca-stat-val text-primary">
                        {p.media_produtos_por_venda || p.pa_medio_com_isca} un
                      </span>
                      <span className="bi-isca-stat-lbl">Leva na Venda</span>
                      <span className="bi-isca-stat-sub">({p.media_pecas_por_venda || p.pa_medio_com_isca} peças)</span>
                    </div>
                    <div className="bi-isca-stat-item">
                      <span className="bi-isca-stat-val text-dark">
                        {p.repetitividade_vendas || 10}
                      </span>
                      <span className="bi-isca-stat-lbl">Repetitividade</span>
                      <span className="bi-isca-stat-sub">cupons c/ item</span>
                    </div>
                    <div className="bi-isca-stat-item">
                      <span className="bi-isca-stat-val text-success">
                        {String(p.taxa_atracao || 100).replace(/%/g, '')}%
                      </span>
                      <span className="bi-isca-stat-lbl">Cesta Composta</span>
                      <span className="bi-isca-stat-sub">+ de 1 produto</span>
                    </div>
                    <div className="bi-isca-stat-item">
                      <span className="bi-isca-stat-val text-warning-emphasis">
                        {formatBRL(p.ticket_medio_com_isca)}
                      </span>
                      <span className="bi-isca-stat-lbl">TM da Venda</span>
                      <span className="bi-isca-stat-sub">ticket c/ isca</span>
                    </div>
                  </div>

                  {/* Companheiros Mais Levados Juntos na Mesma Compra */}
                  {p.produtos_mais_levados_juntos && p.produtos_mais_levados_juntos.length > 0 && (
                    <div className="bi-isca-companion-box">
                      <div className="bi-isca-companion-title">
                        <FiPackage size={11} /> Produtos Mais Levados Juntos nesta Compra:
                      </div>
                      <div className="bi-isca-companion-tags">
                        {p.produtos_mais_levados_juntos.map((comp) => (
                          <span
                            key={`comp-${comp.codprod}`}
                            className="bi-isca-companion-pill"
                            title={`Comprado junto em ${comp.frequencia_junto} vendas`}
                          >
                            <strong>+{comp.descricao}</strong>
                            <span className="bi-isca-freq-badge">{comp.frequencia_junto}x</span>
                          </span>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Box de Venda Casada Sugerida */}
                  {p.sugestao_venda_casada && (
                    <div className="bi-isca-pairing-box">
                      <div className="bi-isca-pairing-title">
                        <FiZap size={11} /> Argumentação de Venda Casada:
                      </div>
                      <p className="bi-isca-pairing-desc">
                        {p.sugestao_venda_casada}
                      </p>
                    </div>
                  )}

                  {/* Motivo Analítico da Cesta */}
                  {p.motivo && (
                    <div className="bi-isca-motivo-box">
                      <span className="bi-isca-motivo-text">
                        💡 {p.motivo}
                      </span>
                    </div>
                  )}
                </div>

                <div className="d-flex justify-content-between align-items-center mt-2 pt-2 border-top">
                  <span className="small text-muted" style={{ fontSize: '0.72rem' }}>
                    Multiplicador de Cesta
                  </span>
                  <button
                    type="button"
                    className="btn btn-sm btn-outline-warning py-1 px-2.5 text-xs d-flex align-items-center gap-1 fw-bold"
                    onClick={() => {
                      setSimProduct(p);
                      setSimQuantity(8);
                    }}
                    title="Simular inclusão deste produto ISCA na meta"
                  >
                    <FiZap size={12} /> Simular na Meta
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="bi-container">
      {/* Barra de Progresso Superior (Indeterminate Gradient) */}
      {loading && <div className="bi-top-loading-bar" />}

      {/* ====================================================================
          1. HEADER & CONTROLES GERAIS
          ==================================================================== */}
      <div className="bi-header">
        <div className="bi-title-group">
          <h1>
            <FiTrendingUp className="text-primary" /> BI de Metas & Performance
          </h1>
          <div className="d-flex align-items-center gap-2 mt-1 flex-wrap">
            <span className="bi-badge-live">
              <span className="bi-pulse-dot"></span> Monitoramento em Tempo Real
            </span>
            {loading && (
              <span className="bi-badge-loading">
                <div className="spinner-border spinner-border-sm" role="status" style={{ width: '12px', height: '12px', borderWidth: '1.5px' }} />
                <span>{data ? 'Atualizando dados...' : 'Carregando dados...'}</span>
              </span>
            )}
            <span className="text-muted small">
              • {period.start_date && period.end_date ? `${formatDateBR(period.start_date)} a ${formatDateBR(period.end_date)} • ` : ''}{period.days_elapsed || 0} de {period.days_in_month || 30} dias corridos • {period.business_days_elapsed || 0} de {period.business_days_total || 26} dias úteis analisados ({period.business_days_remaining || 0} restantes)
            </span>
          </div>
        </div>

        <div className="d-flex align-items-center gap-2">
          <button
            type="button"
            className="btn-modern btn-modern-light"
            onClick={loadBIMetas}
            disabled={loading}
            title="Atualizar Dados"
          >
            <FiRefreshCw size={15} className={loading ? 'spin-ico' : ''} />
            <span className="d-none d-md-inline ms-1">{loading ? 'Atualizando...' : 'Atualizar'}</span>
          </button>
        </div>
      </div>

      {/* ====================================================================
          2. SELETOR RÁPIDO DE FILIAIS (Sticky Branch Bar)
          ==================================================================== */}
      <div className="bi-branches-bar">
        <div className="bi-branches-header">
          <span className="bi-branches-label">
            <FiLayers size={13} /> Filtrar por Filial ({branchesSummary.length} Filiais)
          </span>
          <span className="text-muted small">
            {branch === 'all' ? 'Exibindo consolidado de todas as unidades' : (branch === 'geral' ? 'Metas Gerais (Sem Filial)' : `Filial selecionada: ${branch}`)}
          </span>
        </div>
        <div className="bi-branches-scroll">
          <button
            type="button"
            className={`bi-branch-pill ${branch === 'all' ? 'active' : ''}`}
            onClick={() => handleBranchChange('all')}
          >
            <span>🏢 Todas as Filiais</span>
            <span className="bi-branch-badge batida">{summary.goals_count || 0} metas</span>
          </button>

          {loading && branchesSummary.length === 0 && (
            <div className="d-flex align-items-center gap-2 px-3 py-1 text-muted small">
              <div className="spinner-border spinner-border-sm text-primary" role="status" style={{ width: '13px', height: '13px', borderWidth: '1.5px' }} />
              <span>Carregando filiais...</span>
            </div>
          )}

          {branchesSummary.map((b) => (
            <button
              key={b.code}
              type="button"
              className={`bi-branch-pill ${branch === b.code ? 'active' : ''}`}
              onClick={() => handleBranchChange(b.code)}
            >
              <span>{b.code === 'geral' ? b.name : `${b.code} - ${b.name}`}</span>
              <span className={`bi-branch-badge ${b.status}`}>
                {b.pct}%
              </span>
            </button>
          ))}
        </div>
      </div>

      {/* ====================================================================
          3. BARRA DE FILTROS AVANÇADOS
          ==================================================================== */}
      <div className="bi-toolbar">
        <div className="bi-filters-left" ref={toolbarFiltersRef}>
          {/* Mês Referência (Multi-Seleção Popover) */}
          <div className="bi-filter-dropdown-container bi-filter-primary bi-filter-month" style={{ minWidth: '170px' }}>
            <span className="bi-filter-label">Mês Referência</span>
            <div
              className={`bi-filter-trigger-box ${openFilterPopover === 'month' ? 'open' : ''} ${selectedMonths.length > 1 ? 'active' : ''}`}
              onClick={() => {
                setTempSelectedMonths(selectedMonths);
                setOpenFilterPopover(openFilterPopover === 'month' ? null : 'month');
              }}
              title="Filtrar um ou mais meses de referência"
            >
              <div className="bi-filter-trigger-content">
                <FiCalendar size={14} className="text-primary opacity-75" />
                <span>
                  {selectedMonths.length === 0
                    ? 'Selecione o mês'
                    : (selectedMonths.length === 1
                        ? MONTHS_LIST.find(m => m.value === selectedMonths[0])?.label || `${selectedMonths[0]}`
                        : (selectedMonths.length === 12
                            ? 'Todos os Meses'
                            : `${selectedMonths.map(m => MONTHS_LIST.find(x => x.value === m)?.short).join(', ')}`))}
                </span>
              </div>
              <div className="d-flex align-items-center gap-1">
                {selectedMonths.length > 1 && (
                  <span className="bi-filter-counter-badge">{selectedMonths.length}</span>
                )}
                <FiChevronDown size={14} className={`bi-filter-chevron ${openFilterPopover === 'month' ? 'open' : ''}`} />
              </div>
            </div>

            {openFilterPopover === 'month' && (
              <div className="bi-filter-popover-panel wide">
                <div className="bi-popover-header">
                  <label className="bi-popover-select-all">
                    <input
                      type="checkbox"
                      checked={tempSelectedMonths.length === MONTHS_LIST.length}
                      ref={(el) => {
                        if (el) {
                          el.indeterminate = tempSelectedMonths.length > 0 && tempSelectedMonths.length < MONTHS_LIST.length;
                        }
                      }}
                      onChange={() => {
                        if (tempSelectedMonths.length === MONTHS_LIST.length) {
                          setTempSelectedMonths([]);
                        } else {
                          setTempSelectedMonths(MONTHS_LIST.map(m => m.value));
                        }
                      }}
                    />
                    <span>Selecionar todos ({MONTHS_LIST.length})</span>
                  </label>
                  <div className="bi-popover-header-actions">
                    {tempSelectedMonths.length > 0 && (
                      <button
                        type="button"
                        className="bi-popover-clear-btn"
                        onClick={() => setTempSelectedMonths([])}
                      >
                        Desmarcar
                      </button>
                    )}
                    <span className="bi-popover-count-tag">{tempSelectedMonths.length} / 12</span>
                  </div>
                </div>

                <div className="bi-popover-list" style={{ maxHeight: '250px' }}>
                  {MONTHS_LIST.map((m) => {
                    const isChecked = tempSelectedMonths.includes(m.value);
                    return (
                      <div
                        key={m.value}
                        className={`bi-popover-item ${isChecked ? 'selected' : ''}`}
                        onClick={() => {
                          setTempSelectedMonths(prev =>
                            prev.includes(m.value) ? prev.filter(x => x !== m.value) : [...prev, m.value]
                          );
                        }}
                      >
                        <div className="bi-popover-item-left">
                          <input
                            type="checkbox"
                            className="bi-popover-checkbox"
                            checked={isChecked}
                            onChange={() => {}}
                          />
                          <span className="fw-semibold text-dark" style={{ fontSize: '0.82rem' }}>
                            {m.label}
                          </span>
                        </div>
                        <button
                          type="button"
                          className="bi-popover-select-only-btn"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleApplyMonths([m.value]);
                          }}
                        >
                          Apenas este
                        </button>
                      </div>
                    );
                  })}
                </div>

                <div className="bi-popover-footer">
                  <span className="bi-popover-footer-summary">
                    {tempSelectedMonths.length === 0 ? 'Nenhum mês marcado' : `${tempSelectedMonths.length} mês(es) selecionado(s)`}
                  </span>
                  <div className="bi-popover-footer-btns">
                    <button
                      type="button"
                      className="btn btn-sm btn-primary"
                      disabled={tempSelectedMonths.length === 0}
                      onClick={() => handleApplyMonths(tempSelectedMonths)}
                    >
                      Aplicar na Análise
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Ano (Multi-Seleção Popover) */}
          <div className="bi-filter-dropdown-container bi-filter-primary bi-filter-year" style={{ minWidth: '110px' }}>
            <span className="bi-filter-label">Ano</span>
            <div
              className={`bi-filter-trigger-box ${openFilterPopover === 'year' ? 'open' : ''} ${selectedYears.length > 1 ? 'active' : ''}`}
              onClick={() => {
                setTempSelectedYears(selectedYears);
                setOpenFilterPopover(openFilterPopover === 'year' ? null : 'year');
              }}
              title="Filtrar anos de referência"
            >
              <div className="bi-filter-trigger-content">
                <span>
                  {selectedYears.length === 0
                    ? 'Selecione o ano'
                    : (selectedYears.length === 1
                        ? `${selectedYears[0]}`
                        : `${selectedYears.join(', ')}`)}
                </span>
              </div>
              <div className="d-flex align-items-center gap-1">
                {selectedYears.length > 1 && (
                  <span className="bi-filter-counter-badge">{selectedYears.length}</span>
                )}
                <FiChevronDown size={14} className={`bi-filter-chevron ${openFilterPopover === 'year' ? 'open' : ''}`} />
              </div>
            </div>

            {openFilterPopover === 'year' && (
              <div className="bi-filter-popover-panel">
                <div className="bi-popover-header">
                  <label className="bi-popover-select-all">
                    <input
                      type="checkbox"
                      checked={tempSelectedYears.length === yearsList.length}
                      ref={(el) => {
                        if (el) {
                          el.indeterminate = tempSelectedYears.length > 0 && tempSelectedYears.length < yearsList.length;
                        }
                      }}
                      onChange={() => {
                        if (tempSelectedYears.length === yearsList.length) {
                          setTempSelectedYears([]);
                        } else {
                          setTempSelectedYears([...yearsList]);
                        }
                      }}
                    />
                    <span>Todos os Anos</span>
                  </label>
                  <span className="bi-popover-count-tag">{tempSelectedYears.length} / {yearsList.length}</span>
                </div>

                <div className="bi-popover-list">
                  {yearsList.map((y) => {
                    const isChecked = tempSelectedYears.includes(y);
                    return (
                      <div
                        key={y}
                        className={`bi-popover-item ${isChecked ? 'selected' : ''}`}
                        onClick={() => {
                          setTempSelectedYears(prev =>
                            prev.includes(y) ? prev.filter(x => x !== y) : [...prev, y]
                          );
                        }}
                      >
                        <div className="bi-popover-item-left">
                          <input
                            type="checkbox"
                            className="bi-popover-checkbox"
                            checked={isChecked}
                            onChange={() => {}}
                          />
                          <span className="fw-semibold text-dark">{y}</span>
                        </div>
                        <button
                          type="button"
                          className="bi-popover-select-only-btn"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleApplyYears([y]);
                          }}
                        >
                          Apenas este
                        </button>
                      </div>
                    );
                  })}
                </div>

                <div className="bi-popover-footer">
                  <span className="bi-popover-footer-summary">
                    {tempSelectedYears.length} ano(s)
                  </span>
                  <div className="bi-popover-footer-btns">
                    <button
                      type="button"
                      className="btn btn-sm btn-primary"
                      disabled={tempSelectedYears.length === 0}
                      onClick={() => handleApplyYears(tempSelectedYears)}
                    >
                      Aplicar
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Início & Fim (Intervalo & Presets de Período Popover) */}
          <div className={`bi-filter-dropdown-container bi-filter-secondary bi-filter-period ${isMobileFiltersExpanded ? 'mobile-visible' : ''}`} style={{ minWidth: '190px' }}>
            <span className="bi-filter-label">Início / Fim</span>
            <div
              className={`bi-filter-trigger-box ${openFilterPopover === 'period' ? 'open' : ''}`}
              onClick={() => {
                setTempStartDate(startDate);
                setTempEndDate(endDate);
                setOpenFilterPopover(openFilterPopover === 'period' ? null : 'period');
              }}
              title="Configurar intervalo ou atalhos de período"
            >
              <div className="bi-filter-trigger-content">
                <FiClock size={13} className="text-primary opacity-75" />
                <span style={{ fontSize: '0.78rem' }}>
                  {formatDateBR(startDate)} a {formatDateBR(endDate)}
                </span>
              </div>
              <FiChevronDown size={14} className={`bi-filter-chevron ${openFilterPopover === 'period' ? 'open' : ''}`} />
            </div>

            {openFilterPopover === 'period' && (
              <div className="bi-filter-popover-panel date-panel p-3">
                <div className="fw-bold text-dark small mb-2 d-flex align-items-center gap-1">
                  <FiClock size={14} className="text-primary" /> Intervalo Customizado
                </div>
                <div className="d-flex align-items-center gap-2 mb-3">
                  <div className="flex-1">
                    <label className="text-muted" style={{ fontSize: '0.72rem' }}>De:</label>
                    <input
                      type="date"
                      className="bi-input form-control form-control-sm"
                      value={tempStartDate}
                      onChange={(e) => setTempStartDate(e.target.value)}
                    />
                  </div>
                  <div className="flex-1">
                    <label className="text-muted" style={{ fontSize: '0.72rem' }}>Até:</label>
                    <input
                      type="date"
                      className="bi-input form-control form-control-sm"
                      value={tempEndDate}
                      onChange={(e) => setTempEndDate(e.target.value)}
                    />
                  </div>
                </div>

                <div className="fw-bold text-dark small mb-1.5">Atalhos Rápidos de Período:</div>
                <div className="d-flex flex-column gap-1 mb-3">
                  <button
                    type="button"
                    className="btn btn-sm btn-light text-start border py-1 px-2"
                    style={{ fontSize: '0.76rem' }}
                    onClick={() => {
                      const full = getFullDatesForMonthsAndYears(selectedYears, selectedMonths);
                      handleApplyPeriod(full.start, full.end);
                    }}
                  >
                    📅 Todo o período dos meses selecionados
                  </button>
                  <button
                    type="button"
                    className="btn btn-sm btn-light text-start border py-1 px-2"
                    style={{ fontSize: '0.76rem' }}
                    onClick={() => {
                      const y = selectedYears[0] || now.getFullYear();
                      const m = selectedMonths[0] || (now.getMonth() + 1);
                      const s = `${y}-${String(m).padStart(2, '0')}-01`;
                      const e = `${y}-${String(m).padStart(2, '0')}-15`;
                      handleApplyPeriod(s, e);
                    }}
                  >
                    🌓 1ª Quinzena (Dia 01 ao 15)
                  </button>
                  <button
                    type="button"
                    className="btn btn-sm btn-light text-start border py-1 px-2"
                    style={{ fontSize: '0.76rem' }}
                    onClick={() => {
                      const y = selectedYears[0] || now.getFullYear();
                      const m = selectedMonths[0] || (now.getMonth() + 1);
                      const lastD = new Date(y, m, 0).getDate();
                      const s = `${y}-${String(m).padStart(2, '0')}-16`;
                      const e = `${y}-${String(m).padStart(2, '0')}-${String(lastD).padStart(2, '0')}`;
                      handleApplyPeriod(s, e);
                    }}
                  >
                    🌗 2ª Quinzena (Dia 16 ao Fim)
                  </button>
                  <button
                    type="button"
                    className="btn btn-sm btn-light text-start border py-1 px-2"
                    style={{ fontSize: '0.76rem' }}
                    onClick={() => {
                      const today = new Date();
                      today.setDate(today.getDate() - 1);
                      const y = selectedYears[0] || today.getFullYear();
                      const m = selectedMonths[0] || (today.getMonth() + 1);
                      const s = `${y}-${String(m).padStart(2, '0')}-01`;
                      const e = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(today.getDate()).padStart(2, '0')}`;
                      handleApplyPeriod(s, e);
                    }}
                  >
                    ⏳ Até Ontem (Dias Fechados)
                  </button>
                </div>

                <div className="d-flex justify-content-end">
                  <button
                    type="button"
                    className="btn btn-sm btn-primary px-3"
                    onClick={() => handleApplyPeriod(tempStartDate, tempEndDate)}
                  >
                    Aplicar Intervalo
                  </button>
                </div>
              </div>
            )}
          </div>

          {/* Tipo de Alvo (Multi-Seleção Popover) */}
          <div className={`bi-filter-dropdown-container bi-filter-secondary bi-filter-target-type ${isMobileFiltersExpanded ? 'mobile-visible' : ''}`} style={{ minWidth: '150px' }}>
            <span className="bi-filter-label">Tipo de Alvo</span>
            <div
              className={`bi-filter-trigger-box ${openFilterPopover === 'target_type' ? 'open' : ''} ${selectedTargetTypes.length < TARGET_TYPES_LIST.length ? 'active' : ''}`}
              onClick={() => {
                setTempSelectedTargetTypes(selectedTargetTypes);
                setOpenFilterPopover(openFilterPopover === 'target_type' ? null : 'target_type');
              }}
              title="Filtrar por tipo de alvo da meta (Vendedores, Funcionários, Filiais, Geral)"
            >
              <div className="bi-filter-trigger-content">
                <span>
                  {selectedTargetTypes.length === TARGET_TYPES_LIST.length
                    ? 'Todos os Alvos'
                    : (selectedTargetTypes.length === 1
                        ? TARGET_TYPES_LIST.find(t => t.value === selectedTargetTypes[0])?.label || selectedTargetTypes[0]
                        : `${selectedTargetTypes.length} tipos de alvo`)}
                </span>
              </div>
              <div className="d-flex align-items-center gap-1">
                {selectedTargetTypes.length < TARGET_TYPES_LIST.length && (
                  <span className="bi-filter-counter-badge">{selectedTargetTypes.length}</span>
                )}
                <FiChevronDown size={14} className={`bi-filter-chevron ${openFilterPopover === 'target_type' ? 'open' : ''}`} />
              </div>
            </div>

            {openFilterPopover === 'target_type' && (
              <div className="bi-filter-popover-panel wide">
                <div className="bi-popover-header">
                  <label className="bi-popover-select-all">
                    <input
                      type="checkbox"
                      checked={tempSelectedTargetTypes.length === TARGET_TYPES_LIST.length}
                      ref={(el) => {
                        if (el) {
                          el.indeterminate = tempSelectedTargetTypes.length > 0 && tempSelectedTargetTypes.length < TARGET_TYPES_LIST.length;
                        }
                      }}
                      onChange={() => {
                        if (tempSelectedTargetTypes.length === TARGET_TYPES_LIST.length) {
                          setTempSelectedTargetTypes([]);
                        } else {
                          setTempSelectedTargetTypes(TARGET_TYPES_LIST.map(t => t.value));
                        }
                      }}
                    />
                    <span>Selecionar Todos</span>
                  </label>
                  <span className="bi-popover-count-tag">{tempSelectedTargetTypes.length} / {TARGET_TYPES_LIST.length}</span>
                </div>

                <div className="bi-popover-list">
                  {TARGET_TYPES_LIST.map((t) => {
                    const isChecked = tempSelectedTargetTypes.includes(t.value);
                    const TargetIcon = t.icon || FiTarget;
                    return (
                      <div
                        key={t.value}
                        className={`bi-popover-item ${isChecked ? 'selected' : ''}`}
                        onClick={() => {
                          setTempSelectedTargetTypes(prev =>
                            prev.includes(t.value) ? prev.filter(x => x !== t.value) : [...prev, t.value]
                          );
                        }}
                      >
                        <div className="bi-popover-item-left">
                          <input
                            type="checkbox"
                            className="bi-popover-checkbox"
                            checked={isChecked}
                            onChange={() => {}}
                          />
                          <span className={`bi-target-type-badge ${t.value}`}>
                            <TargetIcon size={12} className="me-1" />
                            {t.label}
                          </span>
                        </div>
                        <button
                          type="button"
                          className="bi-popover-select-only-btn"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleApplyTargetTypes([t.value]);
                          }}
                        >
                          Apenas este
                        </button>
                      </div>
                    );
                  })}
                </div>

                <div className="bi-popover-footer">
                  <span className="bi-popover-footer-summary">
                    {tempSelectedTargetTypes.length} selecionado(s)
                  </span>
                  <div className="bi-popover-footer-btns">
                    <button
                      type="button"
                      className="btn btn-sm btn-primary"
                      disabled={tempSelectedTargetTypes.length === 0}
                      onClick={() => handleApplyTargetTypes(tempSelectedTargetTypes)}
                    >
                      Aplicar
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Classificação da Meta (Multi-Seleção Popover) */}
          <div className={`bi-filter-dropdown-container bi-filter-secondary bi-filter-classification ${isMobileFiltersExpanded ? 'mobile-visible' : ''}`} style={{ minWidth: '150px' }}>
            <span className="bi-filter-label">Classificação</span>
            <div
              className={`bi-filter-trigger-box ${openFilterPopover === 'classification' ? 'open' : ''} ${selectedClassifications.length < CLASSIFICATIONS_LIST.length ? 'active' : ''}`}
              onClick={() => {
                setTempSelectedClassifications(selectedClassifications);
                setOpenFilterPopover(openFilterPopover === 'classification' ? null : 'classification');
              }}
              title="Filtrar por classificação da meta"
            >
              <div className="bi-filter-trigger-content">
                <span>
                  {selectedClassifications.length === CLASSIFICATIONS_LIST.length
                    ? 'Todas'
                    : (selectedClassifications.length === 1
                        ? CLASSIFICATIONS_LIST.find(c => c.value === selectedClassifications[0])?.label || selectedClassifications[0]
                        : `${selectedClassifications.length} classificações`)}
                </span>
              </div>
              <div className="d-flex align-items-center gap-1">
                {selectedClassifications.length < CLASSIFICATIONS_LIST.length && (
                  <span className="bi-filter-counter-badge">{selectedClassifications.length}</span>
                )}
                <FiChevronDown size={14} className={`bi-filter-chevron ${openFilterPopover === 'classification' ? 'open' : ''}`} />
              </div>
            </div>

            {openFilterPopover === 'classification' && (
              <div className="bi-filter-popover-panel wide">
                <div className="bi-popover-header">
                  <label className="bi-popover-select-all">
                    <input
                      type="checkbox"
                      checked={tempSelectedClassifications.length === CLASSIFICATIONS_LIST.length}
                      ref={(el) => {
                        if (el) {
                          el.indeterminate = tempSelectedClassifications.length > 0 && tempSelectedClassifications.length < CLASSIFICATIONS_LIST.length;
                        }
                      }}
                      onChange={() => {
                        if (tempSelectedClassifications.length === CLASSIFICATIONS_LIST.length) {
                          setTempSelectedClassifications([]);
                        } else {
                          setTempSelectedClassifications(CLASSIFICATIONS_LIST.map(c => c.value));
                        }
                      }}
                    />
                    <span>Selecionar Todas</span>
                  </label>
                  <span className="bi-popover-count-tag">{tempSelectedClassifications.length} / {CLASSIFICATIONS_LIST.length}</span>
                </div>

                <div className="bi-popover-list">
                  {CLASSIFICATIONS_LIST.map((c) => {
                    const isChecked = tempSelectedClassifications.includes(c.value);
                    return (
                      <div
                        key={c.value}
                        className={`bi-popover-item ${isChecked ? 'selected' : ''}`}
                        onClick={() => {
                          setTempSelectedClassifications(prev =>
                            prev.includes(c.value) ? prev.filter(x => x !== c.value) : [...prev, c.value]
                          );
                        }}
                      >
                        <div className="bi-popover-item-left">
                          <input
                            type="checkbox"
                            className="bi-popover-checkbox"
                            checked={isChecked}
                            onChange={() => {}}
                          />
                          <span className={`bi-classification-badge ${c.value}`}>
                            {c.label}
                          </span>
                        </div>
                        <button
                          type="button"
                          className="bi-popover-select-only-btn"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleApplyClassifications([c.value]);
                          }}
                        >
                          Apenas esta
                        </button>
                      </div>
                    );
                  })}
                </div>

                <div className="bi-popover-footer">
                  <span className="bi-popover-footer-summary">
                    {tempSelectedClassifications.length} selecionada(s)
                  </span>
                  <div className="bi-popover-footer-btns">
                    <button
                      type="button"
                      className="btn btn-sm btn-primary"
                      disabled={tempSelectedClassifications.length === 0}
                      onClick={() => handleApplyClassifications(tempSelectedClassifications)}
                    >
                      Aplicar
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Subtipo da Meta (Multi-Seleção Popover) */}
          <div className={`bi-filter-dropdown-container bi-filter-secondary bi-filter-subtype ${isMobileFiltersExpanded ? 'mobile-visible' : ''}`} style={{ minWidth: '160px' }}>
            <span className="bi-filter-label">Subtipo da Meta</span>
            <div
              className={`bi-filter-trigger-box ${openFilterPopover === 'subtype' ? 'open' : ''} ${selectedSubtypes.length < SUBTYPES_LIST.length ? 'active' : ''}`}
              onClick={() => {
                setTempSelectedSubtypes(selectedSubtypes);
                setOpenFilterPopover(openFilterPopover === 'subtype' ? null : 'subtype');
              }}
              title="Filtrar por subtipo de meta (Vendas, Produtos Destaque, Clientes Positivados)"
            >
              <div className="bi-filter-trigger-content">
                <span>
                  {selectedSubtypes.length === SUBTYPES_LIST.length
                    ? 'Todos os Subtipos'
                    : (selectedSubtypes.length === 1
                        ? SUBTYPES_LIST.find(s => s.value === selectedSubtypes[0])?.label || selectedSubtypes[0]
                        : `${selectedSubtypes.length} subtipos`)}
                </span>
              </div>
              <div className="d-flex align-items-center gap-1">
                {selectedSubtypes.length < SUBTYPES_LIST.length && (
                  <span className="bi-filter-counter-badge">{selectedSubtypes.length}</span>
                )}
                <FiChevronDown size={14} className={`bi-filter-chevron ${openFilterPopover === 'subtype' ? 'open' : ''}`} />
              </div>
            </div>

            {openFilterPopover === 'subtype' && (
              <div className="bi-filter-popover-panel wide">
                <div className="bi-popover-header">
                  <label className="bi-popover-select-all">
                    <input
                      type="checkbox"
                      checked={tempSelectedSubtypes.length === SUBTYPES_LIST.length}
                      ref={(el) => {
                        if (el) {
                          el.indeterminate = tempSelectedSubtypes.length > 0 && tempSelectedSubtypes.length < SUBTYPES_LIST.length;
                        }
                      }}
                      onChange={() => {
                        if (tempSelectedSubtypes.length === SUBTYPES_LIST.length) {
                          setTempSelectedSubtypes([]);
                        } else {
                          setTempSelectedSubtypes(SUBTYPES_LIST.map(s => s.value));
                        }
                      }}
                    />
                    <span>Selecionar Todos</span>
                  </label>
                  <span className="bi-popover-count-tag">{tempSelectedSubtypes.length} / {SUBTYPES_LIST.length}</span>
                </div>

                <div className="bi-popover-list">
                  {SUBTYPES_LIST.map((s) => {
                    const isChecked = tempSelectedSubtypes.includes(s.value);
                    const SubtypeIcon = s.icon || FiTarget;
                    return (
                      <div
                        key={s.value}
                        className={`bi-popover-item ${isChecked ? 'selected' : ''}`}
                        onClick={() => {
                          setTempSelectedSubtypes(prev =>
                            prev.includes(s.value) ? prev.filter(x => x !== s.value) : [...prev, s.value]
                          );
                        }}
                      >
                        <div className="bi-popover-item-left">
                          <input
                            type="checkbox"
                            className="bi-popover-checkbox"
                            checked={isChecked}
                            onChange={() => {}}
                          />
                          <span className={`badge-pill-modern badge-subtype-${s.value === 'produtos_destaque' ? 'destaque' : (s.value === 'clientes_positivados' ? 'positivados' : 'vendas')}`}>
                            <SubtypeIcon size={12} className="badge-ico" />
                            <span>{s.label}</span>
                          </span>
                        </div>
                        <button
                          type="button"
                          className="bi-popover-select-only-btn"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleApplySubtypes([s.value]);
                          }}
                        >
                          Apenas este
                        </button>
                      </div>
                    );
                  })}
                </div>

                <div className="bi-popover-footer">
                  <span className="bi-popover-footer-summary">
                    {tempSelectedSubtypes.length} selecionado(s)
                  </span>
                  <div className="bi-popover-footer-btns">
                    <button
                      type="button"
                      className="btn btn-sm btn-primary"
                      disabled={tempSelectedSubtypes.length === 0}
                      onClick={() => handleApplySubtypes(tempSelectedSubtypes)}
                    >
                      Aplicar
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Status da Meta (Multi-Seleção Popover) */}
          <div className={`bi-filter-dropdown-container bi-filter-secondary bi-filter-status ${isMobileFiltersExpanded ? 'mobile-visible' : ''}`} style={{ minWidth: '150px' }}>
            <span className="bi-filter-label">Status da Meta</span>
            <div
              className={`bi-filter-trigger-box ${openFilterPopover === 'status' ? 'open' : ''} ${selectedStatuses.length < STATUSES_LIST.length ? 'active' : ''}`}
              onClick={() => {
                setTempSelectedStatuses(selectedStatuses);
                setOpenFilterPopover(openFilterPopover === 'status' ? null : 'status');
              }}
              title="Filtrar por status de atingimento"
            >
              <div className="bi-filter-trigger-content">
                <span>
                  {selectedStatuses.length === STATUSES_LIST.length
                    ? 'Todos'
                    : (selectedStatuses.length === 1
                        ? STATUSES_LIST.find(s => s.value === selectedStatuses[0])?.label || selectedStatuses[0]
                        : `${selectedStatuses.length} status`)}
                </span>
              </div>
              <div className="d-flex align-items-center gap-1">
                {selectedStatuses.length < STATUSES_LIST.length && (
                  <span className="bi-filter-counter-badge">{selectedStatuses.length}</span>
                )}
                <FiChevronDown size={14} className={`bi-filter-chevron ${openFilterPopover === 'status' ? 'open' : ''}`} />
              </div>
            </div>

            {openFilterPopover === 'status' && (
              <div className="bi-filter-popover-panel wide">
                <div className="bi-popover-header">
                  <label className="bi-popover-select-all">
                    <input
                      type="checkbox"
                      checked={tempSelectedStatuses.length === STATUSES_LIST.length}
                      ref={(el) => {
                        if (el) {
                          el.indeterminate = tempSelectedStatuses.length > 0 && tempSelectedStatuses.length < STATUSES_LIST.length;
                        }
                      }}
                      onChange={() => {
                        if (tempSelectedStatuses.length === STATUSES_LIST.length) {
                          setTempSelectedStatuses([]);
                        } else {
                          setTempSelectedStatuses(STATUSES_LIST.map(s => s.value));
                        }
                      }}
                    />
                    <span>Todos os Status</span>
                  </label>
                  <span className="bi-popover-count-tag">{tempSelectedStatuses.length} / {STATUSES_LIST.length}</span>
                </div>

                <div className="bi-popover-list">
                  {STATUSES_LIST.map((s) => {
                    const isChecked = tempSelectedStatuses.includes(s.value);
                    return (
                      <div
                        key={s.value}
                        className={`bi-popover-item ${isChecked ? 'selected' : ''}`}
                        onClick={() => {
                          setTempSelectedStatuses(prev =>
                            prev.includes(s.value) ? prev.filter(x => x !== s.value) : [...prev, s.value]
                          );
                        }}
                      >
                        <div className="bi-popover-item-left">
                          <input
                            type="checkbox"
                            className="bi-popover-checkbox"
                            checked={isChecked}
                            onChange={() => {}}
                          />
                          <span className="fw-semibold text-dark" style={{ fontSize: '0.82rem' }}>
                            {s.label}
                          </span>
                        </div>
                        <button
                          type="button"
                          className="bi-popover-select-only-btn"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleApplyStatuses([s.value]);
                          }}
                        >
                          Apenas este
                        </button>
                      </div>
                    );
                  })}
                </div>

                <div className="bi-popover-footer">
                  <span className="bi-popover-footer-summary">
                    {tempSelectedStatuses.length} selecionado(s)
                  </span>
                  <div className="bi-popover-footer-btns">
                    <button
                      type="button"
                      className="btn btn-sm btn-primary"
                      disabled={tempSelectedStatuses.length === 0}
                      onClick={() => handleApplyStatuses(tempSelectedStatuses)}
                    >
                      Aplicar
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Busca e Submodal Suspenso de Metas / Colaboradores */}
          <div className="bi-filter-item bi-filter-primary bi-filter-search" style={{ flex: 1, minWidth: '260px' }}>
            <span className="bi-filter-label">Buscar Meta / Colaborador</span>
            <div className="bi-search-dropdown-container" ref={searchDropdownRef}>
              <div className="bi-search-input-wrapper">
                <FiSearch
                  size={15}
                  className="bi-search-input-icon"
                />
                <input
                  type="text"
                  className="bi-input bi-search-input-box w-100"
                  placeholder={inputPlaceholder}
                  value={searchQuery}
                  onChange={(e) => {
                    setSearchQuery(e.target.value);
                    if (!isDropdownOpen) setIsDropdownOpen(true);
                  }}
                  onFocus={handleOpenDropdown}
                  onClick={() => {
                    if (!isDropdownOpen) handleOpenDropdown();
                  }}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      handleApplySelection();
                    } else if (e.key === 'Escape') {
                      setIsDropdownOpen(false);
                    }
                  }}
                />

                <div className="bi-search-actions-inside">
                  {selectedGoalIds.length > 0 && (
                    <span className="bi-selected-badge" title="Metas selecionadas para análise">
                      <FiCheckSquare size={12} /> {selectedGoalIds.length}
                    </span>
                  )}
                  {(searchQuery || selectedGoalIds.length > 0) && (
                    <FiX
                      size={14}
                      className="bi-search-clear-icon"
                      title="Limpar busca e seleção"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleClearSelection();
                      }}
                    />
                  )}
                  <FiChevronDown
                    size={16}
                    className={`bi-search-chevron ${isDropdownOpen ? 'open' : ''}`}
                    onClick={(e) => {
                      e.stopPropagation();
                      if (!isDropdownOpen) {
                        handleOpenDropdown();
                      } else {
                        setIsDropdownOpen(false);
                      }
                    }}
                    role="button"
                    tabIndex={0}
                    title={isDropdownOpen ? 'Recolher metas' : 'Expandir metas'}
                  />
                </div>
              </div>

              {/* Submodal Suspenso (Dropdown Popover) */}
              {isDropdownOpen && (
                <div className="bi-search-popover">
                  {/* Cabeçalho */}
                  <div className="bi-popover-header">
                    <label className="bi-popover-select-all">
                      <input
                        type="checkbox"
                        checked={allFilteredSelected}
                        ref={(el) => {
                          if (el) {
                            el.indeterminate = someFilteredSelected && !allFilteredSelected;
                          }
                        }}
                        onChange={handleToggleSelectAllFiltered}
                      />
                      <span>
                        Selecionar todos ({filteredGoals.length})
                      </span>
                    </label>

                    <div className="bi-popover-header-actions">
                      {tempSelectedGoalIds.length > 0 && (
                        <button
                          type="button"
                          className="bi-popover-clear-btn"
                          onClick={() => setTempSelectedGoalIds([])}
                        >
                          Desmarcar Todos
                        </button>
                      )}
                      <span className="bi-popover-count-tag">
                        {tempSelectedGoalIds.length} / {availableGoals.length}
                      </span>
                    </div>
                  </div>

                  {/* Lista de Resultados */}
                  <div className="bi-popover-list">
                    {filteredGoals.length === 0 ? (
                      <div className="bi-popover-empty">
                        <FiHelpCircle size={24} className="d-block mx-auto mb-1 opacity-50" />
                        Nenhum colaborador ou meta encontrada{searchQuery ? ` para "${searchQuery}"` : ''}.
                      </div>
                    ) : (
                      filteredGoals.map((g) => {
                        const isSelected = tempSelectedGoalIds.includes(g.id);
                        const isBranch = g.is_branch_goal || g.type === 'filial';
                        const isGeneral = g.is_general || g.type === 'todos';
                        const initials = (g.name || 'ME').substring(0, 2).toUpperCase();

                        return (
                          <div
                            key={g.id}
                            className={`bi-popover-item ${isSelected ? 'selected' : ''}`}
                            onClick={() => handleToggleGoal(g.id)}
                          >
                            <div className="bi-popover-item-left">
                              <input
                                type="checkbox"
                                className="bi-popover-checkbox"
                                checked={isSelected}
                                onChange={() => { }}
                              />

                              <div className={`bi-popover-avatar ${isBranch ? 'branch' : (isGeneral ? 'general' : '')}`}>
                                {isBranch ? <FiLayers size={13} /> : (isGeneral ? <FiUsers size={13} /> : initials)}
                              </div>

                              <div className="bi-popover-info">
                                <span className="bi-popover-name" title={g.name}>
                                  {g.name}
                                </span>
                                <div className="bi-popover-meta">
                                  <span>🏢 Filial {g.branch_code}</span>
                                  {g.codusur && <span>• RCA: {g.codusur}</span>}
                                  {g.matricula && <span>• Matr: {g.matricula}</span>}
                                  <span className={`bi-popover-tag target-${g.target_type || 'vendor'}`}>
                                    {g.target_type === 'employee' ? 'Funcionário' : (g.target_type === 'branch' ? 'Filial' : (g.target_type === 'general' ? 'Geral' : 'Vendedor'))}
                                  </span>
                                  <span className={`bi-popover-tag ${g.goal_classification || 'vendedor'}`}>
                                    {g.goal_classification === 'bijou' ? 'Bijou' : (g.goal_classification === 'operador_caixa' ? 'Caixa' : 'Vendedor')}
                                  </span>
                                </div>
                              </div>
                            </div>

                            <div className="bi-popover-item-right">
                              <span className="bi-popover-target-val">{formatBRL(g.target)}</span>
                              <button
                                type="button"
                                className="bi-popover-select-only-btn"
                                title="Selecionar apenas esta meta"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleSelectOnlyGoal(g.id);
                                }}
                              >
                                Apenas esta
                              </button>
                            </div>
                          </div>
                        );
                      })
                    )}
                  </div>

                  {/* Rodapé com Ações */}
                  <div className="bi-popover-footer">
                    <span className="bi-popover-footer-summary">
                      {tempSelectedGoalIds.length === 0
                        ? 'Todas as metas ativas'
                        : `${tempSelectedGoalIds.length} selecionada(s)`}
                    </span>

                    <div className="bi-popover-footer-btns">
                      <button
                        type="button"
                        className="btn-modern btn-modern-light btn-sm"
                        onClick={() => setIsDropdownOpen(false)}
                      >
                        Fechar
                      </button>
                      <button
                        type="button"
                        className="btn-modern btn-modern-primary btn-sm"
                        onClick={handleApplySelection}
                      >
                        <FiCheckSquare size={13} />
                        Aplicar na Análise ({tempSelectedGoalIds.length > 0 ? tempSelectedGoalIds.length : 'Todas'})
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Toggle de Filtros Secundários no Mobile */}
          <div className="bi-mobile-filter-toggle-container">
            <button
              type="button"
              className={`bi-mobile-filter-toggle-btn ${activeSecondaryFiltersCount > 0 ? 'has-active' : ''}`}
              onClick={() => setIsMobileFiltersExpanded(prev => !prev)}
            >
              <div className="d-flex align-items-center gap-1.5">
                <FiSliders size={13} />
                <span>{isMobileFiltersExpanded ? 'Ocultar Filtros Adicionais' : 'Filtros Adicionais'}</span>
                {activeSecondaryFiltersCount > 0 && (
                  <span className="bi-filter-counter-badge">{activeSecondaryFiltersCount}</span>
                )}
              </div>
              <FiChevronDown size={14} className={`bi-filter-chevron ${isMobileFiltersExpanded ? 'open' : ''}`} />
            </button>
            {activeSecondaryFiltersCount > 0 && (
              <button
                type="button"
                className="bi-mobile-filter-clear-btn"
                onClick={handleResetSecondaryFilters}
                title="Redefinir filtros adicionais para o padrão"
              >
                <FiRefreshCw size={11} /> Redefinir
              </button>
            )}
          </div>
        </div>
      </div>

      {error && (
        <div className="alert alert-danger d-flex align-items-center gap-2 mb-4" role="alert">
          <FiAlertCircle size={18} /> {error}
        </div>
      )}

      {/* Banner de Análise Filtrada */}
      {selectedGoalIds.length > 0 && (
        <div className="bi-active-filter-banner">
          <div className="bi-active-filter-text">
            <FiCheckSquare size={16} />
            <span>
              Análise personalizada: exibindo dados calculados para <strong>{selectedGoalIds.length} meta(s)/colaborador(es) selecionado(s)</strong>.
            </span>
          </div>
          <button
            type="button"
            className="bi-active-filter-reset"
            onClick={handleClearSelection}
          >
            <FiX size={14} /> Exibir Todas as Metas
          </button>
        </div>
      )}

      {/* ====================================================================
          4. RANKING E MONITORAMENTO DETALHADO POR VENDEDORA (Performance Individual)
          ==================================================================== */}
      {loading && !data ? (
        <div className="bi-initial-loading-card">
          <div className="spinner-modern mb-3" />
          <h5 className="fw-bold text-dark mb-1">Carregando Inteligência de Metas & Performance</h5>
          <p className="text-muted small mb-4" style={{ maxWidth: '480px' }}>
            Consolidando dados de faturamento, cupons, peças por atendimento, ritmo diário e projeções em tempo real...
          </p>
          <div className="bi-skeleton-table">
            {[1, 2, 3, 4, 5, 6].map((idx) => (
              <div key={idx} className="bi-skeleton-row bi-skeleton" />
            ))}
          </div>
        </div>
      ) : (
        <>
          <div className="bi-collaborators-section">
            <div className="d-flex justify-content-between align-items-center flex-wrap gap-2 mb-3">
              <div>
                <h5 className="fw-bold mb-1 text-dark d-flex align-items-center gap-2">
                  <FiUsers className="text-primary" /> Performance Individual das Metas ({sortedCollaborators.length} Colaboradores)
                </h5>
                <p className="text-muted small mb-0">
                  Acompanhamento de atingimento, gap, ticket médio e probabilidade. <strong>Dê um duplo clique</strong> em qualquer linha da tabela para abrir o painel completo de indicadores em carrossel.
                </p>
              </div>

              <div className="d-flex align-items-center gap-2">
                <span className="text-muted small fw-bold">Ordenar por:</span>
                <select
                  className="bi-select"
                  style={{ minWidth: '180px' }}
                  value={sortBy}
                  onChange={(e) => handleSortByDropdownChange(e.target.value)}
                >
                  {!['pct_desc', 'realized_desc', 'daily_sales_desc', 'orders_day_desc', 'ticket_desc', 'via_desc', 'gap_desc'].includes(sortBy) && (
                    <option value="">Personalizado ({sortField} {sortOrder === 'asc' ? '▲' : '▼'})</option>
                  )}
                  <option value="pct_desc">% Atingimento (Maior)</option>
                  <option value="realized_desc">Faturamento Realizado</option>
                  <option value="daily_sales_desc">Venda Líquida Diária</option>
                  <option value="orders_day_desc">Vendas Diárias por Vendedor</option>
                  <option value="ticket_desc">Ticket Médio (TM)</option>
                  <option value="via_desc">VIA (Valor do Item)</option>
                  <option value="gap_desc">Maior Gap a Cobrir</option>
                </select>
              </div>
            </div>

            {/* Legenda dos Indicadores de Referência */}
            <div className="d-flex align-items-center justify-content-between flex-wrap gap-2 px-3 py-2 mb-3 rounded" style={{ backgroundColor: '#f8fafc', border: '1px solid #e2e8f0', fontSize: '0.78rem' }}>
              <div className="d-flex align-items-center gap-2">
                <span style={{ fontSize: '0.95rem' }}>🎯</span>
                <span className="fw-bold text-dark">Legenda dos Indicadores de Referência:</span>
              </div>
              <div className="d-flex align-items-center flex-wrap gap-3">
                <span className="d-flex align-items-center gap-1.5" title="Igual ou acima da meta de referência">
                  <span style={{ width: '9px', height: '9px', borderRadius: '50%', backgroundColor: '#16a34a', display: 'inline-block' }}></span>
                  <strong style={{ color: '#16a34a' }}>Verde:</strong> No Alvo (≥ 100%)
                </span>
                <span className="d-flex align-items-center gap-1.5" title="Próximo da referência (de 85% a 99%)">
                  <span style={{ width: '9px', height: '9px', borderRadius: '50%', backgroundColor: '#d97706', display: 'inline-block' }}></span>
                  <strong style={{ color: '#d97706' }}>Amarelo:</strong> Atenção (85% a 99%)
                </span>
                <span className="d-flex align-items-center gap-1.5" title="Abaixo da referência (< 85%)">
                  <span style={{ width: '9px', height: '9px', borderRadius: '50%', backgroundColor: '#dc2626', display: 'inline-block' }}></span>
                  <strong style={{ color: '#dc2626' }}>Vermelho:</strong> Abaixo (&lt; 85%)
                </span>
                <span className="d-flex align-items-center gap-1.5" title="Nenhum valor de referência cadastrado na meta para comparar">
                  <span style={{ width: '9px', height: '9px', borderRadius: '50%', backgroundColor: '#1e293b', display: 'inline-block' }}></span>
                  <strong style={{ color: '#1e293b' }}>Preto:</strong> Sem valor de referência cadastrado
                </span>
              </div>
            </div>

            <div className="bi-table-container">
              {loading && data && (
                <div className="bi-table-loading-overlay">
                  <div className="bi-table-loading-badge">
                    <div className="spinner-border spinner-border-sm text-primary" role="status" />
                    <span>Atualizando indicadores de performance...</span>
                  </div>
                </div>
              )}
              <table className="bi-table">
                <thead>
                  <tr>
                    <th style={{ width: '42px' }} className="text-center">
                      <input
                        type="checkbox"
                        className="form-check-input bi-table-checkbox"
                        checked={allTableSelected}
                        ref={(el) => {
                          if (el) el.indeterminate = someTableSelected;
                        }}
                        onChange={handleToggleSelectAllTable}
                        title="Selecionar / Desmarcar todas as metas visíveis"
                      />
                    </th>
                    <th className="sortable-header" style={{ cursor: 'pointer' }} onClick={() => handleSort('name')}>
                      Colaborador(a) / RCA{renderSortIcon('name')}
                    </th>
                    <th className="sortable-header" style={{ cursor: 'pointer' }} onClick={() => handleSort('branch')}>
                      Filial{renderSortIcon('branch')}
                    </th>
                    <th className="sortable-header" style={{ cursor: 'pointer' }} onClick={() => handleSort('target_type')}>
                      Alvo{renderSortIcon('target_type')}
                    </th>
                    <th className="sortable-header" style={{ cursor: 'pointer' }} onClick={() => handleSort('goal_subtype')}>
                      Subtipo{renderSortIcon('goal_subtype')}
                    </th>
                    <th className="sortable-header" style={{ cursor: 'pointer' }} onClick={() => handleSort('goal_classification')}>
                      Classificação{renderSortIcon('goal_classification')}
                    </th>
                    <th className="sortable-header text-end" style={{ cursor: 'pointer' }} onClick={() => handleSort('target')}>
                      Meta{renderSortIcon('target')}
                    </th>
                    <th className="sortable-header text-end" style={{ cursor: 'pointer' }} onClick={() => handleSort('realized')}>
                      Realizado{renderSortIcon('realized')}
                    </th>
                    <th className="sortable-header" style={{ width: '180px', cursor: 'pointer' }} onClick={() => handleSort('pct')}>
                      Progresso (% / Gap){renderSortIcon('pct')}
                    </th>
                    <th className="sortable-header text-end" style={{ cursor: 'pointer' }} onClick={() => handleSort('ticket_medio')}>
                      Ticket Médio{renderSortIcon('ticket_medio')}
                    </th>
                    <th className="sortable-header text-center" style={{ cursor: 'pointer' }} title="Itens por Atendimento" onClick={() => handleSort('itens_atend')}>
                      Itens/Atend.{renderSortIcon('itens_atend')}
                    </th>
                    <th className="sortable-header text-end" style={{ cursor: 'pointer' }} title="Valor de Itens por Atendimento" onClick={() => handleSort('via')}>
                      VIA (Itens){renderSortIcon('via')}
                    </th>
                    <th className="sortable-header text-end" style={{ cursor: 'pointer' }} onClick={() => handleSort('required_daily_rate')}>
                      Ritmo Nec./Dia{renderSortIcon('required_daily_rate')}
                    </th>
                    <th className="sortable-header text-center" style={{ cursor: 'pointer' }} onClick={() => handleSort('probability')}>
                      Probabilidade{renderSortIcon('probability')}
                    </th>
                    <th className="text-center">Ações</th>
                  </tr>
                </thead>
                <tbody>
                  {sortedCollaborators.length === 0 ? (
                    loading ? (
                      <Loading.Table
                        colSpan={15}
                        text="Calculando dados das metas..."
                        subtext="Consolidando faturamento, médias diárias, ticket e itens por atendimento..."
                      />
                    ) : (
                      <tr>
                        <td colSpan={15} className="text-center py-5 text-muted">
                          <FiHelpCircle size={32} className="mb-2 d-block mx-auto opacity-50" />
                          Nenhuma meta encontrada para os filtros selecionados.
                        </td>
                      </tr>
                    )
                  ) : (
                sortedCollaborators.map((c) => {
                  const isRowSelected = selectedTableGoalIds.includes(c.goal_id);
                  return (
                  <tr
                    key={`${c.goal_id}_${c.period_key || ''}`}
                    className={`bi-table-row-clickable ${isRowSelected ? 'bi-table-row-selected' : ''}`}
                    onClick={(e) => {
                      if (e.target.tagName === 'INPUT' || e.target.closest('button') || e.target.closest('input')) return;
                      // No mobile ou touch screen, toque simples na linha abre o modal de análise
                      if (typeof window !== 'undefined' && (window.innerWidth <= 768 || ('ontouchstart' in window))) {
                        handleOpenCollabModal(c);
                      }
                    }}
                    onDoubleClick={() => handleOpenCollabModal(c)}
                    title="Toque (mobile) ou duplo clique (desktop) para abrir os cards e indicadores desta meta"
                  >
                    <td className="text-center" onClick={(e) => e.stopPropagation()}>
                      <input
                        type="checkbox"
                        className="form-check-input bi-table-checkbox"
                        checked={isRowSelected}
                        onChange={() => handleToggleTableGoal(c.goal_id)}
                        title={`Selecionar meta de ${c.name} (${c.period_label || ''})`}
                      />
                    </td>
                    <td onClick={() => handleOpenCollabModal(c)} style={{ cursor: 'pointer' }} title="Clique para abrir detalhes e indicadores">
                      <div className="d-flex align-items-center gap-2">
                        <div className="bi-collab-avatar" style={c.is_branch_goal || c.type === 'filial' ? { background: 'linear-gradient(135deg, #0284c7, #2563eb)' } : (c.is_general || c.type === 'todos' ? { background: 'linear-gradient(135deg, #4f46e5, #7c3aed)' } : {})}>
                          {c.is_branch_goal || c.type === 'filial' ? <FiLayers size={16} /> : (c.is_general || c.type === 'todos' ? <FiUsers size={16} /> : c.name.substring(0, 2).toUpperCase())}
                        </div>
                        <div>
                          <div className="fw-bold text-dark d-flex align-items-center gap-1 flex-wrap">
                            {c.name}
                            {c.period_label && (selectedMonths.length > 1 || selectedYears.length > 1) && (
                              <span className="badge bg-dark-subtle text-dark border" style={{ fontSize: '0.65rem', padding: '1px 6px' }}>
                                📅 {c.period_label}
                              </span>
                            )}
                            {c.is_branch_goal || c.type === 'filial' ? (
                              <span className="badge bg-primary text-white" style={{ fontSize: '0.62rem', padding: '1px 6px' }}>Meta da Filial</span>
                            ) : ((c.is_general || c.type === 'todos') && (
                              <span className="badge bg-secondary text-white" style={{ fontSize: '0.62rem', padding: '1px 6px' }}>Todos</span>
                            ))}
                          </div>
                          <div className="text-muted small">Cód: {c.code || `#${c.goal_id}`}</div>
                        </div>
                      </div>
                    </td>
                    <td>
                      <span className="badge bg-light text-dark border">
                        {c.branch_name}
                      </span>
                    </td>
                    <td>
                      {getTargetTypeBadge(c.target_type)}
                    </td>
                    <td>
                      {getGoalSubtypeBadge(c.goal_subtype)}
                    </td>
                    <td>
                      <span className={`bi-classification-badge ${c.goal_classification}`}>
                        {c.goal_classification}
                      </span>
                    </td>
                    <td className="text-end fw-semibold">
                      {isQuantitySubtype(c.goal_subtype) ? (
                        <div>
                          <div>{Number(c.target || 0).toLocaleString('pt-BR')} {c.goal_subtype === 'clientes_positivados' ? 'cli' : 'un'}</div>
                          {Number(c.unit_value || 0) > 0 && (
                            <div className="text-muted" style={{ fontSize: '0.68rem', fontWeight: 500 }} title="Comissão unitária">
                              {formatBRL(c.unit_value)}/{c.goal_subtype === 'clientes_positivados' ? 'cli' : 'un'}
                            </div>
                          )}
                        </div>
                      ) : (
                        formatBRL(c.target)
                      )}
                    </td>
                    <td className="text-end">
                      {isQuantitySubtype(c.goal_subtype) ? (
                        <div>
                          <div className="fw-bold text-dark">
                            {Number(c.realized || 0).toLocaleString('pt-BR')} {c.goal_subtype === 'clientes_positivados' ? 'cli' : 'un'}
                          </div>
                          {Number(c.earned_commission || 0) > 0 && (
                            <div className="text-success" style={{ fontSize: '0.7rem', fontWeight: 600 }} title="Comissão ganha">
                              Comissão: {formatBRL(c.earned_commission)}
                            </div>
                          )}
                          {c.bonus_achieved && Number(c.bonus_amount || 0) > 0 && (
                            <span className="badge bg-warning text-dark border" style={{ fontSize: '0.62rem', padding: '1px 5px' }} title="Bônus atingido!">
                              🎁 +{formatBRL(c.bonus_amount)}
                            </span>
                          )}
                        </div>
                      ) : (
                        <>
                          <div className="fw-bold text-dark">{formatBRL(c.realized)}</div>
                          {c.raw_net > 0 && Math.abs(c.raw_net - c.realized) > 0.01 && (
                            <div className="text-muted" style={{ fontSize: '0.7rem', fontWeight: 500 }} title="Faturamento Líquido dos Filtros">
                              Líq: {formatBRL(c.raw_net)}
                            </div>
                          )}
                        </>
                      )}
                    </td>
                    <td>
                      <div className="d-flex justify-content-between align-items-center small mb-1">
                        <span className="fw-bold">{c.pct}%</span>
                        <span className="text-muted">
                          Falta: {isQuantitySubtype(c.goal_subtype) ? `${Number(c.gap || 0).toLocaleString('pt-BR')} ${c.goal_subtype === 'clientes_positivados' ? 'cli' : 'un'}` : formatBRL(c.gap)}
                        </span>
                      </div>
                      <div className="bi-progress-container" style={{ height: '6px', margin: 0 }}>
                        <div
                          className={`bi-progress-fill ${c.pct >= 100 ? 'blue-emerald' : c.pct >= 75 ? 'purple-indigo' : 'amber-rose'}`}
                          style={{ width: `${Math.min(100, c.pct)}%` }}
                        ></div>
                      </div>
                    </td>
                    <td className="text-end">
                      {(() => {
                        const refComp = getRefComparison(c.ticket_medio, c.ref_average_ticket);
                        return (
                          <div>
                            <div className="fw-bold" style={{ color: refComp ? refComp.statusColor : '#1e293b' }}>
                              {formatBRL(c.ticket_medio)}
                            </div>
                            {refComp && (
                              <div style={{ fontSize: '0.65rem', color: refComp.statusColor, fontWeight: 600 }} title={`Ref: ${formatBRL(c.ref_average_ticket)} (${refComp.badgeText})`}>
                                Ref: {formatBRL(c.ref_average_ticket)}
                              </div>
                            )}
                          </div>
                        );
                      })()}
                    </td>
                    <td className="text-center">
                      {(() => {
                        const cIA = getCollabIA(c);
                        const refComp = getRefComparison(cIA, c.ref_items_per_attendance);
                        return (
                          <div>
                            <div className="fw-semibold" style={{ color: refComp ? refComp.statusColor : '#1e293b' }}>
                              {Number(cIA || 0).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} un
                            </div>
                            {refComp && (
                              <div style={{ fontSize: '0.65rem', color: refComp.statusColor, fontWeight: 600 }} title={`Ref: ${Number(c.ref_items_per_attendance).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} un (${refComp.badgeText})`}>
                                Ref: {Number(c.ref_items_per_attendance).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} un
                              </div>
                            )}
                          </div>
                        );
                      })()}
                    </td>
                    <td className="text-end">
                      {(() => {
                        const valVia = c.via || c.valor_por_produto;
                        const refComp = getRefComparison(valVia, c.ref_item_value_per_attendance);
                        return (
                          <div>
                            <div className="fw-bold" style={{ color: refComp ? refComp.statusColor : '#1e293b' }}>
                              {formatBRL(valVia)}
                            </div>
                            {refComp && (
                              <div style={{ fontSize: '0.65rem', color: refComp.statusColor, fontWeight: 600 }} title={`Ref: ${formatBRL(c.ref_item_value_per_attendance)} (${refComp.badgeText})`}>
                                Ref: {formatBRL(c.ref_item_value_per_attendance)}
                              </div>
                            )}
                          </div>
                        );
                      })()}
                    </td>
                    <td className="text-end text-danger fw-semibold">
                      {c.gap > 0 ? (
                        isQuantitySubtype(c.goal_subtype) ? (
                          `${Number(c.required_daily_rate || 0).toLocaleString('pt-BR', { maximumFractionDigits: 1 })} ${c.goal_subtype === 'clientes_positivados' ? 'cli' : 'un'}/dia`
                        ) : (
                          formatBRL(c.required_daily_rate)
                        )
                      ) : '—'}
                    </td>
                    <td className="text-center">
                      <span className={`bi-probability-badge ${c.probability}`}>
                        {c.probability === 'atingida' && 'Batida'}
                        {c.probability === 'alta' && 'Alta'}
                        {c.probability === 'media' && 'Média'}
                        {c.probability === 'baixa' && 'Em Risco'}
                      </span>
                    </td>
                    <td className="text-center">
                      <div className="d-flex align-items-center justify-content-center gap-1">
                        <button
                          type="button"
                          className="btn btn-sm btn-light border py-1 px-2"
                          onClick={() => {
                            handleOpenCollabModal(c);
                            setModalTab('orders');
                          }}
                          title="Ver Movimentações de Pedidos e Quantitativos desta Meta"
                        >
                          <FiShoppingBag size={14} className="text-cyan" />
                        </button>
                        <button
                          type="button"
                          className="btn btn-sm btn-light border py-1 px-2"
                          onClick={() => handleOpenCollabModal(c)}
                          title="Ver Cards e Indicadores da Meta (ou duplo clique na linha)"
                        >
                          <FiMaximize2 size={14} className="text-primary" />
                        </button>
                      </div>
                    </td>
                  </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>

        {/* Barra de Ação Flutuante para Análise Concatenada Multi-Período */}
        {selectedTableGoalIds.length > 0 && (
          <div className="bi-concatenated-action-bar">
            <div className="d-flex align-items-center gap-3 flex-wrap">
              <div className="bi-concatenated-badge">
                <FiLayers size={16} />
                <span>{selectedTableGoalIds.length} {selectedTableGoalIds.length === 1 ? 'meta selecionada' : 'metas selecionadas'}</span>
              </div>
              {concatenatedStats ? (
                <div className="bi-concatenated-summary d-none d-md-flex align-items-center gap-2">
                  <span>Períodos: <strong>{(concatenatedStats.periods || []).join(', ')}</strong></span>
                  <span className="text-muted">•</span>
                  <span>Meta Consolidada: <strong>{concatenatedStats.areAllQtyGoals ? `${Number(concatenatedStats.totalTarget).toLocaleString('pt-BR')} ${concatenatedStats.qtyUnitLabel}` : formatBRL(concatenatedStats.totalTarget)}</strong></span>
                  <span className="text-muted">•</span>
                  <span>Realizado: <strong className="text-success">{concatenatedStats.areAllQtyGoals ? `${Number(concatenatedStats.totalRealized).toLocaleString('pt-BR')} ${concatenatedStats.qtyUnitLabel}` : formatBRL(concatenatedStats.totalRealized)}</strong> ({concatenatedStats.pct}%)</span>
                </div>
              ) : (
                <div className="bi-concatenated-summary text-muted small">
                  Selecione pelo menos mais 1 meta/período para ativar a análise concatenada combinada.
                </div>
              )}
            </div>

            <div className="d-flex align-items-center gap-2">
              <button
                type="button"
                className="btn btn-sm btn-outline-secondary"
                onClick={() => setSelectedTableGoalIds([])}
                title="Desmarcar todas as seleções"
              >
                Desmarcar Todas
              </button>
              <button
                type="button"
                className="bi-concatenated-btn-analyze"
                disabled={!concatenatedStats || selectedTableGoalIds.length < 2}
                onClick={() => setIsConcatenatedModalOpen(true)}
                title={selectedTableGoalIds.length >= 2 ? "Abrir Painel de Análise Concatenada Multi-Período" : "Selecione ao menos 2 metas/períodos"}
              >
                <FiTrendingUp size={16} />
                <span>Analisar Concatenação Multi-Período ({selectedTableGoalIds.length})</span>
              </button>
            </div>
          </div>
        )}
      </div>
    </>
  )}



      {/* ====================================================================
          MODAL 2: DETALHES ANALÍTICOS DA VENDEDORA (CARROSSEL 90%)
          ==================================================================== */}
      {activeCollab && (() => {
        const selectedCollab = activeCollab;
        return (
        <div className={`bi-modal-overlay ${isIndividualFullscreen ? 'bi-modal-fullscreen-overlay' : ''}`} onClick={handleCloseCollabModal}>
          <div className={`bi-modal-container ${isIndividualFullscreen ? 'bi-modal-fullscreen' : 'bi-modal-90'}`} onClick={(e) => e.stopPropagation()}>
            {/* Header com Avatar, Identificação, Badges e Navegação Carrossel */}
            <div className="bi-modal-header">
              <div className="d-flex align-items-center gap-3 flex-wrap">
                <div className="bi-collab-avatar" style={{ width: '46px', height: '46px', fontSize: '1.1rem', ...(selectedCollab.is_branch_goal || selectedCollab.type === 'filial' ? { background: 'linear-gradient(135deg, #0284c7, #2563eb)' } : (selectedCollab.is_general || selectedCollab.type === 'todos' ? { background: 'linear-gradient(135deg, #4f46e5, #7c3aed)' } : {})) }}>
                  {selectedCollab.is_branch_goal || selectedCollab.type === 'filial' ? <FiLayers size={22} /> : (selectedCollab.is_general || selectedCollab.type === 'todos' ? <FiUsers size={20} /> : selectedCollab.name.substring(0, 2).toUpperCase())}
                </div>
                <div>
                  <div className="d-flex align-items-center gap-2 flex-wrap">
                    <h4 className="fw-bold text-dark mb-0">{selectedCollab.name}</h4>
                    {selectedCollab.is_branch_goal || selectedCollab.type === 'filial' ? (
                      <span className="badge bg-primary text-white" style={{ fontSize: '0.68rem', padding: '2px 8px' }}>Meta da Filial</span>
                    ) : ((selectedCollab.is_general || selectedCollab.type === 'todos') && (
                      <span className="badge bg-secondary text-white" style={{ fontSize: '0.68rem', padding: '2px 8px' }}>Todos os Colaboradores</span>
                    ))}
                    <span className={`bi-probability-badge bi-collab-meta-hide-mobile d-none d-md-inline-flex ${selectedCollab.probability}`}>
                      {selectedCollab.probability === 'atingida' && '🏆 Meta Batida'}
                      {selectedCollab.probability === 'alta' && '🟢 Alta Probabilidade'}
                      {selectedCollab.probability === 'media' && '🟡 Média Probabilidade'}
                      {selectedCollab.probability === 'baixa' && '🔴 Em Risco'}
                    </span>
                    {loading && (
                      <span className="badge bg-primary-subtle text-primary d-inline-flex align-items-center gap-1" style={{ fontSize: '0.72rem', padding: '3px 8px' }}>
                        <div className="spinner-border spinner-border-sm" role="status" style={{ width: '10px', height: '10px', borderWidth: '1.5px' }} />
                        <span>Carregando filial...</span>
                      </span>
                    )}
                  </div>
                  <div className="text-muted small mt-1 d-flex align-items-center gap-2 flex-wrap">
                    <span>🏢 Filial: <strong>{selectedCollab.branch_name}</strong></span>
                    <span className="bi-collab-meta-hide-mobile d-none d-md-inline">•</span>
                    <span className="bi-collab-meta-hide-mobile d-none d-md-inline-flex align-items-center gap-1">Alvo: {getTargetTypeBadge(selectedCollab.target_type)}</span>
                    <span className="bi-collab-meta-hide-mobile d-none d-md-inline">•</span>
                    <span className="bi-collab-meta-hide-mobile d-none d-md-inline">Classificação: <strong className="text-capitalize">{selectedCollab.goal_classification}</strong></span>
                    <span className="bi-collab-meta-hide-mobile d-none d-md-inline">•</span>
                    <span className="bi-collab-meta-hide-mobile d-none d-md-inline">Cód: <strong>{selectedCollab.code || `#${selectedCollab.goal_id}`}</strong></span>
                  </div>
                </div>
              </div>

              {/* Controles de Carrossel no Header */}
              <div className="d-flex align-items-center gap-2 flex-wrap">
                {/* Controles de Navegação por Filial */}
                {availableBranchList.length > 0 && (
                  <div className="d-flex align-items-center gap-1 bi-branch-nav-group">
                    <button
                      type="button"
                      className="bi-carousel-btn bi-branch-nav-btn"
                      onClick={handlePrevBranch}
                      title="Filial Anterior (Alt + Seta Esquerda)"
                      disabled={availableBranchList.length <= 1}
                    >
                      <FiChevronLeft size={15} />
                      <span className="d-none d-md-inline">Filial</span>
                    </button>
                    <div className="position-relative" ref={branchNavDropdownRef}>
                      <span
                        className="bi-carousel-badge bi-branch-nav-badge"
                        onClick={() => setIsBranchNavDropdownOpen(prev => !prev)}
                        title={`${currentBranchFullText} (Clique para alternar entre as filiais)`}
                      >
                        <FiMapPin size={12} className="text-success" />
                        <span>{currentBranchLabel}</span>
                        <span className="bi-branch-nav-counter text-muted" style={{ fontSize: '0.72rem' }}>
                          ({currentBranchIndex + 1}/{availableBranchList.length})
                        </span>
                        <FiChevronDown size={12} className="opacity-75" />
                      </span>

                      {/* Dropdown rápido para escolher diretamente qualquer filial */}
                      {isBranchNavDropdownOpen && (
                        <div className="bi-branch-nav-dropdown shadow">
                          <div className="px-2 py-1 text-muted small fw-bold border-bottom mb-1" style={{ fontSize: '0.7rem' }}>
                            Navegar para outra filial:
                          </div>
                          {availableBranchList.map((b, idx) => (
                            <button
                              key={b.code}
                              type="button"
                              className={`bi-branch-nav-item ${idx === currentBranchIndex ? 'active' : ''}`}
                              onClick={() => {
                                handleBranchChange(b.code);
                                setSelectedCollabIndex(0);
                                setIsBranchNavDropdownOpen(false);
                              }}
                            >
                              <div className="d-flex align-items-center gap-1.5 text-truncate" style={{ maxWidth: '170px' }}>
                                <span className="fw-semibold">
                                  {b.code === 'geral' ? 'Metas Gerais' : `Filial ${b.code}`}
                                </span>
                                <span className="text-muted text-truncate" style={{ fontSize: '0.72rem' }}>
                                  - {b.name}
                                </span>
                              </div>
                              <span className="badge bg-light text-dark border ms-2" style={{ fontSize: '0.68rem' }}>
                                {b.goals_count} metas
                              </span>
                            </button>
                          ))}
                        </div>
                      )}
                    </div>
                    <button
                      type="button"
                      className="bi-carousel-btn bi-branch-nav-btn"
                      onClick={handleNextBranch}
                      title="Próxima Filial (Alt + Seta Direita)"
                      disabled={availableBranchList.length <= 1}
                    >
                      <span className="d-none d-md-inline">Filial</span>
                      <FiChevronRight size={15} />
                    </button>
                  </div>
                )}

                {/* Controles de Navegação por Meta */}
                <div className="d-flex align-items-center gap-1">
                  <button
                    type="button"
                    className="bi-carousel-btn"
                    onClick={handlePrevCollab}
                    title="Meta Anterior (Seta Esquerda)"
                  >
                    <FiChevronLeft size={16} />
                    <span className="d-none d-sm-inline">Anterior</span>
                  </button>
                  <span className="bi-carousel-badge">
                    {selectedCollabIndex + 1} de {sortedCollaborators.length}
                  </span>
                  <button
                    type="button"
                    className="bi-carousel-btn"
                    onClick={handleNextCollab}
                    title="Próxima Meta (Seta Direita)"
                  >
                    <span className="d-none d-sm-inline">Próximo</span>
                    <FiChevronRight size={16} />
                  </button>
                </div>

                <button
                  type="button"
                  className={`bi-modal-fullscreen-btn ms-1 ${isIndividualFullscreen ? 'active' : ''}`}
                  onClick={() => setIsIndividualFullscreen(prev => !prev)}
                  title={isIndividualFullscreen ? "Restaurar tamanho normal" : "Expandir para tela cheia"}
                >
                  {isIndividualFullscreen ? <FiMinimize2 size={17} /> : <FiMaximize2 size={17} />}
                </button>
                <button
                  type="button"
                  className="bi-modal-close-btn ms-1"
                  onClick={handleCloseCollabModal}
                  title="Fechar (Esc)"
                >
                  <FiX size={18} />
                </button>
              </div>
            </div>

            {/* Abas de Navegação do Modal da Meta */}
            <div className="bi-modal-nav-tabs">
              <button
                type="button"
                className={`bi-modal-nav-tab ${modalTab === 'overview' ? 'active' : ''}`}
                onClick={() => setModalTab('overview')}
              >
                <FiTarget size={15} /> Visão Geral & KPIs
              </button>
              <button
                type="button"
                className={`bi-modal-nav-tab ${modalTab === 'charts' ? 'active' : ''}`}
                onClick={() => setModalTab('charts')}
              >
                <FiTrendingUp size={15} /> Gráficos Analíticos
                <span className="bi-modal-tab-badge">6</span>
              </button>
              <button
                type="button"
                className={`bi-modal-nav-tab ${modalTab === 'orders' ? 'active' : ''}`}
                onClick={() => setModalTab('orders')}
              >
                <FiShoppingBag size={15} /> Movimentações de Pedidos
                {selectedCollab.orders_count > 0 && (
                  <span className="bi-modal-tab-badge">{selectedCollab.orders_count.toLocaleString('pt-BR')}</span>
                )}
              </button>
              <button
                type="button"
                className={`bi-modal-nav-tab ${modalTab === 'recommendations' ? 'active' : ''}`}
                onClick={() => setModalTab('recommendations')}
              >
                <FiAward size={15} /> Produtos Estratégicos
                {collabRecs.length > 0 && (
                  <span className="bi-modal-tab-badge">{collabRecs.length}</span>
                )}
              </button>
              <button
                type="button"
                className={`bi-modal-nav-tab ${modalTab === 'reports' ? 'active' : ''}`}
                onClick={() => setModalTab('reports')}
              >
                <FiLayers size={15} /> Fechamentos Vinculados
                {selectedCollab.reports && selectedCollab.reports.length > 0 && (
                  <span className="bi-modal-tab-badge">{selectedCollab.reports.length}</span>
                )}
              </button>
            </div>

            {/* Corpo do Modal */}
            <div className="bi-modal-body">
              {modalTab === 'overview' && (
                <>
                  {/* Grid dos 8 Cards de KPI da Meta Selecionada */}
              {(() => {
                const collabBusinessDays = selectedCollab.business_days_elapsed ?? selectedCollab.business_days_with_sales ?? period.business_days_elapsed ?? 0;
                const calendarBusinessDays = selectedCollab.business_days_calendar_elapsed ?? period.business_days_calendar_elapsed ?? period.business_days_elapsed ?? 14;
                const remainingBusinessDays = selectedCollab.business_days_remaining ?? period.business_days_remaining ?? 12;
                const totalBusinessDays = selectedCollab.business_days_total ?? period.business_days_total ?? 26;
                const currentDaily = selectedCollab.daily_net_sales_average ?? selectedCollab.daily_run_rate ?? 0;
                const totalOrders = selectedCollab.orders_count ?? selectedCollab.orders ?? 0;
                const currentOrdersQty = selectedCollab.daily_orders_qty ?? selectedCollab.avg_orders_per_day ?? (totalOrders && collabBusinessDays ? (totalOrders / collabBusinessDays) : 0);
                const currentTM = selectedCollab.ticket_medio || 0;
                const totalItems = selectedCollab.items_count ?? selectedCollab.items ?? 0;
                const currentIA = getCollabIA(selectedCollab);
                const currentVia = selectedCollab.via || selectedCollab.valor_por_produto || 0;
                const isCollabQty = isQuantitySubtype(selectedCollab.goal_subtype);
                const isCollabCli = selectedCollab.goal_subtype === 'clientes_positivados';
                const collabUnit = isCollabCli ? 'cli' : 'un';

                return (
                  <div className="bi-kpi-grid mb-4">
                    {/* Card 1: Faturamento Realizado / Quantidade Realizada / Clientes Positivados */}
                    <div className="bi-kpi-card">
                      <div className="bi-kpi-top">
                        <span className="bi-kpi-title">{isCollabCli ? 'Clientes Positivados' : (isCollabQty ? 'Produtos Destaque Vendidos' : 'Faturamento Realizado')}</span>
                        <div className={`bi-kpi-icon ${isCollabCli ? 'cyan' : (isCollabQty ? 'blue' : 'green')}`}>
                          {isCollabCli ? <FiCheckCircle /> : (isCollabQty ? <FiTarget /> : <FiDollarSign />)}
                        </div>
                      </div>
                      <div>
                        <div className="bi-kpi-value">
                          {isCollabQty ? `${Number(selectedCollab.realized || 0).toLocaleString('pt-BR')} ${collabUnit}` : formatBRL(selectedCollab.realized)}
                        </div>
                        <div className="bi-kpi-subtext">
                          Meta: <strong>{isCollabQty ? `${Number(selectedCollab.target || 0).toLocaleString('pt-BR')} ${collabUnit}` : formatBRL(selectedCollab.target)}</strong> ({selectedCollab.pct || 0}%)
                        </div>
                        {isCollabQty ? (
                          <div className="mt-1 d-flex align-items-center gap-1 flex-wrap" style={{ fontSize: '0.75rem' }}>
                            {Number(selectedCollab.earned_commission || 0) > 0 && (
                              <span className="text-success fw-bold">
                                Comissão: {formatBRL(selectedCollab.earned_commission)}
                              </span>
                            )}
                            {selectedCollab.bonus_achieved && Number(selectedCollab.bonus_amount || 0) > 0 && (
                              <span className="badge bg-warning text-dark border">
                                🎁 Bônus: +{formatBRL(selectedCollab.bonus_amount)}
                              </span>
                            )}
                            {Number(selectedCollab.unit_value || 0) > 0 && (
                              <span className="text-muted">
                                ({formatBRL(selectedCollab.unit_value)}/{collabUnit})
                              </span>
                            )}
                          </div>
                        ) : (
                          selectedCollab.raw_net > 0 && Math.abs(selectedCollab.raw_net - selectedCollab.realized) > 0.01 && (
                            <div className="text-muted small mt-1" style={{ fontSize: '0.75rem' }}>
                              Líquido dos Filtros: <strong>{formatBRL(selectedCollab.raw_net)}</strong>
                            </div>
                          )
                        )}
                      </div>
                      <div className="bi-progress-container mb-1">
                        <div
                          className={`bi-progress-fill ${selectedCollab.pct >= 100 ? 'blue-emerald' : selectedCollab.pct >= 75 ? 'purple-indigo' : 'amber-rose'}`}
                          style={{ width: `${Math.min(100, selectedCollab.pct || 0)}%` }}
                        ></div>
                      </div>
                      <div className="bi-kpi-footer">
                        <span className="bi-kpi-days-badge highlight" title="Dias úteis com movimentação de venda analisados para esta meta">
                          <FiCalendar size={12} /> {collabBusinessDays} {collabBusinessDays === 1 ? 'dia útil analisado' : 'dias úteis analisados'}
                        </span>
                        <span className="text-muted" title={isCollabCli ? 'Clientes identificados no caixa/vendas' : 'Dias úteis corridos vs total do mês'}>
                          {isCollabCli ? 'Identificados no caixa' : `${calendarBusinessDays} de ${totalBusinessDays} dias úteis`}
                        </span>
                      </div>
                    </div>

                    {/* Card 2: Valor a Alcançar (Gap) / Clientes a Positivar */}
                    <div className="bi-kpi-card">
                      <div className="bi-kpi-top">
                        <span className="bi-kpi-title">{isCollabCli ? 'Clientes a Positivar (Gap)' : (isCollabQty ? 'Quantidade a Alcançar (Gap)' : 'Valor a Alcançar (Gap)')}</span>
                        <div className="bi-kpi-icon amber">
                          <FiTarget />
                        </div>
                      </div>
                      <div>
                        <div className="bi-kpi-value" style={{ color: selectedCollab.gap > 0 ? '#dc2626' : '#16a34a' }}>
                          {selectedCollab.gap > 0 ? (isCollabQty ? `${Number(selectedCollab.gap || 0).toLocaleString('pt-BR')} ${collabUnit}` : formatBRL(selectedCollab.gap)) : 'Superada!'}
                        </div>
                        <div className="bi-kpi-subtext">
                          Ritmo necessário: <strong>{selectedCollab.gap > 0 ? (isCollabQty ? `${Number(selectedCollab.required_daily_rate || 0).toLocaleString('pt-BR', { maximumFractionDigits: 1 })} ${collabUnit}/dia útil` : `${formatBRL(selectedCollab.required_daily_rate)}/dia útil`) : 'Meta batida'}</strong>
                        </div>
                      </div>
                      <div className="bi-kpi-footer">
                        <span className="bi-kpi-days-badge" title="Dias úteis analisados com movimentação">
                          <FiCalendar size={12} /> {collabBusinessDays} {collabBusinessDays === 1 ? 'dia útil analisado' : 'dias úteis analisados'}
                        </span>
                        <span className="text-muted d-flex align-items-center gap-1" title="Dias úteis restantes no mês">
                          <FiClock size={12} /> {remainingBusinessDays} dias úteis restantes
                        </span>
                      </div>
                    </div>

                    {/* Card 3: Venda Líquida Diária / Positivações por Dia */}
                    {(() => {
                      const refComp = !isCollabQty ? getRefComparison(currentDaily, selectedCollab.ref_daily_net_sales) : null;
                      const dailyQtyVal = collabBusinessDays > 0 ? (selectedCollab.realized / collabBusinessDays) : 0;
                      return (
                        <div className="bi-kpi-card">
                          <div className="bi-kpi-top">
                            <span className="bi-kpi-title">{isCollabCli ? 'Ritmo Diário de Positivação' : (isCollabQty ? 'Produtos / Dia Útil' : 'Venda Líquida Diária')}</span>
                            <div className="bi-kpi-icon cyan">
                              <FiTrendingUp />
                            </div>
                          </div>
                          <div>
                            <div className="bi-kpi-value" style={{ color: refComp ? refComp.statusColor : '#1e293b' }}>
                              {isCollabCli ? `${dailyQtyVal.toFixed(1)} cli/dia` : (isCollabQty ? `${dailyQtyVal.toFixed(1)} ${collabUnit}` : formatBRL(currentDaily))}
                            </div>
                            <div className="bi-kpi-subtext">
                              Média por <strong>dia útil ({collabBusinessDays} {collabBusinessDays === 1 ? 'dia' : 'dias'})</strong>
                            </div>
                            {refComp && (
                              <div className="d-flex align-items-center gap-1 mt-1 flex-wrap" style={{ fontSize: '0.72rem' }}>
                                <span style={{
                                  padding: '1px 6px',
                                  borderRadius: '6px',
                                  fontWeight: 600,
                                  backgroundColor: refComp.bgColor,
                                  color: refComp.statusColor,
                                  border: `1px solid ${refComp.borderColor}`
                                }}>
                                  {refComp.badgeText}
                                </span>
                                <span className="text-muted" title="Valor de Referência Diário">
                                  Ref: <strong>{formatBRL(refComp.numRef)}</strong>/dia
                                </span>
                              </div>
                            )}
                            {isCollabQty && selectedCollab.target > 0 && totalBusinessDays > 0 && (
                              <div className="text-muted small mt-1" style={{ fontSize: '0.72rem' }}>
                                Ritmo Ideal: <strong>{(selectedCollab.target / totalBusinessDays).toFixed(1)} {collabUnit}</strong>/dia útil
                              </div>
                            )}
                          </div>
                          <div className="bi-kpi-footer">
                            <span className="bi-kpi-days-badge highlight" title="Cálculo baseado exclusivamente nos dias em que houve venda desta meta">
                              <FiCalendar size={12} /> {collabBusinessDays} {collabBusinessDays === 1 ? 'dia útil analisado' : 'dias úteis analisados'}
                            </span>
                            {isCollabCli ? (
                              <span className="text-muted" title="Ritmo médio realizado de ativações">
                                Positivações diárias
                              </span>
                            ) : !isCollabQty && selectedCollab.daily_net_sales_calendar_average > 0 ? (
                              <span className="text-muted" title="Média por dia corrido de calendário">
                                {formatBRL(selectedCollab.daily_net_sales_calendar_average)}/dia corrido
                              </span>
                            ) : (
                              <span className="text-muted">Movimentação efetiva</span>
                            )}
                          </div>
                        </div>
                      );
                    })()}

                    {/* Card 4: Qtd de Vendas Diárias por Vendedor / Vendas dos Positivados */}
                    {(() => {
                      const refComp = !isCollabCli ? getRefComparison(currentOrdersQty, selectedCollab.ref_daily_orders_qty) : null;
                      const ordersPerClient = selectedCollab.realized > 0 && totalOrders > 0 ? (totalOrders / selectedCollab.realized) : 1;
                      return (
                        <div className="bi-kpi-card">
                          <div className="bi-kpi-top">
                            <span className="bi-kpi-title">{isCollabCli ? 'Volume de Vendas dos Positivados' : 'Qtd de Vendas Diárias por Vendedor'}</span>
                            <div className="bi-kpi-icon teal">
                              <FiPackage />
                            </div>
                          </div>
                          <div>
                            {isCollabCli ? (
                              <>
                                <div className="bi-kpi-value">
                                  {totalOrders.toLocaleString('pt-BR')}{' '}
                                  <span style={{ fontSize: '1rem', fontWeight: 600 }}>pedidos</span>
                                </div>
                                <div className="bi-kpi-subtext">
                                  Frequência: <strong>{ordersPerClient.toFixed(2)} compras/cliente</strong>
                                </div>
                                <div className="text-muted small mt-1" style={{ fontSize: '0.72rem' }}>
                                  Média: <strong>{(totalOrders && collabBusinessDays ? (totalOrders / collabBusinessDays).toFixed(1) : 0)} atend/dia útil</strong>
                                </div>
                              </>
                            ) : (
                              <>
                                <div className="bi-kpi-value" style={{ color: refComp ? refComp.statusColor : '#1e293b' }}>
                                  {Number(currentOrdersQty).toLocaleString('pt-BR', { minimumFractionDigits: 1, maximumFractionDigits: 2 })}{' '}
                                  <span style={{ fontSize: '1rem', fontWeight: 600 }}>ped/dia</span>
                                </div>
                                <div className="bi-kpi-subtext">
                                  Volume de <strong>pedidos diários</strong>
                                </div>
                                {refComp && (
                                  <div className="d-flex align-items-center gap-1 mt-1 flex-wrap" style={{ fontSize: '0.72rem' }}>
                                    <span style={{
                                      padding: '1px 6px',
                                      borderRadius: '6px',
                                      fontWeight: 600,
                                      backgroundColor: refComp.bgColor,
                                      color: refComp.statusColor,
                                      border: `1px solid ${refComp.borderColor}`
                                    }}>
                                      {refComp.badgeText}
                                    </span>
                                    <span className="text-muted" title="Meta de Quantidade de Pedidos Diários de Referência">
                                      Ref: <strong>{refComp.numRef.toLocaleString('pt-BR', { minimumFractionDigits: 1, maximumFractionDigits: 2 })}</strong> ped/dia
                                    </span>
                                  </div>
                                )}
                              </>
                            )}
                          </div>
                          <div className="bi-kpi-footer">
                            <span className="bi-kpi-days-badge highlight" title="Cálculo baseado nos dias com movimentação de venda desta meta">
                              <FiCalendar size={12} /> {collabBusinessDays} {collabBusinessDays === 1 ? 'dia útil analisado' : 'dias úteis analisados'}
                            </span>
                            <span className="text-muted" title={isCollabCli ? 'Total de compras com cliente cadastrado' : 'Total de cupons emitidos'}>
                              {isCollabCli ? 'Clientes identificados' : `Total: ${totalOrders.toLocaleString('pt-BR')} ped`}
                            </span>
                          </div>
                        </div>
                      );
                    })()}

                    {/* Card 5: Projeção & Probabilidade */}
                    <div className="bi-kpi-card">
                      <div className="bi-kpi-top">
                        <span className="bi-kpi-title">{isCollabCli ? 'Projeção de Positivação' : 'Projeção & Probabilidade'}</span>
                        <div className="bi-kpi-icon green">
                          <FiZap />
                        </div>
                      </div>
                      <div>
                        <div className="bi-kpi-value">{isCollabQty ? `${Number(selectedCollab.projected_revenue || 0).toLocaleString('pt-BR')} ${collabUnit}` : formatBRL(selectedCollab.projected_revenue)}</div>
                        <div className="d-flex align-items-center justify-content-between mt-1">
                          <span className="bi-kpi-subtext">
                            Projeção: <strong>{selectedCollab.projected_pct || 0}%</strong> da meta
                          </span>
                          <span className={`bi-probability-badge ${selectedCollab.probability}`}>
                            {selectedCollab.probability === 'atingida' && '🏆 Meta Batida'}
                            {selectedCollab.probability === 'alta' && '🟢 Alta Prob.'}
                            {selectedCollab.probability === 'media' && '🟡 Média Prob.'}
                            {selectedCollab.probability === 'baixa' && '🔴 Em Risco'}
                          </span>
                        </div>
                      </div>
                      <div className="bi-kpi-footer">
                        <span className="bi-kpi-days-badge" title="Base do cálculo: ritmo diário com base nos dias com venda + projeção para os dias restantes">
                          <FiCalendar size={12} /> {collabBusinessDays}d analisados + {remainingBusinessDays}d restantes
                        </span>
                        <span className="text-muted" title="Média projetada por dia útil">
                          {isCollabQty ? `${Number(currentDaily || 0).toLocaleString('pt-BR', { maximumFractionDigits: 1 })} ${collabUnit}/dia` : `${formatBRL(currentDaily)}/dia`}
                        </span>
                      </div>
                    </div>

                    {/* Card 6: Ticket Médio (TM) / Faturamento por Positivado */}
                    {(() => {
                      const refComp = !isCollabCli ? getRefComparison(currentTM, selectedCollab.ref_average_ticket) : null;
                      const clientRevenueVal = selectedCollab.raw_net > 0 && Math.abs(selectedCollab.raw_net - selectedCollab.realized) > 1
                        ? (selectedCollab.raw_net / (selectedCollab.realized || 1))
                        : (Number(selectedCollab.unit_value || 0) > 0 ? Number(selectedCollab.unit_value) : currentTM);
                      return (
                        <div className="bi-kpi-card">
                          <div className="bi-kpi-top">
                            <span className="bi-kpi-title">{isCollabCli ? 'Faturamento por Positivado' : 'Ticket Médio (TM)'}</span>
                            <div className="bi-kpi-icon purple">
                              <FiShoppingBag />
                            </div>
                          </div>
                          <div>
                            <div className="bi-kpi-value" style={{ color: refComp ? refComp.statusColor : '#1e293b' }}>
                              {isCollabCli ? (
                                Number(selectedCollab.unit_value || 0) > 0 && clientRevenueVal === Number(selectedCollab.unit_value)
                                  ? `${formatBRL(clientRevenueVal)}/cli`
                                  : formatBRL(clientRevenueVal)
                              ) : formatBRL(currentTM)}
                            </div>
                            <div className="bi-kpi-subtext">
                              Total de Cupons: <strong>{totalOrders.toLocaleString('pt-BR')}</strong>
                            </div>
                            {isCollabCli ? (
                              <div className="text-muted small mt-1" style={{ fontSize: '0.72rem' }}>
                                {selectedCollab.raw_net > 0 && Math.abs(selectedCollab.raw_net - selectedCollab.realized) > 1 ? (
                                  <>Faturamento Gerado: <strong>{formatBRL(selectedCollab.raw_net)}</strong></>
                                ) : (
                                  <>Receita por cliente identificado</>
                                )}
                              </div>
                            ) : (
                              refComp && (
                                <div className="d-flex align-items-center gap-1 mt-1 flex-wrap" style={{ fontSize: '0.72rem' }}>
                                  <span style={{
                                    padding: '1px 6px',
                                    borderRadius: '6px',
                                    fontWeight: 600,
                                    backgroundColor: refComp.bgColor,
                                    color: refComp.statusColor,
                                    border: `1px solid ${refComp.borderColor}`
                                  }}>
                                    {refComp.badgeText}
                                  </span>
                                  <span className="text-muted" title="Ticket Médio de Referência">
                                    Ref: <strong>{formatBRL(refComp.numRef)}</strong>
                                  </span>
                                </div>
                              )
                            )}
                          </div>
                          <div className="bi-kpi-footer">
                            <span className="bi-kpi-days-badge" title="Dias úteis analisados no período">
                              <FiCalendar size={12} /> {collabBusinessDays} {collabBusinessDays === 1 ? 'dia útil analisado' : 'dias úteis analisados'}
                            </span>
                            <span className="text-muted" title="Média de pedidos por dia útil analisado">
                              {isCollabCli && currentTM > 1 ? `TM Cupom: ${formatBRL(currentTM)}` : `${Number(currentOrdersQty).toLocaleString('pt-BR', { minimumFractionDigits: 1, maximumFractionDigits: 1 })} ped/dia útil`}
                            </span>
                          </div>
                        </div>
                      );
                    })()}

                    {/* Card 7: Itens por Atendimento / Itens por Positivado */}
                    {(() => {
                      const refComp = !isCollabCli ? getRefComparison(currentIA, selectedCollab.ref_items_per_attendance) : null;
                      const itemsPerClient = selectedCollab.realized > 0 && totalItems > 0 && totalItems !== selectedCollab.realized
                        ? (totalItems / selectedCollab.realized)
                        : (currentIA > 1 ? currentIA : 1.0);
                      return (
                        <div className="bi-kpi-card">
                          <div className="bi-kpi-top">
                            <span className="bi-kpi-title">{isCollabCli ? 'Itens por Positivado' : 'Itens por Atendimento'}</span>
                            <div className="bi-kpi-icon indigo">
                              <FiUsers />
                            </div>
                          </div>
                          <div>
                            <div className="bi-kpi-value" style={{ color: refComp ? refComp.statusColor : '#1e293b' }}>
                              {Number(isCollabCli ? itemsPerClient : (currentIA || 0)).toLocaleString('pt-BR', { minimumFractionDigits: 1, maximumFractionDigits: 2 })}{' '}
                              <span style={{ fontSize: '1rem', fontWeight: 600 }}>{isCollabCli ? 'un/cliente' : 'un/venda'}</span>
                            </div>
                            <div className="bi-kpi-subtext">
                              {isCollabCli ? 'Mix de produtos por cliente identificado' : `Média das médias diárias (${collabBusinessDays} ${collabBusinessDays === 1 ? 'dia útil' : 'dias úteis'})`}
                            </div>
                            {refComp && (
                              <div className="d-flex align-items-center gap-1 mt-1 flex-wrap" style={{ fontSize: '0.72rem' }}>
                                <span style={{
                                  padding: '1px 6px',
                                  borderRadius: '6px',
                                  fontWeight: 600,
                                  backgroundColor: refComp.bgColor,
                                  color: refComp.statusColor,
                                  border: `1px solid ${refComp.borderColor}`
                                }}>
                                  {refComp.badgeText}
                                </span>
                                <span className="text-muted" title="Itens por Atendimento de Referência">
                                  Ref: <strong>{refComp.numRef.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</strong> un
                                </span>
                              </div>
                            )}
                          </div>
                          <div className="bi-kpi-footer">
                            <span className="bi-kpi-days-badge" title="Dias úteis analisados com movimentação no período">
                              <FiCalendar size={12} /> {collabBusinessDays} {collabBusinessDays === 1 ? 'dia útil analisado' : 'dias úteis analisados'}
                            </span>
                            <span className="text-muted" title="Volume total de itens e atendimentos acumulados no período">
                              Total: <strong>{totalItems.toLocaleString('pt-BR')}</strong> un ({totalOrders.toLocaleString('pt-BR')} ped)
                            </span>
                          </div>
                        </div>
                      );
                    })()}

                    {/* Card 8: VIA (Valor do Item) / Efetividade de Positivação */}
                    {(() => {
                      const refComp = !isCollabCli ? getRefComparison(currentVia, selectedCollab.ref_item_value_per_attendance) : null;
                      return (
                        <div className="bi-kpi-card">
                          <div className="bi-kpi-top">
                            <span className="bi-kpi-title">{isCollabCli ? 'Efetividade de Positivação' : 'VIA (Valor do Item)'}</span>
                            <div className="bi-kpi-icon teal">
                              <FiLayers />
                            </div>
                          </div>
                          <div>
                            <div className="bi-kpi-value" style={{ color: refComp ? refComp.statusColor : '#1e293b' }}>
                              {isCollabCli ? (
                                <>{selectedCollab.pct || 0}% <span style={{ fontSize: '1rem', fontWeight: 600 }}>atingido</span></>
                              ) : formatBRL(currentVia)}
                            </div>
                            <div className="bi-kpi-subtext">
                              {isCollabCli ? 'Identificação ativa nas vendas' : 'Valor de itens por atendimento'}
                            </div>
                            {refComp && (
                              <div className="d-flex align-items-center gap-1 mt-1 flex-wrap" style={{ fontSize: '0.72rem' }}>
                                <span style={{
                                  padding: '1px 6px',
                                  borderRadius: '6px',
                                  fontWeight: 600,
                                  backgroundColor: refComp.bgColor,
                                  color: refComp.statusColor,
                                  border: `1px solid ${refComp.borderColor}`
                                }}>
                                  {refComp.badgeText}
                                </span>
                                <span className="text-muted" title="VIA de Referência">
                                  Ref: <strong>{formatBRL(refComp.numRef)}</strong>
                                </span>
                              </div>
                            )}
                          </div>
                          <div className="bi-kpi-footer">
                            <span className="bi-kpi-days-badge" title="Dias úteis analisados no período">
                              <FiCalendar size={12} /> {collabBusinessDays} {collabBusinessDays === 1 ? 'dia útil analisado' : 'dias úteis analisados'}
                            </span>
                            <span className="text-muted" title={isCollabCli ? 'Cadastro e retenção no checkout' : 'Fórmula do Ticket Médio'}>
                              {isCollabCli ? 'Fidelização de clientes' : `Itens (${Number(currentIA || 0).toLocaleString('pt-BR', { minimumFractionDigits: 1, maximumFractionDigits: 2 })}) × VIA = TM`}
                            </span>
                          </div>
                        </div>
                      );
                    })()}
                  </div>
                );
              })()}

              {/* Dica Tática & Diagnóstico */}
              {(() => {
                const currentIA = getCollabIA(selectedCollab);
                const isCollabCli = selectedCollab.goal_subtype === 'clientes_positivados';
                const collabBusinessDays = selectedCollab.business_days_elapsed ?? selectedCollab.business_days_with_sales ?? period.business_days_elapsed ?? 0;
                const remainingBusinessDays = selectedCollab.business_days_remaining ?? period.business_days_remaining ?? 12;
                return (
                  <div className="bi-modal-tactic-tip mb-4">
                    <strong>💡 Diagnóstico & Ação Estratégica:</strong>
                    <div className="mt-1">
                      {isCollabCli ? (
                        selectedCollab.gap > 0 ? (
                          <>
                            Para atingir a meta de positivação, faltam <strong>{Number(selectedCollab.gap || 0).toLocaleString('pt-BR')} clientes identificados</strong>.
                            Com o ritmo de positivação atual de <strong>{(collabBusinessDays > 0 ? (selectedCollab.realized / collabBusinessDays) : 0).toFixed(1)} clientes/dia útil</strong>, o colaborador precisa alcançar <strong>{Number(selectedCollab.required_daily_rate || 0).toLocaleString('pt-BR', { maximumFractionDigits: 1 })} clientes/dia útil</strong> nos {remainingBusinessDays} dias úteis restantes.
                            {selectedCollab.bonus_amount > 0 && ` Atingindo a meta de ${Number(selectedCollab.target || 0).toLocaleString('pt-BR')} clientes, libera a bonificação adicional de ${formatBRL(selectedCollab.bonus_amount)}.`}
                            {' '}Ação recomendada: Solicitar ativamente o CPF no caixa/balcão para o Clube de Benefícios em 100% dos atendimentos, reduzindo cupons sob 'Consumidor Final' e fortalecendo a carteira de clientes identificados.
                          </>
                        ) : (
                          <>
                            Parabéns! A meta de positivação foi superada com <strong>{selectedCollab.pct}% de atingimento</strong> ({Number(selectedCollab.realized || 0).toLocaleString('pt-BR')} clientes cadastrados/identificados nas vendas)
                            {selectedCollab.bonus_achieved && selectedCollab.bonus_amount > 0 && `, liberando a bonificação adicional de ${formatBRL(selectedCollab.bonus_amount)}!`}.
                          </>
                        )
                      ) : selectedCollab.gap > 0 ? (
                        <>
                          Para atingir a meta, faltam <strong>{formatBRL(selectedCollab.gap)}</strong>.
                          Com o ticket médio atual de <strong>{formatBRL(selectedCollab.ticket_medio)}</strong> (composto por média de <strong>{Number(currentIA || 0).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} itens/atendimento</strong> e VIA de <strong>{formatBRL(selectedCollab.via || selectedCollab.valor_por_produto)}</strong>), a colaboradora precisa de aproximadamente{' '}
                          <strong>{Math.ceil(selectedCollab.gap / (selectedCollab.ticket_medio || 1))} atendimentos adicionais</strong> até o final do mês,
                          ou alavancar o faturamento elevando o <strong>VIA (itens de maior valor)</strong> e os <strong>itens por atendimento (venda casada)</strong>.
                        </>
                      ) : (
                        <>
                          Parabéns! A meta foi superada com <strong>{selectedCollab.pct}% de atingimento</strong>, Ticket Médio de <strong>{formatBRL(selectedCollab.ticket_medio)}</strong> e VIA de <strong>{formatBRL(selectedCollab.via || selectedCollab.valor_por_produto)}</strong>.
                        </>
                      )}
                      {(selectedCollab.ref_daily_net_sales || selectedCollab.ref_daily_orders_qty || selectedCollab.ref_average_ticket || selectedCollab.ref_items_per_attendance || selectedCollab.ref_item_value_per_attendance) && (
                        <div className="mt-2 pt-2 border-top" style={{ fontSize: '0.75rem' }}>
                          <div className="fw-semibold text-dark mb-1">🎯 Status frente aos Valores de Referência:</div>
                          <div className="d-flex flex-wrap gap-2">
                            {selectedCollab.ref_daily_net_sales && (() => {
                              const c = getRefComparison(selectedCollab.daily_net_sales_average || selectedCollab.daily_run_rate || 0, selectedCollab.ref_daily_net_sales);
                              return c && (
                                <span style={{ padding: '2px 8px', borderRadius: '6px', fontSize: '0.7rem', fontWeight: 600, backgroundColor: c.bgColor, color: c.statusColor, border: `1px solid ${c.borderColor}` }}>
                                  Venda Diária: {formatBRL(selectedCollab.daily_net_sales_average || selectedCollab.daily_run_rate || 0)} vs Ref {formatBRL(c.numRef)} ({c.badgeText})
                                </span>
                              );
                            })()}
                            {selectedCollab.ref_average_ticket && (() => {
                              const c = getRefComparison(selectedCollab.ticket_medio, selectedCollab.ref_average_ticket);
                              return c && (
                                <span style={{ padding: '2px 8px', borderRadius: '6px', fontSize: '0.7rem', fontWeight: 600, backgroundColor: c.bgColor, color: c.statusColor, border: `1px solid ${c.borderColor}` }}>
                                  Ticket Médio: {formatBRL(selectedCollab.ticket_medio)} vs Ref {formatBRL(c.numRef)} ({c.badgeText})
                                </span>
                              );
                            })()}
                            {selectedCollab.ref_items_per_attendance && (() => {
                              const c = getRefComparison(currentIA, selectedCollab.ref_items_per_attendance);
                              return c && (
                                <span style={{ padding: '2px 8px', borderRadius: '6px', fontSize: '0.7rem', fontWeight: 600, backgroundColor: c.bgColor, color: c.statusColor, border: `1px solid ${c.borderColor}` }}>
                                  IA: {Number(currentIA || 0).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} vs Ref {c.numRef.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} un ({c.badgeText})
                                </span>
                              );
                            })()}
                            {selectedCollab.ref_item_value_per_attendance && (() => {
                              const c = getRefComparison(selectedCollab.via || selectedCollab.valor_por_produto, selectedCollab.ref_item_value_per_attendance);
                              return c && (
                                <span style={{ padding: '2px 8px', borderRadius: '6px', fontSize: '0.7rem', fontWeight: 600, backgroundColor: c.bgColor, color: c.statusColor, border: `1px solid ${c.borderColor}` }}>
                                  VIA: {formatBRL(selectedCollab.via || selectedCollab.valor_por_produto)} vs Ref {formatBRL(c.numRef)} ({c.badgeText})
                                </span>
                              );
                            })()}
                            {selectedCollab.ref_daily_orders_qty && (() => {
                              const c = getRefComparison(selectedCollab.daily_orders_qty ?? selectedCollab.avg_orders_per_day ?? 0, selectedCollab.ref_daily_orders_qty);
                              return c && (
                                <span style={{ padding: '2px 8px', borderRadius: '6px', fontSize: '0.7rem', fontWeight: 600, backgroundColor: c.bgColor, color: c.statusColor, border: `1px solid ${c.borderColor}` }}>
                                  Vendas Diárias: {Number(selectedCollab.daily_orders_qty ?? selectedCollab.avg_orders_per_day ?? 0).toLocaleString('pt-BR', { minimumFractionDigits: 1, maximumFractionDigits: 2 })} vs Ref {c.numRef.toLocaleString('pt-BR', { minimumFractionDigits: 1, maximumFractionDigits: 2 })} ({c.badgeText})
                                </span>
                              );
                            })()}
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                );
              })()}

              {/* Callout de Produto Estratégico em Destaque */}
              {collabRecs.length > 0 && (
                <div className="bi-rec-callout-card mb-4" onClick={() => setModalTab('recommendations')}>
                  <div className="d-flex align-items-center justify-content-between flex-wrap gap-2">
                    <div className="d-flex align-items-center gap-3">
                      <div className="bi-kpi-icon gold" style={{ width: '40px', height: '40px', fontSize: '1.15rem' }}>
                        <FiAward />
                      </div>
                      <div>
                        <div className="d-flex align-items-center gap-2 flex-wrap">
                          <span className="badge bg-warning text-dark fw-bold" style={{ fontSize: '0.68rem' }}>
                            {collabRecs[0].category_tag || 'Oportunidade de Alavancagem'}
                          </span>
                          <strong className="text-dark fs-6">{collabRecs[0].descricao}</strong>
                          <span className="text-primary fw-bold">({formatBRL(collabRecs[0].pvenda)})</span>
                          <span className="text-purple fw-bold small">+{collabRecs[0].ticket_boost_pct}% no Ticket Médio</span>
                        </div>
                        <div className="text-muted small mt-0.5">
                          {collabRecs[0].action_tip}
                        </div>
                      </div>
                    </div>
                    <button
                      type="button"
                      className="btn btn-sm btn-primary d-flex align-items-center gap-1 px-3 py-1.5 fw-semibold"
                      onClick={(e) => {
                        e.stopPropagation();
                        setModalTab('recommendations');
                      }}
                    >
                      <span>Ver Todas as {collabRecs.length} Sugestões Estratégicas</span>
                      <FiChevronRight size={14} />
                    </button>
                  </div>
                </div>
              )}

                  {/* Curva de Progressão & Ticket Médio em Destaque na Visão Geral */}
                  <div className="bi-modal-overview-charts mt-3 mb-4">
                    <div className="d-flex justify-content-between align-items-center mb-3 flex-wrap gap-2">
                      <div>
                        <h6 className="fw-bold text-dark mb-0 d-flex align-items-center gap-2">
                          <FiTrendingUp className="text-primary" /> Curva de Progressão & Ticket Médio da Meta
                        </h6>
                        <span className="text-muted small">Evolução diária real da colaboradora vinculada à meta</span>
                      </div>
                      <button
                        type="button"
                        className="btn btn-sm btn-outline-primary d-flex align-items-center gap-1 fw-semibold"
                        onClick={() => setModalTab('charts')}
                      >
                        <FiBarChart2 size={14} /> Abrir Painel Completo de 6 Gráficos Analíticos →
                      </button>
                    </div>

                    <div className="bi-modal-charts-grid">
                      {/* Gráfico 1: Curva de Progressão e Ritmo Acumulado */}
                      <div className="bi-modal-chart-card">
                        <div className="bi-modal-chart-header">
                          <div className="bi-modal-chart-title">
                            <FiTrendingUp className="text-primary" /> Curva de Progressão & Ritmo Ideal
                          </div>
                          <div className="d-flex align-items-center gap-2 flex-wrap">
                            <div className="bi-chart-type-toggle">
                              <button
                                type="button"
                                className={`bi-chart-type-btn ${chartTypes.progression === 'area' ? 'active' : ''}`}
                                onClick={() => toggleChartType('progression', 'area')}
                                title="Visualizar em Área"
                              >
                                Área
                              </button>
                              <button
                                type="button"
                                className={`bi-chart-type-btn ${chartTypes.progression === 'bar' ? 'active' : ''}`}
                                onClick={() => toggleChartType('progression', 'bar')}
                                title="Visualizar em Barras"
                              >
                                Barras
                              </button>
                              <button
                                type="button"
                                className={`bi-chart-type-btn ${chartTypes.progression === 'line' ? 'active' : ''}`}
                                onClick={() => toggleChartType('progression', 'line')}
                                title="Visualizar em Linhas"
                              >
                                Linhas
                              </button>
                            </div>
                            <span className="bi-modal-chart-badge">
                              {collabAnalytics.isCollabQtyGoal ? (collabAnalytics.isCollabClients ? 'Clientes Acumulados' : 'Produtos Acumulados') : 'Faturamento Acumulado'}
                            </span>
                          </div>
                        </div>
                        <div className="bi-chart-body">
                          <ResponsiveContainer width="100%" height={260}>
                            <ComposedChart data={collabAnalytics.timeline} margin={{ top: 10, right: 20, left: 10, bottom: 0 }}>
                              <defs>
                                <linearGradient id="collabOverviewColorRealized" x1="0" y1="0" x2="0" y2="1">
                                  <stop offset="5%" stopColor="#3b82f6" stopOpacity={0.4} />
                                  <stop offset="95%" stopColor="#3b82f6" stopOpacity={0.0} />
                                </linearGradient>
                              </defs>
                              <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#e2e8f0" />
                              <XAxis dataKey="shortLabel" stroke="#64748b" fontSize={12} tickLine={false} />
                              <YAxis
                                stroke="#64748b"
                                fontSize={12}
                                tickLine={false}
                                tickFormatter={(v) => collabAnalytics.isCollabQtyGoal ? `${v} ${collabAnalytics.collabQtyUnit}` : `R$ ${(v / 1000).toFixed(0)}k`}
                              />
                              <Tooltip content={<CustomChartTooltip />} />
                              <Legend verticalAlign="top" height={32} />
                              {chartTypes.progression === 'area' && (
                                <Area
                                  type="monotone"
                                  dataKey="accumulated_realized"
                                  name={collabAnalytics.isCollabQtyGoal ? (collabAnalytics.isCollabClients ? 'Clientes Acumulados' : 'Produtos Acumulados') : "Faturamento Acumulado"}
                                  stroke="#3b82f6"
                                  strokeWidth={3}
                                  fillOpacity={1}
                                  fill="url(#collabOverviewColorRealized)"
                                  unit={collabAnalytics.isCollabQtyGoal ? collabAnalytics.collabQtyUnit : "R$"}
                                />
                              )}
                              {chartTypes.progression === 'bar' && (
                                <Bar
                                  dataKey="accumulated_realized"
                                  name={collabAnalytics.isCollabQtyGoal ? (collabAnalytics.isCollabClients ? 'Clientes Acumulados' : 'Produtos Acumulados') : "Faturamento Acumulado"}
                                  fill="#3b82f6"
                                  radius={[4, 4, 0, 0]}
                                  unit={collabAnalytics.isCollabQtyGoal ? collabAnalytics.collabQtyUnit : "R$"}
                                />
                              )}
                              {chartTypes.progression === 'line' && (
                                <Line
                                  type="monotone"
                                  dataKey="accumulated_realized"
                                  name={collabAnalytics.isCollabQtyGoal ? (collabAnalytics.isCollabClients ? 'Clientes Acumulados' : 'Produtos Acumulados') : "Faturamento Acumulado"}
                                  stroke="#3b82f6"
                                  strokeWidth={3}
                                  dot={{ r: 3 }}
                                  unit={collabAnalytics.isCollabQtyGoal ? collabAnalytics.collabQtyUnit : "R$"}
                                />
                              )}
                              <Line
                                type="monotone"
                                dataKey="ideal_pace"
                                name="Ritmo Ideal Linear"
                                stroke="#10b981"
                                strokeWidth={2}
                                strokeDasharray="5 5"
                                dot={false}
                                unit={collabAnalytics.isCollabQtyGoal ? collabAnalytics.collabQtyUnit : "R$"}
                              />
                            </ComposedChart>
                          </ResponsiveContainer>
                        </div>
                      </div>

                      {/* Gráfico 2: Evolução do Ticket Médio por Período ou Positivação Diária */}
                      <div className="bi-modal-chart-card">
                        <div className="bi-modal-chart-header">
                          <div className="bi-modal-chart-title">
                            {collabAnalytics.isCollabClients ? (
                              <>
                                <FiUsers className="text-purple" /> Positivação Diária vs Meta Diária
                              </>
                            ) : (
                              <>
                                <FiShoppingBag className="text-purple" /> Ticket Médio por Período
                              </>
                            )}
                          </div>
                          <div className="d-flex align-items-center gap-2 flex-wrap">
                            <div className="bi-chart-type-toggle">
                              <button
                                type="button"
                                className={`bi-chart-type-btn ${chartTypes.ticketMedio === 'bar' ? 'active' : ''}`}
                                onClick={() => toggleChartType('ticketMedio', 'bar')}
                                title="Visualizar em Barras"
                              >
                                Barras
                              </button>
                              <button
                                type="button"
                                className={`bi-chart-type-btn ${chartTypes.ticketMedio === 'line' ? 'active' : ''}`}
                                onClick={() => toggleChartType('ticketMedio', 'line')}
                                title="Visualizar em Linhas"
                              >
                                Linhas
                              </button>
                              <button
                                type="button"
                                className={`bi-chart-type-btn ${chartTypes.ticketMedio === 'area' ? 'active' : ''}`}
                                onClick={() => toggleChartType('ticketMedio', 'area')}
                                title="Visualizar em Área"
                              >
                                Área
                              </button>
                            </div>
                            {collabAnalytics.isCollabClients ? (
                              (collabAnalytics.timeline[0]?.ref_daily_sales || 0) > 0 && (
                                <span className="bi-modal-chart-badge">
                                  Meta: {Number(collabAnalytics.timeline[0].ref_daily_sales).toLocaleString('pt-BR', { maximumFractionDigits: 1 })} cli/dia
                                </span>
                              )
                            ) : (
                              selectedCollab.ref_average_ticket && (
                                <span className="bi-modal-chart-badge">
                                  Ref: {formatBRL(selectedCollab.ref_average_ticket)}
                                </span>
                              )
                            )}
                          </div>
                        </div>
                        <div className="bi-chart-body">
                          <ResponsiveContainer width="100%" height={260}>
                            <ComposedChart data={collabAnalytics.timeline} margin={{ top: 10, right: 20, left: 10, bottom: 0 }}>
                              <defs>
                                <linearGradient id="collabOverviewColorTicketMedio" x1="0" y1="0" x2="0" y2="1">
                                  <stop offset="5%" stopColor="#8b5cf6" stopOpacity={0.4} />
                                  <stop offset="95%" stopColor="#8b5cf6" stopOpacity={0.0} />
                                </linearGradient>
                              </defs>
                              <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#e2e8f0" />
                              <XAxis dataKey="shortLabel" stroke="#64748b" fontSize={12} tickLine={false} />
                              <YAxis
                                stroke="#8b5cf6"
                                fontSize={12}
                                tickLine={false}
                                tickFormatter={(v) => collabAnalytics.isCollabClients ? `${v} cli` : `R$ ${v}`}
                              />
                              <Tooltip content={<CustomChartTooltip />} />
                              <Legend verticalAlign="top" height={32} />
                              {chartTypes.ticketMedio === 'bar' && (
                                <Bar
                                  dataKey={collabAnalytics.isCollabClients ? "realized_day" : "ticket_medio"}
                                  name={collabAnalytics.isCollabClients ? "Clientes Positivados (cli)" : "Ticket Médio (R$)"}
                                  fill="#8b5cf6"
                                  radius={[4, 4, 0, 0]}
                                  unit={collabAnalytics.isCollabClients ? "cli" : "R$"}
                                />
                              )}
                              {chartTypes.ticketMedio === 'line' && (
                                <Line
                                  type="monotone"
                                  dataKey={collabAnalytics.isCollabClients ? "realized_day" : "ticket_medio"}
                                  name={collabAnalytics.isCollabClients ? "Clientes Positivados (cli)" : "Ticket Médio (R$)"}
                                  stroke="#8b5cf6"
                                  strokeWidth={3}
                                  dot={{ r: 4, fill: '#8b5cf6' }}
                                  unit={collabAnalytics.isCollabClients ? "cli" : "R$"}
                                />
                              )}
                              {chartTypes.ticketMedio === 'area' && (
                                <Area
                                  type="monotone"
                                  dataKey={collabAnalytics.isCollabClients ? "realized_day" : "ticket_medio"}
                                  name={collabAnalytics.isCollabClients ? "Clientes Positivados (cli)" : "Ticket Médio (R$)"}
                                  stroke="#8b5cf6"
                                  strokeWidth={3}
                                  fillOpacity={1}
                                  fill="url(#collabOverviewColorTicketMedio)"
                                  unit={collabAnalytics.isCollabClients ? "cli" : "R$"}
                                />
                              )}
                              {collabAnalytics.isCollabClients ? (
                                (collabAnalytics.timeline[0]?.ref_daily_sales || 0) > 0 && (
                                  <ReferenceLine
                                    y={Number(collabAnalytics.timeline[0].ref_daily_sales)}
                                    stroke="#10b981"
                                    strokeDasharray="4 4"
                                    strokeWidth={2}
                                    label={{ value: 'Meta/Dia', position: 'insideTopRight', fill: '#059669', fontSize: 11 }}
                                  />
                                )
                              ) : (
                                selectedCollab.ref_average_ticket > 0 && (
                                  <ReferenceLine
                                    y={Number(selectedCollab.ref_average_ticket)}
                                    stroke="#ec4899"
                                    strokeDasharray="4 4"
                                    strokeWidth={2}
                                    label={{ value: 'Ref TM', position: 'insideTopRight', fill: '#db2777', fontSize: 11 }}
                                  />
                                )
                              )}
                            </ComposedChart>
                          </ResponsiveContainer>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Resumo de Fechamentos Vinculados no Overview */}
                  {selectedCollab.reports && selectedCollab.reports.length > 0 && (
                    <div className="border rounded-3 p-3 bg-light d-flex justify-content-between align-items-center flex-wrap gap-2">
                      <div>
                        <div className="fw-bold text-dark d-flex align-items-center gap-2">
                          <FiLayers className="text-primary" /> Fechamentos de Venda Vinculados:
                          <span className="badge bg-primary text-white">{selectedCollab.reports.length} relatório(s)</span>
                        </div>
                        <div className="text-muted small mt-1">
                          Total Realizado: <strong>{formatBRL(selectedCollab.realized)}</strong> • Líquido Filtros: <strong>{formatBRL(selectedCollab.raw_net || selectedCollab.realized)}</strong>
                        </div>
                      </div>
                      <button
                        type="button"
                        className="btn btn-sm btn-outline-secondary"
                        onClick={() => setModalTab('reports')}
                      >
                        Ver Detalhes dos Relatórios →
                      </button>
                    </div>
                  )}
                </>
              )}

              {/* ABA 2: PAINEL COMPLETO DE GRÁFICOS ANALÍTICOS (6 GRÁFICOS) */}
              {modalTab === 'charts' && (
                <div className="bi-modal-charts-container">
                  {/* Barra de Sub-filtros por Categoria de Análise */}
                  <div className="d-flex justify-content-between align-items-center flex-wrap gap-2 mb-3">
                    <div className="bi-modal-chart-subfilter mb-0">
                      <button
                        type="button"
                        className={`bi-chart-category-btn ${modalChartCategory === 'all' ? 'active' : ''}`}
                        onClick={() => setModalChartCategory('all')}
                      >
                        <FiBarChart2 size={13} /> Todos os Gráficos (6)
                      </button>
                      <button
                        type="button"
                        className={`bi-chart-category-btn ${modalChartCategory === 'pace' ? 'active' : ''}`}
                        onClick={() => setModalChartCategory('pace')}
                      >
                        <FiTrendingUp size={13} /> Ritmo & Vendas Diárias (2)
                      </button>
                      <button
                        type="button"
                        className={`bi-chart-category-btn ${modalChartCategory === 'ticket_items' ? 'active' : ''}`}
                        onClick={() => setModalChartCategory('ticket_items')}
                      >
                        <FiShoppingBag size={13} /> Ticket Médio, Itens & VIA (3)
                      </button>
                      <button
                        type="button"
                        className={`bi-chart-category-btn ${modalChartCategory === 'weekday' ? 'active' : ''}`}
                        onClick={() => setModalChartCategory('weekday')}
                      >
                        <FiCalendar size={13} /> Padrão Semanal (1)
                      </button>
                    </div>

                    <div className="d-flex align-items-center gap-2 flex-wrap text-muted small">
                      <span className="badge bg-light text-dark border">
                        Meta: <strong>{collabAnalytics.isCollabQtyGoal ? `${Number(selectedCollab.target || 0).toLocaleString('pt-BR')} ${collabAnalytics.collabQtyUnit}` : formatBRL(selectedCollab.target)}</strong>
                      </span>
                      <span className="badge bg-light text-dark border">
                        Realizado: <strong>{collabAnalytics.isCollabQtyGoal ? `${Number(selectedCollab.realized || 0).toLocaleString('pt-BR')} ${collabAnalytics.collabQtyUnit}` : formatBRL(selectedCollab.realized)}</strong> ({selectedCollab.pct}%)
                      </span>
                      {collabAnalytics.isCollabClients ? (
                        <>
                          <span className="badge bg-light text-dark border">
                            Receita/Cli: <strong>{formatBRL((selectedCollab.identified_clients || selectedCollab.realized) > 0 ? (Number(selectedCollab.raw_net || selectedCollab.net_sales || 0) / (Number(selectedCollab.identified_clients || selectedCollab.realized || 1))) : 0)}</strong>
                          </span>
                          <span className="badge bg-light text-dark border">
                            Freq: <strong>{(Number(selectedCollab.total_orders || 0) > 0 && Number(selectedCollab.realized || 0) > 0) ? (Number(selectedCollab.total_orders) / Number(selectedCollab.realized)).toFixed(2) : '1.00'} ped/cli</strong>
                          </span>
                        </>
                      ) : (
                        <>
                          <span className="badge bg-light text-dark border">
                            TM: <strong>{formatBRL(selectedCollab.ticket_medio)}</strong>
                          </span>
                          <span className="badge bg-light text-dark border">
                            IA: <strong>{Number(getCollabIA(selectedCollab)).toFixed(2)} un</strong>
                          </span>
                        </>
                      )}
                    </div>
                  </div>

                  {/* Grid Responsivo de Gráficos */}
                  <div className="bi-modal-charts-grid">
                    {/* Gráfico 1: Curva de Progressão e Ritmo Acumulado */}
                    {(modalChartCategory === 'all' || modalChartCategory === 'pace') && (
                      <div className="bi-modal-chart-card">
                        <div className="bi-modal-chart-header">
                          <div className="bi-modal-chart-title">
                            <FiTrendingUp className="text-primary" /> Curva de Progressão & Ritmo Ideal
                          </div>
                          <div className="d-flex align-items-center gap-2 flex-wrap">
                            <div className="bi-chart-type-toggle">
                              <button
                                type="button"
                                className={`bi-chart-type-btn ${chartTypes.progression === 'area' ? 'active' : ''}`}
                                onClick={() => toggleChartType('progression', 'area')}
                                title="Visualizar em Área"
                              >
                                Área
                              </button>
                              <button
                                type="button"
                                className={`bi-chart-type-btn ${chartTypes.progression === 'bar' ? 'active' : ''}`}
                                onClick={() => toggleChartType('progression', 'bar')}
                                title="Visualizar em Barras"
                              >
                                Barras
                              </button>
                              <button
                                type="button"
                                className={`bi-chart-type-btn ${chartTypes.progression === 'line' ? 'active' : ''}`}
                                onClick={() => toggleChartType('progression', 'line')}
                                title="Visualizar em Linhas"
                              >
                                Linhas
                              </button>
                            </div>
                            <span className="bi-modal-chart-badge">
                              {collabAnalytics.isCollabQtyGoal ? (collabAnalytics.isCollabClients ? 'Clientes Acumulados' : 'Produtos Acumulados') : 'Faturamento Acumulado'}
                            </span>
                          </div>
                        </div>
                        <div className="bi-chart-body">
                          <ResponsiveContainer width="100%" height={280}>
                            <ComposedChart data={collabAnalytics.timeline} margin={{ top: 10, right: 20, left: 10, bottom: 0 }}>
                              <defs>
                                <linearGradient id="collabColorRealized" x1="0" y1="0" x2="0" y2="1">
                                  <stop offset="5%" stopColor="#3b82f6" stopOpacity={0.4} />
                                  <stop offset="95%" stopColor="#3b82f6" stopOpacity={0.0} />
                                </linearGradient>
                              </defs>
                              <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#e2e8f0" />
                              <XAxis dataKey="shortLabel" stroke="#64748b" fontSize={12} tickLine={false} />
                              <YAxis
                                stroke="#64748b"
                                fontSize={12}
                                tickLine={false}
                                tickFormatter={(v) => collabAnalytics.isCollabQtyGoal ? `${v} ${collabAnalytics.collabQtyUnit}` : `R$ ${(v / 1000).toFixed(0)}k`}
                              />
                              <Tooltip content={<CustomChartTooltip />} />
                              <Legend verticalAlign="top" height={32} />
                              {chartTypes.progression === 'area' && (
                                <Area
                                  type="monotone"
                                  dataKey="accumulated_realized"
                                  name={collabAnalytics.isCollabQtyGoal ? (collabAnalytics.isCollabClients ? 'Clientes Acumulados' : 'Produtos Acumulados') : "Faturamento Acumulado"}
                                  stroke="#3b82f6"
                                  strokeWidth={3}
                                  fillOpacity={1}
                                  fill="url(#collabColorRealized)"
                                  unit={collabAnalytics.isCollabQtyGoal ? collabAnalytics.collabQtyUnit : "R$"}
                                />
                              )}
                              {chartTypes.progression === 'bar' && (
                                <Bar
                                  dataKey="accumulated_realized"
                                  name={collabAnalytics.isCollabQtyGoal ? (collabAnalytics.isCollabClients ? 'Clientes Acumulados' : 'Produtos Acumulados') : "Faturamento Acumulado"}
                                  fill="#3b82f6"
                                  radius={[4, 4, 0, 0]}
                                  unit={collabAnalytics.isCollabQtyGoal ? collabAnalytics.collabQtyUnit : "R$"}
                                />
                              )}
                              {chartTypes.progression === 'line' && (
                                <Line
                                  type="monotone"
                                  dataKey="accumulated_realized"
                                  name={collabAnalytics.isCollabQtyGoal ? (collabAnalytics.isCollabClients ? 'Clientes Acumulados' : 'Produtos Acumulados') : "Faturamento Acumulado"}
                                  stroke="#3b82f6"
                                  strokeWidth={3}
                                  dot={{ r: 3 }}
                                  unit={collabAnalytics.isCollabQtyGoal ? collabAnalytics.collabQtyUnit : "R$"}
                                />
                              )}
                              <Line
                                type="monotone"
                                dataKey="ideal_pace"
                                name="Ritmo Ideal Linear"
                                stroke="#10b981"
                                strokeWidth={2}
                                strokeDasharray="5 5"
                                dot={false}
                                unit={collabAnalytics.isCollabQtyGoal ? collabAnalytics.collabQtyUnit : "R$"}
                              />
                            </ComposedChart>
                          </ResponsiveContainer>
                        </div>
                      </div>
                    )}

                    {/* Gráfico 2: Faturamento / Quantidade Diária vs Meta Diária (Ritmo) */}
                    {(modalChartCategory === 'all' || modalChartCategory === 'pace') && (
                      <div className="bi-modal-chart-card">
                        <div className="bi-modal-chart-header">
                          <div className="bi-modal-chart-title">
                            <FiDollarSign className="text-success" /> {collabAnalytics.isCollabQtyGoal ? (collabAnalytics.isCollabClients ? 'Clientes Positivados / Dia' : 'Produtos Vendidos / Dia') : 'Faturamento Diário vs Meta Diária'}
                          </div>
                          <div className="d-flex align-items-center gap-2 flex-wrap">
                            <div className="bi-chart-type-toggle">
                              <button
                                type="button"
                                className={`bi-chart-type-btn ${chartTypes.dailySales === 'bar' ? 'active' : ''}`}
                                onClick={() => toggleChartType('dailySales', 'bar')}
                                title="Visualizar em Barras"
                              >
                                Barras
                              </button>
                              <button
                                type="button"
                                className={`bi-chart-type-btn ${chartTypes.dailySales === 'line' ? 'active' : ''}`}
                                onClick={() => toggleChartType('dailySales', 'line')}
                                title="Visualizar em Linhas"
                              >
                                Linhas
                              </button>
                              <button
                                type="button"
                                className={`bi-chart-type-btn ${chartTypes.dailySales === 'area' ? 'active' : ''}`}
                                onClick={() => toggleChartType('dailySales', 'area')}
                                title="Visualizar em Área"
                              >
                                Área
                              </button>
                            </div>
                            <span className="bi-modal-chart-badge">
                              Ref: {collabAnalytics.isCollabQtyGoal ? `${Number(collabAnalytics.timeline[0]?.ref_daily_sales || 0).toLocaleString('pt-BR', { maximumFractionDigits: 1 })} ${collabAnalytics.collabQtyUnit}/dia` : `${formatBRL(collabAnalytics.timeline[0]?.ref_daily_sales || selectedCollab.ref_daily_net_sales || 0)}/dia`}
                            </span>
                          </div>
                        </div>
                        <div className="bi-chart-body">
                          <ResponsiveContainer width="100%" height={280}>
                            <ComposedChart data={collabAnalytics.timeline} margin={{ top: 10, right: 20, left: 10, bottom: 0 }}>
                              <defs>
                                <linearGradient id="collabColorDailySales" x1="0" y1="0" x2="0" y2="1">
                                  <stop offset="5%" stopColor="#10b981" stopOpacity={0.4} />
                                  <stop offset="95%" stopColor="#10b981" stopOpacity={0.0} />
                                </linearGradient>
                              </defs>
                              <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#e2e8f0" />
                              <XAxis dataKey="shortLabel" stroke="#64748b" fontSize={12} tickLine={false} />
                              <YAxis
                                stroke="#64748b"
                                fontSize={12}
                                tickLine={false}
                                tickFormatter={(v) => collabAnalytics.isCollabQtyGoal ? `${v} ${collabAnalytics.collabQtyUnit}` : `R$ ${v >= 1000 ? (v / 1000).toFixed(1) + 'k' : v}`}
                              />
                              <Tooltip content={<CustomChartTooltip />} />
                              <Legend verticalAlign="top" height={32} />
                              {chartTypes.dailySales === 'bar' && (
                                <Bar
                                  dataKey="realized_day"
                                  name={collabAnalytics.isCollabQtyGoal ? (collabAnalytics.isCollabClients ? 'Clientes / Dia' : 'Produtos / Dia') : "Venda Líquida Diária (R$)"}
                                  radius={[4, 4, 0, 0]}
                                  unit={collabAnalytics.isCollabQtyGoal ? collabAnalytics.collabQtyUnit : "R$"}
                                >
                                  {collabAnalytics.timeline.map((entry, index) => {
                                    const refVal = entry.ref_daily_sales || 0;
                                    const isHit = refVal > 0 && entry.realized_day >= refVal;
                                    const isNear = refVal > 0 && !isHit && entry.realized_day >= refVal * 0.8;
                                    const fillColor = isHit ? '#10b981' : (isNear ? '#f59e0b' : '#3b82f6');
                                    return <Cell key={`cell-${index}`} fill={fillColor} />;
                                  })}
                                </Bar>
                              )}
                              {chartTypes.dailySales === 'line' && (
                                <Line
                                  type="monotone"
                                  dataKey="realized_day"
                                  name={collabAnalytics.isCollabQtyGoal ? (collabAnalytics.isCollabClients ? 'Clientes / Dia' : 'Produtos / Dia') : "Venda Líquida Diária (R$)"}
                                  stroke="#10b981"
                                  strokeWidth={3}
                                  dot={{ r: 4, fill: '#10b981' }}
                                  unit={collabAnalytics.isCollabQtyGoal ? collabAnalytics.collabQtyUnit : "R$"}
                                />
                              )}
                              {chartTypes.dailySales === 'area' && (
                                <Area
                                  type="monotone"
                                  dataKey="realized_day"
                                  name={collabAnalytics.isCollabQtyGoal ? (collabAnalytics.isCollabClients ? 'Clientes / Dia' : 'Produtos / Dia') : "Venda Líquida Diária (R$)"}
                                  stroke="#10b981"
                                  strokeWidth={3}
                                  fillOpacity={1}
                                  fill="url(#collabColorDailySales)"
                                  unit={collabAnalytics.isCollabQtyGoal ? collabAnalytics.collabQtyUnit : "R$"}
                                />
                              )}
                              {(collabAnalytics.timeline[0]?.ref_daily_sales || 0) > 0 && (
                                <ReferenceLine
                                  y={Number(collabAnalytics.timeline[0].ref_daily_sales)}
                                  stroke="#10b981"
                                  strokeDasharray="4 4"
                                  strokeWidth={2}
                                  label={{ value: collabAnalytics.isCollabQtyGoal ? `Meta Diária (${collabAnalytics.collabQtyUnit})` : 'Meta Diária', position: 'insideTopRight', fill: '#059669', fontSize: 11 }}
                                />
                              )}
                            </ComposedChart>
                          </ResponsiveContainer>
                        </div>
                      </div>
                    )}

                    {/* Gráfico 3: Evolução do Ticket Médio por Período */}
                    {(modalChartCategory === 'all' || modalChartCategory === 'ticket_items') && (
                      <div className="bi-modal-chart-card">
                        <div className="bi-modal-chart-header">
                          <div className="bi-modal-chart-title">
                            <FiShoppingBag className="text-purple" /> {collabAnalytics.isCollabClients ? 'Ticket Médio por Venda (Positivados)' : 'Ticket Médio por Período'}
                          </div>
                          <div className="d-flex align-items-center gap-2 flex-wrap">
                            <div className="bi-chart-type-toggle">
                              <button
                                type="button"
                                className={`bi-chart-type-btn ${chartTypes.ticketMedio === 'bar' ? 'active' : ''}`}
                                onClick={() => toggleChartType('ticketMedio', 'bar')}
                                title="Visualizar em Barras"
                              >
                                Barras
                              </button>
                              <button
                                type="button"
                                className={`bi-chart-type-btn ${chartTypes.ticketMedio === 'line' ? 'active' : ''}`}
                                onClick={() => toggleChartType('ticketMedio', 'line')}
                                title="Visualizar em Linhas"
                              >
                                Linhas
                              </button>
                              <button
                                type="button"
                                className={`bi-chart-type-btn ${chartTypes.ticketMedio === 'area' ? 'active' : ''}`}
                                onClick={() => toggleChartType('ticketMedio', 'area')}
                                title="Visualizar em Área"
                              >
                                Área
                              </button>
                            </div>
                            {selectedCollab.ref_average_ticket && (
                              <span className="bi-modal-chart-badge">
                                Ref: {formatBRL(selectedCollab.ref_average_ticket)}
                              </span>
                            )}
                          </div>
                        </div>
                        <div className="bi-chart-body">
                          <ResponsiveContainer width="100%" height={280}>
                            <ComposedChart data={collabAnalytics.timeline} margin={{ top: 10, right: 20, left: 10, bottom: 0 }}>
                              <defs>
                                <linearGradient id="collabColorTicketMedio" x1="0" y1="0" x2="0" y2="1">
                                  <stop offset="5%" stopColor="#8b5cf6" stopOpacity={0.4} />
                                  <stop offset="95%" stopColor="#8b5cf6" stopOpacity={0.0} />
                                </linearGradient>
                              </defs>
                              <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#e2e8f0" />
                              <XAxis dataKey="shortLabel" stroke="#64748b" fontSize={12} tickLine={false} />
                              <YAxis
                                stroke="#8b5cf6"
                                fontSize={12}
                                tickLine={false}
                                tickFormatter={(v) => `R$ ${v}`}
                              />
                              <Tooltip content={<CustomChartTooltip />} />
                              <Legend verticalAlign="top" height={32} />
                              {chartTypes.ticketMedio === 'bar' && (
                                <Bar
                                  dataKey="ticket_medio"
                                  name="Ticket Médio (R$)"
                                  fill="#8b5cf6"
                                  radius={[4, 4, 0, 0]}
                                  unit="R$"
                                />
                              )}
                              {chartTypes.ticketMedio === 'line' && (
                                <Line
                                  type="monotone"
                                  dataKey="ticket_medio"
                                  name="Ticket Médio (R$)"
                                  stroke="#8b5cf6"
                                  strokeWidth={3}
                                  dot={{ r: 4, fill: '#8b5cf6' }}
                                  unit="R$"
                                />
                              )}
                              {chartTypes.ticketMedio === 'area' && (
                                <Area
                                  type="monotone"
                                  dataKey="ticket_medio"
                                  name="Ticket Médio (R$)"
                                  stroke="#8b5cf6"
                                  strokeWidth={3}
                                  fillOpacity={1}
                                  fill="url(#collabColorTicketMedio)"
                                  unit="R$"
                                />
                              )}
                              {selectedCollab.ref_average_ticket > 0 && (
                                <ReferenceLine
                                  y={Number(selectedCollab.ref_average_ticket)}
                                  stroke="#ec4899"
                                  strokeDasharray="4 4"
                                  strokeWidth={2}
                                  label={{ value: 'Ref TM', position: 'insideTopRight', fill: '#db2777', fontSize: 11 }}
                                />
                              )}
                            </ComposedChart>
                          </ResponsiveContainer>
                        </div>
                      </div>
                    )}

                    {/* Gráfico 4: Itens por Atendimento (PA) & Volume de Atendimentos */}
                    {(modalChartCategory === 'all' || modalChartCategory === 'ticket_items') && (
                      <div className="bi-modal-chart-card">
                        <div className="bi-modal-chart-header">
                          <div className="bi-modal-chart-title">
                            <FiUsers className="text-indigo" /> {collabAnalytics.isCollabClients ? 'Itens por Venda & Volume de Compras' : 'Itens por Atendimento (PA) & Volume'}
                          </div>
                          {selectedCollab.ref_items_per_attendance && (
                            <span className="bi-modal-chart-badge">
                              Ref: {Number(selectedCollab.ref_items_per_attendance).toFixed(2)} un
                            </span>
                          )}
                        </div>
                        <div className="bi-chart-body">
                          <ResponsiveContainer width="100%" height={280}>
                            <ComposedChart data={collabAnalytics.timeline} margin={{ top: 10, right: 20, left: 10, bottom: 0 }}>
                              <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#e2e8f0" />
                              <XAxis dataKey="shortLabel" stroke="#64748b" fontSize={12} tickLine={false} />
                              <YAxis
                                yAxisId="left"
                                stroke="#6366f1"
                                fontSize={12}
                                tickLine={false}
                                tickFormatter={(v) => `${v} un`}
                              />
                              <YAxis
                                yAxisId="right"
                                orientation="right"
                                stroke="#06b6d4"
                                fontSize={12}
                                tickLine={false}
                                tickFormatter={(v) => `${v} ped`}
                              />
                              <Tooltip content={<CustomChartTooltip />} />
                              <Legend verticalAlign="top" height={32} />
                              <Bar
                                yAxisId="left"
                                dataKey="pa"
                                name="Itens por Venda (un)"
                                unit="un"
                                fill="#6366f1"
                                radius={[4, 4, 0, 0]}
                              />
                              <Line
                                yAxisId="right"
                                type="monotone"
                                dataKey="orders"
                                name={collabAnalytics.isCollabClients ? 'Vendas Identificadas' : 'Pedidos do Dia'}
                                unit="ped"
                                stroke="#06b6d4"
                                strokeWidth={2}
                                dot={{ r: 3 }}
                              />
                              {selectedCollab.ref_items_per_attendance > 0 && (
                                <ReferenceLine
                                  yAxisId="left"
                                  y={Number(selectedCollab.ref_items_per_attendance)}
                                  stroke="#f59e0b"
                                  strokeDasharray="4 4"
                                  strokeWidth={2}
                                  label={{ value: 'Ref PA', position: 'insideTopLeft', fill: '#d97706', fontSize: 11 }}
                                />
                              )}
                            </ComposedChart>
                          </ResponsiveContainer>
                        </div>
                      </div>
                    )}

                    {/* Gráfico 5: Composição do Ticket: VIA (Valor do Item) vs TM */}
                    {(modalChartCategory === 'all' || modalChartCategory === 'ticket_items') && (
                      <div className="bi-modal-chart-card">
                        <div className="bi-modal-chart-header">
                          <div className="bi-modal-chart-title">
                            <FiLayers className="text-teal" /> Composição: VIA (Valor do Item) vs TM
                          </div>
                          <span className="bi-modal-chart-badge">Fórmula: VIA × PA = TM</span>
                        </div>
                        <div className="bi-chart-body">
                          <ResponsiveContainer width="100%" height={280}>
                            <ComposedChart data={collabAnalytics.timeline} margin={{ top: 10, right: 20, left: 10, bottom: 0 }}>
                              <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#e2e8f0" />
                              <XAxis dataKey="shortLabel" stroke="#64748b" fontSize={12} tickLine={false} />
                              <YAxis
                                stroke="#0d9488"
                                fontSize={12}
                                tickLine={false}
                                tickFormatter={(v) => `R$ ${v}`}
                              />
                              <Tooltip content={<CustomChartTooltip />} />
                              <Legend verticalAlign="top" height={32} />
                              <Area
                                type="monotone"
                                dataKey="via"
                                name="VIA - Preço Médio do Item (R$)"
                                stroke="#0d9488"
                                fill="#ccfbf1"
                                fillOpacity={0.6}
                                strokeWidth={2}
                              />
                              <Line
                                type="monotone"
                                dataKey="ticket_medio"
                                name="Ticket Médio (R$)"
                                stroke="#8b5cf6"
                                strokeWidth={2}
                                dot={{ r: 3 }}
                              />
                              {selectedCollab.ref_item_value_per_attendance > 0 && (
                                <ReferenceLine
                                  y={Number(selectedCollab.ref_item_value_per_attendance)}
                                  stroke="#0d9488"
                                  strokeDasharray="4 4"
                                  strokeWidth={2}
                                  label={{ value: 'Ref VIA', position: 'insideTopRight', fill: '#0f766e', fontSize: 11 }}
                                />
                              )}
                            </ComposedChart>
                          </ResponsiveContainer>
                        </div>
                      </div>
                    )}

                    {/* Gráfico 6: Distribuição e Performance por Dia da Semana */}
                    {(modalChartCategory === 'all' || modalChartCategory === 'weekday') && (
                      <div className="bi-modal-chart-card full-width">
                        <div className="bi-modal-chart-header">
                          <div className="bi-modal-chart-title">
                            <FiCalendar className="text-warning" /> Performance por Dia da Semana
                          </div>
                          <div className="d-flex align-items-center gap-2 flex-wrap">
                            <div className="bi-chart-type-toggle">
                              <button
                                type="button"
                                className={`bi-chart-type-btn ${chartTypes.weekday === 'composed' ? 'active' : ''}`}
                                onClick={() => toggleChartType('weekday', 'composed')}
                                title="Visualizar Misto (Barras + Linha)"
                              >
                                Misto
                              </button>
                              <button
                                type="button"
                                className={`bi-chart-type-btn ${chartTypes.weekday === 'bar' ? 'active' : ''}`}
                                onClick={() => toggleChartType('weekday', 'bar')}
                                title="Visualizar em Barras"
                              >
                                Barras
                              </button>
                              <button
                                type="button"
                                className={`bi-chart-type-btn ${chartTypes.weekday === 'line' ? 'active' : ''}`}
                                onClick={() => toggleChartType('weekday', 'line')}
                                title="Visualizar em Linhas"
                              >
                                Linhas
                              </button>
                              <button
                                type="button"
                                className={`bi-chart-type-btn ${chartTypes.weekday === 'area' ? 'active' : ''}`}
                                onClick={() => toggleChartType('weekday', 'area')}
                                title="Visualizar em Área"
                              >
                                Área
                              </button>
                            </div>
                            <span className="bi-modal-chart-badge">{collabAnalytics.isCollabClients ? 'Padrão Semanal de Positivação' : 'Padrão de Vendas & Comportamento'}</span>
                          </div>
                        </div>
                        <div className="bi-chart-body">
                          <ResponsiveContainer width="100%" height={280}>
                            <ComposedChart data={collabAnalytics.weekdayData} margin={{ top: 10, right: 20, left: 10, bottom: 0 }}>
                              <defs>
                                <linearGradient id="collabColorWeekdayNet" x1="0" y1="0" x2="0" y2="1">
                                  <stop offset="5%" stopColor="#fb923c" stopOpacity={0.4} />
                                  <stop offset="95%" stopColor="#fb923c" stopOpacity={0.0} />
                                </linearGradient>
                              </defs>
                              <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#e2e8f0" />
                              <XAxis dataKey="name" stroke="#64748b" fontSize={12} tickLine={false} />
                              <YAxis
                                yAxisId="left"
                                stroke="#f97316"
                                fontSize={12}
                                tickLine={false}
                                tickFormatter={(v) => collabAnalytics.isCollabQtyGoal ? `${v} ${collabAnalytics.collabQtyUnit}` : `R$ ${(v / 1000).toFixed(0)}k`}
                              />
                              <YAxis
                                yAxisId="right"
                                orientation="right"
                                stroke="#8b5cf6"
                                fontSize={12}
                                tickLine={false}
                                tickFormatter={(v) => `R$ ${v}`}
                              />
                              <Tooltip content={<CustomChartTooltip />} />
                              <Legend verticalAlign="top" height={32} />

                              {chartTypes.weekday === 'composed' && (
                                <>
                                  <Bar
                                    yAxisId="left"
                                    dataKey={collabAnalytics.isCollabQtyGoal ? "total_qty" : "total_net"}
                                    name={collabAnalytics.isCollabQtyGoal ? (collabAnalytics.isCollabClients ? 'Clientes Totais' : 'Produtos Totais') : "Faturamento Total (R$)"}
                                    fill="#fb923c"
                                    radius={[4, 4, 0, 0]}
                                    unit={collabAnalytics.isCollabQtyGoal ? collabAnalytics.collabQtyUnit : "R$"}
                                  />
                                  <Line
                                    yAxisId="right"
                                    type="monotone"
                                    dataKey="ticket_medio"
                                    name="Ticket Médio (R$)"
                                    stroke="#8b5cf6"
                                    strokeWidth={3}
                                    dot={{ r: 4 }}
                                    unit="R$"
                                  />
                                </>
                              )}

                              {chartTypes.weekday === 'bar' && (
                                <>
                                  <Bar
                                    yAxisId="left"
                                    dataKey={collabAnalytics.isCollabQtyGoal ? "total_qty" : "total_net"}
                                    name={collabAnalytics.isCollabQtyGoal ? (collabAnalytics.isCollabClients ? 'Clientes Totais' : 'Produtos Totais') : "Faturamento Total (R$)"}
                                    fill="#fb923c"
                                    radius={[4, 4, 0, 0]}
                                    unit={collabAnalytics.isCollabQtyGoal ? collabAnalytics.collabQtyUnit : "R$"}
                                  />
                                  <Bar
                                    yAxisId="right"
                                    dataKey="ticket_medio"
                                    name="Ticket Médio (R$)"
                                    fill="#8b5cf6"
                                    radius={[4, 4, 0, 0]}
                                    unit="R$"
                                  />
                                </>
                              )}

                              {chartTypes.weekday === 'line' && (
                                <>
                                  <Line
                                    yAxisId="left"
                                    type="monotone"
                                    dataKey={collabAnalytics.isCollabQtyGoal ? "total_qty" : "total_net"}
                                    name={collabAnalytics.isCollabQtyGoal ? (collabAnalytics.isCollabClients ? 'Clientes Totais' : 'Produtos Totais') : "Faturamento Total (R$)"}
                                    stroke="#fb923c"
                                    strokeWidth={3}
                                    dot={{ r: 4 }}
                                    unit={collabAnalytics.isCollabQtyGoal ? collabAnalytics.collabQtyUnit : "R$"}
                                  />
                                  <Line
                                    yAxisId="right"
                                    type="monotone"
                                    dataKey="ticket_medio"
                                    name="Ticket Médio (R$)"
                                    stroke="#8b5cf6"
                                    strokeWidth={3}
                                    dot={{ r: 4 }}
                                    unit="R$"
                                  />
                                </>
                              )}

                              {chartTypes.weekday === 'area' && (
                                <>
                                  <Area
                                    yAxisId="left"
                                    type="monotone"
                                    dataKey={collabAnalytics.isCollabQtyGoal ? "total_qty" : "total_net"}
                                    name={collabAnalytics.isCollabQtyGoal ? (collabAnalytics.isCollabClients ? 'Clientes Totais' : 'Produtos Totais') : "Faturamento Total (R$)"}
                                    stroke="#fb923c"
                                    strokeWidth={3}
                                    fillOpacity={1}
                                    fill="url(#collabColorWeekdayNet)"
                                    unit={collabAnalytics.isCollabQtyGoal ? collabAnalytics.collabQtyUnit : "R$"}
                                  />
                                  <Line
                                    yAxisId="right"
                                    type="monotone"
                                    dataKey="ticket_medio"
                                    name="Ticket Médio (R$)"
                                    stroke="#8b5cf6"
                                    strokeWidth={3}
                                    dot={{ r: 4 }}
                                    unit="R$"
                                  />
                                </>
                              )}
                            </ComposedChart>
                          </ResponsiveContainer>
                        </div>

                        {/* Cartões de Indicadores por Dia da Semana */}
                        <div className="d-grid gap-2 mt-3" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(130px, 1fr))' }}>
                          {collabAnalytics.weekdayData.map((w, idx) => (
                            <div key={idx} className="bi-weekday-card">
                              <span className="bi-weekday-name">{w.short}</span>
                              <span className="bi-weekday-val">{collabAnalytics.isCollabQtyGoal ? `${w.total_qty || 0} ${collabAnalytics.collabQtyUnit}` : formatBRL(w.total_net)}</span>
                              <span className="bi-weekday-sub">TM: {formatBRL(w.ticket_medio)} • {w.total_orders} ped</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* ABA 3: INDICAÇÕES DE PRODUTOS ESTRATÉGICOS (ALAVANCAGEM DE TICKET MÉDIO) */}
              {modalTab === 'recommendations' && (
                <div className="bi-modal-recommendations-view">
                  {/* Header / Banner Estratégico com Diagnóstico de Perfil */}
                  <div className="bi-modal-rec-header-banner">
                    <div className="d-flex justify-content-between align-items-start flex-wrap gap-3 mb-3">
                      <div>
                        <div className="d-flex align-items-center gap-2 mb-1">
                          <span className="bi-kpi-icon gold" style={{ width: '32px', height: '32px', fontSize: '1rem' }}>
                            <FiAward />
                          </span>
                          <h5 className="fw-bold text-dark mb-0">
                            Produtos Estratégicos para Alavancagem de Ticket Médio
                          </h5>
                        </div>
                        <p className="text-muted small mb-0">
                          Sugestões inteligentes com base no perfil comercial de <strong>{selectedCollab.name}</strong> ({selectedCollab.goal_classification}) para acelerar o fechamento da meta e elevar o valor médio por cupom.
                        </p>
                      </div>

                      <div className="d-flex align-items-center gap-2 flex-wrap">
                        {selectedCollab.gap > 0 ? (
                          <div className="badge bg-danger-subtle text-danger border border-danger-subtle px-3 py-2 fw-bold" style={{ fontSize: '0.85rem' }}>
                            🎯 Gap Restante: {isQuantitySubtype(selectedCollab.goal_subtype) ? `${Number(selectedCollab.gap || 0).toLocaleString('pt-BR')} ${selectedCollab.goal_subtype === 'clientes_positivados' ? 'cli' : 'un'}` : formatBRL(selectedCollab.gap)}
                          </div>
                        ) : (
                          <div className="badge bg-success-subtle text-success border border-success-subtle px-3 py-2 fw-bold" style={{ fontSize: '0.85rem' }}>
                            🏆 Meta Já Atingida!
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Badges com Diagnóstico da Meta Selecionada */}
                    <div className="d-flex align-items-center gap-3 flex-wrap p-2.5 rounded-3 bg-white border">
                      <div className="small">
                        <span className="text-muted">Ticket Médio Atual:</span>{' '}
                        <strong className="text-dark">{formatBRL(selectedCollab.ticket_medio || 0)}</strong>
                        {selectedCollab.ref_average_ticket > 0 && (
                          <span className="text-muted"> (Ref: {formatBRL(selectedCollab.ref_average_ticket)})</span>
                        )}
                      </div>
                      <span className="text-muted">•</span>
                      <div className="small">
                        <span className="text-muted">VIA (Valor do Item):</span>{' '}
                        <strong className="text-dark">{formatBRL(selectedCollab.via || 0)}</strong>
                      </div>
                      <span className="text-muted">•</span>
                      <div className="small">
                        <span className="text-muted">Itens/Atendimento:</span>{' '}
                        <strong className="text-dark">{Number(getCollabIA(selectedCollab)).toFixed(2)} un</strong>
                      </div>
                      <span className="text-muted">•</span>
                      <div className="small">
                        <span className="text-muted">Filial de Atuação:</span>{' '}
                        <strong className="text-dark">{selectedCollab.branch_name}</strong>
                      </div>
                    </div>
                  </div>

                  {/* Subfiltros por Categoria / Objetivo */}
                  <div className="bi-modal-chart-subfilter mb-3">
                    <button
                      type="button"
                      className={`bi-chart-category-btn ${modalRecCategory === 'all' ? 'active' : ''}`}
                      onClick={() => setModalRecCategory('all')}
                    >
                      Todas as Indicações ({collabRecs.length})
                    </button>
                    <button
                      type="button"
                      className={`bi-chart-category-btn ${modalRecCategory === 'gap' ? 'active' : ''}`}
                      onClick={() => setModalRecCategory('gap')}
                    >
                      🎯 Fechar o Gap Rápido ({collabRecs.filter(r => r.tier_type === 'gap').length})
                    </button>
                    <button
                      type="button"
                      className={`bi-chart-category-btn bi-btn-ai-tab ${modalRecCategory === 'ai' ? 'active' : ''}`}
                      onClick={() => setModalRecCategory('ai')}
                    >
                      🤖 Análise IA & Insights
                    </button>
                    <button
                      type="button"
                      className={`bi-chart-category-btn ${modalRecCategory === 'isca' ? 'active' : ''}`}
                      onClick={() => setModalRecCategory('isca')}
                    >
                      🧲 Mapeamento de Cesta & ISCA ({(selectedCollab.isca_products || []).length})
                    </button>
                    <button
                      type="button"
                      className={`bi-chart-category-btn ${modalRecCategory === 'ticket' ? 'active' : ''}`}
                      onClick={() => setModalRecCategory('ticket')}
                    >
                      🚀 Alavanca de Ticket ({collabRecs.filter(r => r.tier_type === 'ticket').length})
                    </button>
                    <button
                      type="button"
                      className={`bi-chart-category-btn ${modalRecCategory === 'cross_sell' ? 'active' : ''}`}
                      onClick={() => setModalRecCategory('cross_sell')}
                    >
                      ⚡ Giro & Cross-Sell ({collabRecs.filter(r => r.tier_type === 'cross_sell').length})
                    </button>
                  </div>

                  {/* VISUALIZAÇÃO CONDICIONAL: ISCA DEDICADO vs ANÁLISE IA vs GRID PADRÃO */}
                  {modalRecCategory === 'isca' ? (
                    renderIscaSection(true)
                  ) : modalRecCategory === 'ai' ? (
                    <div className="bi-ai-container">
                      {/* 1. Hero Card de Diagnóstico IA */}
                      <div className="bi-ai-hero-card">
                        <div className="bi-ai-hero-top">
                          <div>
                            <div className="bi-ai-profile-badge mb-2">
                              <FiCpu size={14} />
                              {selectedCollab.ai_analysis?.commercial_profile || 'Perfil Comercial Estratégico'}
                            </div>
                            <h5 className="fw-bold text-dark mb-1">
                              Diagnóstico de Alavancagem Comercial • {selectedCollab.name}
                            </h5>
                            <p className="text-muted small mb-0">
                              {selectedCollab.ai_analysis?.bottleneck_description ||
                                `Análise inteligente do comportamento de vendas, calibrando a cesta de produtos para alavancar o Ticket Médio de ${formatBRL(selectedCollab.ticket_medio)}.`}
                            </p>
                          </div>

                          <div className="bi-ai-actions-group">
                            <button
                              type="button"
                              className="bi-ai-btn-generate"
                              disabled={aiGenerating}
                              onClick={() => handleOpenAIModal(selectedCollab)}
                              title="Abrir painel ao vivo com acompanhamento do progresso, ferramentas e customização do prompt"
                            >
                              {aiGenerating ? (
                                <>
                                  <FiRefreshCw className="bi-spin" size={14} />
                                  <span>Processando IA...</span>
                                </>
                              ) : (
                                <>
                                  <FiZap size={14} />
                                  <span>Gerar Análise ao Vivo com IA</span>
                                </>
                              )}
                            </button>
                            {collabPitchScript && (
                              <button
                                type="button"
                                className="bi-ai-btn-copy"
                                onClick={() => handleCopyPitch(collabPitchScript)}
                                title="Copiar roteiro de atendimento para WhatsApp ou treinamento"
                              >
                                {copiedPitch ? (
                                  <>
                                    <FiCheck className="text-success" size={14} />
                                    <span className="text-success">Copiado!</span>
                                  </>
                                ) : (
                                  <>
                                    <FiCopy size={14} />
                                    <span>Copiar Roteiro</span>
                                  </>
                                )}
                              </button>
                            )}
                          </div>
                        </div>

                        {/* Grid de 4 Pílulas Estratégicas */}
                        <div className="bi-ai-pill-grid">
                          <div className="bi-ai-pill-card danger-subtle">
                            <span className="bi-ai-pill-label">
                              <FiAlertCircle size={13} className="text-danger" />
                              Gargalo Diagnosticado
                            </span>
                            <span className="bi-ai-pill-value">
                              {selectedCollab.ai_analysis?.bottleneck_indicator || 'Potencial de expansão do ticket por cupom'}
                            </span>
                          </div>

                          <div className="bi-ai-pill-card primary-subtle">
                            <span className="bi-ai-pill-label">
                              <FiZap size={13} className="text-primary" />
                              Alavanca Tática Recomendada
                            </span>
                            <span className="bi-ai-pill-value">
                              {selectedCollab.ai_analysis?.tactical_lever || 'Inserir itens de ticket intermediário com sugestão ativa'}
                            </span>
                          </div>

                          <div className="bi-ai-pill-card purple-subtle">
                            <span className="bi-ai-pill-label">
                              <FiTarget size={13} className="text-purple" />
                              Meta Diária de Peças-Chave
                            </span>
                            <span className="bi-ai-pill-value">
                              {selectedCollab.ai_analysis?.daily_pieces_target || '2 a 3 peças estratégicas/dia'}
                            </span>
                          </div>

                          <div className="bi-ai-pill-card success-subtle">
                            <span className="bi-ai-pill-label">
                              <FiTrendingUp size={13} className="text-success" />
                              Potencial de Ganho TM
                            </span>
                            <span className="bi-ai-pill-value">
                              {selectedCollab.ai_analysis?.potential_tm_increase || '+18% a +25% de salto'}
                            </span>
                          </div>
                        </div>

                        {/* Box do Roteiro / Pitch de Vendas */}
                        {collabPitchScript && (
                          <div className="bi-ai-pitch-box">
                            <div className="bi-ai-pitch-header">
                              <div className="bi-ai-pitch-title">
                                <FiAward size={14} /> Roteiro de Abordagem & Argumentação Recomendado
                              </div>
                              <button
                                type="button"
                                className="btn btn-sm btn-link text-purple p-0 d-flex align-items-center gap-1 text-decoration-none fw-bold"
                                style={{ fontSize: '0.78rem', cursor: 'pointer' }}
                                onClick={() => handleCopyPitch(collabPitchScript)}
                                title="Copiar roteiro de atendimento"
                              >
                                {copiedPitch ? (
                                  <>
                                    <FiCheck className="text-success" size={13} />
                                    <span className="text-success">Copiado!</span>
                                  </>
                                ) : (
                                  <>
                                    <FiCopy size={13} />
                                    <span>Copiar</span>
                                  </>
                                )}
                              </button>
                            </div>
                            <p className="bi-ai-pitch-text">
                              "{collabPitchScript}"
                            </p>
                          </div>
                        )}
                      </div>

                      {/* Parecer Detalhado da IA (Markdown) se gerado */}
                      {aiLiveReport[selectedCollab.goal_id] && (
                        <div className="bi-ai-markdown-box">
                          <div className="d-flex align-items-center justify-content-between mb-3 border-bottom pb-2">
                            <h6 className="fw-bold text-dark mb-0 d-flex align-items-center gap-2">
                              <FiCpu className="text-purple" /> Parecer Estratégico & Plano Tático (IA)
                            </h6>
                            <span className="badge bg-purple-subtle text-purple fw-bold px-2 py-1" style={{ fontSize: '0.72rem' }}>
                              Gerado em Tempo Real
                            </span>
                          </div>
                          <div
                            className="bi-ai-markdown-content"
                            dangerouslySetInnerHTML={{ __html: marked.parse(aiLiveReport[selectedCollab.goal_id]) }}
                          />
                        </div>
                      )}

                      {/* 2. SEÇÃO PRODUTOS ISCA (CROSS-SELL & ANCHOR PRODUCTS) */}
                      {renderIscaSection(false)}

                      {/* 3. SEÇÃO TENDÊNCIA E MOMENTUM DE VENDAS */}
                      <div className="bi-section-card">
                        <div className="bi-section-header">
                          <div className="bi-section-title-group">
                            <h5>
                              <FiTrendingUp className="text-primary" /> Tendência & Momentum de Vendas (Últimas 4 Semanas)
                            </h5>
                            <p>
                              Curva de aceleração de saída dos produtos com maior tração recente no catálogo. Produtos em alta facilitam a abordagem e geram menor resistência de preço.
                            </p>
                          </div>
                          <span className="badge bg-primary-subtle text-primary border border-primary-subtle px-3 py-1.5 fw-bold" style={{ fontSize: '0.78rem' }}>
                            {(selectedCollab.trending_products || []).length} Produtos em Alta
                          </span>
                        </div>

                        <div className="bi-trend-grid">
                          {(selectedCollab.trending_products || []).map((p) => (
                            <div key={`trend-${p.codprod}`} className="bi-trend-card">
                              <div>
                                <div className="bi-rec-top">
                                  <span className="bi-rec-code">Cód. {p.codprod}</span>
                                  <span className="bi-trend-growth-badge">
                                    <FiTrendingUp size={12} /> +{p.growth_rate}% aceleração
                                  </span>
                                </div>
                                <div className="bi-rec-name" title={p.descricao}>{p.descricao}</div>
                                <div className="bi-rec-details">
                                  {p.departamento} • Preço: <strong>{formatBRL(p.pvenda)}</strong>
                                </div>

                                {/* Mini Gráfico de Linha / Área de Vendas Semanais */}
                                <div className="bi-trend-chart-box">
                                  <ResponsiveContainer width="100%" height={85}>
                                    <AreaChart
                                      data={p.history || []}
                                      margin={{ top: 8, right: 10, left: 10, bottom: 0 }}
                                    >
                                      <defs>
                                        <linearGradient id={`colorSales-${p.codprod}`} x1="0" y1="0" x2="0" y2="1">
                                          <stop offset="5%" stopColor="#3b82f6" stopOpacity={0.35} />
                                          <stop offset="95%" stopColor="#3b82f6" stopOpacity={0.0} />
                                        </linearGradient>
                                      </defs>
                                      <XAxis
                                        dataKey="week"
                                        axisLine={false}
                                        tickLine={false}
                                        tick={{ fontSize: 10, fill: '#94a3b8' }}
                                      />
                                      <Tooltip
                                        formatter={(value) => [`${value} un`, 'Vendas']}
                                        labelFormatter={(label) => `Período: ${label}`}
                                        contentStyle={{ fontSize: '0.75rem', borderRadius: '8px', padding: '4px 8px' }}
                                      />
                                      <Area
                                        type="monotone"
                                        dataKey="sales"
                                        stroke="#2563eb"
                                        strokeWidth={2}
                                        fillOpacity={1}
                                        fill={`url(#colorSales-${p.codprod})`}
                                      />
                                    </AreaChart>
                                  </ResponsiveContainer>
                                </div>

                                <div className="bi-rec-action-tip mb-2">
                                  {p.action_tip}
                                </div>
                              </div>

                              <div className="d-flex justify-content-between align-items-center mt-2 pt-2 border-top">
                                <span className="text-primary fw-bold" style={{ fontSize: '0.74rem' }}>
                                  Curva Ascendente
                                </span>
                                <button
                                  type="button"
                                  className="btn btn-sm btn-outline-primary py-1 px-2 text-xs d-flex align-items-center gap-1"
                                  onClick={() => {
                                    setSimProduct(p);
                                    setSimQuantity(5);
                                  }}
                                  title="Simular impacto nas vendas"
                                >
                                  <FiZap size={12} /> Simular
                                </button>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>
                  ) : (
                    /* Grid Padrão de Cards de Produtos */
                    <div className="bi-rec-grid">
                      {filteredCollabRecs.length === 0 ? (
                        <div className="text-center py-5 text-muted col-12 w-100 bg-light rounded-3 border">
                          <FiAward size={32} className="mb-2 d-block mx-auto opacity-50" />
                          Nenhum produto nesta categoria para o perfil desta meta.
                        </div>
                      ) : (
                        filteredCollabRecs.map((p) => (
                          <div key={p.codprod} className="bi-rec-card">
                            <div>
                              <div className="bi-rec-top">
                                <span className="bi-rec-code">Cód. {p.codprod}</span>
                                <span className={`badge bg-${p.badge_color || 'primary'} text-white`} style={{ fontSize: '0.68rem', padding: '3px 8px' }}>
                                  {p.category_tag || `+${p.ticket_boost_pct}% Ticket`}
                                </span>
                              </div>
                              <div className="bi-rec-name" title={p.descricao}>{p.descricao}</div>
                              <div className="bi-rec-details">
                                {p.departamento} • {p.marca}
                              </div>
                            </div>
                            <div>
                              <div className="d-flex justify-content-between align-items-baseline mb-2">
                                <div>
                                  <span className="bi-rec-price">{formatBRL(p.pvenda)}</span>
                                  <div className="text-purple fw-bold" style={{ fontSize: '0.72rem' }}>
                                    +{p.ticket_boost_pct}% no seu Ticket Médio
                                  </div>
                                </div>
                                <button
                                  type="button"
                                  className="btn btn-sm btn-outline-primary py-1 px-2 text-xs d-flex align-items-center gap-1"
                                  onClick={() => {
                                    setSimProduct(p);
                                    setSimQuantity(p.units_to_cover_gap > 0 ? Math.min(50, p.units_to_cover_gap) : 5);
                                  }}
                                  title="Simular impacto no faturamento e ticket desta meta"
                                >
                                  <FiZap size={12} /> Simular
                                </button>
                              </div>
                              <div className="bi-rec-action-tip">
                                {p.action_tip}
                              </div>
                            </div>
                          </div>
                        ))
                      )}
                    </div>
                  )}
                </div>
              )}

              {/* ABA: MOVIMENTAÇÕES DE PEDIDOS REGISTRADOS */}
              {modalTab === 'orders' && (
                <div className="bi-modal-orders-container">
                  {/* Hero Header */}
                  <div className="bi-reports-hero-header mb-3">
                    <div className="bi-reports-title-box">
                      <div className="bi-reports-icon-badge" style={{ backgroundColor: '#ecfeff', color: '#0891b2' }}>
                        <FiShoppingBag />
                      </div>
                      <div>
                        <h5 className="bi-reports-title">
                          Movimentações dos Pedidos Registrados
                        </h5>
                        <p className="bi-reports-subtitle">
                          Relação de todos os pedidos e quantitativos apurados para <strong>{selectedCollab.name}</strong> no período de <strong>{formatDateBR(startDate)} até {formatDateBR(endDate)}</strong>.
                        </p>
                      </div>
                    </div>

                    <div className="bi-reports-header-actions">
                      <span className="badge bg-light text-dark border px-2 py-1.5 d-flex align-items-center gap-1.5" style={{ fontSize: '0.78rem' }}>
                        <FiClock size={13} className="text-primary" />
                        <span>Período: <strong>{formatDateBR(startDate)} a {formatDateBR(endDate)}</strong></span>
                      </span>
                      <button
                        type="button"
                        className="btn btn-sm btn-outline-secondary d-flex align-items-center gap-1 py-1 px-2.5"
                        onClick={() => loadGoalOrders(selectedCollab.goal_id, goalOrdersPage)}
                        title="Atualizar movimentações de pedidos"
                        disabled={goalOrdersLoading}
                      >
                        <FiRefreshCw size={13} className={goalOrdersLoading ? 'spinner-border-sm' : ''} />
                        <span>Atualizar</span>
                      </button>
                    </div>
                  </div>

                  {/* 4 Cards de Quantitativos do Período */}
                  {goalOrdersData?.summary && (
                    <div className="bi-reports-summary-grid mb-3">
                      {/* Card 1: Total de Pedidos */}
                      <div className="bi-rep-stat-card primary">
                        <div className="bi-rep-stat-icon cyan">
                          <FiShoppingBag />
                        </div>
                        <div className="bi-rep-stat-content">
                          <span className="bi-rep-stat-label">
                            {goalOrdersSaleType === 'devolucao' ? 'Total de Devoluções' : 'Total de Pedidos / Cupons'}
                          </span>
                          <span className="bi-rep-stat-value text-cyan">
                            {goalOrdersData.summary.total_orders.toLocaleString('pt-BR')} {goalOrdersSaleType === 'devolucao' ? (goalOrdersData.summary.total_orders === 1 ? 'devolução' : 'devoluções') : 'cupons'}
                          </span>
                          <span className="bi-rep-stat-tag neutral">
                            {goalOrdersSaleType === 'devolucao' ? (
                              `Total Devolvido: ${formatBRL(goalOrdersData.summary.total_returns || 0)}`
                            ) : (
                              `${goalOrdersData.summary.count_sales} vendas • ${goalOrdersData.summary.count_returns} devoluções`
                            )}
                          </span>
                        </div>
                      </div>

                      {/* Card 2: Quantidade de Peças / Itens */}
                      <div className="bi-rep-stat-card amber">
                        <div className="bi-rep-stat-icon amber">
                          <FiPackage />
                        </div>
                        <div className="bi-rep-stat-content">
                          <span className="bi-rep-stat-label">
                            {goalOrdersSaleType === 'devolucao' ? 'Peças Devolvidas' : 'Quantitativo de Peças'}
                          </span>
                          <span className="bi-rep-stat-value text-amber">
                            {Number(goalOrdersData.summary.total_items).toLocaleString('pt-BR')} un
                          </span>
                          <span className="bi-rep-stat-tag amber">
                            Média de {goalOrdersData.summary.pecas_por_atendimento} peças/atendimento
                          </span>
                        </div>
                      </div>

                      {/* Card 3: Faturamento Líquido */}
                      <div className="bi-rep-stat-card blue">
                        <div className="bi-rep-stat-icon blue">
                          <FiDollarSign />
                        </div>
                        <div className="bi-rep-stat-content">
                          <span className="bi-rep-stat-label">
                            {goalOrdersSaleType === 'devolucao' ? 'Total Devolvido' : 'Faturamento dos Pedidos'}
                          </span>
                          <span className={`bi-rep-stat-value ${goalOrdersSaleType === 'devolucao' ? 'text-danger' : 'text-success'}`}>
                            {goalOrdersSaleType === 'devolucao'
                              ? `-${formatBRL(goalOrdersData.summary.total_returns || 0)}`
                              : formatBRL(goalOrdersData.summary.total_net)
                            }
                          </span>
                          <span className={`bi-rep-stat-tag ${goalOrdersSaleType === 'devolucao' ? 'danger' : 'success'}`}>
                            {goalOrdersSaleType === 'devolucao'
                              ? `Líquido residual: ${formatBRL(goalOrdersData.summary.total_net)}`
                              : `Bruto: ${formatBRL(goalOrdersData.summary.total_gross)}`
                            }
                          </span>
                        </div>
                      </div>

                      {/* Card 4: Ticket Médio & VIA */}
                      <div className="bi-rep-stat-card purple">
                        <div className="bi-rep-stat-icon purple">
                          <FiTrendingUp />
                        </div>
                        <div className="bi-rep-stat-content">
                          <span className="bi-rep-stat-label">
                            {goalOrdersSaleType === 'devolucao' ? 'Ticket Médio Devolução' : 'Ticket Médio (TM)'}
                          </span>
                          <span className="bi-rep-stat-value text-purple">
                            {formatBRL(goalOrdersData.summary.ticket_medio)}
                          </span>
                          <span className="bi-rep-stat-tag purple">
                            VIA: {formatBRL(goalOrdersData.summary.via)} / peça
                          </span>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Toolbar de Filtro e Busca dos Pedidos */}
                  <div className="bi-orders-toolbar">
                    <div className="bi-orders-search-box">
                      <FiSearch size={15} className="bi-orders-search-icon" />
                      <input
                        type="text"
                        className="bi-orders-search-input"
                        placeholder="Buscar por Nº do Pedido ou Nome do Cliente..."
                        value={goalOrdersSearch}
                        onChange={(e) => setGoalOrdersSearch(e.target.value)}
                      />
                      {goalOrdersSearch && (
                        <button
                          type="button"
                          className="bi-orders-search-clear"
                          onClick={() => setGoalOrdersSearch('')}
                          title="Limpar busca"
                        >
                          <FiX size={14} />
                        </button>
                      )}
                    </div>

                    <div className="d-flex align-items-center gap-2">
                      <div className="bi-orders-filter-pills" role="group">
                        <button
                          type="button"
                          className={`bi-orders-pill-btn ${goalOrdersSaleType === 'all' ? 'active-all' : ''}`}
                          onClick={() => {
                            setGoalOrdersSaleType('all');
                            setGoalOrdersPage(1);
                          }}
                        >
                          <span>Todos</span>
                          <span className="bi-orders-pill-badge">
                            {goalOrdersData?.summary?.period_total_orders ?? goalOrdersData?.summary?.total_orders ?? 0}
                          </span>
                        </button>
                        <button
                          type="button"
                          className={`bi-orders-pill-btn ${goalOrdersSaleType === 'venda' ? 'active-venda' : ''}`}
                          onClick={() => {
                            setGoalOrdersSaleType('venda');
                            setGoalOrdersPage(1);
                          }}
                        >
                          <span
                            className="bi-orders-dot"
                            style={{ backgroundColor: goalOrdersSaleType === 'venda' ? '#16a34a' : '#94a3b8' }}
                          />
                          <span>Apenas Vendas</span>
                          {goalOrdersData?.summary?.count_sales !== undefined && (
                            <span className="bi-orders-pill-badge">
                              {goalOrdersData.summary.count_sales}
                            </span>
                          )}
                        </button>
                        <button
                          type="button"
                          className={`bi-orders-pill-btn ${goalOrdersSaleType === 'devolucao' ? 'active-devolucao' : ''}`}
                          onClick={() => {
                            setGoalOrdersSaleType('devolucao');
                            setGoalOrdersPage(1);
                          }}
                        >
                          <span
                            className="bi-orders-dot"
                            style={{ backgroundColor: goalOrdersSaleType === 'devolucao' ? '#dc2626' : '#94a3b8' }}
                          />
                          <span>Devoluções</span>
                          {goalOrdersData?.summary?.count_returns !== undefined && (
                            <span className="bi-orders-pill-badge">
                              {goalOrdersData.summary.count_returns}
                            </span>
                          )}
                        </button>
                      </div>
                    </div>
                  </div>

                  {/* Erro ao carregar pedidos */}
                  {goalOrdersError && (
                    <div className="alert alert-danger d-flex align-items-center gap-2 mb-3">
                      <FiAlertCircle size={18} /> {goalOrdersError}
                    </div>
                  )}

                  {/* Tabela de Pedidos Registrados */}
                  <div className="table-responsive bg-white rounded-3 border shadow-sm">
                    <table className="bi-table mb-0">
                      <thead>
                        <tr>
                          <th style={{ width: '110px' }}>Data</th>
                          <th style={{ width: '120px' }}>Nº Pedido</th>
                          <th>Cliente</th>
                          <th className="text-center" style={{ width: '110px' }}>Tipo</th>
                          <th className="text-center" style={{ width: '100px' }}>Peças (Qtd)</th>
                          <th className="text-end" style={{ width: '120px' }}>Valor Bruto</th>
                          <th className="text-end" style={{ width: '130px' }}>Valor Líquido</th>
                          <th className="text-end" style={{ width: '110px' }}>Preço Médio</th>
                          <th>Filtro(s) da Meta</th>
                        </tr>
                      </thead>
                      <tbody>
                        {goalOrdersLoading ? (
                          <Loading.Table
                            colSpan={9}
                            size="sm"
                            text="Carregando pedidos registrados..."
                            subtext="Buscando cupons fiscais e movimentações do período..."
                          />
                        ) : !goalOrdersData?.orders || goalOrdersData.orders.length === 0 ? (
                          <tr>
                            <td colSpan={9} className="text-center py-5 text-muted">
                              <FiShoppingBag size={32} className="mb-2 d-block mx-auto opacity-50" />
                              {goalOrdersSaleType === 'devolucao'
                                ? 'Nenhuma devolução registrada para este colaborador no período selecionado.'
                                : goalOrdersSaleType === 'venda'
                                ? 'Nenhuma venda registrada para os filtros selecionados.'
                                : 'Nenhuma movimentação de pedido registrada para os filtros selecionados.'}
                            </td>
                          </tr>
                        ) : (
                          goalOrdersData.orders.map((ord, idx) => (
                            <tr key={`${ord.order_number}_${ord.sale_date}_${idx}`}>
                              <td>
                                <span className="badge bg-light text-dark border">
                                  {formatDateBR(ord.sale_date)}
                                </span>
                              </td>
                              <td>
                                <strong className="text-primary">#{ord.order_number}</strong>
                              </td>
                              <td>
                                <div className="fw-semibold text-dark">{ord.customer_name}</div>
                                {ord.codcli && (
                                  <div className="text-muted small" style={{ fontSize: '0.7rem' }}>Cód: {ord.codcli}</div>
                                )}
                              </td>
                              <td className="text-center">
                                {ord.sale_type === 'devolucao' ? (
                                  <span className="badge bg-danger-subtle text-danger border border-danger-subtle" style={{ fontSize: '0.7rem', padding: '3px 8px' }}>
                                    Devolução
                                  </span>
                                ) : ord.sale_type === 'devolucao_parcial' ? (
                                  <span className="badge bg-warning-subtle text-warning-emphasis border border-warning-subtle" style={{ fontSize: '0.7rem', padding: '3px 8px' }}>
                                    Devol. Parcial
                                  </span>
                                ) : (
                                  <span className="badge bg-success-subtle text-success border border-success-subtle" style={{ fontSize: '0.7rem', padding: '3px 8px' }}>
                                    Venda
                                  </span>
                                )}
                              </td>
                              <td className="text-center">
                                <span className="badge bg-amber-subtle text-amber border border-amber-subtle fw-bold" style={{ fontSize: '0.76rem', padding: '3px 8px' }}>
                                  {Number(ord.items_count).toLocaleString('pt-BR')} un
                                </span>
                              </td>
                              <td className="text-end text-muted">
                                {formatBRL(ord.gross_amount)}
                              </td>
                              <td className="text-end fw-bold text-dark">
                                {formatBRL(ord.net_amount)}
                                {ord.discount_amount > 0 && (
                                  <div className="text-danger" style={{ fontSize: '0.68rem', fontWeight: 500 }}>
                                    Devolvido: -{formatBRL(ord.discount_amount)}
                                  </div>
                                )}
                              </td>
                              <td className="text-end text-purple" style={{ fontSize: '0.8rem' }}>
                                {formatBRL(ord.ticket_item)}/un
                              </td>
                              <td>
                                <span className="badge bg-light text-secondary border fw-normal" style={{ fontSize: '0.7rem' }}>
                                  {ord.filter_names}
                                </span>
                              </td>
                            </tr>
                          ))
                        )}
                      </tbody>
                    </table>
                  </div>

                  {/* Paginação */}
                  {goalOrdersData?.pagination && goalOrdersData.pagination.total_pages > 1 && (
                    <div className="d-flex justify-content-between align-items-center mt-3 flex-wrap gap-2">
                      <span className="text-muted small">
                        Exibindo página <strong>{goalOrdersData.pagination.page}</strong> de <strong>{goalOrdersData.pagination.total_pages}</strong> ({goalOrdersData.pagination.total_records.toLocaleString('pt-BR')} pedidos no total)
                      </span>
                      <div className="d-flex align-items-center gap-1">
                        <button
                          type="button"
                          className="btn btn-sm btn-outline-secondary"
                          disabled={goalOrdersData.pagination.page <= 1 || goalOrdersLoading}
                          onClick={() => loadGoalOrders(selectedCollab.goal_id, goalOrdersData.pagination.page - 1)}
                        >
                          Anterior
                        </button>
                        <span className="px-2 small fw-bold">
                          {goalOrdersData.pagination.page} / {goalOrdersData.pagination.total_pages}
                        </span>
                        <button
                          type="button"
                          className="btn btn-sm btn-outline-secondary"
                          disabled={goalOrdersData.pagination.page >= goalOrdersData.pagination.total_pages || goalOrdersLoading}
                          onClick={() => loadGoalOrders(selectedCollab.goal_id, goalOrdersData.pagination.page + 1)}
                        >
                          Próxima
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* ABA 4: FECHAMENTOS VINCULADOS */}
              {modalTab === 'reports' && (() => {
                const reportsList = selectedCollab.reports || [];
                const totalReportsNet = reportsList.reduce((acc, r) => acc + Number(r.net || 0), 0);
                const totalReportsRawNet = reportsList.reduce((acc, r) => acc + Number(r.raw_net || r.net || 0), 0);
                const totalReportsOrders = reportsList.reduce((acc, r) => acc + Number(r.orders_count || 0), 0);
                const totalReportsItems = reportsList.reduce((acc, r) => acc + Number(r.items_count || 0), 0);
                const allFinalized = reportsList.length > 0 && reportsList.every(r => (r.status || '').toLowerCase().includes('finaliz'));
                const goalTarget = Number(selectedCollab.target || 0);
                const pctOfTarget = goalTarget > 0 ? ((totalReportsNet / goalTarget) * 100).toFixed(1) : 0;

                return (
                  <div className="bi-modal-reports-container">
                    {/* Header com Descrição e Badge de Status Global */}
                    <div className="bi-reports-hero-header">
                      <div className="bi-reports-title-box">
                        <div className="bi-reports-icon-badge">
                          <FiLayers />
                        </div>
                        <div>
                          <h5 className="bi-reports-title">
                            Fechamentos & Relatórios de Venda Vinculados
                          </h5>
                          <p className="bi-reports-subtitle">
                            Demonstrativo de apuração comercial e adicionais que compõem o faturamento realizado de <strong>{selectedCollab.name}</strong>.
                          </p>
                        </div>
                      </div>

                      <div className="bi-reports-header-actions">
                        {allFinalized ? (
                          <span className="bi-status-pill-finalized">
                            <span className="bi-pulse-dot" style={{ backgroundColor: '#10b981' }} />
                            <FiCheckCircle size={14} /> Fechamentos 100% Auditados
                          </span>
                        ) : reportsList.length > 0 ? (
                          <span className="bi-status-pill-pending">
                            <FiClock size={14} /> Apuração em Processamento
                          </span>
                        ) : null}
                        <span className="bi-counter-badge">
                          {reportsList.length} {reportsList.length === 1 ? 'Fechamento' : 'Fechamentos'}
                        </span>
                      </div>
                    </div>

                    {/* 4 Cards de Métricas Consolidadas do Fechamento */}
                    {reportsList.length > 0 && (
                      <div className="bi-reports-summary-grid">
                        {/* Card 1: Realizado Meta */}
                        <div className="bi-rep-stat-card primary">
                          <div className="bi-rep-stat-icon primary">
                            <FiTarget />
                          </div>
                          <div className="bi-rep-stat-content">
                            <span className="bi-rep-stat-label">Realizado na Meta</span>
                            <span className="bi-rep-stat-value text-primary">{formatBRL(totalReportsNet)}</span>
                            <span className="bi-rep-stat-tag success">
                              <FiTrendingUp size={11} /> {pctOfTarget}% da meta atingida
                            </span>
                          </div>
                        </div>

                        {/* Card 2: Líquido Filtros */}
                        <div className="bi-rep-stat-card blue">
                          <div className="bi-rep-stat-icon blue">
                            <FiDollarSign />
                          </div>
                          <div className="bi-rep-stat-content">
                            <span className="bi-rep-stat-label">Líquido dos Filtros</span>
                            <span className="bi-rep-stat-value text-dark">{formatBRL(totalReportsRawNet)}</span>
                            <span className="bi-rep-stat-tag neutral">
                              Faturamento bruto apurado
                            </span>
                          </div>
                        </div>

                        {/* Card 3: Atendimentos & Ticket Médio */}
                        <div className="bi-rep-stat-card purple">
                          <div className="bi-rep-stat-icon purple">
                            <FiShoppingBag />
                          </div>
                          <div className="bi-rep-stat-content">
                            <span className="bi-rep-stat-label">Volume de Vendas</span>
                            <span className="bi-rep-stat-value text-purple">
                              {totalReportsOrders > 0 ? `${totalReportsOrders.toLocaleString('pt-BR')} cupons` : `${selectedCollab.orders_count || 0} cupons`}
                            </span>
                            <span className="bi-rep-stat-tag purple">
                              TM: {formatBRL(totalReportsOrders > 0 ? totalReportsNet / totalReportsOrders : selectedCollab.ticket_medio)}
                            </span>
                          </div>
                        </div>

                        {/* Card 4: Itens & Peças */}
                        <div className="bi-rep-stat-card amber">
                          <div className="bi-rep-stat-icon amber">
                            <FiPackage />
                          </div>
                          <div className="bi-rep-stat-content">
                            <span className="bi-rep-stat-label">Itens & Peças Vendidas</span>
                            <span className="bi-rep-stat-value text-amber">
                              {totalReportsItems > 0 ? `${totalReportsItems.toLocaleString('pt-BR')} un` : `${(selectedCollab.items_count || 0).toLocaleString('pt-BR')} un`}
                            </span>
                            <span className="bi-rep-stat-tag amber">
                              PA: {getCollabIA(selectedCollab)} un/cupom
                            </span>
                          </div>
                        </div>
                      </div>
                    )}

                    {/* Lista Individual de Fechamentos Vinculados */}
                    {reportsList.length > 0 && (
                      <div className="d-flex flex-column gap-2 mt-3" style={{ maxHeight: '420px', overflowY: 'auto' }}>
                        {reportsList.map((r, idx) => (
                          <div
                            key={r.id || idx}
                            className="d-flex justify-content-between align-items-center p-3 rounded-3 bg-white border shadow-sm flex-wrap gap-2"
                            style={{ transition: 'all 0.15s ease' }}
                          >
                            <div className="d-flex align-items-center gap-3">
                              <div
                                className="d-flex align-items-center justify-content-center rounded-circle"
                                style={{ width: '40px', height: '40px', backgroundColor: '#eff6ff', color: '#2563eb', flexShrink: 0 }}
                              >
                                <FiShoppingBag size={18} />
                              </div>
                              <div>
                                <div className="d-flex align-items-center gap-2 flex-wrap">
                                  <span className="fw-bold text-dark">
                                    {r.report_number ? `Fechamento #${r.report_number}` : (r.name || `Fechamento #${r.id}`)}
                                  </span>
                                  <span className={`badge ${r.status === 'finalized' ? 'bg-success' : 'bg-warning text-dark'}`} style={{ fontSize: '0.65rem', padding: '1px 6px' }}>
                                    {r.status === 'finalized' ? 'Finalizado' : 'Em Aberto'}
                                  </span>
                                </div>
                                <div className="text-muted small mt-0.5">
                                  {r.period_start && r.period_end ? `Período: ${formatDateBR(r.period_start)} até ${formatDateBR(r.period_end)}` : ''}
                                  {r.finalized_at ? ` • Finalizado em: ${formatDateBR(r.finalized_at)}` : ''}
                                </div>
                              </div>
                            </div>

                            <div className="d-flex align-items-center gap-4 text-end">
                              <div>
                                <div className="text-muted small fw-semibold" style={{ fontSize: '0.68rem' }}>LÍQUIDO</div>
                                <div className="fw-bold text-dark">{formatBRL(Number(r.net_value ?? r.net ?? r.raw_net ?? 0))}</div>
                              </div>
                              <div>
                                <div className="text-muted small fw-semibold" style={{ fontSize: '0.68rem' }}>ADICIONAL</div>
                                <div className="fw-bold text-primary">{formatBRL(Number(r.adicional_amount ?? r.commission_amount ?? r.bonus_amount ?? 0))}</div>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}

                    {/* Empty state caso não haja fechamentos vinculados */}
                    {reportsList.length === 0 && (
                      <div className="bi-reports-empty-state">
                        <div className="bi-reports-empty-icon">
                          <FiLayers size={36} />
                        </div>
                        <h6>Nenhum fechamento individual vinculado</h6>
                        <p>
                          Esta meta não possui relatórios de fechamento/adicional vinculados diretamente no período de apuração ativo.
                        </p>
                      </div>
                    )}
                  </div>
                );
              })()}
            </div>

            {/* Footer do Modal com Atalhos e Navegação */}
            <div className="bi-modal-footer bi-collab-modal-footer d-none d-md-flex justify-content-between align-items-center flex-wrap gap-2">
              <div className="text-muted small d-flex align-items-center gap-1">
                <FiInfo size={14} /> Dica: Utilize <strong>←</strong> / <strong>→</strong> para metas, <strong>Alt + ←</strong> / <strong>Alt + →</strong> para filiais, ou <strong>Esc</strong> para fechar.
              </div>
              <div className="d-flex align-items-center gap-2">
                <button
                  type="button"
                  className="bi-carousel-btn"
                  onClick={handlePrevCollab}
                >
                  <FiChevronLeft size={16} /> Anterior
                </button>
                <button
                  type="button"
                  className="bi-carousel-btn"
                  onClick={handleNextCollab}
                >
                  Próxima Meta <FiChevronRight size={16} />
                </button>
                <button
                  type="button"
                  className="btn-modern btn-modern-light d-inline-flex align-items-center gap-1.5"
                  onClick={() => setIsIndividualFullscreen(prev => !prev)}
                  title={isIndividualFullscreen ? "Restaurar tamanho normal" : "Expandir para tela cheia"}
                >
                  {isIndividualFullscreen ? <><FiMinimize2 size={14} /> Restaurar</> : <><FiMaximize2 size={14} /> Tela Cheia</>}
                </button>
                <button
                  type="button"
                  className="btn-modern btn-modern-light"
                  onClick={handleCloseCollabModal}
                >
                  Fechar
                </button>
              </div>
            </div>
          </div>
        </div>
        );
      })()}

      {/* ====================================================================
          MODAL DE SIMULAÇÃO DE IMPACTO NO TICKET MÉDIO (À FRENTE DE TODOS OS MODAIS)
          ==================================================================== */}
      {simProduct && (
        <div className="bi-modal-overlay bi-modal-simulator-overlay" onClick={() => setSimProduct(null)}>
          <div className="bi-modal-container sm" onClick={(e) => e.stopPropagation()}>
            <div className="bi-modal-header">
              <div className="d-flex align-items-center gap-2">
                <div className="bi-kpi-icon purple" style={{ width: '36px', height: '36px', fontSize: '1rem' }}>
                  <FiShoppingBag />
                </div>
                <div>
                  <h6 className="fw-bold text-dark mb-0">Simulador de Ticket Médio</h6>
                  <span className="text-muted small">Impacto de vendas adicionais no fechamento</span>
                </div>
              </div>
              <button
                type="button"
                className="bi-modal-close-btn"
                onClick={() => setSimProduct(null)}
                title="Fechar Simulador"
              >
                <FiX size={18} />
              </button>
            </div>

            <div className="bi-modal-body">
              <div className="p-3 bg-light rounded-3 mb-3 border">
                <div className="d-flex justify-content-between align-items-start mb-1">
                  <span className="badge bg-secondary text-white">Cód. {simProduct.codprod}</span>
                  <span className="text-purple fw-bold small">+{simProduct.ticket_boost_pct}% Ticket</span>
                </div>
                <div className="fw-bold text-dark fs-6 mb-1">{simProduct.descricao}</div>
                <div className="text-muted small mb-2">{simProduct.departamento} • {simProduct.marca}</div>
                <div className="h4 fw-bold text-primary mb-0">{formatBRL(simProduct.pvenda)}</div>
              </div>

              <div className="mb-4">
                <div className="d-flex justify-content-between align-items-center mb-2">
                  <label className="fw-bold text-secondary small mb-0">Quantidade Adicional Simulada:</label>
                  <span className="bi-sim-qty-badge">{simQuantity} un</span>
                </div>
                <input
                  type="range"
                  className="form-range w-100"
                  min={1}
                  max={100}
                  value={simQuantity}
                  onChange={(e) => setSimQuantity(Number(e.target.value))}
                />
                <div className="d-flex justify-content-between text-muted small mt-1">
                  <span>1 unidade</span>
                  <span>50 un</span>
                  <span>100 unidades</span>
                </div>
              </div>

              <div className="border rounded-3 p-3 bg-white mb-2 shadow-sm">
                <div className="d-flex justify-content-between align-items-center py-2 border-bottom">
                  <span className="text-muted">Faturamento Extra Gerado:</span>
                  <strong className="text-success fs-5">{formatBRL(simQuantity * simProduct.pvenda)}</strong>
                </div>
                <div className="d-flex justify-content-between align-items-center pt-2">
                  <span className="text-muted">
                    {selectedCollab && selectedCollab.gap > 0
                      ? `Cobertura do Gap (${selectedCollab.name}):`
                      : 'Cobertura do Gap da Equipe:'}
                  </span>
                  <strong className="text-primary fs-6">
                    {(() => {
                      const activeGap = selectedCollab && selectedCollab.gap > 0 ? selectedCollab.gap : (summary.total_gap || 0);
                      if (activeGap <= 0) return 'Meta já batida';
                      const coveredPct = Math.min(100, Math.round(((simQuantity * simProduct.pvenda) / activeGap) * 100));
                      return `${coveredPct}% coberto`;
                    })()}
                  </strong>
                </div>
              </div>
            </div>

            <div className="bi-modal-footer">
              <button
                type="button"
                className="btn-modern btn-modern-light"
                onClick={() => setSimProduct(null)}
              >
                Fechar
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ====================================================================
          MODAL DE ANÁLISE CONCATENADA MULTI-PERÍODO (COMPARAÇÃO E CONSOLIDAÇÃO)
          ==================================================================== */}
      {isConcatenatedModalOpen && concatenatedStats && (
        <div className={`bi-modal-overlay ${isConcatenatedFullscreen ? 'bi-modal-fullscreen-overlay' : ''}`} onClick={() => { setIsConcatenatedModalOpen(false); setIsConcatenatedFullscreen(false); }}>
          <div className={`bi-modal-container ${isConcatenatedFullscreen ? 'bi-modal-fullscreen' : 'bi-modal-90'}`} onClick={(e) => e.stopPropagation()}>
            {/* Cabeçalho Padronizado */}
            <div className="bi-modal-header">
              <div className="d-flex align-items-center gap-3 flex-wrap">
                <div
                  className="bi-collab-avatar"
                  style={{
                    width: '46px',
                    height: '46px',
                    fontSize: '1.1rem',
                    background: 'linear-gradient(135deg, #10b981, #0d9488)',
                    boxShadow: '0 4px 12px rgba(16, 185, 129, 0.25)'
                  }}
                >
                  <FiLayers size={22} />
                </div>
                <div>
                  <div className="d-flex align-items-center gap-2 flex-wrap">
                    <h4 className="fw-bold text-dark mb-0">Análise Concatenada Multi-Metas</h4>
                    <span className="badge bg-success text-white" style={{ fontSize: '0.68rem', padding: '2px 8px' }}>
                      {(concatenatedStats.selectedGoals || []).length} Metas Selecionadas
                    </span>
                    <span className={`bi-probability-badge ${concatenatedStats.pct >= 100 ? 'atingida' : concatenatedStats.pct >= 85 ? 'alta' : concatenatedStats.pct >= 70 ? 'media' : 'baixa'}`}>
                      {concatenatedStats.pct >= 100 && '🏆 Metas Batidas'}
                      {concatenatedStats.pct >= 85 && concatenatedStats.pct < 100 && '🟢 Alta Probabilidade'}
                      {concatenatedStats.pct >= 70 && concatenatedStats.pct < 85 && '🟡 Média Probabilidade'}
                      {concatenatedStats.pct < 70 && '🔴 Em Risco'}
                    </span>
                    <span className="badge bg-light text-dark border" style={{ fontSize: '0.68rem', padding: '2px 8px' }}>
                      📅 {(concatenatedStats.periods || []).join(', ')}
                    </span>
                  </div>
                  <div className="text-muted small mt-1 d-flex align-items-center gap-2 flex-wrap">
                    <span>👥 <strong>{(concatenatedStats.collabs || []).join(', ')}</strong></span>
                    <span>•</span>
                    <span>Meta: <strong className="text-primary">{concatenatedStats.areAllQtyGoals ? `${Number(concatenatedStats.totalTarget).toLocaleString('pt-BR')} ${concatenatedStats.qtyUnitLabel}` : formatBRL(concatenatedStats.totalTarget)}</strong></span>
                    <span>•</span>
                    <span>Realizado: <strong className="text-success">{concatenatedStats.areAllQtyGoals ? `${Number(concatenatedStats.totalRealized).toLocaleString('pt-BR')} ${concatenatedStats.qtyUnitLabel}` : formatBRL(concatenatedStats.totalRealized)}</strong> ({concatenatedStats.pct}%)</span>
                    <span>•</span>
                    <span>Saldo: <strong className={concatenatedStats.totalGap > 0 ? 'text-danger' : 'text-success'}>{concatenatedStats.totalGap > 0 ? (concatenatedStats.areAllQtyGoals ? `${Number(concatenatedStats.totalGap).toLocaleString('pt-BR')} ${concatenatedStats.qtyUnitLabel}` : formatBRL(concatenatedStats.totalGap)) : (concatenatedStats.areAllQtyGoals ? `0 ${concatenatedStats.qtyUnitLabel}` : 'R$ 0,00')}</strong></span>
                  </div>
                </div>
              </div>

              <div className="d-flex align-items-center gap-2">
                <button
                  type="button"
                  className={`bi-modal-fullscreen-btn ms-2 ${isConcatenatedFullscreen ? 'active' : ''}`}
                  onClick={() => setIsConcatenatedFullscreen(prev => !prev)}
                  title={isConcatenatedFullscreen ? "Restaurar tamanho normal" : "Expandir para tela cheia"}
                >
                  {isConcatenatedFullscreen ? <FiMinimize2 size={17} /> : <FiMaximize2 size={17} />}
                </button>
                <button
                  type="button"
                  className="bi-modal-close-btn"
                  onClick={() => { setIsConcatenatedModalOpen(false); setIsConcatenatedFullscreen(false); }}
                  title="Fechar (Esc)"
                >
                  <FiX size={18} />
                </button>
              </div>
            </div>

            {/* Abas de Navegação do Modal Concatenado (idênticas ao modal individual) */}
            <div className="bi-modal-nav-tabs">
              <button
                type="button"
                className={`bi-modal-nav-tab ${concatenatedTab === 'overview' ? 'active' : ''}`}
                onClick={() => setConcatenatedTab('overview')}
              >
                <FiTarget size={15} /> Visão Geral & Cards Isolados
                <span className="bi-modal-tab-badge">{(concatenatedStats.selectedGoals || []).length}</span>
              </button>
              <button
                type="button"
                className={`bi-modal-nav-tab ${concatenatedTab === 'charts' ? 'active' : ''}`}
                onClick={() => setConcatenatedTab('charts')}
              >
                <FiTrendingUp size={15} /> Gráficos Analíticos & Comparativos
                <span className="bi-modal-tab-badge">11</span>
              </button>
              <button
                type="button"
                className={`bi-modal-nav-tab ${concatenatedTab === 'comparison' ? 'active' : ''}`}
                onClick={() => setConcatenatedTab('comparison')}
              >
                <FiLayers size={15} /> Matriz Comparativa Lado a Lado
                <span className="bi-modal-tab-badge">{(concatenatedStats.sideBySideGoals || []).length}</span>
              </button>
              <button
                type="button"
                className={`bi-modal-nav-tab ${concatenatedTab === 'reports' ? 'active' : ''}`}
                onClick={() => setConcatenatedTab('reports')}
              >
                <FiShoppingBag size={15} /> Fechamentos Vinculados
                {concatenatedStats.allReports && concatenatedStats.allReports.length > 0 && (
                  <span className="bi-modal-tab-badge">{concatenatedStats.allReports.length}</span>
                )}
              </button>
              <button
                type="button"
                className={`bi-modal-nav-tab ${concatenatedTab === 'ai_diagnosis' ? 'active' : ''}`}
                onClick={() => setConcatenatedTab('ai_diagnosis')}
              >
                <FiCpu size={15} /> Diagnóstico Executivo & IA
              </button>
            </div>

            {/* Corpo do Modal */}
            <div className="bi-modal-body">
              {/* ABA 1: VISÃO CONSOLIDADA & KPIS */}
              {concatenatedTab === 'overview' && (
                <div className="d-flex flex-column gap-4">
                  {/* Grid de 8 Cards Consolidados */}
                  <div className="row g-3">
                    <div className="col-12 col-sm-6 col-xl-3">
                      <div className="bi-kpi-card">
                        <div className="bi-kpi-header">
                          <span className="bi-kpi-title">{concatenatedStats.areAllQtyGoals ? (concatenatedStats.areAllClients ? 'Meta Total de Clientes' : 'Meta Total de Produtos') : 'Meta Total Consolidada'}</span>
                          <div className="bi-kpi-icon blue">
                            <FiTarget />
                          </div>
                        </div>
                        <div className="bi-kpi-value text-primary">{concatenatedStats.areAllQtyGoals ? `${Number(concatenatedStats.totalTarget).toLocaleString('pt-BR')} ${concatenatedStats.qtyUnitLabel}` : formatBRL(concatenatedStats.totalTarget)}</div>
                        <span className="bi-kpi-sub">{concatenatedStats.selectedGoals.length} metas somadas</span>
                      </div>
                    </div>

                    <div className="col-12 col-sm-6 col-xl-3">
                      <div className="bi-kpi-card">
                        <div className="bi-kpi-header">
                          <span className="bi-kpi-title">{concatenatedStats.areAllQtyGoals ? (concatenatedStats.areAllClients ? 'Clientes Positivados' : 'Produtos Vendidos') : 'Faturamento Realizado'}</span>
                          <div className="bi-kpi-icon green">
                            <FiDollarSign />
                          </div>
                        </div>
                        <div className="bi-kpi-value text-success">{concatenatedStats.areAllQtyGoals ? `${Number(concatenatedStats.totalRealized).toLocaleString('pt-BR')} ${concatenatedStats.qtyUnitLabel}` : formatBRL(concatenatedStats.totalRealized)}</div>
                        <span className="bi-kpi-sub">
                          {concatenatedStats.areAllQtyGoals ? 'Consolidado acumulado' : (concatenatedStats.totalRawNet > 0 && Math.abs(concatenatedStats.totalRawNet - concatenatedStats.totalRealized) > 0.01
                            ? `Líq: ${formatBRL(concatenatedStats.totalRawNet)}`
                            : 'Consolidado acumulado')}
                        </span>
                      </div>
                    </div>

                    <div className="col-12 col-sm-6 col-xl-3">
                      <div className="bi-kpi-card">
                        <div className="bi-kpi-header">
                          <span className="bi-kpi-title">% Atingimento Geral</span>
                          <div className="bi-kpi-icon purple">
                            <FiTrendingUp />
                          </div>
                        </div>
                        <div className="bi-kpi-value text-purple">{concatenatedStats.pct}%</div>
                        <div className="bi-progress-container mt-2">
                          <div
                            className={`bi-progress-fill ${concatenatedStats.pct >= 100 ? 'blue-emerald' : concatenatedStats.pct >= 75 ? 'purple-indigo' : 'amber-rose'}`}
                            style={{ width: `${Math.min(100, concatenatedStats.pct)}%` }}
                          />
                        </div>
                      </div>
                    </div>

                    <div className="col-12 col-sm-6 col-xl-3">
                      <div className="bi-kpi-card">
                        <div className="bi-kpi-header">
                          <span className="bi-kpi-title">Saldo Restante / Gap</span>
                          <div className="bi-kpi-icon orange">
                            <FiAlertCircle />
                          </div>
                        </div>
                        <div className="bi-kpi-value text-danger">
                          {concatenatedStats.totalGap > 0 ? (concatenatedStats.areAllQtyGoals ? `${Number(concatenatedStats.totalGap).toLocaleString('pt-BR')} ${concatenatedStats.qtyUnitLabel}` : formatBRL(concatenatedStats.totalGap)) : (concatenatedStats.areAllQtyGoals ? `0 ${concatenatedStats.qtyUnitLabel}` : 'R$ 0,00')}
                        </div>
                        <span className="bi-kpi-sub">
                          {concatenatedStats.totalGap <= 0 ? 'Meta global superada!' : 'Diferença acumulada'}
                        </span>
                      </div>
                    </div>

                    <div className="col-12 col-sm-6 col-xl-3">
                      <div className="bi-kpi-card">
                        <div className="bi-kpi-header">
                          <span className="bi-kpi-title">Ticket Médio Ponderado</span>
                          <div className="bi-kpi-icon purple">
                            <FiShoppingBag />
                          </div>
                        </div>
                        <div className="bi-kpi-value text-purple">{formatBRL(concatenatedStats.ticketMedio)}</div>
                        <span className="bi-kpi-sub">Ponderado pelo faturamento real</span>
                      </div>
                    </div>

                    <div className="col-12 col-sm-6 col-xl-3">
                      <div className="bi-kpi-card">
                        <div className="bi-kpi-header">
                          <span className="bi-kpi-title">Itens / Atendimento (PA)</span>
                          <div className="bi-kpi-icon indigo">
                            <FiUsers />
                          </div>
                        </div>
                        <div className="bi-kpi-value text-indigo">
                          {Number(concatenatedStats.pa || 0).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} un
                        </div>
                        <span className="bi-kpi-sub">Média consolidada por atendimento</span>
                      </div>
                    </div>

                    <div className="col-12 col-sm-6 col-xl-3">
                      <div className="bi-kpi-card">
                        <div className="bi-kpi-header">
                          <span className="bi-kpi-title">Valor do Item (VIA)</span>
                          <div className="bi-kpi-icon teal">
                            <FiLayers />
                          </div>
                        </div>
                        <div className="bi-kpi-value text-teal">{formatBRL(concatenatedStats.via)}</div>
                        <span className="bi-kpi-sub">Preço médio por produto vendido</span>
                      </div>
                    </div>

                    <div className="col-12 col-sm-6 col-xl-3">
                      <div className="bi-kpi-card">
                        <div className="bi-kpi-header">
                          <span className="bi-kpi-title">Atendimentos Estimados</span>
                          <div className="bi-kpi-icon cyan">
                            <FiPackage />
                          </div>
                        </div>
                        <div className="bi-kpi-value text-cyan">
                          {concatenatedStats.totalOrders.toLocaleString('pt-BR')} ped
                        </div>
                        <span className="bi-kpi-sub">Total de pedidos/cupons calculados</span>
                      </div>
                    </div>
                  </div>

                  {/* Seção Nova: Cards Isolados por Meta Selecionada */}
                  <div className="p-3 bg-light rounded-3 border">
                    <div className="d-flex justify-content-between align-items-center mb-3 flex-wrap gap-2">
                      <div>
                        <h6 className="fw-bold text-dark mb-0 d-flex align-items-center gap-2">
                          <FiUsers className="text-primary" /> Indicadores Isolados por Meta ({concatenatedStats.isolatedGoals.length})
                        </h6>
                        <span className="text-muted small">
                          Cards comparativos com os parâmetros individuais de cada colaboradora e período selecionado.
                        </span>
                      </div>
                      <div className="d-flex align-items-center gap-2 flex-wrap">
                        {/* Seletor de Modo Grade / Densidade */}
                        <div className="d-flex align-items-center bg-white rounded border p-1 gap-1 shadow-sm">
                          <span className="text-muted px-1 fw-semibold" style={{ fontSize: '0.72rem' }}>Grade:</span>
                          <button
                            type="button"
                            className={`btn btn-xs py-0 px-2 fw-bold ${concatCardsGridCols === '3' ? 'btn-primary shadow-xs' : 'btn-light text-muted'}`}
                            style={{ fontSize: '0.72rem', borderRadius: '4px' }}
                            onClick={() => setConcatCardsGridCols('3')}
                            title="3 cards por linha (Padrão balanceado - ideal para visualizar 6 metas)"
                          >
                            3 Colunas
                          </button>
                          <button
                            type="button"
                            className={`btn btn-xs py-0 px-2 fw-bold ${concatCardsGridCols === '4' ? 'btn-primary shadow-xs' : 'btn-light text-muted'}`}
                            style={{ fontSize: '0.72rem', borderRadius: '4px' }}
                            onClick={() => setConcatCardsGridCols('4')}
                            title="4 cards por linha (Visualização compacta / alta densidade)"
                          >
                            4 Colunas (Compacto)
                          </button>
                          <button
                            type="button"
                            className={`btn btn-xs py-0 px-2 fw-bold ${concatCardsGridCols === '2' ? 'btn-primary shadow-xs' : 'btn-light text-muted'}`}
                            style={{ fontSize: '0.72rem', borderRadius: '4px' }}
                            onClick={() => setConcatCardsGridCols('2')}
                            title="2 cards por linha (Modo amplo e detalhado)"
                          >
                            2 Colunas
                          </button>
                        </div>

                        {concatenatedStats.isolatedGoals.length > 4 && (
                          <div className="position-relative">
                            <input
                              type="text"
                              className="form-control form-control-sm bg-white"
                              style={{ width: '150px', fontSize: '0.72rem', padding: '0.25rem 0.5rem' }}
                              placeholder="Filtrar vendedora..."
                              value={concatCardsSearch}
                              onChange={(e) => setConcatCardsSearch(e.target.value)}
                            />
                            {concatCardsSearch && (
                              <button
                                type="button"
                                className="btn btn-sm btn-link text-muted position-absolute end-0 top-50 translate-middle-y p-0 me-1"
                                onClick={() => setConcatCardsSearch('')}
                                style={{ fontSize: '0.7rem' }}
                              >
                                <FiX size={12} />
                              </button>
                            )}
                          </div>
                        )}

                        <span className="badge bg-white text-dark border py-1.5 px-2.5">
                          {concatenatedStats.isolatedGoals.length} Metas Analisadas
                        </span>
                      </div>
                    </div>

                    <div className={`bi-multi-goal-cards-grid mode-${concatCardsGridCols}`}>
                      {concatenatedStats.isolatedGoals
                        .filter((c) => {
                          if (!concatCardsSearch.trim()) return true;
                          const q = concatCardsSearch.toLowerCase();
                          return (
                            (c.name || '').toLowerCase().includes(q) ||
                            (c.branch_name || '').toLowerCase().includes(q)
                          );
                        })
                        .map((c) => (
                          <div className="bi-multi-goal-card" key={`${c.goal_id}_${c.periodLabel}`}>
                            <div className="bi-multi-goal-card-stripe" style={{ backgroundColor: c.color }} />
                            
                            {/* Header do Card Individual */}
                            <div className="d-flex justify-content-between align-items-start gap-2 mb-2">
                              <div className="d-flex align-items-center gap-2 min-w-0" style={{ minWidth: 0 }}>
                                <div className="bi-multi-goal-avatar" style={{ backgroundColor: c.color }}>
                                  {c.name.substring(0, 2).toUpperCase()}
                                </div>
                                <div className="text-truncate" style={{ minWidth: 0 }}>
                                  <h6 className="fw-bold text-dark mb-0 text-truncate" style={{ fontSize: '0.84rem' }} title={c.name}>
                                    {c.name}
                                  </h6>
                                  <div className="text-muted text-truncate" style={{ fontSize: '0.7rem' }} title={c.branch_name}>
                                    {c.branch_name}
                                  </div>
                                </div>
                              </div>
                              <div className="d-flex flex-column align-items-end gap-1 flex-shrink-0">
                                <div className="d-flex align-items-center gap-1">
                                  <span className="badge bg-light text-dark border fw-bold" style={{ fontSize: '0.66rem', padding: '2px 6px' }}>
                                    📅 {c.periodLabel}
                                  </span>
                                  {c.goal_subtype && getGoalSubtypeBadge(c.goal_subtype)}
                                </div>
                                <span className={`bi-probability-badge ${c.probability}`} style={{ fontSize: '0.62rem', padding: '1px 5px' }}>
                                  {c.probability === 'atingida' && 'Batida'}
                                  {c.probability === 'alta' && 'Alta'}
                                  {c.probability === 'media' && 'Média'}
                                  {c.probability === 'baixa' && 'Em Risco'}
                                </span>
                              </div>
                            </div>

                            {/* Barra de Progresso Individual Compacta */}
                            <div className="mb-2">
                              <div className="d-flex justify-content-between align-items-center mb-1">
                                <span className="text-muted fw-semibold" style={{ fontSize: '0.68rem' }}>Atingimento</span>
                                <span className={`fw-bold ${c.pct >= 100 ? 'text-success' : c.pct >= 85 ? 'text-warning' : 'text-danger'}`} style={{ fontSize: '0.74rem' }}>
                                  {c.pct}%
                                </span>
                              </div>
                              <div className="bi-progress-container" style={{ height: '5px' }}>
                                <div
                                  className={`bi-progress-fill ${c.pct >= 100 ? 'blue-emerald' : c.pct >= 75 ? 'purple-indigo' : 'amber-rose'}`}
                                  style={{ width: `${Math.min(100, c.pct)}%` }}
                                />
                              </div>
                            </div>

                            {/* Grid com os 8 Parâmetros Isolados em 2 linhas de 4 caixas */}
                            <div className="bi-multi-goal-kpi-grid">
                              <div className="bi-multi-goal-kpi-box" title={`Meta: ${isQuantitySubtype(c.goal_subtype) ? `${Number(c.target || 0).toLocaleString('pt-BR')} ${c.goal_subtype === 'clientes_positivados' ? 'cli' : 'un'}` : formatBRL(c.target)}`}>
                                <span className="bi-multi-goal-kpi-label">{isQuantitySubtype(c.goal_subtype) ? `Meta (${c.goal_subtype === 'clientes_positivados' ? 'cli' : 'un'})` : 'Meta (R$)'}</span>
                                <span className="bi-multi-goal-kpi-val text-primary">{isQuantitySubtype(c.goal_subtype) ? `${Number(c.target || 0).toLocaleString('pt-BR')} ${c.goal_subtype === 'clientes_positivados' ? 'cli' : 'un'}` : formatBRL(c.target)}</span>
                              </div>
                              <div className="bi-multi-goal-kpi-box" title={`Realizado: ${isQuantitySubtype(c.goal_subtype) ? `${Number(c.realized || 0).toLocaleString('pt-BR')} ${c.goal_subtype === 'clientes_positivados' ? 'cli' : 'un'}` : formatBRL(c.realized)}`}>
                                <span className="bi-multi-goal-kpi-label">Realizado</span>
                                <span className="bi-multi-goal-kpi-val text-success">{isQuantitySubtype(c.goal_subtype) ? `${Number(c.realized || 0).toLocaleString('pt-BR')} ${c.goal_subtype === 'clientes_positivados' ? 'cli' : 'un'}` : formatBRL(c.realized)}</span>
                              </div>
                              <div className="bi-multi-goal-kpi-box" title={`Saldo: ${c.gap > 0 ? (isQuantitySubtype(c.goal_subtype) ? `${Number(c.gap || 0).toLocaleString('pt-BR')} ${c.goal_subtype === 'clientes_positivados' ? 'cli' : 'un'}` : formatBRL(c.gap)) : 'Superada!'}`}>
                                <span className="bi-multi-goal-kpi-label">Saldo / Gap</span>
                                <span className={`bi-multi-goal-kpi-val ${c.gap > 0 ? 'text-danger' : 'text-success'}`}>
                                  {c.gap > 0 ? (isQuantitySubtype(c.goal_subtype) ? `${Number(c.gap || 0).toLocaleString('pt-BR')} ${c.goal_subtype === 'clientes_positivados' ? 'cli' : 'un'}` : formatBRL(c.gap)) : 'Superada!'}
                                </span>
                              </div>
                              <div className="bi-multi-goal-kpi-box" title={`Vendas/Dia Ativo: ${isQuantitySubtype(c.goal_subtype) ? `${(c.business_days_with_sales > 0 ? Number(c.realized / c.business_days_with_sales).toFixed(1) : 0)} ${c.goal_subtype === 'clientes_positivados' ? 'cli' : 'un'}` : formatBRL(c.daily_net_sales_average || c.daily_run_rate || (c.business_days_with_sales > 0 ? c.realized / c.business_days_with_sales : 0))}`}>
                                <span className="bi-multi-goal-kpi-label">{isQuantitySubtype(c.goal_subtype) ? 'Qtd/Dia Ativo' : 'Vda/Dia Ativo'}</span>
                                <span className="bi-multi-goal-kpi-val text-secondary">
                                  {isQuantitySubtype(c.goal_subtype) ? `${(c.business_days_with_sales > 0 ? Number(c.realized / c.business_days_with_sales).toFixed(1) : 0)} ${c.goal_subtype === 'clientes_positivados' ? 'cli' : 'un'}` : formatBRL(c.daily_net_sales_average || c.daily_run_rate || (c.business_days_with_sales > 0 ? c.realized / c.business_days_with_sales : 0))}
                                </span>
                              </div>
                              <div className="bi-multi-goal-kpi-box" title={`Ticket Médio: ${formatBRL(c.ticketMedio)}`}>
                                <span className="bi-multi-goal-kpi-label">Ticket Médio</span>
                                <span className="bi-multi-goal-kpi-val text-purple">{formatBRL(c.ticketMedio)}</span>
                              </div>
                              <div className="bi-multi-goal-kpi-box" title={`Itens por Atendimento: ${Number(c.pa || 0).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} un`}>
                                <span className="bi-multi-goal-kpi-label">Itens/Atend (PA)</span>
                                <span className="bi-multi-goal-kpi-val text-indigo">
                                  {Number(c.pa || 0).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} un
                                </span>
                              </div>
                              <div className="bi-multi-goal-kpi-box" title={`Valor Médio do Item: ${formatBRL(c.via)}`}>
                                <span className="bi-multi-goal-kpi-label">Valor Item (VIA)</span>
                                <span className="bi-multi-goal-kpi-val text-teal">{formatBRL(c.via)}</span>
                              </div>
                              <div className="bi-multi-goal-kpi-box" title={`Atendimentos: ${c.orders.toLocaleString('pt-BR')} pedidos`}>
                                <span className="bi-multi-goal-kpi-label">Atendimentos</span>
                                <span className="bi-multi-goal-kpi-val text-cyan">{c.orders.toLocaleString('pt-BR')} ped</span>
                              </div>
                            </div>

                            {/* Rodapé do Card Compacto */}
                            <div className="d-flex justify-content-between align-items-center mt-2 pt-2 border-top text-muted" style={{ fontSize: '0.67rem' }}>
                              <span className="text-truncate">
                                <strong className="text-capitalize">{c.goal_classification}</strong>
                              </span>
                              <span>{c.business_days_with_sales || 0} dias c/ vendas</span>
                              <span><strong>#{c.goal_id}</strong></span>
                            </div>
                          </div>
                        ))}
                    </div>
                  </div>

                  {/* Tabela Resumo das Metas Selecionadas nesta Concatenação */}
                  <div className="p-3 bg-light rounded-3 border">
                    <h6 className="fw-bold text-dark mb-3 d-flex align-items-center gap-2">
                      <FiCheckCircle className="text-success" /> Metas Integrantes da Concatenação
                    </h6>
                    <div className="table-responsive">
                      <table className="bi-table mb-0">
                        <thead>
                          <tr>
                            <th>Período</th>
                            <th>Colaborador(a) / Filial</th>
                            <th>Alvo</th>
                            <th>Subtipo</th>
                            <th>Classificação</th>
                            <th className="text-end">Meta</th>
                            <th className="text-end">Realizado</th>
                            <th className="text-end">% Ating.</th>
                            <th className="text-end">Ticket Médio</th>
                            <th className="text-center">Itens/Atend.</th>
                            <th className="text-end">VIA</th>
                            <th className="text-end">Gap</th>
                            <th className="text-center">Probabilidade</th>
                          </tr>
                        </thead>
                        <tbody>
                          {concatenatedStats.selectedGoals.map((c) => (
                            <tr key={`${c.goal_id}_${c.period_key || ''}`}>
                              <td>
                                <span className="badge bg-dark-subtle text-dark border fw-bold" style={{ fontSize: '0.72rem', padding: '2px 8px' }}>
                                  📅 {c.period_label || `${c.reference_month}/${c.reference_year}`}
                                </span>
                              </td>
                              <td>
                                <div className="fw-bold text-dark">{c.name}</div>
                                <div className="text-muted small">{c.branch_name}</div>
                              </td>
                              <td>
                                {getTargetTypeBadge(c.target_type)}
                              </td>
                              <td>
                                {getGoalSubtypeBadge(c.goal_subtype)}
                              </td>
                              <td>
                                <span className={`bi-classification-badge ${c.goal_classification}`}>
                                  {c.goal_classification}
                                </span>
                              </td>
                              <td className="text-end fw-semibold">
                                {isQuantitySubtype(c.goal_subtype) ? `${Number(c.target || 0).toLocaleString('pt-BR')} ${c.goal_subtype === 'clientes_positivados' ? 'cli' : 'un'}` : formatBRL(c.target)}
                              </td>
                              <td className="text-end fw-bold text-dark">
                                {isQuantitySubtype(c.goal_subtype) ? `${Number(c.realized || 0).toLocaleString('pt-BR')} ${c.goal_subtype === 'clientes_positivados' ? 'cli' : 'un'}` : formatBRL(c.realized)}
                              </td>
                              <td className="text-end">
                                <span className={`fw-bold ${c.pct >= 100 ? 'text-success' : c.pct >= 85 ? 'text-warning' : 'text-danger'}`}>
                                  {c.pct}%
                                </span>
                              </td>
                              <td className="text-end">{formatBRL(c.ticket_medio)}</td>
                              <td className="text-center">
                                {Number(c.media_itens_por_atendimento || c.itens_por_atendimento || 0).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} un
                              </td>
                              <td className="text-end">{formatBRL(c.via || c.valor_por_produto)}</td>
                              <td className="text-end text-danger">
                                {c.gap > 0 ? (isQuantitySubtype(c.goal_subtype) ? `${Number(c.gap || 0).toLocaleString('pt-BR')} ${c.goal_subtype === 'clientes_positivados' ? 'cli' : 'un'}` : formatBRL(c.gap)) : '—'}
                              </td>
                              <td className="text-center">
                                <span className={`bi-probability-badge ${c.probability}`}>
                                  {c.probability === 'atingida' && 'Batida'}
                                  {c.probability === 'alta' && 'Alta'}
                                  {c.probability === 'media' && 'Média'}
                                  {c.probability === 'baixa' && 'Em Risco'}
                                </span>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>
              )}

              {/* ABA 2: COMPARATIVO PERÍODO A PERÍODO / META A META */}
              {concatenatedTab === 'comparison' && (
                <div className="d-flex flex-column gap-4">
                  <div className="p-3 bg-light rounded-3 border">
                    <div className="d-flex justify-content-between align-items-center mb-3 flex-wrap gap-2">
                      <div>
                        <h6 className="fw-bold text-dark mb-1 d-flex align-items-center gap-2">
                          <FiLayers className="text-primary" /> Matriz Comparativa Lado a Lado
                        </h6>
                        <span className="text-muted small">
                          Acompanhe lado a lado a oscilação de faturamento, metas, tickets e volumes de cada {concatMatrixMode === 'by_period' && concatenatedStats.periods.length > 1 ? 'período analisado' : 'meta analisada'}.
                        </span>
                      </div>
                      <div className="d-flex align-items-center gap-2 flex-wrap">
                        {concatenatedStats.periods.length > 1 && (
                          <div className="btn-group btn-group-sm" role="group">
                            <button
                              type="button"
                              className={`btn btn-sm ${concatMatrixMode === 'by_goal' ? 'btn-primary' : 'btn-outline-secondary'}`}
                              onClick={() => setConcatMatrixMode('by_goal')}
                              title="Visualizar cada meta selecionada individualmente"
                            >
                              Por Meta ({concatenatedStats.sideBySideGoals.length})
                            </button>
                            <button
                              type="button"
                              className={`btn btn-sm ${concatMatrixMode === 'by_period' ? 'btn-primary' : 'btn-outline-secondary'}`}
                              onClick={() => setConcatMatrixMode('by_period')}
                              title="Agrupar e comparar por período de referência"
                            >
                              Por Período ({concatenatedStats.periods.length})
                            </button>
                          </div>
                        )}
                        <span className="bi-modal-chart-badge">
                          {concatMatrixMode === 'by_period' && concatenatedStats.periods.length > 1
                            ? `${concatenatedStats.sideBySidePeriods.length} Períodos Agrupados`
                            : `${concatenatedStats.sideBySideGoals.length} Metas Comparadas`}
                        </span>
                      </div>
                    </div>

                    {(() => {
                      const isByPeriod = concatMatrixMode === 'by_period' && concatenatedStats.periods.length > 1;
                      const activeCols = isByPeriod ? concatenatedStats.sideBySidePeriods : concatenatedStats.sideBySideGoals;
                      const activeVars = isByPeriod ? concatenatedStats.periodVariations : concatenatedStats.goalVariations;

                      return (
                        <div className="table-responsive">
                          <table className="bi-comparison-table w-100">
                            <thead>
                              <tr>
                                <th>Indicador / Métrica</th>
                                {activeCols.map((p, idx) => (
                                  <th key={p.goal_id || p.period || idx} className="text-end">
                                    <div className="d-flex align-items-center justify-content-end gap-1 flex-wrap">
                                      {p.color && (
                                        <span
                                          style={{
                                            width: '8px',
                                            height: '8px',
                                            borderRadius: '50%',
                                            backgroundColor: p.color,
                                            display: 'inline-block',
                                            flexShrink: 0
                                          }}
                                        />
                                      )}
                                      <span>📅 {p.period || p.periodLabel}</span>
                                      {p.goal_subtype && getGoalSubtypeBadge(p.goal_subtype)}
                                    </div>
                                    <div
                                      className="text-muted small fw-normal text-truncate"
                                      style={{ fontSize: '0.72rem', maxWidth: '180px' }}
                                      title={p.name}
                                    >
                                      {p.name}
                                    </div>
                                    {p.branch_name && p.branch_name !== 'Filial Principal' && (
                                      <div className="text-muted fw-normal text-truncate" style={{ fontSize: '0.65rem', maxWidth: '180px' }}>
                                        {p.branch_name}
                                      </div>
                                    )}
                                  </th>
                                ))}
                                <th className="text-end bi-consolidated-col">Consolidado Total</th>
                                <th className="text-end">Variação (Extremos)</th>
                              </tr>
                            </thead>
                            <tbody>
                              {/* Linha Meta */}
                              <tr>
                                <td className="fw-semibold">{concatenatedStats.areAllQtyGoals ? (concatenatedStats.areAllClients ? 'Meta de Clientes' : 'Meta de Produtos') : 'Meta de Vendas'}</td>
                                {activeCols.map((p, idx) => (
                                  <td key={idx} className="text-end">{isQuantitySubtype(p.goal_subtype) ? `${Number(p.target || 0).toLocaleString('pt-BR')} ${p.goal_subtype === 'clientes_positivados' ? 'cli' : 'un'}` : formatBRL(p.target)}</td>
                                ))}
                                <td className="text-end fw-bold bi-consolidated-col">{concatenatedStats.areAllQtyGoals ? `${Number(concatenatedStats.totalTarget).toLocaleString('pt-BR')} ${concatenatedStats.qtyUnitLabel}` : formatBRL(concatenatedStats.totalTarget)}</td>
                                <td className="text-end">
                                  {activeVars?.target_diff != null && activeVars.target_diff > 0 ? (
                                    <span className={`bi-var-badge ${activeVars.target_diff >= 0 ? 'bi-var-positive' : 'bi-var-negative'}`}>
                                      +{activeVars.target_pct}%
                                    </span>
                                  ) : (
                                    <span className="text-muted small">0%</span>
                                  )}
                                </td>
                              </tr>

                              {/* Linha Faturamento Realizado */}
                              <tr>
                                <td className="fw-semibold">{concatenatedStats.areAllQtyGoals ? (concatenatedStats.areAllClients ? 'Clientes Positivados' : 'Produtos Vendidos') : 'Faturamento Realizado'}</td>
                                {activeCols.map((p, idx) => (
                                  <td key={idx} className="text-end fw-bold text-dark">{isQuantitySubtype(p.goal_subtype) ? `${Number(p.realized || 0).toLocaleString('pt-BR')} ${p.goal_subtype === 'clientes_positivados' ? 'cli' : 'un'}` : formatBRL(p.realized)}</td>
                                ))}
                                <td className="text-end fw-bold text-success bi-consolidated-col">{concatenatedStats.areAllQtyGoals ? `${Number(concatenatedStats.totalRealized).toLocaleString('pt-BR')} ${concatenatedStats.qtyUnitLabel}` : formatBRL(concatenatedStats.totalRealized)}</td>
                                <td className="text-end">
                                  {activeVars?.sales_diff != null && activeVars.sales_diff > 0 ? (
                                    <span className={`bi-var-badge ${activeVars.sales_diff >= 0 ? 'bi-var-positive' : 'bi-var-negative'}`}>
                                      +{activeVars.sales_pct}%
                                    </span>
                                  ) : (
                                    <span className="text-muted small">0%</span>
                                  )}
                                </td>
                              </tr>

                              {/* Linha % Atingimento */}
                              <tr>
                                <td className="fw-semibold">% Atingimento da Meta</td>
                                {activeCols.map((p, idx) => (
                                  <td key={idx} className="text-end">
                                    <span className={`fw-bold ${p.pct >= 100 ? 'text-success' : p.pct >= 85 ? 'text-warning' : 'text-danger'}`}>
                                      {p.pct}%
                                    </span>
                                  </td>
                                ))}
                                <td className="text-end fw-bold bi-consolidated-col">{concatenatedStats.pct}%</td>
                                <td className="text-end">
                                  {activeVars?.pct_points != null && activeVars.pct_points > 0 ? (
                                    <span className={`bi-var-badge ${activeVars.pct_points >= 0 ? 'bi-var-positive' : 'bi-var-negative'}`}>
                                      +{activeVars.pct_points} p.p.
                                    </span>
                                  ) : (
                                    <span className="text-muted small">0 p.p.</span>
                                  )}
                                </td>
                              </tr>

                              {/* Linha Gap */}
                              <tr>
                                <td className="fw-semibold">{concatenatedStats.areAllQtyGoals ? 'Gap / Saldo Restante' : 'Gap / Saldo Restante (R$)'}</td>
                                {activeCols.map((p, idx) => (
                                  <td key={idx} className="text-end text-danger">{p.gap > 0 ? (isQuantitySubtype(p.goal_subtype) ? `${Number(p.gap || 0).toLocaleString('pt-BR')} ${p.goal_subtype === 'clientes_positivados' ? 'cli' : 'un'}` : formatBRL(p.gap)) : '—'}</td>
                                ))}
                                <td className="text-end fw-bold text-danger bi-consolidated-col">
                                  {concatenatedStats.totalGap > 0 ? (concatenatedStats.areAllQtyGoals ? `${Number(concatenatedStats.totalGap).toLocaleString('pt-BR')} ${concatenatedStats.qtyUnitLabel}` : formatBRL(concatenatedStats.totalGap)) : (concatenatedStats.areAllQtyGoals ? `0 ${concatenatedStats.qtyUnitLabel}` : 'R$ 0,00')}
                                </td>
                                <td className="text-end text-muted">—</td>
                              </tr>

                              {/* Linha Ticket Médio */}
                              <tr>
                                <td className="fw-semibold">Ticket Médio (R$)</td>
                                {activeCols.map((p, idx) => (
                                  <td key={idx} className="text-end">{formatBRL(p.ticket_medio)}</td>
                                ))}
                                <td className="text-end fw-bold bi-consolidated-col">{formatBRL(concatenatedStats.ticketMedio)}</td>
                                <td className="text-end">
                                  {activeVars?.ticket_diff != null && activeVars.ticket_diff > 0 ? (
                                    <span className={`bi-var-badge ${activeVars.ticket_diff >= 0 ? 'bi-var-positive' : 'bi-var-negative'}`}>
                                      +{activeVars.ticket_pct}%
                                    </span>
                                  ) : (
                                    <span className="text-muted small">0%</span>
                                  )}
                                </td>
                              </tr>

                              {/* Linha Itens por Atendimento (PA) */}
                              <tr>
                                <td className="fw-semibold">Itens por Atendimento (PA)</td>
                                {activeCols.map((p, idx) => (
                                  <td key={idx} className="text-end">
                                    {Number(p.pa || 0).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} un
                                  </td>
                                ))}
                                <td className="text-end fw-bold bi-consolidated-col">
                                  {Number(concatenatedStats.pa || 0).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} un
                                </td>
                                <td className="text-end">
                                  {activeVars?.pa_diff != null && activeVars.pa_diff > 0 ? (
                                    <span className={`bi-var-badge ${activeVars.pa_diff >= 0 ? 'bi-var-positive' : 'bi-var-negative'}`}>
                                      +{Number(activeVars.pa_diff).toFixed(2)} un
                                    </span>
                                  ) : (
                                    <span className="text-muted small">0.00 un</span>
                                  )}
                                </td>
                              </tr>

                              {/* Linha VIA */}
                              <tr>
                                <td className="fw-semibold">Valor do Item (VIA)</td>
                                {activeCols.map((p, idx) => (
                                  <td key={idx} className="text-end">{formatBRL(p.via)}</td>
                                ))}
                                <td className="text-end fw-bold bi-consolidated-col">{formatBRL(concatenatedStats.via)}</td>
                                <td className="text-end">
                                  {activeVars?.via_diff != null && activeVars.via_diff > 0 ? (
                                    <span className={`bi-var-badge ${activeVars.via_diff >= 0 ? 'bi-var-positive' : 'bi-var-negative'}`}>
                                      +{activeVars.via_pct}%
                                    </span>
                                  ) : (
                                    <span className="text-muted small">0%</span>
                                  )}
                                </td>
                              </tr>

                              {/* Linha Atendimentos Estimados */}
                              <tr>
                                <td className="fw-semibold">Atendimentos / Pedidos</td>
                                {activeCols.map((p, idx) => (
                                  <td key={idx} className="text-end">{Number(p.orders || 0).toLocaleString('pt-BR')}</td>
                                ))}
                                <td className="text-end fw-bold bi-consolidated-col">{concatenatedStats.totalOrders.toLocaleString('pt-BR')}</td>
                                <td className="text-end">
                                  {activeVars?.orders_diff != null && activeVars.orders_diff > 0 ? (
                                    <span className={`bi-var-badge ${activeVars.orders_diff >= 0 ? 'bi-var-positive' : 'bi-var-negative'}`}>
                                      +{activeVars.orders_diff.toLocaleString('pt-BR')}
                                    </span>
                                  ) : (
                                    <span className="text-muted small">0</span>
                                  )}
                                </td>
                              </tr>

                              {/* Linha Probabilidade */}
                              <tr>
                                <td className="fw-semibold">Probabilidade</td>
                                {activeCols.map((p, idx) => (
                                  <td key={idx} className="text-end">
                                    <span className={`bi-probability-badge ${p.probability}`}>
                                      {p.probability === 'atingida' && 'Batida'}
                                      {p.probability === 'alta' && 'Alta'}
                                      {p.probability === 'media' && 'Média'}
                                      {p.probability === 'baixa' && 'Em Risco'}
                                    </span>
                                  </td>
                                ))}
                                <td className="text-end bi-consolidated-col">
                                  <span className={`bi-probability-badge ${concatenatedStats.pct >= 100 ? 'atingida' : concatenatedStats.pct >= 85 ? 'alta' : concatenatedStats.pct >= 70 ? 'media' : 'baixa'}`}>
                                    {concatenatedStats.pct >= 100 ? 'Batida' : concatenatedStats.pct >= 85 ? 'Alta' : concatenatedStats.pct >= 70 ? 'Média' : 'Em Risco'}
                                  </span>
                                </td>
                                <td className="text-end text-muted">—</td>
                              </tr>
                            </tbody>
                          </table>
                        </div>
                      );
                    })()}
                  </div>
                </div>
              )}

              {/* ABA 3: GRÁFICOS ANALÍTICOS & COMPARATIVOS */}
              {concatenatedTab === 'charts' && (
                <div className="bi-modal-charts-container">
                  {/* Barra de Sub-filtros por Categoria de Análise e Legenda das Metas */}
                  <div className="d-flex justify-content-between align-items-center flex-wrap gap-2 mb-3">
                    <div className="bi-modal-chart-subfilter mb-0">
                      <button
                        type="button"
                        className={`bi-chart-category-btn ${concatChartCategory === 'all' ? 'active' : ''}`}
                        onClick={() => setConcatChartCategory('all')}
                      >
                        <FiBarChart2 size={13} /> Todos os Gráficos (11)
                      </button>
                      <button
                        type="button"
                        className={`bi-chart-category-btn ${concatChartCategory === 'pace' ? 'active' : ''}`}
                        onClick={() => setConcatChartCategory('pace')}
                      >
                        <FiTrendingUp size={13} /> Ritmo & Vendas Diárias (2)
                      </button>
                      <button
                        type="button"
                        className={`bi-chart-category-btn ${concatChartCategory === 'ticket_items' ? 'active' : ''}`}
                        onClick={() => setConcatChartCategory('ticket_items')}
                      >
                        <FiShoppingBag size={13} /> Ticket Médio, Itens & VIA (3)
                      </button>
                      <button
                        type="button"
                        className={`bi-chart-category-btn ${concatChartCategory === 'weekday' ? 'active' : ''}`}
                        onClick={() => setConcatChartCategory('weekday')}
                      >
                        <FiCalendar size={13} /> Padrão Semanal (1)
                      </button>
                      <button
                        type="button"
                        className={`bi-chart-category-btn ${concatChartCategory === 'multi_goals' ? 'active' : ''}`}
                        onClick={() => setConcatChartCategory('multi_goals')}
                      >
                        <FiLayers size={13} /> Comparativo Multi-Metas (6)
                      </button>
                    </div>

                    <div className="d-flex align-items-center gap-2 flex-wrap text-muted small">
                      <span className="badge bg-light text-dark border">
                        Meta Total: <strong className="text-primary">{concatenatedStats.areAllQtyGoals ? `${Number(concatenatedStats.totalTarget).toLocaleString('pt-BR')} ${concatenatedStats.qtyUnitLabel}` : formatBRL(concatenatedStats.totalTarget)}</strong>
                      </span>
                      <span className="badge bg-light text-dark border">
                        Realizado: <strong className="text-success">{concatenatedStats.areAllQtyGoals ? `${Number(concatenatedStats.totalRealized).toLocaleString('pt-BR')} ${concatenatedStats.qtyUnitLabel}` : formatBRL(concatenatedStats.totalRealized)}</strong> ({concatenatedStats.pct}%)
                      </span>
                      <span className="badge bg-light text-dark border">
                        TM Médio: <strong className="text-purple">{formatBRL(concatenatedStats.ticketMedio)}</strong>
                      </span>
                      <span className="badge bg-light text-dark border">
                        PA Médio: <strong className="text-indigo">{Number(concatenatedStats.pa || 0).toFixed(2)} un</strong>
                      </span>
                      <span className="badge bg-light text-dark border">
                        VIA Médio: <strong className="text-teal">{formatBRL(concatenatedStats.via)}</strong>
                      </span>
                    </div>
                  </div>

                  {/* Legenda de Cores das Metas quando em exibição Multi-Metas ou Todos */}
                  {(concatChartCategory === 'all' || concatChartCategory === 'multi_goals') && (
                    <div className="d-flex align-items-center gap-2 flex-wrap p-2 bg-light rounded-2 border mb-3">
                      <span className="text-muted small fw-semibold me-1" style={{ fontSize: '0.72rem' }}>
                        Metas Selecionadas:
                      </span>
                      {concatenatedStats.isolatedGoals.map((g) => (
                        <span key={`${g.goal_id}_${g.periodLabel}`} className="bi-multi-goal-legend-badge">
                          <span className="bi-multi-goal-legend-dot" style={{ backgroundColor: g.color }} />
                          {g.shortName}
                        </span>
                      ))}
                    </div>
                  )}

                  {/* Grid Responsivo de Gráficos */}
                  <div className="bi-modal-charts-grid">
                    {/* Gráfico 1: Curva de Progressão e Ritmo Acumulado Consolidado */}
                    {(concatChartCategory === 'all' || concatChartCategory === 'pace') && (
                      <div className="bi-modal-chart-card">
                        <div className="bi-modal-chart-header">
                          <div className="bi-modal-chart-title">
                            <FiTrendingUp className="text-primary" /> Curva de Progressão & Ritmo Ideal (Consolidado)
                          </div>
                          <div className="d-flex align-items-center gap-2 flex-wrap">
                            <div className="bi-chart-type-toggle">
                              <button
                                type="button"
                                className={`bi-chart-type-btn ${concatChartTypes.progression === 'area' ? 'active' : ''}`}
                                onClick={() => toggleConcatChartType('progression', 'area')}
                                title="Visualizar em Área"
                              >
                                Área
                              </button>
                              <button
                                type="button"
                                className={`bi-chart-type-btn ${concatChartTypes.progression === 'bar' ? 'active' : ''}`}
                                onClick={() => toggleConcatChartType('progression', 'bar')}
                                title="Visualizar em Barras"
                              >
                                Barras
                              </button>
                              <button
                                type="button"
                                className={`bi-chart-type-btn ${concatChartTypes.progression === 'line' ? 'active' : ''}`}
                                onClick={() => toggleConcatChartType('progression', 'line')}
                                title="Visualizar em Linhas"
                              >
                                Linhas
                              </button>
                            </div>
                            <span className="bi-modal-chart-badge">
                              {concatenatedStats.areAllQtyGoals ? (concatenatedStats.areAllClients ? 'Clientes Acumulados' : 'Produtos Acumulados') : 'Faturamento Acumulado'}
                            </span>
                          </div>
                        </div>
                        <div className="bi-chart-body">
                          <ResponsiveContainer width="100%" height={280}>
                            <ComposedChart data={concatenatedStats.timeline} margin={{ top: 10, right: 20, left: 10, bottom: 0 }}>
                              <defs>
                                <linearGradient id="concatColorRealized" x1="0" y1="0" x2="0" y2="1">
                                  <stop offset="5%" stopColor="#3b82f6" stopOpacity={0.4} />
                                  <stop offset="95%" stopColor="#3b82f6" stopOpacity={0.0} />
                                </linearGradient>
                              </defs>
                              <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#e2e8f0" />
                              <XAxis dataKey="shortLabel" stroke="#64748b" fontSize={12} tickLine={false} />
                              <YAxis
                                stroke="#64748b"
                                fontSize={12}
                                tickLine={false}
                                tickFormatter={(v) => concatenatedStats.areAllQtyGoals ? `${v} ${concatenatedStats.qtyUnitLabel}` : `R$ ${(v / 1000).toFixed(0)}k`}
                              />
                              <Tooltip content={<CustomChartTooltip />} />
                              <Legend verticalAlign="top" height={32} />
                              {concatChartTypes.progression === 'area' && (
                                <Area
                                  type="monotone"
                                  dataKey="accumulated_realized"
                                  name={concatenatedStats.areAllQtyGoals ? (concatenatedStats.areAllClients ? 'Clientes Acumulados' : 'Produtos Acumulados') : "Faturamento Acumulado"}
                                  stroke="#3b82f6"
                                  strokeWidth={3}
                                  fillOpacity={1}
                                  fill="url(#concatColorRealized)"
                                  unit={concatenatedStats.areAllQtyGoals ? concatenatedStats.qtyUnitLabel : "R$"}
                                />
                              )}
                              {concatChartTypes.progression === 'bar' && (
                                <Bar
                                  dataKey="accumulated_realized"
                                  name={concatenatedStats.areAllQtyGoals ? (concatenatedStats.areAllClients ? 'Clientes Acumulados' : 'Produtos Acumulados') : "Faturamento Acumulado"}
                                  fill="#3b82f6"
                                  radius={[4, 4, 0, 0]}
                                  unit={concatenatedStats.areAllQtyGoals ? concatenatedStats.qtyUnitLabel : "R$"}
                                />
                              )}
                              {concatChartTypes.progression === 'line' && (
                                <Line
                                  type="monotone"
                                  dataKey="accumulated_realized"
                                  name={concatenatedStats.areAllQtyGoals ? (concatenatedStats.areAllClients ? 'Clientes Acumulados' : 'Produtos Acumulados') : "Faturamento Acumulado"}
                                  stroke="#3b82f6"
                                  strokeWidth={3}
                                  dot={{ r: 3 }}
                                  unit={concatenatedStats.areAllQtyGoals ? concatenatedStats.qtyUnitLabel : "R$"}
                                />
                              )}
                              <Line
                                type="monotone"
                                dataKey="ideal_pace"
                                name="Ritmo Ideal Linear"
                                stroke="#10b981"
                                strokeWidth={2}
                                strokeDasharray="5 5"
                                dot={false}
                                unit={concatenatedStats.areAllQtyGoals ? concatenatedStats.qtyUnitLabel : "R$"}
                              />
                            </ComposedChart>
                          </ResponsiveContainer>
                        </div>
                      </div>
                    )}

                    {/* Gráfico 2: Faturamento / Quantidade Diária vs Meta Diária Consolidado */}
                    {(concatChartCategory === 'all' || concatChartCategory === 'pace') && (
                      <div className="bi-modal-chart-card">
                        <div className="bi-modal-chart-header">
                          <div className="bi-modal-chart-title">
                            <FiDollarSign className="text-success" /> {concatenatedStats.areAllQtyGoals ? (concatenatedStats.areAllClients ? 'Clientes Positivados / Dia (Consolidado)' : 'Produtos Vendidos / Dia (Consolidado)') : 'Faturamento Diário vs Meta Diária (Consolidado)'}
                          </div>
                          <div className="d-flex align-items-center gap-2 flex-wrap">
                            <div className="bi-chart-type-toggle">
                              <button
                                type="button"
                                className={`bi-chart-type-btn ${concatChartTypes.dailySales === 'bar' ? 'active' : ''}`}
                                onClick={() => toggleConcatChartType('dailySales', 'bar')}
                                title="Visualizar em Barras"
                              >
                                Barras
                              </button>
                              <button
                                type="button"
                                className={`bi-chart-type-btn ${concatChartTypes.dailySales === 'line' ? 'active' : ''}`}
                                onClick={() => toggleConcatChartType('dailySales', 'line')}
                                title="Visualizar em Linhas"
                              >
                                Linhas
                              </button>
                              <button
                                type="button"
                                className={`bi-chart-type-btn ${concatChartTypes.dailySales === 'area' ? 'active' : ''}`}
                                onClick={() => toggleConcatChartType('dailySales', 'area')}
                                title="Visualizar em Área"
                              >
                                Área
                              </button>
                            </div>
                            <span className="bi-modal-chart-badge">
                              Ref: {concatenatedStats.areAllQtyGoals ? `${Number(concatenatedStats.timeline[0]?.ref_daily_sales || 0).toLocaleString('pt-BR', { maximumFractionDigits: 1 })} ${concatenatedStats.qtyUnitLabel}/dia` : `${formatBRL(concatenatedStats.timeline[0]?.ref_daily_sales || 0)}/dia`}
                            </span>
                          </div>
                        </div>
                        <div className="bi-chart-body">
                          <ResponsiveContainer width="100%" height={280}>
                            <ComposedChart data={concatenatedStats.timeline} margin={{ top: 10, right: 20, left: 10, bottom: 0 }}>
                              <defs>
                                <linearGradient id="concatColorDailySales" x1="0" y1="0" x2="0" y2="1">
                                  <stop offset="5%" stopColor="#10b981" stopOpacity={0.4} />
                                  <stop offset="95%" stopColor="#10b981" stopOpacity={0.0} />
                                </linearGradient>
                              </defs>
                              <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#e2e8f0" />
                              <XAxis dataKey="shortLabel" stroke="#64748b" fontSize={12} tickLine={false} />
                              <YAxis
                                stroke="#64748b"
                                fontSize={12}
                                tickLine={false}
                                tickFormatter={(v) => concatenatedStats.areAllQtyGoals ? `${v} ${concatenatedStats.qtyUnitLabel}` : `R$ ${v >= 1000 ? (v / 1000).toFixed(1) + 'k' : v}`}
                              />
                              <Tooltip content={<CustomChartTooltip />} />
                              <Legend verticalAlign="top" height={32} />
                              {concatChartTypes.dailySales === 'bar' && (
                                <Bar
                                  dataKey="realized_day"
                                  name={concatenatedStats.areAllQtyGoals ? (concatenatedStats.areAllClients ? 'Clientes / Dia' : 'Produtos / Dia') : "Venda Líquida Diária (R$)"}
                                  radius={[4, 4, 0, 0]}
                                  unit={concatenatedStats.areAllQtyGoals ? concatenatedStats.qtyUnitLabel : "R$"}
                                >
                                  {concatenatedStats.timeline.map((entry, index) => {
                                    const refVal = entry.ref_daily_sales || 0;
                                    const isHit = refVal > 0 && entry.realized_day >= refVal;
                                    const isNear = refVal > 0 && !isHit && entry.realized_day >= refVal * 0.8;
                                    const fillColor = isHit ? '#10b981' : (isNear ? '#f59e0b' : '#3b82f6');
                                    return <Cell key={`cell-concat-daily-${index}`} fill={fillColor} />;
                                  })}
                                </Bar>
                              )}
                              {concatChartTypes.dailySales === 'line' && (
                                <Line
                                  type="monotone"
                                  dataKey="realized_day"
                                  name={concatenatedStats.areAllQtyGoals ? (concatenatedStats.areAllClients ? 'Clientes / Dia' : 'Produtos / Dia') : "Venda Líquida Diária (R$)"}
                                  stroke="#10b981"
                                  strokeWidth={3}
                                  dot={{ r: 4, fill: '#10b981' }}
                                  unit={concatenatedStats.areAllQtyGoals ? concatenatedStats.qtyUnitLabel : "R$"}
                                />
                              )}
                              {concatChartTypes.dailySales === 'area' && (
                                <Area
                                  type="monotone"
                                  dataKey="realized_day"
                                  name={concatenatedStats.areAllQtyGoals ? (concatenatedStats.areAllClients ? 'Clientes / Dia' : 'Produtos / Dia') : "Venda Líquida Diária (R$)"}
                                  stroke="#10b981"
                                  strokeWidth={3}
                                  fillOpacity={1}
                                  fill="url(#concatColorDailySales)"
                                  unit={concatenatedStats.areAllQtyGoals ? concatenatedStats.qtyUnitLabel : "R$"}
                                />
                              )}
                              {(concatenatedStats.timeline[0]?.ref_daily_sales || 0) > 0 && (
                                <ReferenceLine
                                  y={Number(concatenatedStats.timeline[0].ref_daily_sales)}
                                  stroke="#10b981"
                                  strokeDasharray="4 4"
                                  strokeWidth={2}
                                  label={{ value: concatenatedStats.areAllQtyGoals ? `Meta Diária (${concatenatedStats.qtyUnitLabel})` : 'Meta Diária Consolidada', position: 'insideTopRight', fill: '#059669', fontSize: 11 }}
                                />
                              )}
                            </ComposedChart>
                          </ResponsiveContainer>
                        </div>
                      </div>
                    )}

                    {/* Gráfico 3: Evolução do Ticket Médio por Período Consolidado */}
                    {(concatChartCategory === 'all' || concatChartCategory === 'ticket_items') && (
                      <div className="bi-modal-chart-card">
                        <div className="bi-modal-chart-header">
                          <div className="bi-modal-chart-title">
                            <FiShoppingBag className="text-purple" /> Ticket Médio por Período (Consolidado)
                          </div>
                          <div className="d-flex align-items-center gap-2 flex-wrap">
                            <div className="bi-chart-type-toggle">
                              <button
                                type="button"
                                className={`bi-chart-type-btn ${concatChartTypes.ticketMedio === 'bar' ? 'active' : ''}`}
                                onClick={() => toggleConcatChartType('ticketMedio', 'bar')}
                                title="Visualizar em Barras"
                              >
                                Barras
                              </button>
                              <button
                                type="button"
                                className={`bi-chart-type-btn ${concatChartTypes.ticketMedio === 'line' ? 'active' : ''}`}
                                onClick={() => toggleConcatChartType('ticketMedio', 'line')}
                                title="Visualizar em Linhas"
                              >
                                Linhas
                              </button>
                              <button
                                type="button"
                                className={`bi-chart-type-btn ${concatChartTypes.ticketMedio === 'area' ? 'active' : ''}`}
                                onClick={() => toggleConcatChartType('ticketMedio', 'area')}
                                title="Visualizar em Área"
                              >
                                Área
                              </button>
                            </div>
                            {concatenatedStats.ticketMedio > 0 && (
                              <span className="bi-modal-chart-badge">
                                Ref: {formatBRL(concatenatedStats.ticketMedio)}
                              </span>
                            )}
                          </div>
                        </div>
                        <div className="bi-chart-body">
                          <ResponsiveContainer width="100%" height={280}>
                            <ComposedChart data={concatenatedStats.timeline} margin={{ top: 10, right: 20, left: 10, bottom: 0 }}>
                              <defs>
                                <linearGradient id="concatColorTicketMedio" x1="0" y1="0" x2="0" y2="1">
                                  <stop offset="5%" stopColor="#8b5cf6" stopOpacity={0.4} />
                                  <stop offset="95%" stopColor="#8b5cf6" stopOpacity={0.0} />
                                </linearGradient>
                              </defs>
                              <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#e2e8f0" />
                              <XAxis dataKey="shortLabel" stroke="#64748b" fontSize={12} tickLine={false} />
                              <YAxis
                                stroke="#8b5cf6"
                                fontSize={12}
                                tickLine={false}
                                tickFormatter={(v) => `R$ ${v}`}
                              />
                              <Tooltip content={<CustomChartTooltip />} />
                              <Legend verticalAlign="top" height={32} />
                              {concatChartTypes.ticketMedio === 'bar' && (
                                <Bar
                                  dataKey="ticket_medio"
                                  name="Ticket Médio (R$)"
                                  fill="#8b5cf6"
                                  radius={[4, 4, 0, 0]}
                                  unit="R$"
                                />
                              )}
                              {concatChartTypes.ticketMedio === 'line' && (
                                <Line
                                  type="monotone"
                                  dataKey="ticket_medio"
                                  name="Ticket Médio (R$)"
                                  stroke="#8b5cf6"
                                  strokeWidth={3}
                                  dot={{ r: 4, fill: '#8b5cf6' }}
                                  unit="R$"
                                />
                              )}
                              {concatChartTypes.ticketMedio === 'area' && (
                                <Area
                                  type="monotone"
                                  dataKey="ticket_medio"
                                  name="Ticket Médio (R$)"
                                  stroke="#8b5cf6"
                                  strokeWidth={3}
                                  fillOpacity={1}
                                  fill="url(#concatColorTicketMedio)"
                                  unit="R$"
                                />
                              )}
                              {concatenatedStats.ticketMedio > 0 && (
                                <ReferenceLine
                                  y={Number(concatenatedStats.ticketMedio)}
                                  stroke="#ec4899"
                                  strokeDasharray="4 4"
                                  strokeWidth={2}
                                  label={{ value: 'Ref TM', position: 'insideTopRight', fill: '#db2777', fontSize: 11 }}
                                />
                              )}
                            </ComposedChart>
                          </ResponsiveContainer>
                        </div>
                      </div>
                    )}

                    {/* Gráfico 4: Itens por Atendimento (PA) & Volume Consolidado */}
                    {(concatChartCategory === 'all' || concatChartCategory === 'ticket_items') && (
                      <div className="bi-modal-chart-card">
                        <div className="bi-modal-chart-header">
                          <div className="bi-modal-chart-title">
                            <FiUsers className="text-indigo" /> Itens por Atendimento (PA) & Volume (Consolidado)
                          </div>
                          {concatenatedStats.pa > 0 && (
                            <span className="bi-modal-chart-badge">
                              Ref: {Number(concatenatedStats.pa).toFixed(2)} un
                            </span>
                          )}
                        </div>
                        <div className="bi-chart-body">
                          <ResponsiveContainer width="100%" height={280}>
                            <ComposedChart data={concatenatedStats.timeline} margin={{ top: 10, right: 20, left: 10, bottom: 0 }}>
                              <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#e2e8f0" />
                              <XAxis dataKey="shortLabel" stroke="#64748b" fontSize={12} tickLine={false} />
                              <YAxis
                                yAxisId="left"
                                stroke="#6366f1"
                                fontSize={12}
                                tickLine={false}
                                tickFormatter={(v) => `${v} un`}
                              />
                              <YAxis
                                yAxisId="right"
                                orientation="right"
                                stroke="#06b6d4"
                                fontSize={12}
                                tickLine={false}
                                tickFormatter={(v) => `${v} ped`}
                              />
                              <Tooltip content={<CustomChartTooltip />} />
                              <Legend verticalAlign="top" height={32} />
                              <Bar
                                yAxisId="left"
                                dataKey="pa"
                                name="Itens por Venda (un)"
                                unit="un"
                                fill="#6366f1"
                                radius={[4, 4, 0, 0]}
                              />
                              <Line
                                yAxisId="right"
                                type="monotone"
                                dataKey="orders"
                                name="Pedidos do Dia"
                                unit="ped"
                                stroke="#06b6d4"
                                strokeWidth={2}
                                dot={{ r: 3 }}
                              />
                              {concatenatedStats.pa > 0 && (
                                <ReferenceLine
                                  yAxisId="left"
                                  y={Number(concatenatedStats.pa)}
                                  stroke="#f59e0b"
                                  strokeDasharray="4 4"
                                  strokeWidth={2}
                                  label={{ value: 'Ref PA', position: 'insideTopLeft', fill: '#d97706', fontSize: 11 }}
                                />
                              )}
                            </ComposedChart>
                          </ResponsiveContainer>
                        </div>
                      </div>
                    )}

                    {/* Gráfico 5: Composição do Ticket: VIA vs TM Consolidado */}
                    {(concatChartCategory === 'all' || concatChartCategory === 'ticket_items') && (
                      <div className="bi-modal-chart-card">
                        <div className="bi-modal-chart-header">
                          <div className="bi-modal-chart-title">
                            <FiLayers className="text-teal" /> Composição: VIA (Valor do Item) vs TM (Consolidado)
                          </div>
                          <span className="bi-modal-chart-badge">Fórmula: VIA × PA = TM</span>
                        </div>
                        <div className="bi-chart-body">
                          <ResponsiveContainer width="100%" height={280}>
                            <ComposedChart data={concatenatedStats.timeline} margin={{ top: 10, right: 20, left: 10, bottom: 0 }}>
                              <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#e2e8f0" />
                              <XAxis dataKey="shortLabel" stroke="#64748b" fontSize={12} tickLine={false} />
                              <YAxis
                                stroke="#0d9488"
                                fontSize={12}
                                tickLine={false}
                                tickFormatter={(v) => `R$ ${v}`}
                              />
                              <Tooltip content={<CustomChartTooltip />} />
                              <Legend verticalAlign="top" height={32} />
                              <Area
                                type="monotone"
                                dataKey="via"
                                name="VIA - Preço Médio do Item (R$)"
                                stroke="#0d9488"
                                fill="#ccfbf1"
                                fillOpacity={0.6}
                                strokeWidth={2}
                                unit="R$"
                              />
                              <Line
                                type="monotone"
                                dataKey="ticket_medio"
                                name="Ticket Médio (R$)"
                                stroke="#8b5cf6"
                                strokeWidth={2}
                                dot={{ r: 3 }}
                                unit="R$"
                              />
                              {concatenatedStats.via > 0 && (
                                <ReferenceLine
                                  y={Number(concatenatedStats.via)}
                                  stroke="#0d9488"
                                  strokeDasharray="4 4"
                                  strokeWidth={2}
                                  label={{ value: 'Ref VIA', position: 'insideTopRight', fill: '#0f766e', fontSize: 11 }}
                                />
                              )}
                            </ComposedChart>
                          </ResponsiveContainer>
                        </div>
                      </div>
                    )}

                    {/* Gráfico 6: Distribuição e Performance por Dia da Semana Consolidado / Por Meta */}
                    {(concatChartCategory === 'all' || concatChartCategory === 'weekday' || concatChartCategory === 'multi_goals') && (
                      <div className="bi-modal-chart-card full-width">
                        <div className="bi-modal-chart-header">
                          <div className="bi-modal-chart-title">
                            <FiCalendar className="text-warning" /> Performance por Dia da Semana {concatenatedStats.isolatedGoals?.length > 1 ? (concatWeekdayMode === 'by_goal' ? 'por Meta' : '(Consolidado)') : '(Consolidado)'}
                          </div>
                          <div className="d-flex align-items-center gap-2 flex-wrap">
                            {concatenatedStats.isolatedGoals?.length > 1 && (
                              <div className="btn-group btn-group-sm" role="group">
                                <button
                                  type="button"
                                  className={`btn btn-sm ${concatWeekdayMode === 'by_goal' ? 'btn-primary' : 'btn-outline-secondary'}`}
                                  onClick={() => setConcatWeekdayMode('by_goal')}
                                  title="Exibir barras individuais para cada meta selecionada"
                                >
                                  Por Meta ({concatenatedStats.isolatedGoals.length})
                                </button>
                                <button
                                  type="button"
                                  className={`btn btn-sm ${concatWeekdayMode === 'consolidated' ? 'btn-primary' : 'btn-outline-secondary'}`}
                                  onClick={() => setConcatWeekdayMode('consolidated')}
                                  title="Exibir faturamento consolidado somado"
                                >
                                  Consolidado
                                </button>
                              </div>
                            )}
                            <div className="bi-chart-type-toggle">
                              <button
                                type="button"
                                className={`bi-chart-type-btn ${concatChartTypes.weekday === 'composed' ? 'active' : ''}`}
                                onClick={() => toggleConcatChartType('weekday', 'composed')}
                                title="Visualizar Misto (Barras + Linha)"
                              >
                                Misto
                              </button>
                              <button
                                type="button"
                                className={`bi-chart-type-btn ${concatChartTypes.weekday === 'bar' ? 'active' : ''}`}
                                onClick={() => toggleConcatChartType('weekday', 'bar')}
                                title="Visualizar em Barras"
                              >
                                Barras
                              </button>
                              <button
                                type="button"
                                className={`bi-chart-type-btn ${concatChartTypes.weekday === 'line' ? 'active' : ''}`}
                                onClick={() => toggleConcatChartType('weekday', 'line')}
                                title="Visualizar em Linhas"
                              >
                                Linhas
                              </button>
                              <button
                                type="button"
                                className={`bi-chart-type-btn ${concatChartTypes.weekday === 'area' ? 'active' : ''}`}
                                onClick={() => toggleConcatChartType('weekday', 'area')}
                                title="Visualizar em Área"
                              >
                                Área
                              </button>
                            </div>
                            <span className="bi-modal-chart-badge">
                              {concatenatedStats.isolatedGoals?.length > 1 && concatWeekdayMode === 'by_goal'
                                ? `${concatenatedStats.isolatedGoals.length} Metas Comparadas`
                                : 'Padrão de Vendas & Comportamento Consolidado'}
                            </span>
                          </div>
                        </div>

                        {(() => {
                          const isByGoal = concatWeekdayMode === 'by_goal' && (concatenatedStats.isolatedGoals?.length || 0) > 1;
                          return (
                            <>
                              <div className="bi-chart-body">
                                <ResponsiveContainer width="100%" height={isByGoal ? 330 : 280}>
                                  <ComposedChart data={concatenatedStats.weekdayData} margin={{ top: 10, right: 20, left: 10, bottom: 0 }}>
                                    <defs>
                                      <linearGradient id="concatColorWeekdayNet" x1="0" y1="0" x2="0" y2="1">
                                        <stop offset="5%" stopColor="#fb923c" stopOpacity={0.4} />
                                        <stop offset="95%" stopColor="#fb923c" stopOpacity={0.0} />
                                      </linearGradient>
                                      {isByGoal && concatenatedStats.isolatedGoals.map((g) => (
                                        <linearGradient key={`grad_w_${g.goal_id}`} id={`concatColorWeekday_${g.goal_id}`} x1="0" y1="0" x2="0" y2="1">
                                          <stop offset="5%" stopColor={g.color} stopOpacity={0.45} />
                                          <stop offset="95%" stopColor={g.color} stopOpacity={0.05} />
                                        </linearGradient>
                                      ))}
                                    </defs>
                                    <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#e2e8f0" />
                                    <XAxis dataKey="name" stroke="#64748b" fontSize={12} tickLine={false} />
                                    <YAxis
                                      yAxisId="left"
                                      stroke="#f97316"
                                      fontSize={12}
                                      tickLine={false}
                                      tickFormatter={(v) => concatenatedStats.areAllQtyGoals ? `${v} ${concatenatedStats.qtyUnitLabel}` : `R$ ${(v / 1000).toFixed(0)}k`}
                                    />
                                    <YAxis
                                      yAxisId="right"
                                      orientation="right"
                                      stroke="#8b5cf6"
                                      fontSize={12}
                                      tickLine={false}
                                      tickFormatter={(v) => `R$ ${v}`}
                                    />
                                    <Tooltip content={<CustomChartTooltip />} />
                                    <Legend verticalAlign="top" height={isByGoal && concatenatedStats.isolatedGoals.length > 3 ? 42 : 32} />

                                    {isByGoal ? (
                                      <>
                                        {concatChartTypes.weekday === 'composed' && (
                                          <>
                                            {concatenatedStats.isolatedGoals.map((g) => (
                                              <Bar
                                                key={g.goal_id}
                                                yAxisId="left"
                                                dataKey={isQuantitySubtype(g.goal_subtype) ? `goal_${g.goal_id}_qty` : `goal_${g.goal_id}_net`}
                                                name={`${g.name} (${g.periodLabel})`}
                                                fill={g.color}
                                                radius={[4, 4, 0, 0]}
                                                unit={isQuantitySubtype(g.goal_subtype) ? (g.goal_subtype === 'clientes_positivados' ? 'cli' : 'un') : 'R$'}
                                              />
                                            ))}
                                            <Line
                                              yAxisId="right"
                                              type="monotone"
                                              dataKey="ticket_medio"
                                              name="Ticket Médio Geral (R$)"
                                              stroke="#8b5cf6"
                                              strokeWidth={3}
                                              strokeDasharray="4 4"
                                              dot={{ r: 4 }}
                                              unit="R$"
                                            />
                                          </>
                                        )}

                                        {concatChartTypes.weekday === 'bar' && (
                                          <>
                                            {concatenatedStats.isolatedGoals.map((g) => (
                                              <Bar
                                                key={g.goal_id}
                                                yAxisId="left"
                                                dataKey={isQuantitySubtype(g.goal_subtype) ? `goal_${g.goal_id}_qty` : `goal_${g.goal_id}_net`}
                                                name={`${g.name} (${g.periodLabel})`}
                                                fill={g.color}
                                                radius={[4, 4, 0, 0]}
                                                unit={isQuantitySubtype(g.goal_subtype) ? (g.goal_subtype === 'clientes_positivados' ? 'cli' : 'un') : 'R$'}
                                              />
                                            ))}
                                          </>
                                        )}

                                        {concatChartTypes.weekday === 'line' && (
                                          <>
                                            {concatenatedStats.isolatedGoals.map((g) => (
                                              <Line
                                                key={g.goal_id}
                                                yAxisId="left"
                                                type="monotone"
                                                dataKey={isQuantitySubtype(g.goal_subtype) ? `goal_${g.goal_id}_qty` : `goal_${g.goal_id}_net`}
                                                name={`${g.name} (${g.periodLabel})`}
                                                stroke={g.color}
                                                strokeWidth={2.5}
                                                dot={{ r: 4, fill: g.color }}
                                                activeDot={{ r: 6 }}
                                                unit={isQuantitySubtype(g.goal_subtype) ? (g.goal_subtype === 'clientes_positivados' ? 'cli' : 'un') : 'R$'}
                                              />
                                            ))}
                                            <Line
                                              yAxisId="right"
                                              type="monotone"
                                              dataKey="ticket_medio"
                                              name="Ticket Médio Geral (R$)"
                                              stroke="#8b5cf6"
                                              strokeWidth={2}
                                              strokeDasharray="4 4"
                                              dot={{ r: 3 }}
                                              unit="R$"
                                            />
                                          </>
                                        )}

                                        {concatChartTypes.weekday === 'area' && (
                                          <>
                                            {concatenatedStats.isolatedGoals.map((g) => (
                                              <Area
                                                key={g.goal_id}
                                                yAxisId="left"
                                                type="monotone"
                                                dataKey={isQuantitySubtype(g.goal_subtype) ? `goal_${g.goal_id}_qty` : `goal_${g.goal_id}_net`}
                                                name={`${g.name} (${g.periodLabel})`}
                                                stroke={g.color}
                                                fill={`url(#concatColorWeekday_${g.goal_id})`}
                                                fillOpacity={0.35}
                                                strokeWidth={2}
                                                unit={isQuantitySubtype(g.goal_subtype) ? (g.goal_subtype === 'clientes_positivados' ? 'cli' : 'un') : 'R$'}
                                              />
                                            ))}
                                            <Line
                                              yAxisId="right"
                                              type="monotone"
                                              dataKey="ticket_medio"
                                              name="Ticket Médio Geral (R$)"
                                              stroke="#8b5cf6"
                                              strokeWidth={2}
                                              strokeDasharray="4 4"
                                              dot={{ r: 3 }}
                                              unit="R$"
                                            />
                                          </>
                                        )}
                                      </>
                                    ) : (
                                      <>
                                        {concatChartTypes.weekday === 'composed' && (
                                          <>
                                            <Bar
                                              yAxisId="left"
                                              dataKey={concatenatedStats.areAllQtyGoals ? (concatenatedStats.areAllClients ? 'total_orders' : 'total_items') : "total_net"}
                                              name={concatenatedStats.areAllQtyGoals ? (concatenatedStats.areAllClients ? 'Clientes Atendidos' : 'Itens Vendidos') : "Faturamento Total (R$)"}
                                              fill="#fb923c"
                                              radius={[4, 4, 0, 0]}
                                              unit={concatenatedStats.areAllQtyGoals ? concatenatedStats.qtyUnitLabel : "R$"}
                                            />
                                            <Line
                                              yAxisId="right"
                                              type="monotone"
                                              dataKey="ticket_medio"
                                              name="Ticket Médio (R$)"
                                              stroke="#8b5cf6"
                                              strokeWidth={3}
                                              dot={{ r: 4 }}
                                              unit="R$"
                                            />
                                          </>
                                        )}

                                        {concatChartTypes.weekday === 'bar' && (
                                          <>
                                            <Bar
                                              yAxisId="left"
                                              dataKey={concatenatedStats.areAllQtyGoals ? (concatenatedStats.areAllClients ? 'total_orders' : 'total_items') : "total_net"}
                                              name={concatenatedStats.areAllQtyGoals ? (concatenatedStats.areAllClients ? 'Clientes Atendidos' : 'Itens Vendidos') : "Faturamento Total (R$)"}
                                              fill="#fb923c"
                                              radius={[4, 4, 0, 0]}
                                              unit={concatenatedStats.areAllQtyGoals ? concatenatedStats.qtyUnitLabel : "R$"}
                                            />
                                            <Bar
                                              yAxisId="right"
                                              dataKey="ticket_medio"
                                              name="Ticket Médio (R$)"
                                              fill="#8b5cf6"
                                              radius={[4, 4, 0, 0]}
                                              unit="R$"
                                            />
                                          </>
                                        )}

                                        {concatChartTypes.weekday === 'line' && (
                                          <>
                                            <Line
                                              yAxisId="left"
                                              type="monotone"
                                              dataKey={concatenatedStats.areAllQtyGoals ? (concatenatedStats.areAllClients ? 'total_orders' : 'total_items') : "total_net"}
                                              name={concatenatedStats.areAllQtyGoals ? (concatenatedStats.areAllClients ? 'Clientes Atendidos' : 'Itens Vendidos') : "Faturamento Total (R$)"}
                                              stroke="#fb923c"
                                              strokeWidth={3}
                                              dot={{ r: 4 }}
                                              unit={concatenatedStats.areAllQtyGoals ? concatenatedStats.qtyUnitLabel : "R$"}
                                            />
                                            <Line
                                              yAxisId="right"
                                              type="monotone"
                                              dataKey="ticket_medio"
                                              name="Ticket Médio (R$)"
                                              stroke="#8b5cf6"
                                              strokeWidth={3}
                                              dot={{ r: 4 }}
                                              unit="R$"
                                            />
                                          </>
                                        )}

                                        {concatChartTypes.weekday === 'area' && (
                                          <>
                                            <Area
                                              yAxisId="left"
                                              type="monotone"
                                              dataKey={concatenatedStats.areAllQtyGoals ? (concatenatedStats.areAllClients ? 'total_orders' : 'total_items') : "total_net"}
                                              name={concatenatedStats.areAllQtyGoals ? (concatenatedStats.areAllClients ? 'Clientes Atendidos' : 'Itens Vendidos') : "Faturamento Total (R$)"}
                                              stroke="#fb923c"
                                              strokeWidth={3}
                                              fillOpacity={1}
                                              fill="url(#concatColorWeekdayNet)"
                                              unit={concatenatedStats.areAllQtyGoals ? concatenatedStats.qtyUnitLabel : "R$"}
                                            />
                                            <Line
                                              yAxisId="right"
                                              type="monotone"
                                              dataKey="ticket_medio"
                                              name="Ticket Médio (R$)"
                                              stroke="#8b5cf6"
                                              strokeWidth={3}
                                              dot={{ r: 4 }}
                                              unit="R$"
                                            />
                                          </>
                                        )}
                                      </>
                                    )}
                                  </ComposedChart>
                                </ResponsiveContainer>
                              </div>

                              {/* Cartões de Indicadores por Dia da Semana (Consolidado com Detalhe por Meta) */}
                              <div className="d-grid gap-2 mt-3" style={{ gridTemplateColumns: isByGoal ? 'repeat(auto-fit, minmax(150px, 1fr))' : 'repeat(auto-fit, minmax(130px, 1fr))' }}>
                                {(concatenatedStats.weekdayData || []).map((w, idx) => (
                                  <div key={idx} className="bi-weekday-card">
                                    <span className="bi-weekday-name">{w.short}</span>
                                    <span className="bi-weekday-val">
                                      {concatenatedStats.areAllQtyGoals
                                        ? `${(concatenatedStats.areAllClients ? w.total_orders : w.total_items)} ${concatenatedStats.qtyUnitLabel}`
                                        : formatBRL(w.total_net)}
                                    </span>
                                    <span className="bi-weekday-sub">
                                      {concatenatedStats.areAllQtyGoals
                                        ? `Média: ${w.count > 0 ? ((concatenatedStats.areAllClients ? w.total_orders : w.total_items) / w.count).toFixed(1) : 0} ${concatenatedStats.qtyUnitLabel}/dia`
                                        : `TM: ${formatBRL(w.ticket_medio)} • ${w.total_orders} ped`}
                                    </span>
                                    {isByGoal && (
                                      <div className="mt-2 pt-2 border-top d-flex flex-column gap-1" style={{ fontSize: '0.73rem' }}>
                                        {concatenatedStats.isolatedGoals.map((g) => {
                                          const gIsQty = isQuantitySubtype(g.goal_subtype);
                                          const gVal = gIsQty ? (g.goal_subtype === 'clientes_positivados' ? (w[`goal_${g.goal_id}_orders`] || 0) : (w[`goal_${g.goal_id}_items`] || 0)) : (w[`goal_${g.goal_id}_net`] || 0);
                                          const gFormatted = gIsQty ? `${gVal} ${g.goal_subtype === 'clientes_positivados' ? 'cli' : 'un'}` : formatBRL(gVal);
                                          return (
                                            <div key={g.goal_id} className="d-flex align-items-center justify-content-between text-truncate" title={`${g.name}: ${gFormatted}`}>
                                              <span className="d-flex align-items-center gap-1 text-truncate" style={{ maxWidth: '62%' }}>
                                                <span style={{ width: 8, height: 8, borderRadius: '50%', backgroundColor: g.color, display: 'inline-block', flexShrink: 0 }} />
                                                <span className="text-truncate">{g.shortName || g.name}</span>
                                              </span>
                                              <span className="fw-bold text-muted">{gFormatted}</span>
                                            </div>
                                          );
                                        })}
                                      </div>
                                    )}
                                  </div>
                                ))}
                              </div>
                            </>
                          );
                        })()}
                      </div>
                    )}

                    {/* Gráfico 7: Meta vs Realizado por Meta Selecionada (Isolados) */}
                    {(concatChartCategory === 'all' || concatChartCategory === 'multi_goals') && (
                      <div className="bi-modal-chart-card">
                        <div className="bi-modal-chart-header">
                          <div className="bi-modal-chart-title">
                            <FiDollarSign className="text-primary" /> {concatenatedStats.areAllQtyGoals ? (concatenatedStats.areAllClients ? 'Meta vs Realizado de Clientes por Meta' : 'Meta vs Realizado de Produtos por Meta') : 'Meta vs Realizado por Meta Selecionada'}
                          </div>
                          <div className="d-flex align-items-center gap-2">
                            <div className="bi-chart-type-toggle">
                              <button
                                type="button"
                                className={`bi-chart-type-btn ${concatChartTypes.salesTarget === 'bar' ? 'active' : ''}`}
                                onClick={() => toggleConcatChartType('salesTarget', 'bar')}
                              >
                                Barras
                              </button>
                              <button
                                type="button"
                                className={`bi-chart-type-btn ${concatChartTypes.salesTarget === 'line' ? 'active' : ''}`}
                                onClick={() => toggleConcatChartType('salesTarget', 'line')}
                              >
                                Linhas
                              </button>
                              <button
                                type="button"
                                className={`bi-chart-type-btn ${concatChartTypes.salesTarget === 'area' ? 'active' : ''}`}
                                onClick={() => toggleConcatChartType('salesTarget', 'area')}
                              >
                                Área
                              </button>
                            </div>
                            <span className="bi-modal-chart-badge">{concatenatedStats.isolatedGoals.length} Metas</span>
                          </div>
                        </div>
                        <div className="bi-chart-body">
                          <ResponsiveContainer width="100%" height={300}>
                            <ComposedChart data={concatenatedStats.isolatedGoals} margin={{ top: 15, right: 25, left: 10, bottom: 35 }}>
                              <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#e2e8f0" />
                              <XAxis dataKey="shortName" tick={{ fill: '#64748b', fontSize: 11 }} angle={-15} textAnchor="end" height={50} />
                              <YAxis tick={{ fill: '#64748b', fontSize: 11 }} tickFormatter={(v) => concatenatedStats.areAllQtyGoals ? `${v} ${concatenatedStats.qtyUnitLabel}` : `R$ ${(v / 1000).toFixed(0)}k`} />
                              <Tooltip content={<CustomChartTooltip />} />
                              <Legend verticalAlign="top" height={32} />
                              {concatChartTypes.salesTarget === 'bar' && (
                                <>
                                  <Bar dataKey="target" name={concatenatedStats.areAllQtyGoals ? `Meta Estabelecida (${concatenatedStats.qtyUnitLabel})` : 'Meta Estabelecida (R$)'} fill="#94a3b8" radius={[4, 4, 0, 0]} unit={concatenatedStats.areAllQtyGoals ? concatenatedStats.qtyUnitLabel : 'R$'} />
                                  <Bar dataKey="realized" name={concatenatedStats.areAllQtyGoals ? (concatenatedStats.areAllClients ? 'Clientes Realizados' : 'Produtos Realizados') : 'Faturamento Realizado (R$)'} fill="#10b981" radius={[4, 4, 0, 0]} unit={concatenatedStats.areAllQtyGoals ? concatenatedStats.qtyUnitLabel : 'R$'} />
                                </>
                              )}
                              {concatChartTypes.salesTarget === 'line' && (
                                <>
                                  <Line type="monotone" dataKey="target" name={concatenatedStats.areAllQtyGoals ? `Meta Estabelecida (${concatenatedStats.qtyUnitLabel})` : 'Meta Estabelecida (R$)'} stroke="#94a3b8" strokeWidth={2} strokeDasharray="4 4" dot={{ r: 4 }} unit={concatenatedStats.areAllQtyGoals ? concatenatedStats.qtyUnitLabel : 'R$'} />
                                  <Line type="monotone" dataKey="realized" name={concatenatedStats.areAllQtyGoals ? (concatenatedStats.areAllClients ? 'Clientes Realizados' : 'Produtos Realizados') : 'Faturamento Realizado (R$)'} stroke="#10b981" strokeWidth={3} dot={{ r: 5, fill: '#10b981' }} unit={concatenatedStats.areAllQtyGoals ? concatenatedStats.qtyUnitLabel : 'R$'} />
                                </>
                              )}
                              {concatChartTypes.salesTarget === 'area' && (
                                <>
                                  <Area type="monotone" dataKey="target" name={concatenatedStats.areAllQtyGoals ? `Meta Estabelecida (${concatenatedStats.qtyUnitLabel})` : 'Meta Estabelecida (R$)'} fill="#94a3b8" fillOpacity={0.15} stroke="#94a3b8" strokeWidth={2} unit={concatenatedStats.areAllQtyGoals ? concatenatedStats.qtyUnitLabel : 'R$'} />
                                  <Area type="monotone" dataKey="realized" name={concatenatedStats.areAllQtyGoals ? (concatenatedStats.areAllClients ? 'Clientes Realizados' : 'Produtos Realizados') : 'Faturamento Realizado (R$)'} fill="#10b981" fillOpacity={0.35} stroke="#10b981" strokeWidth={3} unit={concatenatedStats.areAllQtyGoals ? concatenatedStats.qtyUnitLabel : 'R$'} />
                                </>
                              )}
                            </ComposedChart>
                          </ResponsiveContainer>
                        </div>
                      </div>
                    )}

                    {/* Gráfico 8: Trajetória Temporal Diária com Curvas Isoladas por Meta */}
                    {(concatChartCategory === 'all' || concatChartCategory === 'multi_goals') && (
                      <div className="bi-modal-chart-card full-width">
                        <div className="bi-modal-chart-header">
                          <div className="bi-modal-chart-title">
                            <FiTrendingUp className="text-primary" /> Trajetória Diária com Curvas Isoladas por Meta ({concatenatedStats.isolatedGoals.length} Curvas)
                          </div>
                          <div className="d-flex align-items-center gap-2">
                            <div className="btn-group btn-group-sm" role="group">
                              <button
                                type="button"
                                className={`btn btn-sm ${concatTrajectoryMode === 'accum' ? 'btn-primary' : 'btn-outline-secondary'}`}
                                onClick={() => setConcatTrajectoryMode('accum')}
                              >
                                {concatenatedStats.areAllQtyGoals ? (concatenatedStats.areAllClients ? 'Clientes Acumulados' : 'Produtos Acumulados') : 'Faturamento Acumulado'}
                              </button>
                              <button
                                type="button"
                                className={`btn btn-sm ${concatTrajectoryMode === 'daily' ? 'btn-primary' : 'btn-outline-secondary'}`}
                                onClick={() => setConcatTrajectoryMode('daily')}
                              >
                                {concatenatedStats.areAllQtyGoals ? (concatenatedStats.areAllClients ? 'Clientes por Dia' : 'Produtos por Dia') : 'Vendas Diárias'}
                              </button>
                              <button
                                type="button"
                                className={`btn btn-sm ${concatTrajectoryMode === 'orders' ? 'btn-primary' : 'btn-outline-secondary'}`}
                                onClick={() => setConcatTrajectoryMode('orders')}
                              >
                                Pedidos por Dia
                              </button>
                            </div>
                          </div>
                        </div>
                        <div className="bi-chart-body">
                          <ResponsiveContainer width="100%" height={340}>
                            <ComposedChart data={concatenatedStats.isolatedTimeline} margin={{ top: 15, right: 25, left: 10, bottom: 20 }}>
                              <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#e2e8f0" />
                              <XAxis dataKey="shortLabel" tick={{ fill: '#64748b', fontSize: 11 }} />
                              <YAxis
                                tick={{ fill: '#64748b', fontSize: 11 }}
                                tickFormatter={(v) => concatTrajectoryMode === 'orders' ? `${v} ped` : (concatenatedStats.areAllQtyGoals ? `${v} ${concatenatedStats.qtyUnitLabel}` : `R$ ${(v / 1000).toFixed(0)}k`)}
                              />
                              <Tooltip content={<CustomChartTooltip />} />
                              <Legend wrapperStyle={{ paddingTop: '8px' }} />
                              {concatenatedStats.isolatedGoals.map((g) => {
                                const gIsQty = isQuantitySubtype(g.goal_subtype);
                                const dataKey = concatTrajectoryMode === 'accum'
                                  ? `goal_${g.goal_id}_accum`
                                  : concatTrajectoryMode === 'daily'
                                    ? (gIsQty ? `goal_${g.goal_id}_qty` : `goal_${g.goal_id}_net`)
                                    : `goal_${g.goal_id}_orders`;
                                const unit = concatTrajectoryMode === 'orders'
                                  ? 'ped'
                                  : (gIsQty ? (g.goal_subtype === 'clientes_positivados' ? 'cli' : 'un') : 'R$');
                                return (
                                  <Line
                                    key={g.goal_id}
                                    type="monotone"
                                    dataKey={dataKey}
                                    name={`${g.name} (${g.periodLabel})`}
                                    stroke={g.color}
                                    strokeWidth={2.5}
                                    dot={{ r: 3, fill: g.color }}
                                    activeDot={{ r: 6 }}
                                    unit={unit}
                                  />
                                );
                              })}
                            </ComposedChart>
                          </ResponsiveContainer>
                        </div>
                      </div>
                    )}

                    {/* Gráfico 9: Eficiência Operacional (Ticket Médio & PA por Meta) */}
                    {(concatChartCategory === 'all' || concatChartCategory === 'multi_goals') && (
                      <div className="bi-modal-chart-card">
                        <div className="bi-modal-chart-header">
                          <div className="bi-modal-chart-title">
                            <FiShoppingBag className="text-purple" /> Eficiência de Vendas: Ticket Médio (R$) e Peças por Atendimento (PA)
                          </div>
                          <span className="bi-modal-chart-badge">Ponderado vs Quantidade</span>
                        </div>
                        <div className="bi-chart-body">
                          <ResponsiveContainer width="100%" height={320}>
                            <ComposedChart data={concatenatedStats.isolatedGoals} margin={{ top: 15, right: 25, left: 10, bottom: 35 }}>
                              <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#e2e8f0" />
                              <XAxis dataKey="shortName" tick={{ fill: '#64748b', fontSize: 11 }} angle={-15} textAnchor="end" height={50} />
                              <YAxis yAxisId="left" stroke="#8b5cf6" tick={{ fill: '#8b5cf6', fontSize: 11 }} tickFormatter={(v) => `R$ ${v}`} />
                              <YAxis yAxisId="right" orientation="right" stroke="#f59e0b" tick={{ fill: '#f59e0b', fontSize: 11 }} tickFormatter={(v) => `${v} un`} />
                              <Tooltip content={<CustomChartTooltip />} />
                              <Legend verticalAlign="top" height={32} />
                              <Bar yAxisId="left" dataKey="ticketMedio" name="Ticket Médio (R$)" fill="#8b5cf6" radius={[4, 4, 0, 0]} unit="R$" />
                              <Line yAxisId="right" type="monotone" dataKey="pa" name="Itens/Atend. (PA)" stroke="#f59e0b" strokeWidth={3} dot={{ r: 5, fill: '#f59e0b' }} unit="un" />
                            </ComposedChart>
                          </ResponsiveContainer>
                        </div>
                      </div>
                    )}

                    {/* Gráfico 10: Ranking de % de Atingimento vs Linha 100% Meta */}
                    {(concatChartCategory === 'all' || concatChartCategory === 'multi_goals') && (
                      <div className="bi-modal-chart-card">
                        <div className="bi-modal-chart-header">
                          <div className="bi-modal-chart-title">
                            <FiAward className="text-purple" /> Ranking de Atingimento (%) vs Linha de Meta 100%
                          </div>
                          <span className="bi-modal-chart-badge">Comparativo de Desempenho</span>
                        </div>
                        <div className="bi-chart-body">
                          <ResponsiveContainer width="100%" height={320}>
                            <BarChart data={concatenatedStats.isolatedGoals} margin={{ top: 20, right: 25, left: 10, bottom: 35 }}>
                              <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#e2e8f0" />
                              <XAxis dataKey="shortName" tick={{ fill: '#64748b', fontSize: 11 }} angle={-15} textAnchor="end" height={50} />
                              <YAxis tick={{ fill: '#64748b', fontSize: 11 }} tickFormatter={(v) => `${v}%`} domain={[0, 'dataMax + 20']} />
                              <Tooltip formatter={(v) => `${v}%`} labelFormatter={(l) => `Meta: ${l}`} />
                              <ReferenceLine y={100} label={{ value: 'Meta 100%', fill: '#dc2626', position: 'top', fontSize: 11, fontWeight: 700 }} stroke="#dc2626" strokeDasharray="4 4" strokeWidth={2} />
                              <Legend verticalAlign="top" height={32} />
                              <Bar dataKey="pct" name="% Atingimento" radius={[4, 4, 0, 0]}>
                                {concatenatedStats.isolatedGoals.map((entry, index) => (
                                  <Cell
                                    key={`cell-pct-${index}`}
                                    fill={entry.pct >= 100 ? '#10b981' : entry.pct >= 85 ? '#f59e0b' : '#ef4444'}
                                  />
                                ))}
                              </Bar>
                            </BarChart>
                          </ResponsiveContainer>
                        </div>
                      </div>
                    )}

                    {/* Gráfico 11: Volumes Operacionais (Pedidos vs Peças Vendidas) */}
                    {(concatChartCategory === 'all' || concatChartCategory === 'multi_goals') && (
                      <div className="bi-modal-chart-card">
                        <div className="bi-modal-chart-header">
                          <div className="bi-modal-chart-title">
                            <FiPackage className="text-teal" /> Volumes Operacionais: Pedidos vs Peças Vendidas
                          </div>
                          <span className="bi-modal-chart-badge">Volume Físico & Transações</span>
                        </div>
                        <div className="bi-chart-body">
                          <ResponsiveContainer width="100%" height={320}>
                            <BarChart data={concatenatedStats.isolatedGoals} margin={{ top: 15, right: 25, left: 10, bottom: 35 }}>
                              <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#e2e8f0" />
                              <XAxis dataKey="shortName" tick={{ fill: '#64748b', fontSize: 11 }} angle={-15} textAnchor="end" height={50} />
                              <YAxis tick={{ fill: '#64748b', fontSize: 11 }} />
                              <Tooltip content={<CustomChartTooltip />} />
                              <Legend verticalAlign="top" height={32} />
                              <Bar dataKey="orders" name="Pedidos / Atendimentos" fill="#06b6d4" radius={[4, 4, 0, 0]} unit="ped" />
                              <Bar dataKey="items" name="Peças / Itens Vendidos" fill="#6366f1" radius={[4, 4, 0, 0]} unit="un" />
                            </BarChart>
                          </ResponsiveContainer>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* ABA 4: FECHAMENTOS VINCULADOS */}
              {concatenatedTab === 'reports' && (
                <div className="d-flex flex-column gap-3">
                  <div className="d-flex justify-content-between align-items-center flex-wrap gap-2 p-3 bg-light rounded-3 border">
                    <div>
                      <h6 className="fw-bold text-dark mb-0 d-flex align-items-center gap-2">
                        <FiShoppingBag className="text-primary" /> Fechamentos Consolidados de Vendas
                      </h6>
                      <span className="text-muted small">
                        Todos os fechamentos fiscais/comerciais vinculados às metas e períodos selecionados.
                      </span>
                    </div>
                    <div className="d-flex align-items-center gap-3">
                      <div className="text-end">
                        <div className="text-muted small" style={{ fontSize: '0.7rem' }}>TOTAL DE FECHAMENTOS</div>
                        <strong className="text-dark fs-6">{concatenatedStats.allReports.length}</strong>
                      </div>
                      <div className="text-end">
                        <div className="text-muted small" style={{ fontSize: '0.7rem' }}>VALOR LÍQUIDO TOTAL</div>
                        <strong className="text-success fs-6">
                          {formatBRL(concatenatedStats.allReports.reduce((acc, r) => acc + Number(r.net_value ?? r.net ?? r.raw_net ?? 0), 0))}
                        </strong>
                      </div>
                      <div className="text-end">
                        <div className="text-muted small" style={{ fontSize: '0.7rem' }}>ADICIONAIS GERADOS</div>
                        <strong className="text-primary fs-6">
                          {formatBRL(concatenatedStats.allReports.reduce((acc, r) => acc + Number(r.adicional_amount ?? r.commission_amount ?? r.bonus_amount ?? 0), 0))}
                        </strong>
                      </div>
                    </div>
                  </div>

                  {concatenatedStats.allReports.length === 0 ? (
                    <div className="p-4 text-center text-muted bg-light rounded-3 border">
                      <FiAlertCircle size={28} className="mb-2 opacity-50" />
                      <div>Nenhum fechamento vinculado encontrado para as metas selecionadas.</div>
                    </div>
                  ) : (
                    <div className="d-flex flex-column gap-2" style={{ maxHeight: '480px', overflowY: 'auto' }}>
                      {concatenatedStats.allReports.map((r, idx) => (
                        <div
                          key={r.id || idx}
                          className="d-flex justify-content-between align-items-center p-3 rounded-3 bg-white border shadow-sm flex-wrap gap-2"
                          style={{ transition: 'all 0.15s ease' }}
                        >
                          <div className="d-flex align-items-center gap-3">
                            <div
                              className="d-flex align-items-center justify-content-center rounded-circle"
                              style={{ width: '40px', height: '40px', backgroundColor: '#eff6ff', color: '#2563eb', flexShrink: 0 }}
                            >
                              <FiShoppingBag size={18} />
                            </div>
                            <div>
                              <div className="d-flex align-items-center gap-2 flex-wrap">
                                <span className="fw-bold text-dark">
                                  {r.report_number ? `Fechamento #${r.report_number}` : (r.name || `Fechamento #${r.id}`)}
                                </span>
                                {r.period_label && (
                                  <span className="badge bg-dark-subtle text-dark border" style={{ fontSize: '0.65rem', padding: '1px 6px' }}>
                                    📅 {r.period_label}
                                  </span>
                                )}
                                {r.collab_name && (
                                  <span className="badge bg-secondary-subtle text-secondary border" style={{ fontSize: '0.65rem', padding: '1px 6px' }}>
                                    {r.collab_name}
                                  </span>
                                )}
                                <span className={`badge ${r.status === 'finalized' ? 'bg-success' : 'bg-warning text-dark'}`} style={{ fontSize: '0.65rem', padding: '1px 6px' }}>
                                  {r.status === 'finalized' ? 'Finalizado' : 'Em Aberto'}
                                </span>
                              </div>
                              <div className="text-muted small mt-0.5">
                                {r.reference_date ? `Data Ref: ${formatDateBR(r.reference_date)}` : ''}
                                {r.period_start && r.period_end ? `${r.reference_date ? ' • ' : ''}Período: ${formatDateBR(r.period_start)} até ${formatDateBR(r.period_end)}` : ''}
                                {r.branch_name ? ` • Filial: ${r.branch_name}` : ''}
                              </div>
                            </div>
                          </div>

                          <div className="d-flex align-items-center gap-4 text-end">
                            <div>
                              <div className="text-muted small fw-semibold" style={{ fontSize: '0.68rem' }}>LÍQUIDO</div>
                              <div className="fw-bold text-dark">{formatBRL(Number(r.net_value ?? r.net ?? r.raw_net ?? 0))}</div>
                            </div>
                            <div>
                              <div className="text-muted small fw-semibold" style={{ fontSize: '0.68rem' }}>ADICIONAL</div>
                              <div className="fw-bold text-primary">{formatBRL(Number(r.adicional_amount ?? r.commission_amount ?? r.bonus_amount ?? 0))}</div>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}

              {/* ABA 5: DIAGNÓSTICO COMPARATIVO & IA */}
              {concatenatedTab === 'ai_diagnosis' && (
                <div className="d-flex flex-column gap-3">
                  {/* Top Header Card com Botões de Ação da IA */}
                  <div className="p-3 bg-white rounded-3 border shadow-sm d-flex justify-content-between align-items-center flex-wrap gap-3">
                    <div className="d-flex align-items-center gap-3">
                      <div
                        className="bi-collab-avatar"
                        style={{
                          width: '42px',
                          height: '42px',
                          fontSize: '1.1rem',
                          background: 'linear-gradient(135deg, #6366f1, #8b5cf6)',
                          color: '#fff',
                          borderRadius: '10px',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          boxShadow: '0 4px 10px rgba(99, 102, 241, 0.25)'
                        }}
                      >
                        <FiCpu size={22} />
                      </div>
                      <div>
                        <div className="d-flex align-items-center gap-2">
                          <h6 className="fw-bold text-dark mb-0">Diagnóstico Executivo Consolidado com IA</h6>
                          {concatAIReport && (
                            <span className="badge bg-success-subtle text-success border border-success-subtle d-inline-flex align-items-center gap-1" style={{ fontSize: '0.72rem' }}>
                              <FiCheckCircle size={11} /> Diagnóstico Ativo
                            </span>
                          )}
                        </div>
                        <span className="text-muted small">
                          Análise preditiva, auditoria de dispersão entre metas e diretrizes táticas para a liderança
                        </span>
                      </div>
                    </div>

                    <div className="d-flex align-items-center gap-2">
                      <button
                        type="button"
                        className="btn btn-sm btn-outline-secondary d-flex align-items-center gap-2 fw-semibold px-3 py-2"
                        onClick={() => handleOpenConcatAIModal('prompt')}
                        title="Configurar prompt executivo, variáveis e escolher o provedor de IA"
                      >
                        <FiSliders size={14} />
                        <span>Configurar Prompt</span>
                      </button>

                      <button
                        type="button"
                        className="btn btn-sm btn-primary d-flex align-items-center gap-2 fw-bold px-3 py-2 shadow-sm"
                        style={{
                          background: 'linear-gradient(135deg, #6366f1, #4f46e5)',
                          border: 'none'
                        }}
                        disabled={concatAIGenerating}
                        onClick={() => handleOpenConcatAIModal('execution')}
                        title="Acionar motor de IA para auditar os indicadores e gerar o diagnóstico"
                      >
                        {concatAIGenerating ? (
                          <>
                            <FiRefreshCw className="bi-spin" size={14} />
                            <span>Analisando Metas...</span>
                          </>
                        ) : (
                          <>
                            <FiZap size={14} />
                            <span>{concatAIReport ? 'Re-executar Diagnóstico com IA' : 'Gerar Diagnóstico com IA'}</span>
                          </>
                        )}
                      </button>
                    </div>
                  </div>

                  {/* Parecer e Diretrizes Geradas pela IA */}
                  {concatAIReport && (
                    <div className="d-flex flex-column gap-3">
                      {/* Parâmetros Calibrados pela IA */}
                      {concatAIUpdatedParams && (
                        <div className="bi-ai-sync-section mb-0">
                          <div className="bi-ai-sync-header">
                            <h6 className="bi-ai-sync-title">
                              <FiActivity size={16} className="text-success" />
                              Diretrizes Estratégicas Calibradas pela IA
                            </h6>
                            <span className="bi-ai-sync-badge-live">
                              <FiCheck size={12} /> Consolidado Executivo
                            </span>
                          </div>

                          <div className="bi-ai-sync-grid">
                            <div className="bi-ai-sync-card">
                              <span className="bi-ai-sync-label text-primary">
                                <FiTarget size={13} /> Perfil do Grupo
                              </span>
                              <span className="bi-ai-sync-value">{concatAIUpdatedParams.commercial_profile || 'Consolidado Multi-Metas'}</span>
                            </div>

                            <div className="bi-ai-sync-card">
                              <span className="bi-ai-sync-label text-danger">
                                <FiAlertCircle size={13} /> Gargalo Identificado
                              </span>
                              <span className="bi-ai-sync-value">{concatAIUpdatedParams.bottleneck_indicator}</span>
                            </div>

                            <div className="bi-ai-sync-card">
                              <span className="bi-ai-sync-label text-info">
                                <FiZap size={13} /> Alavanca Tática
                              </span>
                              <span className="bi-ai-sync-value">{concatAIUpdatedParams.tactical_lever}</span>
                            </div>

                            <div className="bi-ai-sync-card">
                              <span className="bi-ai-sync-label text-purple">
                                <FiPackage size={13} /> Ritmo Diário / Peças
                              </span>
                              <span className="bi-ai-sync-value">{concatAIUpdatedParams.daily_pieces_target}</span>
                            </div>

                            <div className="bi-ai-sync-card">
                              <span className="bi-ai-sync-label text-success">
                                <FiTrendingUp size={13} /> Potencial de Incremento
                              </span>
                              <span className="bi-ai-sync-value">{concatAIUpdatedParams.potential_tm_increase}</span>
                            </div>
                          </div>

                          {/* Roteiro / Pitch de Liderança Extraído */}
                          {concatAIUpdatedParams.pitch_script && (
                            <div className="bi-ai-pitch-box mb-0 mt-3">
                              <div className="bi-ai-pitch-header">
                                <div className="bi-ai-pitch-title">
                                  <FiAward size={14} /> Briefing de Alinhamento da Liderança de Loja
                                </div>
                                <button
                                  type="button"
                                  className="btn btn-sm btn-link text-purple p-0 d-flex align-items-center gap-1 text-decoration-none fw-bold"
                                  style={{ fontSize: '0.78rem', cursor: 'pointer' }}
                                  onClick={() => {
                                    copyToClipboard(concatAIUpdatedParams.pitch_script, () => {
                                      setConcatAICopiedPitch(true);
                                      setTimeout(() => setConcatAICopiedPitch(false), 2500);
                                    });
                                  }}
                                >
                                  {concatAICopiedPitch ? (
                                    <>
                                      <FiCheck className="text-success" size={13} />
                                      <span className="text-success">Copiado!</span>
                                    </>
                                  ) : (
                                    <>
                                      <FiCopy size={13} />
                                      <span>Copiar Briefing</span>
                                    </>
                                  )}
                                </button>
                              </div>
                              <p className="bi-ai-pitch-text">
                                "{concatAIUpdatedParams.pitch_script}"
                              </p>
                            </div>
                          )}
                        </div>
                      )}

                      {/* Parecer Completo da IA em Markdown */}
                      <div className="bi-ai-markdown-box">
                        <div className="d-flex align-items-center justify-content-between mb-2 border-bottom pb-2">
                          <span className="fw-bold text-dark small d-flex align-items-center gap-1">
                            <FiCode size={14} /> Parecer Executivo Detalhado
                          </span>
                          <div className="d-flex align-items-center gap-2">
                            {concatAIProviderInfo && (
                              <span className="text-muted small" style={{ fontSize: '0.75rem' }}>
                                Processado por: <strong>{concatAIProviderInfo.name}</strong> ({concatAIProviderInfo.model})
                              </span>
                            )}
                            <button
                              type="button"
                              className="btn btn-sm btn-outline-secondary py-0 px-2"
                              style={{ fontSize: '0.75rem' }}
                              onClick={() => {
                                copyToClipboard(concatAIReport, () => {
                                  setConcatAICopiedReport(true);
                                  setTimeout(() => setConcatAICopiedReport(false), 2500);
                                });
                              }}
                            >
                              {concatAICopiedReport ? (
                                <>
                                  <FiCheck className="text-success me-1" size={12} />
                                  <span className="text-success">Copiado!</span>
                                </>
                              ) : (
                                <>
                                  <FiCopy size={12} className="me-1" /> Copiar Parecer
                                </>
                              )}
                            </button>
                          </div>
                        </div>
                        <div
                          className="bi-ai-markdown-content"
                          dangerouslySetInnerHTML={{ __html: marked.parse(concatAIReport) }}
                        />
                      </div>
                    </div>
                  )}

                  {/* Caso ainda não tenha gerado relatório IA: Card explicativo com chamada para ação */}
                  {!concatAIReport && (
                    <div className="p-4 bg-white rounded-3 border shadow-sm text-center">
                      <h5 className="fw-bold text-dark mb-1">Auditoria & Diagnóstico Executivo com IA</h5>
                      <p className="text-muted small mx-auto mb-3" style={{ maxWidth: '580px' }}>
                        Analise de forma agregada as {concatenatedStats.selectedGoals.length} metas e períodos selecionados.
                        O motor de IA audita os dados em tempo real através de ferramentas analíticas, calcula dispersão,
                        recomenda alavancas de produtos ISCA e gera um plano tático executivo com metas diárias.
                      </p>
                      <div className="d-flex justify-content-center gap-2">
                        <button
                          type="button"
                          className="btn btn-outline-secondary d-flex align-items-center gap-2 fw-semibold px-3 py-2"
                          onClick={() => handleOpenConcatAIModal('prompt')}
                        >
                          <FiSliders size={15} />
                          <span>Configurar Prompt</span>
                        </button>
                        <button
                          type="button"
                          className="btn btn-primary d-flex align-items-center gap-2 fw-bold px-4 py-2 shadow-sm"
                          style={{
                            background: 'linear-gradient(135deg, #6366f1, #4f46e5)',
                            border: 'none'
                          }}
                          onClick={() => handleOpenConcatAIModal('execution')}
                        >
                          <FiZap size={16} />
                          <span>Acionar Diagnóstico com IA Agora</span>
                        </button>
                      </div>
                    </div>
                  )}

                  {/* Card com os dados históricos e variação entre períodos ou metas */}
                  <div className="p-4 bg-light rounded-3 border">
                    <div className="d-flex align-items-center gap-2 mb-3">
                      <div className="bi-collab-avatar" style={{ width: '36px', height: '36px', fontSize: '0.9rem', background: 'linear-gradient(135deg, #6366f1, #4f46e5)' }}>
                        <FiBarChart2 size={18} />
                      </div>
                      <div>
                        <h6 className="fw-bold text-dark mb-0">
                          {concatenatedStats.periods.length > 1
                            ? 'Análise Histórica de Tendência Multi-Período'
                            : 'Análise Comparativa de Desempenho entre as Metas'}
                        </h6>
                        <span className="text-muted small">
                          {concatenatedStats.periods.length > 1
                            ? 'Evolução empírica comparativa dos períodos selecionados'
                            : `Dispersão de performance entre as ${concatenatedStats.selectedGoals.length} metas selecionadas (${concatenatedStats.periods[0] || ''})`}
                        </span>
                      </div>
                    </div>

                    {(() => {
                      const isMultiPeriod = concatenatedStats.periods.length > 1;
                      const itemsToCompare = isMultiPeriod
                        ? concatenatedStats.sideBySidePeriods
                        : concatenatedStats.sideBySideGoals;
                      if (!itemsToCompare || itemsToCompare.length === 0) return null;

                      const bestItem = [...itemsToCompare].sort((a, b) => b.pct - a.pct)[0];
                      const lowestItem = [...itemsToCompare].sort((a, b) => a.pct - b.pct)[0];
                      const highestSales = [...itemsToCompare].sort((a, b) => b.realized - a.realized)[0];
                      const lowestSales = [...itemsToCompare].sort((a, b) => a.realized - b.realized)[0];
                      const activeVars = isMultiPeriod ? concatenatedStats.periodVariations : concatenatedStats.goalVariations;

                      return (
                        <div className="d-flex flex-column gap-3">
                          <div className="row g-3">
                            <div className="col-12 col-md-4">
                              <div className="p-3 bg-white rounded-3 border shadow-sm">
                                <span className="text-muted small fw-semibold">
                                  {isMultiPeriod ? 'MELHOR PERFORMANCE (% ATINGIMENTO)' : 'META COM MAIOR ATINGIMENTO'}
                                </span>
                                <div className="h5 fw-bold text-success mb-1 mt-1 text-truncate" title={bestItem.name || bestItem.period}>
                                  {isMultiPeriod ? `📅 ${bestItem.period}` : bestItem.name}
                                </div>
                                <div className="text-muted small">
                                  Atingiu <strong>{bestItem.pct}%</strong> da meta ({formatBRL(bestItem.realized)})
                                </div>
                              </div>
                            </div>
                            <div className="col-12 col-md-4">
                              <div className="p-3 bg-white rounded-3 border shadow-sm">
                                <span className="text-muted small fw-semibold">
                                  {isMultiPeriod ? 'MAIOR FATURAMENTO BRUTO' : 'META COM MAIOR FATURAMENTO'}
                                </span>
                                <div className="h5 fw-bold text-primary mb-1 mt-1 text-truncate" title={highestSales.name || highestSales.period}>
                                  {isMultiPeriod ? `📅 ${highestSales.period}` : highestSales.name}
                                </div>
                                <div className="text-muted small">
                                  Volume recorde de <strong>{formatBRL(highestSales.realized)}</strong> faturados
                                </div>
                              </div>
                            </div>
                            <div className="col-12 col-md-4">
                              <div className="p-3 bg-white rounded-3 border shadow-sm">
                                <span className="text-muted small fw-semibold">
                                  {isMultiPeriod ? 'PERÍODO MAIS DESAFIADOR' : 'META COM MAIOR DESAFIO (MENOR %)'}
                                </span>
                                <div className="h5 fw-bold text-danger mb-1 mt-1 text-truncate" title={lowestItem.name || lowestItem.period}>
                                  {isMultiPeriod ? `📅 ${lowestItem.period}` : lowestItem.name}
                                </div>
                                <div className="text-muted small">
                                  Atingiu <strong>{lowestItem.pct}%</strong> da meta (Gap de {formatBRL(lowestItem.gap)})
                                </div>
                              </div>
                            </div>
                          </div>

                          <div className="p-3 bg-white rounded-3 border shadow-sm mt-2">
                            <h6 className="fw-bold text-dark mb-2">💡 Parecer de Tendência & Recomendações Estratégicas:</h6>
                            <ul className="mb-0 text-muted small ps-3 d-flex flex-column gap-2" style={{ lineHeight: 1.6 }}>
                              {isMultiPeriod ? (
                                <li>
                                  <strong>Variação de Receita:</strong> Entre o primeiro período analisado ({itemsToCompare[0].period}) e o último ({itemsToCompare[itemsToCompare.length - 1].period}), o faturamento apresentou variação de{' '}
                                  <strong className={activeVars?.sales_diff >= 0 ? 'text-success' : 'text-danger'}>
                                    {activeVars?.sales_diff >= 0 ? '+' : ''}{activeVars?.sales_pct}% ({formatBRL(activeVars?.sales_diff || 0)})
                                  </strong>.
                                </li>
                              ) : (
                                <li>
                                  <strong>Dispersão de Faturamento:</strong> Entre as metas selecionadas, a de maior faturamento ({highestSales.name} - {formatBRL(highestSales.realized)}) superou a de menor faturamento ({lowestSales.name} - {formatBRL(lowestSales.realized)}) em{' '}
                                  <strong className="text-primary">
                                    +{activeVars?.sales_pct}% ({formatBRL(activeVars?.sales_diff || 0)})
                                  </strong>.
                                </li>
                              )}
                              {!isMultiPeriod && (
                                <li>
                                  <strong>Dispersão no Atingimento de Metas:</strong> Há uma amplitude de{' '}
                                  <strong className="text-dark">
                                    {activeVars?.pct_points} pontos percentuais
                                  </strong>{' '}
                                  entre a meta mais adiantada ({bestItem.name}: {bestItem.pct}%) e a mais desafiadora ({lowestItem.name}: {lowestItem.pct}%).
                                </li>
                              )}
                              <li>
                                <strong>Comportamento do Ticket Médio:</strong>{' '}
                                {isMultiPeriod ? (
                                  <>
                                    O ticket médio oscilou de {formatBRL(itemsToCompare[0].ticket_medio)} para {formatBRL(itemsToCompare[itemsToCompare.length - 1].ticket_medio)}, representando uma variação de{' '}
                                    <strong className={activeVars?.ticket_diff >= 0 ? 'text-success' : 'text-danger'}>
                                      {activeVars?.ticket_diff >= 0 ? '+' : ''}{activeVars?.ticket_pct}%
                                    </strong>.
                                  </>
                                ) : (
                                  <>
                                    A amplitude do ticket médio entre os colaboradores analisados foi de{' '}
                                    <strong className="text-dark">
                                      {formatBRL(activeVars?.ticket_diff || 0)} (+{activeVars?.ticket_pct}%)
                                    </strong>.
                                  </>
                                )}
                              </li>
                              <li>
                                <strong>Densidade de Vendas (PA):</strong> A média de itens por atendimento fechou consolidada em <strong>{Number(concatenatedStats.pa).toFixed(2)} unidades</strong>. {concatenatedStats.pa < 2 ? 'Recomenda-se incentivar técnicas de cross-selling (venda casada) para elevar o PA acima de 2.0 un.' : 'A densidade de produtos no carrinho demonstra boa capacidade de agregação de valor.'}
                              </li>
                              <li>
                                <strong>Consistência Operacional:</strong> A probabilidade média consolidada resultou em status <strong>{concatenatedStats.pct >= 100 ? 'Meta Batida' : concatenatedStats.pct >= 85 ? 'Alta' : concatenatedStats.pct >= 70 ? 'Média' : 'Em Risco'}</strong> com atingimento geral de <strong>{concatenatedStats.pct}%</strong>.
                              </li>
                            </ul>
                          </div>
                        </div>
                      );
                    })()}
                  </div>
                </div>
              )}
            </div>

            {/* Rodapé do Modal */}
            <div className="bi-modal-footer d-flex justify-content-between align-items-center">
              <span className="text-muted small">
                Consolidando {concatenatedStats.selectedGoals.length} períodos/metas selecionadas em Performance Individual.
              </span>
              <div className="d-flex align-items-center gap-2">
                <button
                  type="button"
                  className="btn-modern btn-modern-light d-inline-flex align-items-center gap-1.5"
                  onClick={() => setIsConcatenatedFullscreen(prev => !prev)}
                  title={isConcatenatedFullscreen ? "Restaurar tamanho normal" : "Expandir para tela cheia"}
                >
                  {isConcatenatedFullscreen ? <><FiMinimize2 size={14} /> Restaurar</> : <><FiMaximize2 size={14} /> Tela Cheia</>}
                </button>
                <button
                  type="button"
                  className="btn-modern btn-modern-light"
                  onClick={() => { setIsConcatenatedModalOpen(false); setIsConcatenatedFullscreen(false); }}
                >
                  Fechar
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ====================================================================
          MODAL INTERATIVO: ANÁLISE AO VIVO COM IA, PROGRESSO, FERRAMENTAS E PROMPT
          ==================================================================== */}
      {isAIModalOpen && selectedCollab && (
        <div className="bi-ai-modal-overlay" onClick={() => setIsAIModalOpen(false)}>
          <div className="bi-ai-modal-container" onClick={(e) => e.stopPropagation()}>
            {/* Cabeçalho do Modal */}
            <div className="bi-ai-modal-header">
              <div className="bi-ai-modal-title-group">
                <div className="bi-ai-modal-icon-badge">
                  <FiCpu />
                </div>
                <div>
                  <h4 className="bi-ai-modal-title">Análise Estratégica ao Vivo com IA</h4>
                  <div className="bi-ai-modal-subtitle">
                    <span><strong>{selectedCollab.name}</strong></span>
                    <span>•</span>
                    <span>Filial {selectedCollab.branch_name || 'Geral'}</span>
                    <span>•</span>
                    <span>Meta: {formatBRL(selectedCollab.target)}</span>
                    <span>•</span>
                    <span>Gap: {formatBRL(selectedCollab.gap)}</span>
                  </div>
                </div>
              </div>
              <button
                type="button"
                className="bi-ai-modal-close-btn"
                onClick={() => setIsAIModalOpen(false)}
                title="Fechar Painel de IA"
              >
                <FiX size={18} />
              </button>
            </div>

            {/* Abas de Navegação */}
            <div className="bi-ai-modal-tabs">
              <button
                type="button"
                className={`bi-ai-modal-tab-btn ${aiModalTab === 'execution' ? 'active' : ''}`}
                onClick={() => setAiModalTab('execution')}
              >
                <FiZap size={14} />
                <span>Execução & Ferramentas em Tempo Real</span>
              </button>
              <button
                type="button"
                className={`bi-ai-modal-tab-btn ${aiModalTab === 'prompt' ? 'active' : ''}`}
                onClick={() => setAiModalTab('prompt')}
              >
                <FiSliders size={14} />
                <span>Personalizar Prompt & Provedor</span>
              </button>
            </div>

            {/* Corpo do Modal */}
            <div className="bi-ai-modal-body">
              {aiModalTab === 'execution' ? (
                <>
                  {/* Stepper de Progresso Visual */}
                  <div className="bi-ai-stepper-box">
                    <div className="bi-ai-stepper">
                      <div
                        className="bi-ai-stepper-progress-fill"
                        style={{ width: `${Math.min(100, Math.max(0, (aiExecutionStep / 4) * 100))}%` }}
                      />
                      <div className={`bi-ai-step-item ${aiExecutionStep >= 1 ? (aiExecutionStep > 1 ? 'completed' : 'active') : ''}`}>
                        <div className="bi-ai-step-circle">
                          {aiExecutionStep > 1 ? <FiCheck size={16} /> : 1}
                        </div>
                        <span className="bi-ai-step-title">1. KPIs da Meta</span>
                      </div>
                      <div className={`bi-ai-step-item ${aiExecutionStep >= 2 ? (aiExecutionStep > 2 ? 'completed' : 'active') : ''}`}>
                        <div className="bi-ai-step-circle">
                          {aiExecutionStep > 2 ? <FiCheck size={16} /> : 2}
                        </div>
                        <span className="bi-ai-step-title">2. Cesta & ISCA</span>
                      </div>
                      <div className={`bi-ai-step-item ${aiExecutionStep >= 3 ? (aiExecutionStep > 3 ? 'completed' : 'active') : ''}`}>
                        <div className="bi-ai-step-circle">
                          {aiExecutionStep > 3 ? <FiCheck size={16} /> : 3}
                        </div>
                        <span className="bi-ai-step-title">3. Giro & Margem</span>
                      </div>
                      <div className={`bi-ai-step-item ${aiExecutionStep >= 4 ? (!aiGenerating ? 'completed' : 'active') : ''}`}>
                        <div className="bi-ai-step-circle">
                          {aiExecutionStep >= 4 && !aiGenerating ? <FiCheck size={16} /> : 4}
                        </div>
                        <span className="bi-ai-step-title">4. Síntese IA</span>
                      </div>
                    </div>
                  </div>

                  {/* Banner de Status Atual */}
                  <div className={`bi-ai-status-banner ${aiGenerating ? 'running' : 'done'}`}>
                    <div className="d-flex align-items-center gap-2">
                      {aiGenerating ? (
                        <FiRefreshCw className="bi-spin text-primary" size={16} />
                      ) : (
                        <FiCheckCircle className="text-success" size={16} />
                      )}
                      <span className="fw-semibold">{aiStatusMessage}</span>
                    </div>
                    {aiProviderInfo && (
                      <div className="small text-muted d-flex align-items-center gap-1">
                        <FiCpu size={13} />
                        <span>{aiProviderInfo.name} ({aiProviderInfo.model})</span>
                      </div>
                    )}
                  </div>

                  {/* Seção 1: Ferramentas Executadas (Tools Log com Inputs e Outputs) */}
                  <div className="bi-ai-tools-section">
                    <div className="bi-ai-tools-header">
                      <h6 className="bi-ai-tools-title">
                        <FiTerminal size={15} />
                        Ferramentas Utilizadas na Coleta & Análise dos Dados
                      </h6>
                      <span className="badge bg-light text-secondary border">
                        {aiToolsLog.length} ferramentas acionadas
                      </span>
                    </div>

                    {aiToolsLog.length === 0 ? (
                      <div className="p-3 text-center text-muted small">
                        Carregando ferramentas de análise...
                      </div>
                    ) : (
                      aiToolsLog.map((tool) => {
                        const isExpanded = aiExpandedToolId === tool.id;
                        return (
                          <div
                            key={tool.id}
                            className={`bi-ai-tool-card ${isExpanded ? 'active' : ''}`}
                          >
                            <div
                              className="bi-ai-tool-summary"
                              onClick={() => setAiExpandedToolId(isExpanded ? null : tool.id)}
                            >
                              <div className="bi-ai-tool-name-box">
                                <span className="bi-ai-tool-badge">{tool.name}</span>
                                <span className="bi-ai-tool-title-text">{tool.title}</span>
                              </div>
                              <div className="bi-ai-tool-meta">
                                {tool.execution_time_ms && (
                                  <span className="bi-ai-tool-time">{tool.execution_time_ms}ms</span>
                                )}
                                <span className="bi-ai-tool-status-tag">
                                  ✓ Sucesso
                                </span>
                                <FiChevronDown
                                  size={14}
                                  style={{
                                    transform: isExpanded ? 'rotate(180deg)' : 'none',
                                    transition: 'transform 0.2s ease'
                                  }}
                                />
                              </div>
                            </div>

                            {isExpanded && (
                              <div className="bi-ai-tool-details">
                                <p className="text-muted small mb-2">{tool.description}</p>

                                {tool.id === 'tool_scan_basket' && tool.output?.ranking_produtos_alavancadores && (
                                  <div className="bi-ai-tool-basket-preview mb-3">
                                    <div className="d-flex justify-content-between align-items-center mb-2 flex-wrap gap-2">
                                      <span className="fw-bold text-warning-emphasis small d-flex align-items-center gap-1">
                                        🧲 {tool.output.maior_alavanca_cesta || 'Ranking da Matriz de Co-ocorrência por Cupom'}
                                      </span>
                                      <span className="badge bg-warning-subtle text-warning-emphasis border border-warning-subtle" style={{ fontSize: '0.72rem' }}>
                                        {tool.output.metodo_analise || 'Matriz de Cesta PCMOV'}
                                      </span>
                                    </div>
                                    <div className="bi-ai-tool-basket-grid">
                                      {tool.output.ranking_produtos_alavancadores.map((item, iIdx) => (
                                        <div key={iIdx} className="bi-ai-tool-basket-card">
                                          <div className="d-flex justify-content-between align-items-start gap-2 mb-1">
                                            <strong className="text-dark small">{item.posicao}: {item.produto}</strong>
                                            <span className="badge bg-primary-subtle text-primary fw-bold" style={{ fontSize: '0.7rem' }}>
                                              {item.media_produtos_por_venda}
                                            </span>
                                          </div>
                                          <div className="small text-muted mb-1" style={{ fontSize: '0.75rem' }}>
                                            Repetitividade: <strong>{item.repetitividade_vendas}</strong> • TM Cesta: <strong>{item.ticket_medio_cesta}</strong> • Atração: <strong>{item.taxa_atracao}</strong>
                                          </div>
                                          {item.companheiros_frequentes && item.companheiros_frequentes.length > 0 && (
                                            <div className="bi-ai-tool-companions-line">
                                              <span className="text-success fw-bold me-1">🤝 Mais levados juntos:</span>
                                              <span>{item.companheiros_frequentes.slice(0, 2).join(' | ')}</span>
                                            </div>
                                          )}
                                        </div>
                                      ))}
                                    </div>
                                  </div>
                                )}

                                <div className="row g-2">
                                  <div className="col-md-6">
                                    <strong className="text-dark small d-block mb-1">
                                      📥 Parâmetros de Entrada (Input):
                                    </strong>
                                    <pre>{JSON.stringify(tool.input, null, 2)}</pre>
                                  </div>
                                  <div className="col-md-6">
                                    <strong className="text-dark small d-block mb-1">
                                      📤 Retorno Estruturado (Output):
                                    </strong>
                                    <pre>{JSON.stringify(tool.output, null, 2)}</pre>
                                  </div>
                                </div>
                              </div>
                            )}
                          </div>
                        );
                      })
                    )}
                  </div>

                  {/* Seção 2: Validação dos Parâmetros Atualizados pela IA */}
                  {aiUpdatedParams && (
                    <div className="bi-ai-sync-section">
                      <div className="bi-ai-sync-header">
                        <h6 className="bi-ai-sync-title">
                          <FiActivity size={16} className="text-success" />
                          Parâmetros Comerciais Calibrados pela IA
                        </h6>
                        <span className="bi-ai-sync-badge-live">
                          <FiCheck size={12} /> Pronto para Sincronização
                        </span>
                      </div>

                      <div className="bi-ai-sync-grid">
                        <div className="bi-ai-sync-card">
                          <span className="bi-ai-sync-label text-primary">
                            <FiTarget size={13} /> Perfil Comercial
                          </span>
                          <span className="bi-ai-sync-value">{aiUpdatedParams.commercial_profile}</span>
                        </div>

                        <div className="bi-ai-sync-card">
                          <span className="bi-ai-sync-label text-danger">
                            <FiAlertCircle size={13} /> Gargalo Diagnosticado
                          </span>
                          <span className="bi-ai-sync-value">{aiUpdatedParams.bottleneck_indicator}</span>
                        </div>

                        <div className="bi-ai-sync-card">
                          <span className="bi-ai-sync-label text-info">
                            <FiZap size={13} /> Alavanca Tática
                          </span>
                          <span className="bi-ai-sync-value">{aiUpdatedParams.tactical_lever}</span>
                        </div>

                        <div className="bi-ai-sync-card">
                          <span className="bi-ai-sync-label text-purple">
                            <FiPackage size={13} /> Meta Diária em Peças
                          </span>
                          <span className="bi-ai-sync-value">{aiUpdatedParams.daily_pieces_target}</span>
                        </div>

                        <div className="bi-ai-sync-card">
                          <span className="bi-ai-sync-label text-success">
                            <FiTrendingUp size={13} /> Salto Estimado no TM
                          </span>
                          <span className="bi-ai-sync-value">{aiUpdatedParams.potential_tm_increase}</span>
                        </div>
                      </div>

                      {/* Roteiro / Pitch Comercial Extraído */}
                      {aiUpdatedParams.pitch_script && (
                        <div className="bi-ai-pitch-box mb-0">
                          <div className="bi-ai-pitch-header">
                            <div className="bi-ai-pitch-title">
                              <FiAward size={14} /> Roteiro / Pitch Comercial Gerado
                            </div>
                            <button
                              type="button"
                              className="btn btn-sm btn-link text-purple p-0 d-flex align-items-center gap-1 text-decoration-none fw-bold"
                              style={{ fontSize: '0.78rem', cursor: 'pointer' }}
                              onClick={() => {
                                copyToClipboard(aiUpdatedParams.pitch_script, () => {
                                  setAiCopiedScriptModal(true);
                                  setTimeout(() => setAiCopiedScriptModal(false), 2500);
                                });
                              }}
                            >
                              {aiCopiedScriptModal ? (
                                <>
                                  <FiCheck className="text-success" size={13} />
                                  <span className="text-success">Copiado!</span>
                                </>
                              ) : (
                                <>
                                  <FiCopy size={13} />
                                  <span>Copiar Pitch</span>
                                </>
                              )}
                            </button>
                          </div>
                          <p className="bi-ai-pitch-text">
                            "{aiUpdatedParams.pitch_script}"
                          </p>
                        </div>
                      )}
                    </div>
                  )}

                  {/* Seção 3: Prévia do Relatório em Markdown */}
                  {aiCurrentReport && (
                    <div className="bi-ai-markdown-box">
                      <div className="d-flex align-items-center justify-content-between mb-2 border-bottom pb-2">
                        <span className="fw-bold text-dark small d-flex align-items-center gap-1">
                          <FiCode size={14} /> Parecer Executivo Completo da IA
                        </span>
                        <button
                          type="button"
                          className="btn btn-sm btn-outline-secondary py-0 px-2"
                          style={{ fontSize: '0.75rem' }}
                          onClick={() => {
                            copyToClipboard(aiCurrentReport, () => {
                              alert('Plano completo copiado para a área de transferência!');
                            });
                          }}
                        >
                          <FiCopy size={12} className="me-1" /> Copiar Markdown
                        </button>
                      </div>
                      <div
                        className="bi-ai-markdown-content"
                        dangerouslySetInnerHTML={{ __html: marked.parse(aiCurrentReport) }}
                      />
                    </div>
                  )}
                </>
              ) : (
                /* Aba de Personalização do Prompt & Provedor */
                <div className="bi-ai-prompt-editor-section">
                  {/* Barra de Provedor */}
                  <div className="bi-ai-provider-bar">
                    <div className="bi-ai-provider-select-group">
                      <FiCpu size={16} className="text-primary" />
                      <label className="small fw-bold text-dark mb-0">Provedor de Inteligência Artificial:</label>
                      <select
                        className="bi-ai-provider-select"
                        value={aiSelectedProviderId}
                        onChange={(e) => setAiSelectedProviderId(e.target.value)}
                      >
                        {aiProviders.length === 0 ? (
                          <option value="">Padrão do Sistema</option>
                        ) : (
                          aiProviders.map((p) => (
                            <option key={p.id} value={p.id}>
                              {p.name} ({p.default_model || p.provider_type}) {p.is_default ? '★ Padrão' : ''}
                            </option>
                          ))
                        )}
                      </select>
                    </div>

                    <div className="d-flex align-items-center gap-2">
                      {aiRawTemplate && (
                        <button
                          type="button"
                          className="btn btn-sm btn-outline-secondary"
                          onClick={() => setAiPromptText(aiRawTemplate)}
                          title="Carregar o modelo padrão com as tags {{variáveis}} para edição estruturada"
                        >
                          Template com Tags
                        </button>
                      )}
                      <button
                        type="button"
                        className="btn btn-sm btn-outline-secondary"
                        onClick={() => setAiPromptText(aiDefaultCompiledPrompt)}
                        title="Restaurar o prompt original compilado com as métricas da colaboradora"
                      >
                        Restaurar Prompt Padrão
                      </button>
                    </div>
                  </div>

                  {/* Área de Edição do Prompt */}
                  <div className="bi-ai-prompt-textarea-box">
                    <label className="small fw-bold text-secondary mb-1 d-flex justify-content-between">
                      <span>Prompt Direcionador da Análise Comercial:</span>
                      <span className="text-muted">
                        {aiPromptText.length} caracteres • {aiPromptText.trim().split(/\s+/).filter(Boolean).length} palavras
                      </span>
                    </label>
                    <textarea
                      className="bi-ai-prompt-textarea"
                      value={aiPromptText}
                      onChange={(e) => setAiPromptText(e.target.value)}
                      placeholder="Escreva ou personalize as instruções do prompt que a IA irá executar..."
                    />

                    <div className="bi-ai-prompt-helpers">
                      <span className="small text-muted me-1">Variáveis disponíveis:</span>
                      {[
                        '{{collab_name}}',
                        '{{target}}',
                        '{{realized}}',
                        '{{gap}}',
                        '{{ticket_medio}}',
                        '{{pa}}',
                        '{{via}}',
                        '{{remaining_days}}'
                      ].map((tag) => (
                        <span
                          key={tag}
                          className="bi-ai-tag-pill"
                          title="Clique para inserir no prompt"
                          onClick={() => setAiPromptText((prev) => prev + ' ' + tag)}
                        >
                          {tag}
                        </span>
                      ))}
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Rodapé do Modal com Ações */}
            <div className="bi-ai-modal-footer">
              <div className="bi-ai-modal-footer-left">
                {aiModalTab === 'execution' ? (
                  <button
                    type="button"
                    className="bi-ai-btn-secondary"
                    onClick={() => setAiModalTab('prompt')}
                  >
                    <FiSliders size={14} />
                    <span>Personalizar Prompt</span>
                  </button>
                ) : (
                  <button
                    type="button"
                    className="bi-ai-btn-secondary"
                    onClick={() => setAiModalTab('execution')}
                  >
                    <FiZap size={14} />
                    <span>Ver Execução & Ferramentas</span>
                  </button>
                )}

                <button
                  type="button"
                  className="bi-ai-btn-secondary"
                  disabled={aiGenerating}
                  onClick={() => runLiveAIAnalysis(selectedCollab, aiPromptText, aiSelectedProviderId)}
                  title="Executar novamente a análise mantendo ou alterando o prompt"
                >
                  <FiRefreshCw className={aiGenerating ? 'bi-spin' : ''} size={14} />
                  <span>Re-executar Análise</span>
                </button>
              </div>

              <div className="bi-ai-modal-footer-right">
                <button
                  type="button"
                  className="bi-ai-btn-secondary"
                  onClick={() => setIsAIModalOpen(false)}
                >
                  Fechar
                </button>

                {aiUpdatedParams && (
                  <button
                    type="button"
                    className="bi-ai-btn-success"
                    onClick={handleApplyAIParameters}
                    title="Salvar e sincronizar estes parâmetros e plano tático na meta da colaboradora"
                  >
                    <FiCheck size={14} />
                    <span>Aplicar Parâmetros & Sincronizar</span>
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Modal Interativo de Análise Executiva ao Vivo com IA para Metas Concatenadas */}
      {isConcatAIModalOpen && concatenatedStats && (
        <div className="bi-ai-modal-overlay" onClick={() => setIsConcatAIModalOpen(false)}>
          <div className="bi-ai-modal-container" onClick={(e) => e.stopPropagation()}>
            {/* Cabeçalho do Modal */}
            <div className="bi-ai-modal-header">
              <div className="bi-ai-modal-title-group">
                <div className="bi-ai-modal-icon-badge">
                  <FiCpu />
                </div>
                <div>
                  <h4 className="bi-ai-modal-title">Diagnóstico Executivo Consolidado com IA</h4>
                  <div className="bi-ai-modal-subtitle">
                    <span><strong>{concatenatedStats.selectedGoals.length} Metas / Períodos Selecionados</strong></span>
                    <span>•</span>
                    <span>Meta: {formatBRL(concatenatedStats.totalTarget)}</span>
                    <span>•</span>
                    <span>Realizado: {formatBRL(concatenatedStats.totalRealized)} ({concatenatedStats.pct}%)</span>
                    <span>•</span>
                    <span>Gap: {formatBRL(concatenatedStats.totalGap)}</span>
                  </div>
                </div>
              </div>
              <button
                type="button"
                className="bi-ai-modal-close-btn"
                onClick={() => setIsConcatAIModalOpen(false)}
                title="Fechar Painel de IA"
              >
                <FiX size={18} />
              </button>
            </div>

            {/* Abas de Navegação */}
            <div className="bi-ai-modal-tabs">
              <button
                type="button"
                className={`bi-ai-modal-tab-btn ${concatAIModalTab === 'execution' ? 'active' : ''}`}
                onClick={() => setConcatAIModalTab('execution')}
              >
                <FiZap size={14} />
                <span>Execução & Ferramentas em Tempo Real</span>
              </button>
              <button
                type="button"
                className={`bi-ai-modal-tab-btn ${concatAIModalTab === 'prompt' ? 'active' : ''}`}
                onClick={() => setConcatAIModalTab('prompt')}
              >
                <FiSliders size={14} />
                <span>Personalizar Prompt & Provedor</span>
              </button>
            </div>

            {/* Corpo do Modal */}
            <div className="bi-ai-modal-body">
              {concatAIModalTab === 'execution' ? (
                <>
                  {/* Stepper de Progresso Visual */}
                  <div className="bi-ai-stepper-box">
                    <div className="bi-ai-stepper">
                      <div
                        className="bi-ai-stepper-progress-fill"
                        style={{ width: `${Math.min(100, Math.max(0, (concatAIExecutionStep / 4) * 100))}%` }}
                      />
                      <div className={`bi-ai-step-item ${concatAIExecutionStep >= 1 ? (concatAIExecutionStep > 1 ? 'completed' : 'active') : ''}`}>
                        <div className="bi-ai-step-circle">
                          {concatAIExecutionStep > 1 ? <FiCheck size={16} /> : 1}
                        </div>
                        <span className="bi-ai-step-title">1. KPIs Globais</span>
                      </div>
                      <div className={`bi-ai-step-item ${concatAIExecutionStep >= 2 ? (concatAIExecutionStep > 2 ? 'completed' : 'active') : ''}`}>
                        <div className="bi-ai-step-circle">
                          {concatAIExecutionStep > 2 ? <FiCheck size={16} /> : 2}
                        </div>
                        <span className="bi-ai-step-title">2. Dispersão & Metas</span>
                      </div>
                      <div className={`bi-ai-step-item ${concatAIExecutionStep >= 3 ? (concatAIExecutionStep > 3 ? 'completed' : 'active') : ''}`}>
                        <div className="bi-ai-step-circle">
                          {concatAIExecutionStep > 3 ? <FiCheck size={16} /> : 3}
                        </div>
                        <span className="bi-ai-step-title">3. Cesta & Tendência</span>
                      </div>
                      <div className={`bi-ai-step-item ${concatAIExecutionStep >= 4 ? (!concatAIGenerating ? 'completed' : 'active') : ''}`}>
                        <div className="bi-ai-step-circle">
                          {concatAIExecutionStep >= 4 && !concatAIGenerating ? <FiCheck size={16} /> : 4}
                        </div>
                        <span className="bi-ai-step-title">4. Veredito Executivo</span>
                      </div>
                    </div>
                  </div>

                  {/* Banner de Status Atual */}
                  <div className={`bi-ai-status-banner ${concatAIGenerating ? 'running' : 'done'}`}>
                    <div className="d-flex align-items-center gap-2">
                      {concatAIGenerating ? (
                        <FiRefreshCw className="bi-spin text-primary" size={16} />
                      ) : (
                        <FiCheckCircle className="text-success" size={16} />
                      )}
                      <span className="fw-semibold">{concatAIStatusMessage}</span>
                    </div>
                    {concatAIProviderInfo && (
                      <div className="small text-muted d-flex align-items-center gap-1">
                        <FiCpu size={13} />
                        <span>{concatAIProviderInfo.name} ({concatAIProviderInfo.model})</span>
                      </div>
                    )}
                  </div>

                  {/* Seção 1: Ferramentas Executadas (Tools Log) */}
                  <div className="bi-ai-tools-section">
                    <div className="bi-ai-tools-header">
                      <h6 className="bi-ai-tools-title">
                        <FiTerminal size={15} />
                        Ferramentas Utilizadas na Auditoria Consolidada
                      </h6>
                      <span className="badge bg-light text-secondary border">
                        {concatAIToolsLog.length} ferramentas acionadas
                      </span>
                    </div>

                    {concatAIToolsLog.length === 0 ? (
                      <div className="p-3 text-center text-muted small">
                        Carregando ferramentas de análise...
                      </div>
                    ) : (
                      concatAIToolsLog.map((tool) => {
                        const isExpanded = concatAIExpandedToolId === tool.id;
                        return (
                          <div
                            key={tool.id}
                            className={`bi-ai-tool-card ${isExpanded ? 'active' : ''}`}
                          >
                            <div
                              className="bi-ai-tool-summary"
                              onClick={() => setConcatAIExpandedToolId(isExpanded ? null : tool.id)}
                            >
                              <div className="bi-ai-tool-name-box">
                                <span className="bi-ai-tool-badge">{tool.name}</span>
                                <span className="bi-ai-tool-title-text">{tool.title}</span>
                              </div>
                              <div className="bi-ai-tool-meta">
                                {tool.execution_time_ms && (
                                  <span className="bi-ai-tool-time">{tool.execution_time_ms}ms</span>
                                )}
                                <span className="bi-ai-tool-status-tag">
                                  ✓ Sucesso
                                </span>
                                <FiChevronDown
                                  size={14}
                                  style={{
                                    transform: isExpanded ? 'rotate(180deg)' : 'none',
                                    transition: 'transform 0.2s ease'
                                  }}
                                />
                              </div>
                            </div>

                            {isExpanded && (
                              <div className="bi-ai-tool-details">
                                <p className="text-muted small mb-2">{tool.description}</p>

                                {tool.id === 'tool_scan_basket' && tool.output?.ranking_produtos_alavancadores && (
                                  <div className="bi-ai-tool-basket-preview mb-3">
                                    <div className="d-flex justify-content-between align-items-center mb-2 flex-wrap gap-2">
                                      <span className="fw-bold text-warning-emphasis small d-flex align-items-center gap-1">
                                        🧲 {tool.output.maior_alavanca_cesta || 'Ranking da Matriz de Co-ocorrência por Cupom'}
                                      </span>
                                      <span className="badge bg-warning-subtle text-warning-emphasis border border-warning-subtle" style={{ fontSize: '0.72rem' }}>
                                        {tool.output.metodo_analise || 'Matriz de Cesta PCMOV'}
                                      </span>
                                    </div>
                                    <div className="bi-ai-tool-basket-grid">
                                      {tool.output.ranking_produtos_alavancadores.map((item, iIdx) => (
                                        <div key={iIdx} className="bi-ai-tool-basket-card">
                                          <div className="d-flex justify-content-between align-items-start gap-2 mb-1">
                                            <strong className="text-dark small">{item.posicao}: {item.produto}</strong>
                                            <span className="badge bg-primary-subtle text-primary fw-bold" style={{ fontSize: '0.7rem' }}>
                                              {item.media_produtos_por_venda}
                                            </span>
                                          </div>
                                          <div className="small text-muted mb-1" style={{ fontSize: '0.75rem' }}>
                                            Repetitividade: <strong>{item.repetitividade_vendas}</strong> • TM Cesta: <strong>{item.ticket_medio_cesta}</strong> • Atração: <strong>{item.taxa_atracao}</strong>
                                          </div>
                                          {item.companheiros_frequentes && item.companheiros_frequentes.length > 0 && (
                                            <div className="bi-ai-tool-companions-line">
                                              <span className="text-success fw-bold me-1">🤝 Mais levados juntos:</span>
                                              <span>{item.companheiros_frequentes.slice(0, 2).join(' | ')}</span>
                                            </div>
                                          )}
                                        </div>
                                      ))}
                                    </div>
                                  </div>
                                )}

                                <div className="row g-2">
                                  <div className="col-md-6">
                                    <strong className="text-dark small d-block mb-1">
                                      📥 Parâmetros de Entrada (Input):
                                    </strong>
                                    <pre>{JSON.stringify(tool.input, null, 2)}</pre>
                                  </div>
                                  <div className="col-md-6">
                                    <strong className="text-dark small d-block mb-1">
                                      📤 Retorno Estruturado (Output):
                                    </strong>
                                    <pre>{JSON.stringify(tool.output, null, 2)}</pre>
                                  </div>
                                </div>
                              </div>
                            )}
                          </div>
                        );
                      })
                    )}
                  </div>

                  {/* Seção 2: Validação dos Parâmetros Atualizados pela IA */}
                  {concatAIUpdatedParams && (
                    <div className="bi-ai-sync-section">
                      <div className="bi-ai-sync-header">
                        <h6 className="bi-ai-sync-title">
                          <FiActivity size={16} className="text-success" />
                          Diretrizes Estratégicas Calibradas pela IA
                        </h6>
                        <span className="bi-ai-sync-badge-live">
                          <FiCheck size={12} /> Consolidado Executivo
                        </span>
                      </div>

                      <div className="bi-ai-sync-grid">
                        <div className="bi-ai-sync-card">
                          <span className="bi-ai-sync-label text-primary">
                            <FiTarget size={13} /> Perfil do Grupo
                          </span>
                          <span className="bi-ai-sync-value">{concatAIUpdatedParams.commercial_profile || 'Consolidado Multi-Metas'}</span>
                        </div>

                        <div className="bi-ai-sync-card">
                          <span className="bi-ai-sync-label text-danger">
                            <FiAlertCircle size={13} /> Gargalo Identificado
                          </span>
                          <span className="bi-ai-sync-value">{concatAIUpdatedParams.bottleneck_indicator}</span>
                        </div>

                        <div className="bi-ai-sync-card">
                          <span className="bi-ai-sync-label text-info">
                            <FiZap size={13} /> Alavanca Tática
                          </span>
                          <span className="bi-ai-sync-value">{concatAIUpdatedParams.tactical_lever}</span>
                        </div>

                        <div className="bi-ai-sync-card">
                          <span className="bi-ai-sync-label text-purple">
                            <FiPackage size={13} /> Ritmo Diário / Peças
                          </span>
                          <span className="bi-ai-sync-value">{concatAIUpdatedParams.daily_pieces_target}</span>
                        </div>

                        <div className="bi-ai-sync-card">
                          <span className="bi-ai-sync-label text-success">
                            <FiTrendingUp size={13} /> Potencial de Incremento
                          </span>
                          <span className="bi-ai-sync-value">{concatAIUpdatedParams.potential_tm_increase}</span>
                        </div>
                      </div>

                      {/* Roteiro / Pitch de Liderança Extraído */}
                      {concatAIUpdatedParams.pitch_script && (
                        <div className="bi-ai-pitch-box mb-0">
                          <div className="bi-ai-pitch-header">
                            <div className="bi-ai-pitch-title">
                              <FiAward size={14} /> Briefing de Alinhamento da Liderança de Loja
                            </div>
                            <button
                              type="button"
                              className="btn btn-sm btn-link text-purple p-0 d-flex align-items-center gap-1 text-decoration-none fw-bold"
                              style={{ fontSize: '0.78rem', cursor: 'pointer' }}
                              onClick={() => {
                                copyToClipboard(concatAIUpdatedParams.pitch_script, () => {
                                  setConcatAICopiedPitch(true);
                                  setTimeout(() => setConcatAICopiedPitch(false), 2500);
                                });
                              }}
                            >
                              {concatAICopiedPitch ? (
                                <>
                                  <FiCheck className="text-success" size={13} />
                                  <span className="text-success">Copiado!</span>
                                </>
                              ) : (
                                <>
                                  <FiCopy size={13} />
                                  <span>Copiar Briefing</span>
                                </>
                              )}
                            </button>
                          </div>
                          <p className="bi-ai-pitch-text">
                            "{concatAIUpdatedParams.pitch_script}"
                          </p>
                        </div>
                      )}
                    </div>
                  )}

                  {/* Seção 3: Prévia do Relatório em Markdown */}
                  {concatAIReport && (
                    <div className="bi-ai-markdown-box">
                      <div className="d-flex align-items-center justify-content-between mb-2 border-bottom pb-2">
                        <span className="fw-bold text-dark small d-flex align-items-center gap-1">
                          <FiCode size={14} /> Parecer Executivo Completo da IA
                        </span>
                        <button
                          type="button"
                          className="btn btn-sm btn-outline-secondary py-0 px-2"
                          style={{ fontSize: '0.75rem' }}
                          onClick={() => {
                            copyToClipboard(concatAIReport, () => {
                              alert('Plano executivo copiado para a área de transferência!');
                            });
                          }}
                        >
                          <FiCopy size={12} className="me-1" /> Copiar Markdown
                        </button>
                      </div>
                      <div
                        className="bi-ai-markdown-content"
                        dangerouslySetInnerHTML={{ __html: marked.parse(concatAIReport) }}
                      />
                    </div>
                  )}
                </>
              ) : (
                /* Aba de Personalização do Prompt & Provedor */
                <div className="bi-ai-prompt-editor-section">
                  {/* Barra de Provedor */}
                  <div className="bi-ai-provider-bar">
                    <div className="bi-ai-provider-select-group">
                      <FiCpu size={16} className="text-primary" />
                      <label className="small fw-bold text-dark mb-0">Provedor de Inteligência Artificial:</label>
                      <select
                        className="bi-ai-provider-select"
                        value={concatAISelectedProviderId}
                        onChange={(e) => setConcatAISelectedProviderId(e.target.value)}
                      >
                        {concatAIProviders.length === 0 ? (
                          <option value="">Padrão do Sistema</option>
                        ) : (
                          concatAIProviders.map((p) => (
                            <option key={p.id} value={p.id}>
                              {p.name} ({p.default_model || p.provider_type}) {p.is_default ? '★ Padrão' : ''}
                            </option>
                          ))
                        )}
                      </select>
                    </div>

                    <div className="d-flex align-items-center gap-2">
                      {concatAIRawTemplate && (
                        <button
                          type="button"
                          className="btn btn-sm btn-outline-secondary"
                          onClick={() => setConcatAIPromptText(concatAIRawTemplate)}
                          title="Carregar o modelo padrão com as tags {{variáveis}} para edição estruturada"
                        >
                          Template com Tags
                        </button>
                      )}
                      <button
                        type="button"
                        className="btn btn-sm btn-outline-secondary"
                        onClick={() => setConcatAIPromptText(concatAIDefaultCompiledPrompt)}
                        title="Restaurar o prompt original compilado com as métricas consolidadas"
                      >
                        Restaurar Prompt Padrão
                      </button>
                    </div>
                  </div>

                  {/* Área de Edição do Prompt */}
                  <div className="bi-ai-prompt-textarea-box">
                    <label className="small fw-bold text-secondary mb-1 d-flex justify-content-between">
                      <span>Prompt Direcionador da Análise Executiva:</span>
                      <span className="text-muted">
                        {concatAIPromptText.length} caracteres • {concatAIPromptText.trim().split(/\s+/).filter(Boolean).length} palavras
                      </span>
                    </label>
                    <textarea
                      className="bi-ai-prompt-textarea"
                      value={concatAIPromptText}
                      onChange={(e) => setConcatAIPromptText(e.target.value)}
                      placeholder="Escreva ou personalize as instruções do prompt que a IA irá executar..."
                      rows={12}
                    />

                    <div className="bi-ai-prompt-helpers">
                      <span className="small text-muted me-1">Variáveis disponíveis:</span>
                      {[
                        '{{collab_names}}',
                        '{{periods}}',
                        '{{goals_count}}',
                        '{{target}}',
                        '{{realized}}',
                        '{{pct}}',
                        '{{gap}}',
                        '{{ticket_medio}}',
                        '{{pa}}',
                        '{{via}}',
                        '{{remaining_days}}',
                        '{{required_daily_rate}}',
                        '{{isolated_summary}}'
                      ].map((tag) => (
                        <span
                          key={tag}
                          className="bi-ai-tag-pill"
                          title="Clique para inserir no prompt"
                          onClick={() => setConcatAIPromptText((prev) => prev + ' ' + tag)}
                        >
                          {tag}
                        </span>
                      ))}
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Rodapé do Modal com Ações */}
            <div className="bi-ai-modal-footer">
              <div className="bi-ai-modal-footer-left">
                {concatAIModalTab === 'execution' ? (
                  <button
                    type="button"
                    className="bi-ai-btn-secondary"
                    onClick={() => setConcatAIModalTab('prompt')}
                  >
                    <FiSliders size={14} />
                    <span>Personalizar Prompt</span>
                  </button>
                ) : (
                  <button
                    type="button"
                    className="bi-ai-btn-secondary"
                    onClick={() => setConcatAIModalTab('execution')}
                  >
                    <FiZap size={14} />
                    <span>Ver Execução & Ferramentas</span>
                  </button>
                )}

                <button
                  type="button"
                  className="bi-ai-btn-secondary"
                  disabled={concatAIGenerating}
                  onClick={() => runConcatAIAnalysis(concatAIPromptText, concatAISelectedProviderId)}
                  title="Executar novamente a análise mantendo ou alterando o prompt"
                >
                  <FiRefreshCw className={concatAIGenerating ? 'bi-spin' : ''} size={14} />
                  <span>Re-executar Diagnóstico</span>
                </button>
              </div>

              <div className="bi-ai-modal-footer-right">
                <button
                  type="button"
                  className="bi-ai-btn-secondary"
                  onClick={() => setIsConcatAIModalOpen(false)}
                >
                  Fechar
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default BIMetas;
